import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { PaymentConfirmationPreview, partyAndRowsForInvoiceDeposit } from "./paymentConfirmation";
import useDocumentTitle from "../useDocumentTitle";
import DraggableModal from "./DraggableModal";
import { LOGO_B64 } from "./SalesReports";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkStatusDialog from "./BulkStatusDialog";
import SearchSelect from "./SearchSelect";
import EmailDialog from "./EmailDialog";
import ButtonMenu from "./ButtonMenu";
import DocPreviewModal from "./DocPreviewModal";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import useDragReorder, { DragHandle } from "./useDragReorder";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadCompanyProfile, composeEmailBody } from "./CompanyProfile";
import { SWEETLEAF_LOGO_B64, GFI_ISOTIPO_B64, SL_ISOTIPO_B64, AGROCAP_LOGO_B64, ARTURO_SIGNATURE_B64 } from "./brandLogos";
import SweetLeafSettingsPanel from "./SweetLeafSettingsPanel";
import SLPaymentsRegistry from "./SLPaymentsRegistry";
import { derivePalletStatus, deriveDeliveryStatus } from "./saleStatusCalc";
import {
  isConfigured as driveConfigured,
  isSignedIn as driveSignedIn,
  signIn as driveSignIn,
  ensurePath as driveEnsurePath,
  folderViewUrl as driveFolderViewUrl,
  rootPathSegments,
  seasonFolderName,
  embarqueFolderName,
  invoiceFolderName,
  uploadFile as driveUploadFile,
} from "../googleDrive";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";
import { sendEmail } from "../resendApi";
import { appendSlDisclosure, nonFourSeasonsRoutingBlockedMessage, isFourSeasonsClientName } from "./emailHelpers";
import { uploadFileToStorage, removeFromStorage } from "../storageImages";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase } from "../supabaseDB";
import { computeLotBalances, lotBalanceMismatches } from "./lotBalanceCheck";

/* Bucket reused from ShipperPortal — already provisioned with
   public RLS reads + authenticated writes. SL invoice attachments
   live under sweetleaf/{invoiceNumber|id}/{slot}-{ts}.{ext}. */
var SL_DOCS_BUCKET = "shipment-docs";

/* Safe path segment helper — strip accents + reduce to alphanumeric
   + dashes so Storage keys never reject. Same shape as ShipperPortal. */
function slSafeSeg(s) {
  return String(s == null ? "" : s)
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .substring(0, 80) || "doc";
}

function slExtFromFile(file) {
  if (file && file.name) {
    var i = file.name.lastIndexOf(".");
    if (i > 0 && i < file.name.length - 1) return file.name.substring(i + 1).toLowerCase();
  }
  if (file && file.type) {
    var t = String(file.type).toLowerCase();
    if (t === "application/pdf") return "pdf";
    if (t.indexOf("png") >= 0) return "png";
    if (t.indexOf("jpeg") >= 0 || t.indexOf("jpg") >= 0) return "jpg";
  }
  return "pdf";
}
/* Per the user spec the Invoices Summary export breaks "Advance
   Financing Fee" and "Other Fees" out into their own columns. The
   bucket "Other Fees" here means anything that is neither the Advance
   Financing Fee nor in the canonical fee list. The whole-fees-bucket
   "Fees" column collects everything except Advance Financing Fee. */
function isAdvanceFinancingFeeInv(fe) {
  var n = fe && fe.feeType ? String(fe.feeType).trim().toLowerCase() : "";
  return n === "advance financing fee";
}
var INV_KNOWN_FEE_NAMES = [
  "pti labeling fee", "qc inspection", "warehouse fees", "warehouse fee",
  "wire transfer fee", "late fee", "factoring fee", "freight",
];
function isKnownFeeName(fe) {
  var n = fe && fe.feeType ? String(fe.feeType).trim().toLowerCase() : "";
  return INV_KNOWN_FEE_NAMES.indexOf(n) >= 0;
}
function feeNameInv(fe) {
  if (!fe) return "";
  return fe.feeType === "__custom__" ? (fe.feeCustom || "Custom Fee") : (fe.feeType || "Fee");
}

/* Full presentation/pack/label for a line — same fallback contract
   as BillRegistry.resolveProductDesc: explicit productDescription
   first, otherwise look up the product by productId in gfi-products
   and rebuild it. Avoids the "shows only commodity" bug that the
   simple `productDescription || productName` fallback caused. */
function resolveInvLineDesc(line) {
  if (!line) return "";
  if (line.productDescription) return line.productDescription;
  if (!line.productId) return "";
  var prod = (function () { try { return JSON.parse(localStorage.getItem("gfi-products") || "[]"); } catch (e) { return []; } })().find(function (p) { return String(p.id) === String(line.productId) || String(p._uuid) === String(line.productId); });
  if (!prod) return "";
  var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
  return (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
}

/* Build the list of Export Cert entries for a printed doc. Spec:
     - Only ORGANIC product lines on the invoice count (USDA cert
       applies to OG only — CV lines stay out of the printed block).
     - Cert lookup priority per invoice line:
         line.exportCertNumber → line.exportCertManual
         → line.exportCertificate / line.importCertificate (legacy
           aliases some imports used)
         → matching shipment line's cert (when the invoice line
           inherited from a bill that didn't propagate the field).
     - Group certs by product (productBase / productName). If EVERY
       organic product carries the same single cert, the output
       collapses to one line `[cert]`. Otherwise each entry reads
       `<Product> — <cert>` so the auditor sees which cert covers
       which commodity.
   Returns [] when no organic lines have a cert — caller renders
   "—" or skips the row. */
function buildExportCertEntries(invoice, shipment) {
  var lines = (invoice && invoice.lines) || [];
  var shipLines = (shipment && shipment.lines) || [];
  /* shipment-line cert lookup keyed by lowercased product name —
     fallback when the invoice line itself lost the cert during the
     bill → invoice handoff. */
  var shipCertByProduct = {};
  shipLines.forEach(function (sl) {
    var key = String(sl.productBase || sl.productName || "").toLowerCase().trim();
    if (!key) return;
    var c = sl.exportCertNumber || sl.exportCertManual || "";
    if (c && !shipCertByProduct[key]) shipCertByProduct[key] = c;
  });
  var perProduct = {};
  var allCerts = {};
  lines.forEach(function (l) {
    var cat = String(l.category || "").toLowerCase();
    if (cat !== "organic" && cat !== "og") return;
    var product = (l.productBase || l.productName || "").trim();
    if (!product) return;
    var cert = (l.exportCertNumber || l.exportCertManual || l.exportCertificate || l.importCertificate || shipCertByProduct[product.toLowerCase()] || "").trim();
    if (!cert) return;
    if (!perProduct[product]) perProduct[product] = {};
    perProduct[product][cert] = true;
    allCerts[cert] = true;
  });
  var products = Object.keys(perProduct);
  if (products.length === 0) return [];
  var uniqueCerts = Object.keys(allCerts);
  /* Every organic product shares the same cert → collapse to a
     single line (matches the user-described format). */
  if (uniqueCerts.length === 1) return [uniqueCerts[0]];
  return products.map(function (p) {
    return p + " — " + Object.keys(perProduct[p]).join(", ");
  });
}

/* ======================== TRANSLATIONS ======================== */
const T = {
  en: {
    title: "SweetLeaf Produce", subtitle: "Invoices & documents for Four Seasons",
    add: "New invoice", edit: "Edit invoice",
    search: "Search by INV#, PU#, lot, or PO...",
    showing: "Showing", of: "of", total: "invoices", noResults: "No invoices match",
    invoiceNum: "Invoice #", puNum: "PU #", invNum: "INV #", gfiPuNum: "GFI PU#",
    pickup: "Pickup (BOL)", thermographLabel: "Temp Recorder #",
    date: "Date", dueDate: "Due Date",
    client: "Client", selectClient: "Select client",
    clientRef: "Customer Ref # (PO)", clientRefColLine1: "Customer", clientRefColLine2: "Ref # (PO)", lot: "Lot", shipper: "Shipper", thermograph: "Temp Recorder #",
    commPerCase: "Comm/Case $", commTotal: "Comm Total", advanceFinancingFee: "Advance Financing Fee",
    docsSection: "Documents", xeroInvoicePdf: "Xero Invoice PDF", bolPdf: "BOL (Bill of Lading)", uploadPdf: "Upload PDF", removeFile: "Remove",
    dropZoneText: "Drop PDF files here", dropZoneHint: "First PDF → Xero Invoice, second → BOL", dropZoneReplaceXero: "Both slots full. Replace Xero Invoice?\nOK = Xero, Cancel = check BOL", dropZoneReplaceBol: "Replace BOL?", dropZoneNotPdf: "Only PDF files are accepted",
    selectPO: "Select PO from Client Pricing...", enterPOManually: "✏️ Enter manually...",
    manualPOPlaceholder: "Manual PO #", noAvailablePOs: "No POs available for this client",
    poTypeFixed: "Fixed", poTypeSemifixed: "Semi-fixed", poTypeConsignment: "Cons.",
    product: "Product", description: "Type/Cond/Var/Package", category: "Cat",
    cases: "Qty", pallets: "Pallets", salePrice: "Value", amount: "Amount", deductions: "Deductions", paidCol: "Paid",
    lineComm: "Comm %", lineCommAmt: "Comm $", lineNetAmt: "Net Amount",
    palletAllocations: "Pallet Allocations", addFromOtherLot: "+ Add pallet from another lot",
    allocCases: "Cases", palletNumCol: "Pallet #", tagIdCol: "Tag ID", varietyCol: "Variety", growerCol: "Grower", weightCol: "Weight", palletStatusCol: "Status",
    populateFromLot: "Populate from Lot",
    confirmRelot: "Changing this lot will erase its current pallet allocations. Continue?",
    noPalletsToAdd: "No pallets available to add",
    emailInboundFB: "Inbound Five Brothers", emailOutboundACS: "Outbound American Cold Storage",
    emailCustomer: "Email Customer",
    emailWarehouse: "Email Warehouse",
    emailWarehouseTitle: "Email warehouse",
    emailWarehouseDefaultSubject: "Inbound_{lot}_{pu}",
    emailNoWarehouse: "This invoice has no linked warehouse or warehouse has no contacts",
    sendPassing: "Send Passing / BOL",
    emailReminderPickup: "Send Pickup Reminder",
    emailReminderKindPickup: "Pickup Reminder",
    emailReminderSubjectPickup: "Reminder_PU_{pu}_PO_{po}",
    emailReminderDefaultBody: "Hi,\n\nAs of today, PU# {pu} / PO# {po} hasn't been picked up yet. Could you please confirm when it will be picked up?\n\nThanks,\nSweetLeaf Produce",
    emailProtectionReminder: "Send Protection Status Reminder",
    emailProtectionReminderKind: "Protection Status Reminder",
    emailProtectionReminderSubject: "Protection Status — PU# {pu} / PO# {po}",
    emailProtectionReminderBody: "Hi,\n\nWe'd like to follow up on the status of the following file, currently under Protection:\n\nPU#: {pu}\nPO#: {po}\nReported on: {reportedDate} {reportedTime}\nArrival at your facility: {arrivalDate} {arrivalTime}\n{previousResponseNote}\nCould you please share an update on the current status?\n\nThanks,\nSweetLeaf Produce",
    protectionResponseLabel: "Client Response — Protection",
    protectionResponseCol: "Response", protectionResponseColLine1: "Protection", protectionResponseColLine2: "Outcome",
    protectionResponsePlaceholder: "No response yet",
    willPayFull: "Full Pay",
    settled: "Settled",
    noResultYet: "No Result Yet",
    previousResponseNoteLine: function (dateStr) { return "On " + dateStr + ", you let us know the sale result wasn't available yet — following up again for the latest update.\n"; },
    protectionReminderAutoLabel: "Send automatic reminders for this file",
    protectionReminderAutoCountLabel: function (n, max) { return "(" + n + "/" + max + " sent automatically)"; },
    protectionResponseNoteLabel: "Note",
    protectionResponseNotePlaceholder: "Credit amount, reason, etc.",
    protectionResponseClear: "Clear",
    protectionReminderDueCol: "Reminder due",
    protectionReminderDueBadge: "⚠ Reminder due",
    protectionReminderDueDay: function (n) { return "Day " + n + " since arrival, no response yet"; },
    protectionReminderDueBanner: function (n) { return "⚠ It's been " + n + " days since the client reported arrival with no protection response registered — a Protection Status Reminder is due. Use the kebab menu (⋮) to send one."; },
    emailFactoring: "Email Factoring",
    emailFactoringTitle: "Email Factoring — AGROCAP",
    emailFactoringSelect: "Select invoices to include",
    emailFactoringNoInvoices: "No invoices available",
    emailFactoringMissingDocs: "All selected invoices must have Xero Invoice and BOL uploaded before sending",
    emailFactoringMissingHeader: "Missing documents:",
    emailFactoringMissingXero: "missing Xero Invoice",
    emailFactoringMissingBol: "missing BOL",
    emailFactoringMissingBoth: "missing Xero Invoice and BOL",
    emailFactoringSend: "Send",
    emailFactoringCancel: "Cancel",
    emailFactoringSubject: "Received Confirmation Request",
    emailFactoringUploadXero: "Upload Xero Invoice",
    emailFactoringNote: "Disclosure: Invoices under Protection or PAS status are subject to price adjustment upon final settlement. AgroCap's advance is based on the original invoice value; if the final settled amount is less than the amount advanced, the shortfall shall be resolved solely between AgroCap and SweetLeaf Produce, with no involvement or liability on the part of Four Seasons Produce.",
    emailFactoringSent: "Factoring email sent",
    emailCustomerTitle: "Email customer",
    emailCustomerDocInbound: "Inbound",
    emailCustomerDocMaster: "Master Inbound",
    emailCustomerDocPacking: "Packing List",
    emailCustomerDocInvoice: "Invoice",
    emailCustomerDefaultSubject: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectInbound: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectPick: "Pick_Ticket_{pu}_PO_{po}",
    emailCustomerKindInbound: "Inbound",
    emailCustomerKindPick: "Pick Ticket",
    emailCustomerDefaultBody: "Hi,\n\nPlease find attached the documents for the upcoming delivery.\n\nPU#: {pu}\nPO#: {po}\n\nThanks,\nGround Fresh International",
    emailNoClient: "This invoice has no client assigned",
    emailNoShipment: "This invoice has no linked shipment — required to build the documents",
    emailHtmlCopied: "HTML copied to clipboard — paste into Outlook",
    emailHtmlError: "Couldn't copy HTML — browser blocked clipboard access",
    paymentsSection: "Payments", originalTotal: "Original Total", currentBalance: "Current Balance",
    noPayments: "No payments recorded", paymentRef: "Reference", paymentMethodCol: "Method",
    editLockedTitle: "Editing locked",
    editLockedMsg: "This record has payments applied. To edit, first delete the payments.",
    editLockedHint: "Click any payment below to open it in Payments and delete it.",
    lotNum: "Lot #",
    lessAdvance: "Less Advance", otherFees: "Other Fees", lessCredits: "Less Credits",
    totalDue: "TOTAL DUE", remarks: "Remarks",
    billTo: "BILL TO",
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    confirmDel: "Delete this invoice?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "Unsaved changes. Exit?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Invoice saved", deleted: "Invoice deleted",
    all: "All", allClients: "All clients", allStatuses: "All statuses", allSaleStatuses: "All sale statuses",
    allProtectionOutcomes: "All", protectionOutcomeNone: "No response yet",
    statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid", statusOverdue: "Overdue", status: "Status",
    printPreview: "Print / PDF", sourceBill: "Source Bill",
    preview: "Preview", printPdf: "Print PDF", more: "More actions",
    noInventory: "No available inventory",
    selectLot: "Select lot (from received shipments)",
    saleStatus: "Sale Status",
    goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection",
    pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery",
    computedFromPallets: "Pushed from GFI invoice's pallet status",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transfer",
    // Batch deposit modal
    recordDeposit: "Record Deposit", recordDeposits: "Record Deposits",
    batchDepositTitle: "Batch Deposit",
    depositDate: "Deposit Date", depositMethod: "Method", depositReference: "Reference / Wire #",
    refColumn: "Reference",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    depositAmount: "Deposit Amount", totalDeposit: "Total Deposit",
    dueAmount: "Due Amount",
    noOpenInvoices: "Selected invoices have no outstanding balance.",
    depositSaved: "Deposit recorded",
    overApplyInv: "Deposit cannot exceed Due Amount",
    concept: "Concept", conceptDeposit: "Deposit", conceptAdvance: "Advance",
    editPaymentTitle: "Edit Payment",
    notes: "Notes",
    fees: "Fees & Deductions", feeType: "Fee Type", feeQty: "Qty", feeRate: "Rate", feeAmount: "Amount",
    selectFee: "Select fee", customFee: "Custom fee…", addFee: "Add fee",
    credits: "Credits", creditType: "Type", creditFree: "Free credit", creditFromInvoice: "From Invoice",
    creditConcept: "Concept", creditRefInvoice: "Invoice ref", addCredit: "Add credit",
    grossSales: "Gross Sales",
    balanceTransfers: "Balance Transfers", btDirection: "Direction", btSend: "Send my negative to…", btReceive: "Receive negative from…",
    btOtherInvoice: "Other invoice", btAmount: "Amount", btCreate: "Create transfer", btType: "Type",
    btCredit: "Credit (received)", btDebit: "Debit (sent)", btCancel: "Cancel",
  },
  es: {
    title: "SweetLeaf Produce", subtitle: "Facturas y documentos para Four Seasons",
    add: "Nueva factura", edit: "Editar factura",
    search: "Buscar por INV#, PU#, lote o PO...",
    showing: "Mostrando", of: "de", total: "facturas", noResults: "Sin resultados",
    invoiceNum: "Factura #", puNum: "PU #", invNum: "INV #", gfiPuNum: "GFI PU#",
    pickup: "Recolección (BOL)", thermographLabel: "No. Temp Recorder",
    date: "Fecha", dueDate: "Fecha de Pago",
    commPerCase: "Comisión/Caja $", commTotal: "Comisión Total", advanceFinancingFee: "Adelanto Financiero",
    docsSection: "Documentos", xeroInvoicePdf: "Factura Xero PDF", bolPdf: "BOL (Guía de Carga)", uploadPdf: "Subir PDF", removeFile: "Eliminar",
    dropZoneText: "Arrastra archivos PDF aquí", dropZoneHint: "Primer PDF → Factura Xero, segundo → BOL", dropZoneReplaceXero: "Ambos slots están llenos. ¿Reemplazar Factura Xero?\nOK = Xero, Cancelar = ver BOL", dropZoneReplaceBol: "¿Reemplazar BOL?", dropZoneNotPdf: "Solo se aceptan archivos PDF",
    client: "Cliente", selectClient: "Seleccionar cliente",
    clientRef: "Ref. Cliente (PO)", clientRefColLine1: "Cliente", clientRefColLine2: "Ref. (PO)", lot: "Lote", shipper: "Exportador", thermograph: "No. de Termógrafo",
    selectPO: "Seleccionar PO de Precios al Cliente...", enterPOManually: "✏️ Capturar manualmente...",
    manualPOPlaceholder: "PO # manual", noAvailablePOs: "Sin POs disponibles para este cliente",
    poTypeFixed: "Fijo", poTypeSemifixed: "Semi-fijo", poTypeConsignment: "Cons.",
    product: "Producto", description: "Tipo/Cond/Var/Paquete", category: "Cat",
    cases: "Cant", pallets: "Pallets", salePrice: "Valor", amount: "Monto", deductions: "Deducciones", paidCol: "Pagado",
    lineComm: "Com %", lineCommAmt: "Com $", lineNetAmt: "Total Neto",
    palletAllocations: "Asignación de Pallets", addFromOtherLot: "+ Agregar pallet de otro lote",
    allocCases: "Cajas", palletNumCol: "Pallet #", tagIdCol: "ID Tag", varietyCol: "Variedad", growerCol: "Productor", weightCol: "Peso", palletStatusCol: "Status",
    populateFromLot: "Traer del Lote",
    confirmRelot: "Cambiar este lote borrará sus allocations actuales. ¿Continuar?",
    noPalletsToAdd: "No hay pallets disponibles para agregar",
    emailInboundFB: "Inbound Five Brothers", emailOutboundACS: "Outbound American Cold Storage",
    emailWarehouse: "Email Bodega",
    emailWarehouseTitle: "Email bodega",
    emailWarehouseDefaultSubject: "Inbound_{lot}_{pu}",
    emailNoWarehouse: "Este invoice no tiene bodega asignada o la bodega no tiene contactos",
    /* Pre-existing gap fixed 2026-09-14 — same bug found and fixed in
       InvoiceRegistry.js: emailCustomer* only ever existed in the `en`
       block, so the "Email Customer" kebab button rendered blank in
       Spanish (this module's own default language, per REGLA — Four
       Seasons/SweetLeaf portal defaults to Spanish too). */
    emailCustomer: "Email Cliente",
    emailCustomerTitle: "Email cliente",
    emailCustomerDocInbound: "Inbound",
    emailCustomerDocMaster: "Master Inbound",
    emailCustomerDocPacking: "Packing List",
    emailCustomerDocInvoice: "Invoice",
    emailCustomerDefaultSubject: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectInbound: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectPick: "Pick_Ticket_{pu}_PO_{po}",
    emailCustomerKindInbound: "Inbound",
    emailCustomerKindPick: "Pick Ticket",
    emailCustomerDefaultBody: "Hola,\n\nAdjunto los documentos para la próxima entrega.\n\nPU#: {pu}\nPO#: {po}\n\nGracias,\nGround Fresh International",
    sendPassing: "Enviar Passing / BOL",
    emailReminderPickup: "Enviar Recordatorio de Recolección",
    emailReminderKindPickup: "Recordatorio de Recolección",
    emailReminderSubjectPickup: "Recordatorio_PU_{pu}_PO_{po}",
    emailReminderDefaultBody: "Hola,\n\nA la fecha de hoy, el PU# {pu} / PO# {po} aún no ha sido recolectado. ¿Podría confirmarnos cuándo se estaría recolectando?\n\nGracias,\nSweetLeaf Produce",
    emailProtectionReminder: "Enviar Recordatorio de Estatus — Protección",
    emailProtectionReminderKind: "Recordatorio de Estatus — Protección",
    emailProtectionReminderSubject: "Estatus de Protección — PU# {pu} / PO# {po}",
    emailProtectionReminderBody: "Hola,\n\nQueremos dar seguimiento al estatus del siguiente file, actualmente en Protección:\n\nPU#: {pu}\nPO#: {po}\nReportado el: {reportedDate} {reportedTime}\nLlegada a su bodega: {arrivalDate} {arrivalTime}\n{previousResponseNote}\n¿Podrían compartirnos una actualización del estatus actual?\n\nGracias,\nSweetLeaf Produce",
    protectionResponseLabel: "Respuesta del Cliente — Protección",
    protectionResponseCol: "Respuesta", protectionResponseColLine1: "Protección", protectionResponseColLine2: "Resultado",
    protectionResponsePlaceholder: "Sin respuesta",
    willPayFull: "Pago Completo",
    settled: "Precio Ajustado",
    noResultYet: "Aún sin Resultado",
    previousResponseNoteLine: function (dateStr) { return "El " + dateStr + " nos comentaron que aún no tenían el resultado de la venta — damos seguimiento nuevamente para la actualización más reciente.\n"; },
    protectionReminderAutoLabel: "Enviar recordatorios automáticos para este file",
    protectionReminderAutoCountLabel: function (n, max) { return "(" + n + "/" + max + " enviados automáticamente)"; },
    protectionResponseNoteLabel: "Nota",
    protectionResponseNotePlaceholder: "Monto del crédito, motivo, etc.",
    protectionResponseClear: "Limpiar",
    protectionReminderDueCol: "Recordatorio pendiente",
    protectionReminderDueBadge: "⚠ Recordatorio pendiente",
    protectionReminderDueDay: function (n) { return "Día " + n + " desde la llegada, sin respuesta todavía"; },
    protectionReminderDueBanner: function (n) { return "⚠ Han pasado " + n + " días desde que el cliente reportó la llegada sin registrar respuesta de protección — toca enviar un Recordatorio de Estatus. Usa el menú (⋮) para enviarlo."; },
    emailFactoring: "Email Factoring",
    emailFactoringTitle: "Email Factoring — AGROCAP",
    emailFactoringSelect: "Seleccionar facturas a incluir",
    emailFactoringNoInvoices: "No hay facturas disponibles",
    emailFactoringMissingDocs: "Todas las facturas seleccionadas deben tener Factura Xero y BOL subidos antes de enviar",
    emailFactoringMissingHeader: "Documentos faltantes:",
    emailFactoringMissingXero: "falta Factura Xero",
    emailFactoringMissingBol: "falta BOL",
    emailFactoringMissingBoth: "falta Factura Xero y BOL",
    emailFactoringSend: "Enviar",
    emailFactoringCancel: "Cancelar",
    emailFactoringSubject: "Received Confirmation Request",
    emailFactoringUploadXero: "Subir Factura Xero",
    emailFactoringNote: "Disclosure: Invoices under Protection or PAS status are subject to price adjustment upon final settlement. AgroCap's advance is based on the original invoice value; if the final settled amount is less than the amount advanced, the shortfall shall be resolved solely between AgroCap and SweetLeaf Produce, with no involvement or liability on the part of Four Seasons Produce.",
    emailFactoringSent: "Email de factoraje enviado",
    emailHtmlCopied: "HTML copiado al portapapeles — pega en Outlook",
    emailHtmlError: "No se pudo copiar HTML — tu navegador bloqueó el portapapeles",
    paymentsSection: "Pagos", originalTotal: "Total Original", currentBalance: "Saldo Actual",
    noPayments: "Sin pagos registrados", paymentRef: "Referencia", paymentMethodCol: "Método",
    editLockedTitle: "Edición bloqueada",
    editLockedMsg: "Este registro tiene pagos aplicados. Para editarlo, primero elimina los pagos.",
    editLockedHint: "Click en un pago para abrirlo en Pagos y eliminarlo.",
    lotNum: "Lote #",
    lessAdvance: "Menos Anticipo", otherFees: "Otros Gastos", lessCredits: "Menos Créditos",
    totalDue: "TOTAL A PAGAR", remarks: "Observaciones",
    billTo: "FACTURAR A",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    confirmDel: "¿Eliminar esta factura?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Cambios sin guardar. ¿Salir?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Factura guardada", deleted: "Factura eliminada",
    all: "Todos", allClients: "Todos", allStatuses: "Todos", allSaleStatuses: "Todos los estados",
    allProtectionOutcomes: "Todos", protectionOutcomeNone: "Sin respuesta todavía",
    statusDraft: "Borrador", statusSent: "Enviada", statusPaid: "Pagada", statusOverdue: "Vencida", status: "Estado",
    printPreview: "Imprimir / PDF", sourceBill: "Bill Origen",
    preview: "Vista previa", printPdf: "Imprimir / PDF", more: "Más acciones",
    noInventory: "Sin inventario disponible",
    selectLot: "Seleccionar lote (embarques recibidos)",
    saleStatus: "Status de Venta",
    goodDelivery: "Good Delivery", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial",
    pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega",
    computedFromPallets: "Empujado desde el status de pallets del invoice de GFI",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transferencia",
    // Batch deposit modal
    recordDeposit: "Registrar Depósito", recordDeposits: "Registrar Depósitos",
    batchDepositTitle: "Depósito en Lote",
    depositDate: "Fecha del Depósito", depositMethod: "Método", depositReference: "Referencia / # Transferencia",
    refColumn: "Referencia",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    depositAmount: "Monto del Depósito", totalDeposit: "Total del Depósito",
    dueAmount: "Por Cobrar",
    noOpenInvoices: "Las facturas seleccionadas no tienen saldo pendiente.",
    depositSaved: "Depósito registrado",
    overApplyInv: "El depósito no puede exceder el saldo pendiente",
    concept: "Concepto", conceptDeposit: "Depósito", conceptAdvance: "Adelanto",
    editPaymentTitle: "Editar Pago",
    notes: "Notas",
    fees: "Cargos y Deducciones", feeType: "Tipo", feeQty: "Cant", feeRate: "Tarifa", feeAmount: "Monto",
    selectFee: "Seleccionar cargo", customFee: "Cargo personalizado…", addFee: "Agregar cargo",
    credits: "Créditos", creditType: "Tipo", creditFree: "Crédito libre", creditFromInvoice: "De factura",
    creditConcept: "Concepto", creditRefInvoice: "Ref. factura", addCredit: "Agregar crédito",
    grossSales: "Venta Bruta",
    balanceTransfers: "Transferencias de Saldo", btDirection: "Dirección", btSend: "Enviar mi saldo negativo a…", btReceive: "Recibir saldo negativo de…",
    btOtherInvoice: "Otra factura", btAmount: "Monto", btCreate: "Crear transferencia", btType: "Tipo",
    btCredit: "Crédito (recibido)", btDebit: "Débito (enviado)", btCancel: "Cancelar",
  },
};

/* ======================== CONSTANTS ======================== */
var STORAGE_KEY = "gfi-sl-invoices";
var INV_STATUSES = ["draft", "sent", "paid"];
var INV_STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  sent: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)" },
  paid: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
};
var SALE_STATUSES = ["goodDelivery", "protection", "pas", "dumped", "donated", "samples", "transfer"];
var SALE_STATUS_COLORS = {
  goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  /* PAS = Price After Sale. Market condition (oversupply), NOT a
     quality issue. Less severe than Protection but more severe than
     Partial Protection. Orange to distinguish from red and amber. */
  pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" },
  /* Computed only (2026-09-12) — pushed from the GFI invoice's
     pallet-derived status, never set manually here. */
  partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" },
  /* Pre-delivery lifecycle stages (2026-09-12, saleStatusCalc.js
     resolvePendingStage) — display-only, never manually selectable. */
  pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" },
  pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" },
  dumped: { bg: "rgba(239,68,68,0.10)", text: "#DC2626", border: "rgba(239,68,68,0.25)" },
  donated: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  samples: { bg: "rgba(139,92,246,0.10)", text: "#7C3AED", border: "rgba(139,92,246,0.25)" },
  /* Transfer = cases moved to another invoice/lot. Excluded from gross.
     Blue for "informational, no revenue on this invoice". */
  transfer: { bg: "rgba(59,130,246,0.10)", text: "#2563EB", border: "rgba(59,130,246,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to every
   SALE_STATUS_COLORS/PROTECTION_RESPONSE_COLORS badge in this file so
   Sale Status pills read the same squared, beveled way everywhere in
   the app. */
var SALE_STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
/* 2026-09-14 — Arturo: client (Four Seasons) follow-up response after a
   Protection Status Reminder — separate from saleStatus (which stays
   Protection/PAS, the pallet-level truth per REGLA DE STATUS DE PALLET,
   never touched here). This is purely an annotation recording what FS
   said WILL happen with the money, so Arturo can tell at a glance which
   protected files are actually going to pay in full vs. carry a credit,
   without ever losing the underlying Protection status. Distinct colors
   from SALE_STATUS_COLORS.protection (red) on purpose, so the two badges
   never look like the same thing. */
/* 2026-10-01 — Arturo: "uno de los cambios no debe ser has credit si
   no settled, settled significa que el precio se ajusto" — renamed
   from "hasCredit" (wrong concept — this was never literally about a
   credit balance) to "settled" (the shipper/client and Arturo agreed
   on an adjusted price). Same slot, same color, same final-status
   role — just the correct concept, applied identically on the
   Ground-Fresh side (InvoiceRegistry.js's own copy of this set). */
var PROTECTION_RESPONSE_STATUSES = ["willPayFull", "settled", "noResultYet"];
var PROTECTION_RESPONSE_COLORS = {
  willPayFull: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  /* Red, not blue (2026-09-14, Arturo: "si tiene creditos no la pongas
     azul, ponla roja porque eso implica que me pagan menos de lo
     pactado") — an adjusted/settled price means less money than
     originally agreed, same severity family as the real Protection/
     PAS badge it sits next to. */
  settled: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  /* Amber, not green/red — this is not a final outcome, just an
     acknowledgment that the client still doesn't have a number
     (2026-09-23, Arturo: "en ocasiones puede que el cliente me
     responda que aun no tiene el resuktado de la venta... quiero
     agregar esa opcion para que el siguiente recordatorio que se
     envie, tome en cuenta la respuesta anterior"). Unlike willPayFull/
     settled, registering this response does NOT end the reminder
     cycle — see computeProtectionReminderStatus below. */
  noResultYet: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.3)" },
};
/* Statuses that represent a genuinely final answer from the client —
   only these stop the automatic reminder cycle. "noResultYet" is
   registered but intentionally excluded here so the cycle keeps going. */
var PROTECTION_RESPONSE_FINAL_STATUSES = ["willPayFull", "settled"];

/* Protection Status Reminder cadence (added 2026-09-16, Arturo: "NECESITO
   QUE ME ESTE RECORDANDO ENVIAR PROTECTION STATUS REMINDER CUANDO UN SL
   INVOICE ENTRE EN PROTECCION Y NO TENGA AUN RESPUESTA REGISTRADA EN EL
   Client Response — Protection BOX, LA PRIMERA A 5 DIAS DE LA FECHA
   REGISTRADA EN... Arrival date at their warehouse, DESPUES CADA 5 DIAS
   EN TANTO NO SE REGISTRE RESPUESTA DEL CLIENTE"). Never persisted — like
   every other status calc in this app (see CLAUDE.md's REGLA DE STATUS
   DE PALLET), this is always computed live from real fields:
     - clientArrivalDate: "Arrival date at their warehouse" (client-
       reported, captured in Ground-Fresh's Protection Notice dialog,
       synced into this SL mirror).
     - protectionResponseAt: set the moment a response is registered in
       the Client Response — Protection box — a FINAL response
       (willPayFull/hasCredit) ends the reminder cycle immediately, no
       matter the day count. "noResultYet" is deliberately NOT final
       (2026-09-23, Arturo: "en ocasiones puede que el cliente me
       responda que aun no tiene el resuktado de la venta... quiero
       agregar esa opcion para que el siguiente recordatorio que se
       envie, tome en cuenta la respuesta anterior") — the cycle keeps
       going, and protectionResponseAt itself becomes an extra baseline
       candidate below, so the next reminder counts 5 days from the
       client's "not yet" answer rather than from the original arrival/
       last-sent date (avoids nagging again the same day they replied).
     - protectionReminderSentAt: already stamped by the existing "Send
       Protection Status Reminder" action (see the email-send handler
       below) — reused here as the baseline for the "every 5 days"
       repeat cadence, so a reminder Arturo already sent doesn't
       immediately show as due again.
   Baseline for the 5-day window is whichever is LATEST among: the
   arrival date (first reminder), the last reminder actually sent, and
   — only for a non-final response like "noResultYet" — the moment that
   response was registered (each repeat). */
var PROTECTION_REMINDER_INTERVAL_DAYS = 5;
/* intervalDays: optional override — callers pass
   getSLSettings().protectionReminderIntervalDays (configurable per
   2026-09-23, Arturo: "quiero tener las opciones de seleccionar el
   intervalo") so this always reflects whatever Arturo set in SweetLeaf
   Settings, not a hardcoded constant. Falls back to the 5-day default
   when settings haven't loaded yet or the field is unset. */
function computeProtectionReminderStatus(inv, todayStr, intervalDays) {
  var hasProtection = (inv.lines || []).some(function (l) { return l && (l.saleStatus === "protection" || l.saleStatus === "pas"); });
  if (!hasProtection) return null;
  var isFinalResponse = inv.protectionResponseAt && PROTECTION_RESPONSE_FINAL_STATUSES.indexOf(inv.protectionResponseStatus) >= 0;
  if (isFinalResponse) return null; // final response already registered — cycle ends
  if (!inv.clientArrivalDate) return null; // nothing to count from yet
  var arrival = new Date(inv.clientArrivalDate + "T00:00:00");
  if (isNaN(arrival.getTime())) return null;
  var today = new Date((todayStr || new Date().toISOString().slice(0, 10)) + "T00:00:00");
  var daysSinceArrival = Math.floor((today - arrival) / 86400000);
  if (daysSinceArrival < 0) return null;
  var interval = intervalDays > 0 ? intervalDays : PROTECTION_REMINDER_INTERVAL_DAYS;
  var lastSent = inv.protectionReminderSentAt ? new Date(inv.protectionReminderSentAt) : null;
  var lastSentDateOnly = lastSent && !isNaN(lastSent.getTime()) ? new Date(lastSent.getFullYear(), lastSent.getMonth(), lastSent.getDate()) : null;
  var baseline = lastSentDateOnly && lastSentDateOnly > arrival ? lastSentDateOnly : arrival;
  if (inv.protectionResponseStatus === "noResultYet" && inv.protectionResponseAt) {
    var respAt = new Date(inv.protectionResponseAt);
    var respDateOnly = !isNaN(respAt.getTime()) ? new Date(respAt.getFullYear(), respAt.getMonth(), respAt.getDate()) : null;
    if (respDateOnly && respDateOnly > baseline) baseline = respDateOnly;
  }
  var dueDate = new Date(baseline);
  dueDate.setDate(dueDate.getDate() + interval);
  return { isDue: today >= dueDate, daysSinceArrival: daysSinceArrival };
}

var inp ={ padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

/* GFI Company Info */
var GFI = { name: "Ground-Fresh International", address: "152 W Hoffman Ave Ste. 5", city: "Lindenhurst, NY 11757-4041", phone: "631-5650034" };

/* ======================== HELPERS ======================== */
function loadLS(key, fb) { try { var d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }
/* Paid amount with historical-import fallback: prefer sum(payments[])
   when present; otherwise fall back to amountPaid (used by bulk-paid
   imports that intentionally have no individual payment records). */
function paidAmount(rec) {
  var sum = ((rec && rec.payments) || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  if (sum > 0) return sum;
  return parseFloat(rec && rec.amountPaid) || 0;
}
function parseTermDays(term) {
  if (term === undefined || term === null || term === "") return null;
  var m = String(term).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
function addDaysToISO(isoDate, days) {
  if (!isoDate) return "";
  var d = new Date(isoDate + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return d.toISOString().substring(0, 10);
}
function saveLS(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { console.error("[Invoices] storage error", key, e); return false; } }
function nextPaymentId(items) { var nums = (items || []).map(function(i) { return parseInt((i.id || "").replace("PAY-", ""), 10); }).filter(function(n) { return !isNaN(n); }); return "PAY-" + String(Math.max(0, ...nums) + 1).padStart(4, "0"); }
function todayISO() { return new Date().toISOString().substring(0, 10); }
var PAYMENT_METHODS = ["wire", "check", "ach", "transfer", "cash", "other"];
function getClients() { return loadLS("gfi-clients", []); }
function getBills() { return loadLS("gfi-bills", []); }
function getShipments() { return loadLS("gfi-shipments", []); }
function getProducts() { return loadLS("gfi-products", []); }
function getSuppliers() { return loadLS("gfi-suppliers", []); }
function getFixedPOs() { return loadLS("gfi-fixed-pos", []); }
function getConsignmentPOs() { return loadLS("gfi-consignment-pos", []); }

/* ISO week number — used by the new Week filter on the invoice
   list. Mirrors BillRegistry's getISOWeek so the two modules agree
   on which week a given date belongs to. */
function getISOWeekInv(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}

/* Read/write PO linkage directly to localStorage. The Fixed/
   Consignment Pricing window globals only exist while their tabs
   are mounted — invoices save from a different module, so we go
   straight to the source key here. CompositeId is "F:<id>" for a
   Fixed PO line and "C:<id>" for a Consignment line, matching
   the convention CombinedWeeklySummary already uses. */
function poStorageKeyFor(compositeId) {
  if (!compositeId) return null;
  var src = String(compositeId).split(":")[0];
  return src === "F" ? "gfi-fixed-pos" : src === "C" ? "gfi-consignment-pos" : null;
}
function poIdFor(compositeId) {
  if (!compositeId) return "";
  var parts = String(compositeId).split(":");
  parts.shift();
  return parts.join(":");
}
/* Sync the linkage between an invoice and the PO line it points to.
   Writes BACK to the Pricing module so the PO row reflects:
     - linkedInvoiceId  → invoice's stable id
     - linkedPuNumber   → invoice's PU# (the human-readable invoice
                          number Arturo recognizes)
     - saleStatus       → mirrors invoice.status ("draft"/"sent"/"paid").
                          When unlinking, resets to "pending".

   Args:
     prevCompositeId — PO link the invoice carried previously
                       (empty string on first save)
     newCompositeId  — PO link the invoice now carries
                       (empty string when manual / unlinked)
     invoiceData     — { id, puNumber, status } for the just-saved
                       invoice. Status drives the new PO saleStatus.

   When prev != new, both POs are touched: prev unlinked + reset,
   new linked + stamped. Both updates persist with a fresh
   updatedAt timestamp. */
function syncPoLinkage(prevCompositeId, newCompositeId, invoiceData) {
  function updatePO(compositeId, mutator) {
    var key = poStorageKeyFor(compositeId);
    var id = poIdFor(compositeId);
    if (!key || !id) return;
    try {
      var list = JSON.parse(localStorage.getItem(key) || "[]");
      var next = list.map(function (p) {
        if (String(p.id) !== String(id)) return p;
        return { ...p, ...mutator(p), updatedAt: new Date().toISOString() };
      });
      localStorage.setItem(key, JSON.stringify(next));
    } catch (e) { /* corrupt JSON — skip silently rather than crash save */ }
  }
  var inv = invoiceData || {};
  var invId       = inv.id || "";
  var invPu       = inv.puNumber || "";
  var invStatus   = inv.status || "draft";
  /* eslint-disable no-console */
  console.log("[syncPoLinkage] prev=" + prevCompositeId + " new=" + newCompositeId + " inv=" + invId + " pu=" + invPu + " status=" + invStatus);
  /* eslint-enable no-console */
  if (prevCompositeId && prevCompositeId !== newCompositeId) {
    /* UNLINK — clear PU# (since it was the invoice's PU#) and
       reset sale status. linkedInvoiceId cleared too so the PO is
       eligible to re-appear in other invoices' PO picker. */
    updatePO(prevCompositeId, function () {
      return { linkedInvoiceId: "", puNumber: "", saleStatus: "pending" };
    });
  }
  if (newCompositeId) {
    /* LINK — always write linkedInvoiceId and the invoice's PU#
       (the PU# field on the PO mirrors the invoice). saleStatus
       is only seeded on the FIRST link or while the PO is still
       "pending" — once the user has manually picked a sale-cycle
       status in Pricing, we must not silently clobber it on every
       subsequent invoice save. */
    updatePO(newCompositeId, function (existing) {
      var patch = { linkedInvoiceId: invId, puNumber: invPu };
      var isFirstLink = !existing.linkedInvoiceId;
      var stillPending = !existing.saleStatus || existing.saleStatus === "pending";
      if (isFirstLink || stillPending) patch.saleStatus = invStatus;
      return patch;
    });
  }
}

function fmtDate(d) { if (!d) return "\u2014"; var dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); }
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
/* "YYYY-MM-DD HH:MM" \u2014 exact format fix-dashboard.md's Passing Sent
   tooltip asks for, distinct from fmtDate's US date-only format. */
function fmtPassingSentAt(iso) {
  if (!iso) return "";
  var d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  var pad = function (n) { return String(n).padStart(2, "0"); };
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + " " + pad(d.getHours()) + ":" + pad(d.getMinutes());
}

function nextPU(invoices) {
  var nums = invoices.map(function(i) { return parseInt(i.puNumber, 10); }).filter(function(n) { return !isNaN(n); });
  return nums.length > 0 ? String(Math.max(...nums) + 1) : "17303";
}
function nextId(items) {
  var nums = items.map(function(i) { return parseInt((i.id || "").replace("INV-", ""), 10); }).filter(function(n) { return !isNaN(n); });
  return "INV-" + String((Math.max(0, ...nums) + 1)).padStart(4, "0");
}

function resolveClientName(clientId) {
  if (!clientId) return "";
  var c = getClients().find(function(x) { return String(x.id) === String(clientId); });
  if (!c) return "";
  return c.shortName || c.fullName || c.name || c.companyName || "";
}
function resolveClientFull(clientId) {
  if (!clientId) return null;
  return getClients().find(function(x) { return String(x.id) === String(clientId); }) || null;
}

/* SweetLeaf is single-tenant on Four Seasons — every invoice in this
   module is for them. Resolves the catalog record by case-insensitive
   substring match on shortName / fullName / name / companyName.
   Returns null when the catalog hasn't seeded yet; callers should
   fall back to a hard-coded "Four Seasons Produce Inc." string. */
var FOUR_SEASONS_FALLBACK_NAME = "Four Seasons Produce Inc.";
function findFourSeasonsClient() {
  var clients = getClients();
  return clients.find(function (c) {
    var hay = ((c.shortName || "") + " " + (c.fullName || "") + " " + (c.name || "") + " " + (c.companyName || "")).toLowerCase();
    return hay.indexOf("four seasons") >= 0;
  }) || null;
}

/* SweetLeaf settings — local cache loader. Returns the merged
   defaults + LS overrides without touching Supabase. */
var SL_DEFAULT_SETTINGS = { nextInvoiceNumber: 16229, invoicePrefix: "INV-", emailAccount: "a.gonzalez@sweetleafproduce.com", maskedEmailAccount: "a.gonzalez.sweetleaf@gfinexus.com", emailSignature: "", protectionReminderIntervalDays: 5, protectionReminderMaxAuto: 3 };
function getSLSettings() {
  try {
    var raw = localStorage.getItem("gfi-sl-settings");
    console.log("[getSLSettings] localStorage raw:", raw);
    if (!raw) return Object.assign({}, SL_DEFAULT_SETTINGS);
    var parsed = JSON.parse(raw);
    console.log("[getSLSettings] parsed:", parsed);
    return Object.assign({}, SL_DEFAULT_SETTINGS, parsed);
  } catch (e) {
    console.log("[getSLSettings] parse/read failed:", e && e.message);
    return Object.assign({}, SL_DEFAULT_SETTINGS);
  }
}

/* Same shape as getSLSettings() but reads Supabase directly instead
   of trusting this device's localStorage mirror. 2026-09-12 — Arturo
   reported Passing/Payment Confirmation emails failing ONLY from his
   phone with "from must be an @gfinexus.com address" (see
   netlify/functions/send-email.js's domain check). Root cause:
   getSLSettings() is 100% localStorage, no Supabase fallback — a
   phone that hasn't opened SweetLeaf Settings recently (or since
   maskedEmailAccount was last changed) can hold an empty/stale
   `gfi-sl-settings` mirror, silently falling back to the REAL inbox
   address as "from" instead of the masked @gfinexus.com alias, which
   Resend then rejects. Used at SEND time (not render time) in
   EmailPassingModal/EmailFactoringModal so the masked email is always
   current regardless of what this device's local cache last saw. */
async function getFreshSlSettings() {
  try {
    var rows = await loadFromSupabase("sweetleaf_settings");
    var row = (rows || [])[0];
    return row ? Object.assign({}, SL_DEFAULT_SETTINGS, row) : getSLSettings();
  } catch (e) {
    console.log("[getFreshSlSettings] Supabase read failed, falling back to local cache:", e && e.message);
    return getSLSettings();
  }
}

/* Same stale-cache class of bug as getFreshSlSettings above, found
   2026-09-25 (Arturo: "en las facturas de sweetleaf existe un pick up
   reminder que puedo enviar al cliente cuando no ha cargado la fruta,
   en mi telefono no funciona porque me dice que esa factura no tiene
   esta ligada a un embarque... solo en la pc funciona"). The kebab's
   "Send Pickup Reminder" (and Email Customer / Send Passing /
   Protection Reminder, same gate) all resolved the linked shipment via
   findShipmentForInvoice(fresh, loadLS("gfi-shipments", [])) — a pure
   localStorage read, never Supabase. A device that has never opened
   Shipment Registry (a phone, typically) has an empty/stale
   "gfi-shipments" mirror, so the lookup always fails and the action
   throws "not linked to a shipment" even for a real, correctly-linked
   invoice — exactly why it only ever worked from the PC (which had
   already primed that cache by visiting Shipment Registry there).
   Fetched fresh at click time, same pattern as getFreshSlSettings. */
async function getFreshShipments() {
  try {
    var rows = await loadFromSupabase("shipments");
    return rows || [];
  } catch (e) {
    console.log("[getFreshShipments] Supabase read failed, falling back to local cache:", e && e.message);
    return loadLS("gfi-shipments", []);
  }
}

/* SweetLeaf-only email templates. Replaces the CompanyProfile
   pickEmailTemplate path so SL sends never inherit a Ground-Fresh
   body or auto-append field set. Same shape composeEmailBody
   expects ({ body, fields }). */
var SL_EMAIL_TEMPLATES = {
  pickTicket: {
    body: "Good morning,\n\nPlease find attached the pick ticket for the upcoming pickup order.\n\nThanks,\nSweetleaf Produce Inc.",
    fields: { pu: true, po: true, lot: true, arrival: true, warehouse: true },
  },
  inbound: {
    body: "Good morning,\n\nPlease find attached the receiving documents for the upcoming inbound shipment.\n\nThanks,\nSweetleaf Produce Inc.",
    fields: { pu: true, po: true, lot: true, arrival: true, warehouse: true },
  },
};

/* Build the single-account list + signature list the EmailDialog
   expects, both sourced 100% from SweetLeaf settings. No
   Company Profile crossover — these helpers exist so the dialog
   never sees Ground-Fresh data even if a future caller forgets. */
/* EmailDialog.js's handleSend re-fetches "fresh" accounts right before
   sending (see its own 2026-09-14 comment) — by default that means
   Ground-Fresh's company_profile, which is wrong for every SweetLeaf
   send: company_profile DOES carry a "Sweetleaf" entry too, but with
   a typo'd address ("a.gonzalez@wseetlefproduce.com" vs the real
   "a.gonzalez@sweetleafproduce.com" in sweetleaf_settings), so the
   account lookup there never matched and silently dropped the masked
   @gfinexus.com alias — real bug hit live: "Send failed: from must be
   an @gfinexus.com address" sending the new Protection Reminder.
   Passed as EmailDialog's refreshAccountsBeforeSend so SweetLeaf sends
   re-resolve against the CORRECT source (sweetleaf_settings) instead. */
async function refreshSlAccountsForSend() {
  var fresh = await getFreshSlSettings();
  return [{
    id: "sl",
    name: "SweetLeaf",
    email: fresh.emailAccount || "a.gonzalez@sweetleafproduce.com",
    maskedFrom: fresh.maskedEmailAccount || "a.gonzalez.sweetleaf@gfinexus.com",
    signature: fresh.emailSignature || "",
  }];
}
function slEmailAccounts() {
  var s = getSLSettings();
  return [{
    id: "sl",
    name: "SweetLeaf",
    email: s.emailAccount || "a.gonzalez@sweetleafproduce.com",
    maskedFrom: s.maskedEmailAccount || "a.gonzalez.sweetleaf@gfinexus.com",
    signature: s.emailSignature || "",
  }];
}
function slEmailSignatures() {
  var s = getSLSettings();
  return s.emailSignature
    ? [{ id: "sl-sig", name: "SweetLeaf", html: s.emailSignature, isDefault: true }]
    : [];
}

/* Compute the next INV-XXXXX consecutive WITHOUT bumping the
   counter. Used by the form to pre-fill an editable INV# field —
   the counter is only advanced on save (and only when the saved
   number actually matches this suggestion). */
function peekSLInvoiceNumber() {
  var settings = getSLSettings();
  var nextNum = settings.nextInvoiceNumber || 16229;
  var prefix = settings.invoicePrefix || "INV-";
  return prefix + String(nextNum).padStart(5, "0");
}

/* Bump the counter only when `used` matches what peek would have
   returned — i.e. the user accepted the suggested consecutive.
   If they typed a custom INV# (e.g. INV-16100 for a historical
   import), the counter stays put so the next + New still issues
   the next sequential number. */
function commitSLInvoiceNumberIfNext(used) {
  if (!used) return;
  var peek = peekSLInvoiceNumber();
  if (String(used).trim() !== peek) return;
  var settings = getSLSettings();
  settings.nextInvoiceNumber = (settings.nextInvoiceNumber || 16229) + 1;
  try { localStorage.setItem("gfi-sl-settings", JSON.stringify(settings)); } catch (e) {}
  saveToSupabase("sweetleaf_settings", Object.assign({ id: "sl" }, settings)).catch(function () {});
}

/* Reserve the next INV-XXXXX consecutive. Bumps the counter in
   gfi-sl-settings LS immediately. Kept for the Copy-to-SweetLeaf
   path in InvoiceRegistry which writes the SL invoice in one shot
   without going through the form. */
function reserveSLInvoiceNumber() {
  var settings = getSLSettings();
  var nextNum = settings.nextInvoiceNumber || 16229;
  var prefix = settings.invoicePrefix || "INV-";
  var invoiceNumber = prefix + String(nextNum).padStart(5, "0");
  settings.nextInvoiceNumber = nextNum + 1;
  try { localStorage.setItem("gfi-sl-settings", JSON.stringify(settings)); } catch (e) {}
  saveToSupabase("sweetleaf_settings", Object.assign({ id: "sl" }, settings)).catch(function () {});
  return invoiceNumber;
}

/* Lots available as line sources for an invoice. Previously this
   filtered `status === "received" || "billed"`, which silently
   dropped every shipper-portal-promoted shipment — acceptPortal-
   Submission writes them with status="draft" (ShipmentRegistry line
   ~3999), so even a fully-billed portal lot like 58027 never showed
   up in the dropdown until someone manually flipped the status.
   Showing all three statuses (draft / received / billed) matches
   the user's ask "TODOS los embarques sin importar su origen" and
   restores parity with BillRegistry, which already looks up
   shipments by lot regardless of status. */
function getAvailableLots() {
  return getShipments();
}

/* Shared SearchSelect imported from ./SearchSelect (project-wide). */

/* ======================== TOAST ======================== */
function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function() {
    var fadeT = setTimeout(function() { setFading(true); }, 2700);
    var closeT = setTimeout(function() { onClose(); }, 3000);
    return function() { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* ======================== PRINT ======================== */
/* Builds the printable HTML for one invoice. Same navy look as Sales Reports \u2014
   LOGO_B64 image, #2A7A6F accents, Ground Fresh footer. The full <style> block
   is included so multi-print can extract & share it. */
function fmtDateLongInv(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }

export function buildInvoicePrintHTML(invoice, client) {
  /* SweetLeaf invoice — commissions render as negative product
     lines inside the items table (one per product), not as a
     summary block. Each line's commission = cases × slCommissionPerCase.
     Advance Financing Fee is a separate negative line at the
     bottom of the items table when > 0. */
  var totalCases = 0, totalPallets = 0, totalAmount = 0, totalFees = 0, totalCredits = 0, totalTransfers = 0, commissionAmt = 0;
  (invoice.lines || []).forEach(function(l) {
    totalCases += parseInt(l.cases, 10) || 0;
    totalPallets += parseInt(l.pallets, 10) || 0;
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var cases = parseInt(l.cases, 10) || 0;
    var lineAmt = cases * (parseFloat(l.salePrice) || 0);
    var perCase = parseFloat(l.slCommissionPerCase) || 0;
    totalAmount += lineAmt;
    commissionAmt += cases * perCase;
  });
  var advFee = Math.abs(parseFloat(invoice.advanceFinancingFee) || 0);
  (invoice.fees || []).forEach(function(fe) { totalFees += Math.abs(parseFloat(fe.amount) || 0); });
  (invoice.credits || []).forEach(function(cr) { totalCredits += Math.abs(parseFloat(cr.amount) || 0); });
  (invoice.balanceTransfers || []).forEach(function(t) { var amt = parseFloat(t.amount) || 0; if (t.type === "credit") totalTransfers += amt; else if (t.type === "debit") totalTransfers -= amt; });
  var paid = (invoice.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  /* Net to client = gross − commission − advFinFee − fees + credits ± transfers. */
  var subtotal = totalAmount - commissionAmt - advFee - totalFees + totalCredits + totalTransfers;
  var due = subtotal - paid;

  var invNum = invoice.invoiceNumber || invoice.puNumber || "";
  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice ' + invNum + '</title><style>';
  h += '@page{size:letter;margin:0.6in 0.7in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #2A7A6F}';
  /* Bumped to 100 — matches other GFI docs so "International" reads. */
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:26px;font-weight:300;color:#2A7A6F;letter-spacing:2px;text-align:right}';
  h += '.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0;padding:12px 16px;background:#f7f8fa;border-radius:6px;border-left:4px solid #2A7A6F}';
  h += '.meta-grid .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.bill-to{margin:10px 0 14px;padding:10px 16px;border:1px solid #e0e0e0;border-radius:6px}';
  h += '.bill-to .bt-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.bill-to .bt-name{font-size:13px;font-weight:700;color:#2A7A6F}';
  h += '.bill-to .bt-addr{font-size:9px;color:#666;margin-top:2px;line-height:1.5}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#2A7A6F;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:6px 8px;border-bottom:1px solid #eee;font-size:10px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace;font-size:10px}';
  h += '.ded{color:#c0392b}';
  h += '.summary{margin-top:10px;display:flex;justify-content:flex-end}';
  h += '.summary-tbl{width:380px}';
  h += '.summary-tbl td{padding:5px 8px;font-size:10px}';
  h += '.summary-tbl .total td{font-size:13px;font-weight:700;color:#2A7A6F;padding-top:8px}';
  h += '.summary-tbl .due td{font-size:14px;font-weight:800;color:#2A7A6F;border-top:3px solid #2A7A6F;padding-top:10px}';
  h += '.footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header"><div class="logo"><img src="' + SWEETLEAF_LOGO_B64 + '" /></div>';
  h += '<div class="doc-title">INVOICE</div></div>';

  /* Meta grid */
  h += '<div class="meta-grid">';
  h += '<div><div class="label">Invoice #</div><div class="value">' + (invNum || "\u2014") + '</div></div>';
  h += '<div><div class="label">Date</div><div class="value">' + fmtDateLongInv(invoice.date) + '</div></div>';
  if (invoice.lot) h += '<div><div class="label">Lot</div><div class="value">' + invoice.lot + '</div></div>';
  if (invoice.clientRef) h += '<div><div class="label">Customer Ref</div><div class="value">' + invoice.clientRef + '</div></div>';
  if (invoice.dueDate) h += '<div><div class="label">Due Date</div><div class="value">' + fmtDateLongInv(invoice.dueDate) + '</div></div>';
  if (invoice.clientInitials) h += '<div><div class="label">Customer ID</div><div class="value">' + invoice.clientInitials + '</div></div>';
  h += '</div>';

  /* Bill To */
  if (client) {
    var addrParts = [client.address || "", (client.city || "") + (client.state ? ", " + client.state : "") + (client.zip ? " " + client.zip : ""), client.country || "", client.phone || ""].filter(Boolean);
    h += '<div class="bill-to"><div class="bt-label">Bill To</div>';
    h += '<div class="bt-name">' + (client.fullName || client.shortName || client.name || "") + '</div>';
    if (addrParts.length > 0) h += '<div class="bt-addr">' + addrParts.join(" \u00b7 ") + '</div>';
    h += '</div>';
  }

  /* Product lines + SweetLeaf commission rows inline. Commission
     rendering per spec: one negative line per product showing
     "SweetLeaf Commission - {ProductName}" with qty = cases,
     price = -perCase, amount = -(cases × perCase). Advance Financing
     Fee follows as a single negative qty=1 row when set. */
  h += '<table class="items"><thead><tr>';
  h += '<th style="text-align:left;width:38%">Description</th>';
  h += '<th class="c" style="width:8%">Pallets</th>';
  h += '<th class="c" style="width:8%">Cases</th>';
  h += '<th class="r" style="width:14%">Price</th>';
  h += '<th class="c" style="width:13%">Status</th>';
  h += '<th class="r" style="width:19%">Amount</th>';
  h += '</tr></thead><tbody>';
  (invoice.lines || []).forEach(function(l) {
    var c = parseInt(l.cases, 10) || 0;
    var plt = parseInt(l.pallets, 10) || 0;
    var p = parseFloat(l.salePrice) || 0;
    var amt = c * p;
    var desc = l.productDescription || (l.productName || l.productBase || "");
    var statusLabel = l.saleStatus === "goodDelivery" ? "Good Delivery" : l.saleStatus === "protection" ? "Protection" : l.saleStatus === "pas" ? "PAS" : l.saleStatus === "dumped" ? "Dumped" : l.saleStatus === "donated" ? "Donated" : l.saleStatus === "samples" ? "Samples" : l.saleStatus === "transfer" ? "Transfer" : "";
    var statusColor = l.saleStatus === "goodDelivery" ? "#059669" : l.saleStatus === "pas" ? "#D97706" : l.saleStatus === "protection" || l.saleStatus === "dumped" ? "#DC2626" : l.saleStatus === "donated" ? "#6B7280" : l.saleStatus === "samples" ? "#7C3AED" : l.saleStatus === "transfer" ? "#2563EB" : "#666";
    var statusHTML = statusLabel ? '<span style="color:' + statusColor + ';font-weight:700;font-size:9px">' + statusLabel + '</span>' : '<span style="color:#bbb">—</span>';
    h += '<tr><td><b>' + (l.productName || l.productBase || "") + '</b>';
    if (desc && desc !== (l.productName || l.productBase)) h += '<div style="font-size:8px;color:#666;margin-top:2px">' + desc + '</div>';
    h += '</td><td class="c mono">' + plt + '</td><td class="c mono">' + c + '</td><td class="r mono">$' + fmtMoneyNoSign(p) + '</td><td class="c">' + statusHTML + '</td><td class="r mono">$' + fmtMoneyNoSign(amt) + '</td></tr>';
  });
  /* SweetLeaf commission lines — negative qty/price/amount per product. */
  (invoice.lines || []).forEach(function(l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var c = parseInt(l.cases, 10) || 0;
    var perCase = parseFloat(l.slCommissionPerCase) || 0;
    if (!(c > 0) || !(perCase > 0)) return;
    var lineComm = c * perCase;
    var name = l.productName || l.productBase || "Line";
    h += '<tr style="background:#fdf3f3">';
    h += '<td style="color:#c0392b"><b>SweetLeaf Commission</b><div style="font-size:8px;color:#666;margin-top:2px">' + name + '</div></td>';
    h += '<td class="c mono" style="color:#c0392b">—</td>';
    h += '<td class="c mono" style="color:#c0392b">' + c + '</td>';
    h += '<td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(perCase) + ')</td>';
    h += '<td class="c"></td>';
    h += '<td class="r mono" style="color:#c0392b;font-weight:700">($' + fmtMoneyNoSign(lineComm) + ')</td>';
    h += '</tr>';
  });
  /* Advance Financing Fee — single negative row when > 0. */
  if (advFee > 0) {
    h += '<tr style="background:#fdf3f3">';
    h += '<td style="color:#c0392b"><b>Advance Financing Fee</b></td>';
    h += '<td class="c mono" style="color:#c0392b">—</td>';
    h += '<td class="c mono" style="color:#c0392b">1</td>';
    h += '<td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(advFee) + ')</td>';
    h += '<td class="c"></td>';
    h += '<td class="r mono" style="color:#c0392b;font-weight:700">($' + fmtMoneyNoSign(advFee) + ')</td>';
    h += '</tr>';
  }
  h += '</tbody></table>';

  /* Summary — Total Due = Gross − Commission − AdvFinFee − Fees + Credits + Transfers − Payments.
     Commission + AdvFinFee already itemized in the table above; the
     summary just shows the running net so the customer can audit. */
  h += '<div class="summary"><table class="summary-tbl">';
  h += '<tr><td class="r"><b>Gross Sales</b></td><td></td><td class="r mono"><b>$' + fmtMoneyNoSign(totalAmount) + '</b></td></tr>';
  if (commissionAmt > 0) {
    h += '<tr><td class="r" style="color:#c0392b;font-size:9px">SweetLeaf Commission (total)</td><td></td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(commissionAmt) + ')</td></tr>';
  }
  if (advFee > 0) {
    h += '<tr><td class="r" style="color:#c0392b;font-size:9px">Advance Financing Fee</td><td></td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(advFee) + ')</td></tr>';
  }
  if (totalFees > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Fees &amp; Deductions</td></tr>';
    (invoice.fees || []).forEach(function(fe) {
      var amtAbs = Math.abs(parseFloat(fe.amount) || 0);
      if (!(amtAbs > 0)) return;
      var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "Fee");
      h += '<tr><td class="r" style="color:#c0392b">' + fname + '</td><td></td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(amtAbs) + ')</td></tr>';
    });
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Total Deductions</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:#c0392b;padding-top:4px;border-top:1px solid #ddd">($' + fmtMoneyNoSign(totalFees) + ')</td></tr>';
  }
  if (totalCredits > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Credits</td></tr>';
    (invoice.credits || []).forEach(function(cr) {
      var amtAbs = Math.abs(parseFloat(cr.amount) || 0);
      if (!(amtAbs > 0)) return;
      var label = cr.type === "fromInvoice"
        ? ("Credit from PU# " + (cr.refPuNumber || "—"))
        : (cr.concept || "Credit");
      h += '<tr><td class="r" style="color:#059669">' + label + '</td><td></td><td class="r mono" style="color:#059669">+$' + fmtMoneyNoSign(amtAbs) + '</td></tr>';
    });
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Total Credits</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:#059669;padding-top:4px;border-top:1px solid #ddd">+$' + fmtMoneyNoSign(totalCredits) + '</td></tr>';
  }
  /* Balance Transfers — cross-invoice linkage. + (credit) or − (debit). */
  if ((invoice.balanceTransfers || []).length > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Balance Transfers</td></tr>';
    (invoice.balanceTransfers || []).forEach(function(tr) {
      var amt = parseFloat(tr.amount) || 0;
      if (!(amt > 0)) return;
      var isCredit = tr.type === "credit";
      var color = isCredit ? "#059669" : "#c0392b";
      var sign = isCredit ? "+" : "−";
      var label = (isCredit ? "Credit from PU# " : "Debit to PU# ") + (tr.otherPuNumber || "?");
      h += '<tr><td class="r" style="color:' + color + '">' + label + '</td><td></td><td class="r mono" style="color:' + color + '">' + sign + '$' + fmtMoneyNoSign(amt) + '</td></tr>';
    });
    var transfersAbs = Math.abs(totalTransfers);
    var ttColor = totalTransfers >= 0 ? "#059669" : "#c0392b";
    var ttSign = totalTransfers >= 0 ? "+" : "−";
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Net Transfers</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:' + ttColor + ';padding-top:4px;border-top:1px solid #ddd">' + ttSign + '$' + fmtMoneyNoSign(transfersAbs) + '</td></tr>';
  }
  if (paid > 0) {
    h += '<tr class="total"><td class="r" colspan="2">Net</td><td class="r mono">$' + fmtMoneyNoSign(subtotal) + '</td></tr>';
    h += '<tr><td class="r" colspan="2" style="color:#c0392b">Less: Amount Paid</td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(paid) + ')</td></tr>';
  }
  h += '<tr class="due"><td class="r" colspan="2">TOTAL DUE</td><td class="r mono">$' + fmtMoneyNoSign(due) + '</td></tr>';
  h += '</table></div>';

  h += '<div class="footer">Sweetleaf Produce Inc. \u00b7 152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757-4041 \u00b7 USA</div>';
  h += '</div></body></html>';
  return h;
}

function fmtMoneyNoSign(n) { var v = parseFloat(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

/* ─────────────────────────────────────────────────────────────
   Transaction Approval Form (AGROCAP).
   PACA-aligned approval doc the Buyer signs to authorize AGROCAP
   to pay the Seller. Bilingual (EN/ES) per AGROCAP's standard.

   Inputs:
     invoice : an SL invoice record (must carry invoiceNumber,
               gfiPuNumber, clientRef, lines, etc.)
     ctx     : { shipments, warehouses } so we can resolve the
               delivery location + arrival date from the linked
               GFI shipment.
     opts    : { protection?: boolean } — when true renders the
               yellow "THIS FILE IS UNDER PROTECTION" banner so
               the doc clearly flags an active claim.

   Seller signature date is auto-computed: the next Wednesday
   after the Sunday following the shipment's arrival date. */
export function buildTransactionApprovalFormHTML(invoice, ctx, opts) {
  ctx = ctx || {};
  opts = opts || {};
  var shipments = ctx.shipments || [];
  var warehouses = ctx.warehouses || [];
  /* Find the linked GFI shipment (by gfiInvoiceId path: shipmentId
     or lot fallback). The shipment supplies warehouse + arrival. */
  var shipment = null;
  if (invoice.shipmentId) shipment = shipments.find(function (s) { return String(s.id) === String(invoice.shipmentId); });
  if (!shipment && invoice.gfiLot) shipment = shipments.find(function (s) { return String(s.lot) === String(invoice.gfiLot); });
  if (!shipment && invoice.lot) shipment = shipments.find(function (s) { return String(s.lot) === String(invoice.lot); });
  var wh = shipment ? warehouses.find(function (w) { return String(w.id) === String(shipment.warehouseId); }) : null;
  var whName = wh ? (wh.shortName || wh.fullName || "") : "";
  var arrivalDate = (shipment && (shipment.dateArrival || shipment.dateDeparture)) || invoice.date || "";

  /* Seller signature date = next Wednesday after the Sunday that
     follows the arrival date. Empty when arrival is unknown. */
  var sellerSignStr = "";
  if (arrivalDate) {
    try {
      var arrDate = new Date(arrivalDate + "T00:00:00");
      var day = arrDate.getDay(); /* 0=Sun..6=Sat */
      var daysToNextSunday = (7 - day) % 7 || 7;
      var nextSunday = new Date(arrDate);
      nextSunday.setDate(arrDate.getDate() + daysToNextSunday);
      var nextWednesday = new Date(nextSunday);
      nextWednesday.setDate(nextSunday.getDate() + 3);
      sellerSignStr = (nextWednesday.getMonth() + 1) + "/" + nextWednesday.getDate() + "/" + nextWednesday.getFullYear();
    } catch (e) { sellerSignStr = ""; }
  }

  /* Total = invoice gross MINUS commission, advFinFee, fees + credits ± transfers.
     Mirrors calcInvoiceTotal but inlined to keep this builder self-contained. */
  var totalGross = 0, commission = 0;
  (invoice.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var cases = parseInt(l.cases, 10) || 0;
    totalGross += cases * (parseFloat(l.salePrice) || 0);
    commission += cases * (parseFloat(l.slCommissionPerCase) || 0);
  });
  var advFee = Math.abs(parseFloat(invoice.advanceFinancingFee) || 0);
  var tFees = 0; (invoice.fees || []).forEach(function (fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0; (invoice.credits || []).forEach(function (cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  var tTransfers = 0; (invoice.balanceTransfers || []).forEach(function (tr) { var amt = parseFloat(tr.amount) || 0; if (tr.type === "credit") tTransfers += amt; else if (tr.type === "debit") tTransfers -= amt; });
  var invoiceTotal = totalGross - commission - advFee - tFees + tCredits + tTransfers;
  var totalStr = "$" + (invoiceTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /* Distinct product names — drop excluded statuses since they
     wouldn't appear on the priced invoice the doc is approving. */
  var seenProducts = {};
  var productList = [];
  (invoice.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var n = l.productName || l.productBase || "";
    if (n && !seenProducts[n]) { seenProducts[n] = true; productList.push(n); }
  });
  var productStr = productList.join(", ") || "—";

  var po = invoice.clientRef || "—";
  var bol = invoice.gfiPuNumber || invoice.puNumber || "—";
  var invNum = invoice.invoiceNumber || invoice.puNumber || "—";
  function fmtUS(iso) { if (!iso) return "—"; try { var d = new Date(iso + "T00:00:00"); return (d.getMonth() + 1) + "/" + d.getDate() + "/" + d.getFullYear(); } catch (e) { return iso; } }
  var dateStr = fmtUS(invoice.date);
  var arrivalStr = fmtUS(arrivalDate);
  function esc(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Transaction Approval Form - ' + esc(invNum) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.5in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:760px;margin:0 auto}';
  h += '.header{display:flex;align-items:center;gap:18px;padding-bottom:12px;border-bottom:2px solid #0E2F4F;margin-bottom:14px}';
  h += '.logo img{height:60px;display:block}';
  h += '.company-block{font-size:10px;line-height:1.5}';
  h += '.company-name{font-weight:700;font-size:14px;margin-bottom:2px;color:#0E2F4F}';
  h += '.doc-title{text-align:center;font-size:24px;font-weight:700;color:#0E2F4F;margin:8px 0}';
  h += '.protection{background:#FFD700;color:#7F1D1D;font-weight:800;text-transform:uppercase;padding:6px;text-align:center;letter-spacing:1.5px;margin:0 0 12px;font-size:12px;border:1px solid #B91C1C}';
  h += '.section-header{background:#D9D9D9;border:1px solid #000;padding:6px;text-align:center;font-weight:700;text-transform:uppercase;font-size:11px;letter-spacing:0.5px;color:#000}';
  h += 'table.details{width:100%;border-collapse:collapse;border:1px solid #000;margin-bottom:12px}';
  h += 'table.details td{padding:5px 8px;border:1px solid #000;font-size:10px}';
  h += 'table.details td.label-en{width:28%;font-weight:700}';
  h += 'table.details td.value{width:44%;text-align:center}';
  h += 'table.details td.label-es{width:28%;font-weight:700}';
  h += 'table.terms{width:100%;border-collapse:collapse;border:1px solid #000;margin-bottom:12px}';
  h += 'table.terms td{padding:7px 8px;border:1px solid #000;vertical-align:top;font-size:9px;line-height:1.35;width:50%}';
  h += '.confirmation{display:flex;gap:0;margin:10px 0 10px}';
  h += '.confirmation .col{flex:1;padding:8px;border:1px solid #000;background:#f6f6f6;font-weight:700;font-size:9px;text-transform:uppercase;line-height:1.3}';
  h += '.confirmation .col + .col{border-left:none}';
  h += 'table.sign{width:100%;border-collapse:collapse;border:1px solid #000;margin-bottom:12px}';
  /* SELLER / BUYER headers — olive drab #6B8E23 per the AGROCAP master. */
  h += 'table.sign th{background:#6B8E23;color:#fff;padding:6px;border:1px solid #000;font-weight:700;font-size:11px;text-align:center;letter-spacing:0.5px}';
  h += 'table.sign td{border:1px solid #000;padding:5px 8px;font-size:10px;vertical-align:middle}';
  h += 'table.sign td.field{font-weight:700;width:18%;background:#f6f6f6;text-transform:uppercase;font-size:9px;letter-spacing:0.5px}';
  /* Signature row sits tall so Arturo\'s scanned signature renders
     full-size inside the seller cell. */
  h += 'table.sign tr.sig-row td{height:80px;text-align:center;vertical-align:middle}';
  /* Signature image: 70px tall, contain to keep aspect, mix-blend-mode
     darken so the white background of the JPG drops out and only the
     ink shows over the cell. */
  h += 'table.sign tr.sig-row img{height:70px;object-fit:contain;mix-blend-mode:darken;display:inline-block}';
  h += '.footer-row{display:flex;gap:10px;margin-top:8px;align-items:stretch}';
  /* For Internal Use box — light gray fill, 3-column grid with
     write-on lines under each label. */
  h += '.footer-internal{flex:2;border:1px solid #000;background:#F0F0F0;padding:10px 12px;font-size:9px}';
  h += '.footer-internal .ttl{font-weight:700;font-size:10px;margin-bottom:8px;text-transform:uppercase;letter-spacing:0.5px}';
  h += '.footer-internal .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px}';
  h += '.footer-internal .grid .lbl{font-weight:700;text-transform:uppercase;font-size:9px;margin-bottom:18px;letter-spacing:0.3px}';
  h += '.footer-internal .grid .line{border-bottom:1px solid #000;height:1px}';
  /* Buyer warning box — gold/amber border, black bold text, white fill. */
  h += '.footer-warning{flex:1;border:3px solid #DAA520;background:#fff;padding:10px;font-size:9px;font-weight:700;color:#000;line-height:1.35;text-transform:uppercase;display:flex;align-items:center;justify-content:center;text-align:center}';
  h += '.footer-warning b{font-weight:800}';
  h += '</style></head><body><div class="page">';

  h += '<div class="header">';
  h += '<div class="logo"><img src="' + AGROCAP_LOGO_B64 + '" /></div>';
  h += '<div class="company-block">';
  h += '<div class="company-name">AGROCAP LLC.</div>';
  h += '<div>2505 Anthem Village Dr., Ste. E524</div>';
  h += '<div>Henderson, NV 89052 USA</div>';
  h += '<div>(702) 359-3416</div>';
  h += '<div>hello@agrocap.us</div>';
  h += '</div></div>';

  h += '<div class="doc-title">Transaction Approval Form</div>';
  if (opts.protection) {
    h += '<div class="protection">THIS FILE IS UNDER PROTECTION</div>';
  }

  h += '<div class="section-header">TRANSACTION DETAILS</div>';
  h += '<table class="details">';
  h += '<tr><td class="label-en">DATE</td><td class="value">' + esc(dateStr) + '</td><td class="label-es">FECHA</td></tr>';
  h += '<tr><td class="label-en">PURCHASE ORDER</td><td class="value">' + esc(po) + '</td><td class="label-es">ORDEN DE COMPRA</td></tr>';
  h += '<tr><td class="label-en">BILL OF LADING NUMBER</td><td class="value">' + esc(bol) + '</td><td class="label-es">NÚMERO DE GUIA DE CARGA</td></tr>';
  h += '<tr><td class="label-en">INVOICE NUMBER</td><td class="value">' + esc(invNum) + '</td><td class="label-es">NÚMERO DE FACTURA</td></tr>';
  h += '<tr><td class="label-en">SELLER</td><td class="value">Sweetleaf Produce Inc</td><td class="label-es">PROVEEDOR</td></tr>';
  h += '<tr><td class="label-en">BUYER</td><td class="value">Four Seasons Produce</td><td class="label-es">CLIENTE</td></tr>';
  h += '<tr><td class="label-en">DELIVERY LOCATION</td><td class="value">' + esc(whName || "—") + '</td><td class="label-es">LUGAR DE ENTREGA</td></tr>';
  h += '<tr><td class="label-en">PRODUCT</td><td class="value">' + esc(productStr) + '</td><td class="label-es">PRODUCTO</td></tr>';
  h += '<tr><td class="label-en">ARRIVAL DATE</td><td class="value">' + esc(arrivalStr) + '</td><td class="label-es">FECHA DE ARRIBO</td></tr>';
  h += '<tr><td class="label-en">INVOICE VALUE USD</td><td class="value"><b>' + esc(totalStr) + '</b></td><td class="label-es">VALOR DE FACTURA USD</td></tr>';
  h += '</table>';

  h += '<div class="section-header">TRANSACTION TERMS</div>';
  h += '<table class="terms">';
  h += '<tr><td>Payment to AGROCAP by the Buyer will be due per PACA terms: 21 days from acceptance of product by the Buyer.</td><td>El pago del Cliente a AGROCAP está regido por la regulación PACA: 21 días posteriores a la aceptación del producto por parte del Cliente.</td></tr>';
  h += '<tr><td>Buyer has not previously advanced any funds (pre-season, pre-shipment, or any other) to Seller.</td><td>El Cliente no le ha proporcionado ningún avance monetario (pre-temporada, pre-envío, u otro) al Proveedor.</td></tr>';
  h += '<tr><td>In the case of a quality and/or condition claim, Buyer agrees to provide both the Seller and AGROCAP, an official USDA inspection within 24 hours of product arrival.</td><td>En el caso de un reclamo por calidad y/o condición, el Cliente acuerda proveerle tanto al Proveedor como a AGROCAP, una copia de la inspección USDA oficial dentro de las 24 horas posteriores a la llegada del producto.</td></tr>';
  h += '<tr><td>In the case of a quality and/or condition claim, AGROCAP is unable to advance any funds to the Seller, and these can only be paid once the Buyer and the Seller have agreed on any possible revised pricing in writing.</td><td>En el caso de un reclamo por calidad y/o condición, AGROCAP en ningún caso puede avanzar el pago al Proveedor, y este solo podrá emitirse cuando el Proveedor y el Cliente hayan acordado por escrito cualquier revisión al precio del producto.</td></tr>';
  h += '</table>';

  h += '<div class="confirmation">';
  h += '<div class="col">BUYER REPORTS GOOD ARRIVAL OF PRODUCT (NO CLAIM, DEDUCTIONS OR DISCREPANCIES) AND APPROVES PAYMENT BY AGROCAP TO SELLER.</div>';
  h += '<div class="col">CLIENTE REPORTA EL BUEN ARRIBO DEL PRODUCTO (SIN RECLAMO, DESCUENTOS, O DISCREPANCIAS) Y APRUEBA EL PAGO DE AGROCAP AL PROVEEDOR.</div>';
  h += '</div>';

  h += '<table class="sign">';
  h += '<tr><th></th><th>SELLER</th><th>BUYER *</th></tr>';
  /* Seller signature cell shows Arturo\'s scanned signature with the
     white background blended out (see CSS mix-blend-mode:darken
     rule above). Buyer cell stays empty for handwritten sign-off. */
  h += '<tr class="sig-row"><td class="field">Signature</td><td><img src="' + ARTURO_SIGNATURE_B64 + '" alt="signature" /></td><td></td></tr>';
  h += '<tr><td class="field">Company</td><td>Sweetleaf Produce Inc</td><td>Four Seasons Produce</td></tr>';
  h += '<tr><td class="field">Representative</td><td>Arturo Gonzalez</td><td>Brian Arena</td></tr>';
  h += '<tr><td class="field">Date</td><td>' + esc(sellerSignStr) + '</td><td></td></tr>';
  h += '</table>';

  h += '<div class="footer-row">';
  h += '<div class="footer-internal">';
  h += '<div class="ttl">FOR INTERNAL USE / PARA USO INTERNO</div>';
  h += '<div class="grid">';
  h += '<div><div class="lbl">Internal Reference Number</div><div class="line"></div></div>';
  h += '<div><div class="lbl">Approved by</div><div class="line"></div></div>';
  h += '<div><div class="lbl">Approved Date</div><div class="line"></div></div>';
  h += '</div>';
  h += '</div>';
  h += '<div class="footer-warning"><span><b>*</b>BUYER: ONLY SIGN IF PRODUCT ARRIVED IN SATISFACTORY CONDITION AND YOU APPROVE THAT WE SEND PAYMENT TO SELLER.</span></div>';
  h += '</div>';

  h += '</div></body></html>';
  return h;
}

/* Multi-invoice print: reuses the shared <style> block from the first doc and
   concatenates each body with a hard page-break in between. Mirrors
   printMultipleSalesReports so behavior stays identical across modules. */
export function printMultipleInvoices(invoices, clients, onError) {
  if (!Array.isArray(invoices) || invoices.length === 0) return;
  var byId = {};
  (clients || []).forEach(function(c) { byId[String(c.id)] = c; });

  var htmls = invoices.map(function(inv) {
    var cli = byId[String(inv.clientId)] || null;
    return buildInvoicePrintHTML(inv, cli);
  });
  var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodies = htmls.map(function(html) {
    var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
    return m ? m[1] : "";
  });
  var today = new Date().toISOString().substring(0, 10);
  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoices - ' + today + '</title><style>' + sharedCss + ' .inv-pagebreak{page-break-before:always;break-before:page;height:0;margin:0;padding:0}</style></head><body>';
  bodies.forEach(function(body, i) {
    if (i > 0) doc += '<div class="inv-pagebreak"></div>';
    doc += body;
  });
  doc += '</body></html>';

  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write(doc);
  w.document.close();
  setTimeout(function() { try { w.focus(); w.print(); } catch (e) {} }, 600);
}

/* ============= BRAND-AWARE SALES DOCS (English-only) =============
   Two printable docs × two brands = four variants:
     1. Pick Ticket / Inbound — product summary, picked at print time
     2. Packing List          — per-pallet detail
   Brands:
     - GFI       (Ground-Fresh International, navy #2A7A6F, LOGO_B64)
     - SweetLeaf (Sweetleaf Produce Inc.,    teal #2A7A6F, SWEETLEAF_LOGO_B64)
   The "kind" string passed to printInvoiceDocs follows
   "<brand>-<doc>" where brand=gfi|sl, doc=pick|inbound|packing.
   All four use inline CSS so print preserves formatting. Multi-print
   concatenates bodies separated by page-break. */

function escDocHtml(s) {
  if (s === undefined || s === null) return "";
  return String(s).replace(/[&<>"']/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]; });
}
function fmtDocDate(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); }

/* Per-pallet weight mirror of ShipmentRegistry's calcPalletWeight.
   Inlined here so this file stays self-contained for printing. */
var DOC_SOZ = { "6oz": 6, "12oz": 12, "18oz": 18, "1 dry pint": 16, "1lb": 16, "2lb": 32 };
function docPalletWeight(p) {
  if (!p) return { lb: 0, kg: 0 };
  var oz = (DOC_SOZ[p.size] || 0) * (parseFloat(p.unitsCase) || 0) * (parseFloat(p.casesPallet) || 0);
  if (!oz) return { lb: 0, kg: 0 };
  var lb = oz / 16;
  return { lb: lb, kg: lb * 0.453592 };
}

/* Brand config table. The shipperName + shipperAddr are hard-coded
   per the spec rather than pulled from CompanyProfile because the
   docs are brand-specific (SweetLeaf is a trade name; the Company
   Profile holds GFI's legal entity). The contact phone/website still
   read from CompanyProfile so a single update propagates. */
function brandConfig(brandKey) {
  if (brandKey === "sl") {
    return {
      key: "sl",
      name: "Sweetleaf Produce Inc.",
      accent: "#2A7A6F",
      accentRgba: "rgba(42,122,111,0.06)",
      panelBg: "#eef5f4",
      logo: SWEETLEAF_LOGO_B64,
      shipperName: "Sweetleaf Produce Inc.",
      shipperAddr: "152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757-4041",
      footer: "Sweetleaf Produce Inc. · Lindenhurst, NY, USA",
      organicCert: false,
      tempRecorder: true,
      lotColumn: true,
    };
  }
  return {
    key: "gfi",
    name: "Ground-Fresh International Inc.",
    accent: "#2A7A6F",
    accentRgba: "rgba(27,58,75,0.06)",
    panelBg: "#eef4f5",
    logo: LOGO_B64,
    shipperName: "Ground-Fresh International Inc.",
    shipperAddr: "152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757-4041",
    footer: "Ground Fresh International Inc. · Lindenhurst, NY, USA · ground-fresh.com",
    organicCert: true,
    tempRecorder: false,
    lotColumn: false,
  };
}

/* Brand-aware stylesheet — accent color swaps per brand, everything
   else identical. Class names stable so the body markup is shared
   across the 4 doc variants. */
function brandDocCss(brand) {
  var c = brand.accent;
  var bg = brand.panelBg;
  var css = '@page{size:letter;margin:0.5in 0.55in}';
  css += '*{margin:0;padding:0;box-sizing:border-box}';
  css += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  css += '.page{max-width:730px;margin:0 auto}';
  css += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid ' + c + ';margin-bottom:14px}';
  /* Bumped from 60 → 95. Both brand logos benefit: the GFI raster
     "Ground-Fresh International" wordmark becomes readable, and the
     SweetLeaf SVG (wider aspect ratio) still fits comfortably in the
     header next to the centered/right document title. */
  css += '.logo img{height:95px}';
  css += '.doc-title{font-size:22px;font-weight:300;color:' + c + ';letter-spacing:1.5px;text-align:right}';
  css += '.doc-title.center{text-align:center;flex:1}';
  css += '.doc-meta{font-size:9px;color:#666;text-align:right;margin-top:6px;line-height:1.6}';
  css += '.doc-meta b{color:' + c + ';font-weight:700}';
  css += '.party-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}';
  css += '.party-block{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  css += '.party-block .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  css += '.party-block .name{font-size:12px;font-weight:700;color:' + c + ';margin-bottom:3px}';
  css += '.party-block .line{font-size:10px;color:#444;line-height:1.5}';
  css += '.fields-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px;margin-bottom:6px;padding:10px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid ' + c + '}';
  css += '.fields-grid .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  css += '.fields-grid .val{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px;font-family:"Courier New",monospace}';
  /* ETA disclaimer — bumped to 10px because the prior 8px was almost
     invisible on screen and barely readable in print preview. */
  css += '.eta-note{font-size:10px;color:#666;font-style:italic;margin:4px 14px 12px;letter-spacing:0.3px}';
  css += 'table{width:100%;border-collapse:collapse}';
  css += '.items th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:' + c + ';font-weight:600}';
  css += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  css += '.items td{padding:6px 8px;border-bottom:1px solid #eee;font-size:10px;vertical-align:middle}';
  css += '.items tr:nth-child(even){background:#fafbfc}';
  css += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  css += '.totals{background:' + c + ';color:#fff;font-weight:800}';
  css += '.totals td{font-size:11px;color:#fff;padding:8px 8px;letter-spacing:0.5px;border-bottom:none}';
  css += '.summary{margin-top:14px;padding:10px 14px;background:' + bg + ';border-radius:6px;display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px}';
  css += '.summary .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:' + c + ';font-weight:700}';
  css += '.summary .val{font-size:14px;font-weight:800;color:' + c + ';font-family:"Courier New",monospace;margin-top:2px}';
  css += '.remarks{margin-top:14px;padding:12px 14px;border:1px dashed #ccc;border-radius:6px;min-height:60px}';
  css += '.remarks .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  css += '.cert-strip{margin-top:14px;padding:8px 14px;background:' + c + ';color:#fff;border-radius:6px;font-size:9px;text-align:center;letter-spacing:0.5px}';
  css += '.footer{margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  return css;
}

/* Resolve the shipment that fed a given invoice line-set. Tries 3
   match strategies in order — strict, trimmed, then numeric — to
   tolerate the small inconsistencies that creep in over time
   (whitespace from imports, leading zeros, numeric vs string types).
   Returns null when none match. */
/* Identity match that tolerates BOTH the legacy id (rowToRecord sets
   record.id = row.legacy_id, which may be a Date.now() string from
   the LS era) AND the Supabase UUID (rowToRecord sets
   record._uuid = row.id). Foreign-key references stored on shipments
   / invoices / bills can be either form depending on when the parent
   row was created; without checking both, lookups fail silently on
   any row whose FK was written with the "other" id shape and the UI
   renders "—" / "Warehouse: —" / "No shipment found". */
function idMatches(record, ref) {
  if (!record || ref === undefined || ref === null || ref === "") return false;
  var s = String(ref);
  return String(record.id) === s || String(record._uuid) === s;
}
function findShipmentForInvoice(invoice, shipments) {
  if (!invoice) return null;
  var lotRaw = invoice.lot || (invoice.lines && invoice.lines[0] && invoice.lines[0].lot) || "";
  if (!lotRaw && lotRaw !== 0) return null;
  var lotStr = String(lotRaw);
  var list = shipments || [];
  /* 1. Exact string match — fast path that handles 95% of cases. */
  var hit = list.find(function (s) { return String(s.lot) === lotStr; });
  if (hit) return hit;
  /* 2. Trimmed match — guards against stray whitespace from Excel imports. */
  var trimmed = lotStr.trim();
  hit = list.find(function (s) { return String(s.lot || "").trim() === trimmed; });
  if (hit) return hit;
  /* 3. Numeric match — handles "57916" vs 57916 type mismatches. */
  var lotNum = parseInt(lotStr, 10);
  if (!isNaN(lotNum)) {
    hit = list.find(function (s) { var n = parseInt(s.lot, 10); return !isNaN(n) && n === lotNum; });
    if (hit) return hit;
  }
  return null;
}
/* Find the bill associated with a given lot. Same tolerance ladder
   as findShipmentForInvoice (exact → trimmed → numeric) so the
   common "57916" / 57916 / " 57916 " mismatches don't drop the link. */
function findBillByLot(lot, bills) {
  if (!lot && lot !== 0) return null;
  var s = String(lot);
  var list = bills || [];
  var hit = list.find(function (b) { return String(b.lot) === s; });
  if (hit) return hit;
  var trimmed = s.trim();
  hit = list.find(function (b) { return String(b.lot || "").trim() === trimmed; });
  if (hit) return hit;
  var n = parseInt(s, 10);
  if (!isNaN(n)) {
    hit = list.find(function (b) { var bn = parseInt(b.lot, 10); return !isNaN(bn) && bn === n; });
    if (hit) return hit;
  }
  return null;
}

function resolveWarehouse(shipment, warehouses) {
  if (!shipment) return null;
  return (warehouses || []).find(function (x) { return idMatches(x, shipment.warehouseId); }) || null;
}

/* ──────── BUILDER 1 — PICK TICKET / INBOUND ────────
   opts: { brandKey: "gfi"|"sl", kind: "pick"|"inbound" }
   Same body for both Pick Ticket and Inbound — only the title changes.
   SweetLeaf inserts a Lot # column between Description and Pallets. */
export function buildPickTicketOrInboundHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brand = brandConfig(opts.brandKey || "gfi");
  var kind = opts.kind === "pick" ? "pick" : "inbound";
  var titlePrefix = kind === "pick" ? "Pick Ticket" : "Inbound";
  var clients = ctx && ctx.clients ? ctx.clients : [];
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var warehouses = ctx && ctx.warehouses ? ctx.warehouses : [];
  var company = (ctx && ctx.company) || loadCompanyProfile();
  var client = clients.find(function (c) { return idMatches(c, invoice.clientId); }) || null;
  var shipment = findShipmentForInvoice(invoice, shipments);
  var wh = resolveWarehouse(shipment, warehouses);
  var whName = wh ? (wh.fullName || wh.shortName || wh.name || "") : (shipment ? (shipment.warehouseName || "") : "");
  var whAddr = wh ? [wh.address, (wh.city || "") + (wh.state ? ", " + wh.state : "") + (wh.zip ? " " + wh.zip : "")].filter(Boolean).join(", ") : "";
  var clientName = client ? (client.fullName || client.shortName || client.name || client.companyName || invoice.clientName || "") : (invoice.clientName || "");
  var clientAddr = client ? [client.address, (client.city || "") + (client.state ? ", " + client.state : "") + (client.zip || client.zipCode ? " " + (client.zip || client.zipCode) : "")].filter(Boolean).join(", ") : "";
  var clientPhone = client ? (client.phone || (client.phoneArea && client.phoneNumber ? "(" + client.phoneArea + ") " + client.phoneNumber : "")) : "";
  var customerId = client ? (client.initials || "") : "";
  /* Per FIX: main "Date" is the shipment's Arrival ETA, not Departure.
     Loading Date is now user-chosen for Pick Ticket (passed via
     opts.loadingDate from the remarks modal); Inbound falls back to
     the actual shipment departure date as before. */
  var etaDate = shipment ? (shipment.dateArrival || "") : "";
  var loadingDate = opts.loadingDate || (shipment ? (shipment.dateDeparture || "") : "");
  var lot = invoice.lot || (shipment ? shipment.lot : "");
  /* PU# displayed on the customer-facing doc is ALWAYS the GFI PU#
     (gfiPuNumber on SL records). The SL invoiceNumber (INV-XXXXX)
     never appears on Pick Ticket / Inbound / Packing List — the
     customer references the GFI PU# from their PO history. Falls
     back to puNumber so GFI invoices (no gfiPuNumber) keep working. */
  var pu = invoice.gfiPuNumber || invoice.puNumber || "";

  /* Aggregate invoice lines — Inbound/Pick Ticket shows product totals,
     not pallet-level detail. UOM defaults to "Flats". */
  var rows = (invoice.lines || []).filter(function (l) { return l.productName || l.productBase; }).map(function (l) {
    return {
      product: l.productBase || l.productName || "",
      desc: l.productDescription || ((l.productName || l.productBase || "") + " " + (l.size || "") + " " + (l.category || "")).trim(),
      pallets: parseInt(l.pallets, 10) || 0,
      cases: parseInt(l.cases, 10) || 0,
      lot: l.lot || lot,
    };
  });
  var totalPallets = rows.reduce(function (s, r) { return s + r.pallets; }, 0);
  var totalCases = rows.reduce(function (s, r) { return s + r.cases; }, 0);

  /* Filename per spec — different prefix per brand × kind. */
  var fname;
  if (brand.key === "sl") fname = "PU_" + pu + "_PO_" + (invoice.clientRef || "");
  else fname = (kind === "pick" ? "Pick_Ticket_" : "Inbound_") + pu + "_PO_" + (invoice.clientRef || "");

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += brandDocCss(brand);
  h += '</style></head><body><div class="page">';

  h += '<div class="header"><div class="logo"><img src="' + brand.logo + '" /></div>';
  h += '<div><div class="doc-title">' + titlePrefix + ' ' + escDocHtml(pu) + '</div></div></div>';

  /* Two-column block: Shipper on the left, Warehouse on the right. */
  var shipperPhone = company.phone || "";
  h += '<div class="party-grid">';
  h += '<div class="party-block"><div class="lbl">From</div>';
  h += '<div class="name">' + escDocHtml(brand.shipperName) + '</div>';
  h += '<div class="line">' + escDocHtml(brand.shipperAddr) + '</div>';
  if (shipperPhone) h += '<div class="line">Tel: ' + escDocHtml(shipperPhone) + '</div>';
  h += '</div>';
  h += '<div class="party-block"><div class="lbl">Warehouse</div>';
  h += '<div class="name">' + escDocHtml(whName || "—") + '</div>';
  if (whAddr) h += '<div class="line">' + escDocHtml(whAddr) + '</div>';
  if (wh && wh.phone) h += '<div class="line">Tel: ' + escDocHtml(wh.phone) + '</div>';
  h += '</div></div>';

  /* Customer block (full width) */
  h += '<div class="party-block" style="margin-bottom:12px"><div class="lbl">Customer</div>';
  h += '<div class="name">' + escDocHtml(clientName || "—") + '</div>';
  if (clientAddr) h += '<div class="line">' + escDocHtml(clientAddr) + '</div>';
  if (clientPhone) h += '<div class="line">Tel: ' + escDocHtml(clientPhone) + '</div>';
  h += '</div>';

  /* Customs / cert references from the linked shipment + invoice.
     Entry # is one-per-shipment (shipment.entryNumber). Export Certs
     are computed per organic invoice line and grouped by product;
     buildExportCertEntries returns either [single cert] or
     ["Product — cert", ...] depending on whether all organic
     products share one cert. */
  var entryNumber = shipment ? (shipment.entryNumber || "") : "";
  var exportCertEntries = buildExportCertEntries(invoice, shipment);
  /* Rendering: span 2 for a single line (matches Customs Entry), or
     span 4 when there are multiple per-product lines so they wrap
     cleanly across the grid. */
  var certCellSpan = exportCertEntries.length <= 1 ? 2 : 4;
  var certCellBody = exportCertEntries.length === 0
    ? "—"
    : exportCertEntries.map(escDocHtml).join("<br>");

  /* Right-hand fields strip. Per FIX the headline "Date" is ETA. */
  h += '<div class="fields-grid">';
  h += '<div><div class="lbl">Date</div><div class="val">' + escDocHtml(fmtDocDate(etaDate) || "—") + '</div></div>';
  h += '<div><div class="lbl">' + (kind === "pick" ? "Pick Up" : "Inbound") + '</div><div class="val">' + escDocHtml(pu || "—") + '</div></div>';
  h += '<div><div class="lbl">Customer ID</div><div class="val">' + escDocHtml(customerId || "—") + '</div></div>';
  h += '<div><div class="lbl">Customer PO</div><div class="val">' + escDocHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div style="grid-column:span 4"><div class="lbl">Loading Date</div><div class="val">' + escDocHtml(fmtDocDate(loadingDate) || "—") + '</div></div>';
  h += '<div style="grid-column:span 2"><div class="lbl">Customs Entry #</div><div class="val mono">' + escDocHtml(entryNumber || "—") + '</div></div>';
  h += '<div style="grid-column:span ' + certCellSpan + '"><div class="lbl">Export Cert #</div><div class="val mono">' + certCellBody + '</div></div>';
  h += '</div>';
  /* ETA disclaimer per FIX — placed right under the fields block so
     the user reads it before the table. */
  h += '<div class="eta-note">* Subject to customs clearance</div>';

  /* Product table. SweetLeaf inserts a Lot # column between
     Description and Pallets per spec. Column widths recomputed when
     the lot column is present so the row stays balanced. */
  h += '<table class="items"><thead><tr>';
  if (brand.lotColumn) {
    h += '<th style="text-align:left;width:16%">Product</th>';
    h += '<th style="text-align:left;width:38%">Description</th>';
    h += '<th class="c" style="width:10%">Lot #</th>';
    h += '<th class="c" style="width:9%">Pallets</th>';
    h += '<th class="c" style="width:13%">Fill (Cases)</th>';
    h += '<th class="c" style="width:14%">UOM</th>';
  } else {
    h += '<th style="text-align:left;width:18%">Product</th>';
    h += '<th style="text-align:left;width:46%">Description</th>';
    h += '<th class="c" style="width:10%">Pallets</th>';
    h += '<th class="c" style="width:14%">Fill (Cases)</th>';
    h += '<th class="c" style="width:12%">UOM</th>';
  }
  h += '</tr></thead><tbody>';
  rows.forEach(function (r) {
    h += '<tr>';
    h += '<td><b>' + escDocHtml(r.product) + '</b></td>';
    h += '<td>' + escDocHtml(r.desc) + '</td>';
    if (brand.lotColumn) h += '<td class="c mono">' + escDocHtml(r.lot || "—") + '</td>';
    h += '<td class="c mono">' + r.pallets + '</td>';
    h += '<td class="c mono"><b>' + r.cases + '</b></td>';
    h += '<td class="c">Flats</td>';
    h += '</tr>';
  });
  /* TOTAL row — colspan adjusts for the optional Lot column. */
  if (brand.lotColumn) {
    h += '<tr class="totals"><td colspan="3" style="text-align:right">TOTAL</td>';
  } else {
    h += '<tr class="totals"><td colspan="2" style="text-align:right">TOTAL</td>';
  }
  h += '<td class="c mono">' + totalPallets + '</td>';
  h += '<td class="c mono">' + totalCases + '</td>';
  h += '<td class="c">Flats</td></tr>';
  h += '</tbody></table>';

  /* Remarks block — Pick Ticket only. Inbound omits it entirely
     because the fruit lands at the customer's warehouse and GFI is
     no longer responsible for the load; the receiver doesn't need a
     remarks field. Pick Ticket renders the lines the user chose in
     the remarks modal (mandatory + optional + free-text). */
  if (kind === "pick") {
    var remarks = Array.isArray(opts.remarks) ? opts.remarks : [];
    h += '<div class="remarks"><div class="lbl">Remarks</div>';
    if (remarks.length > 0) {
      /* All remarks render as alert banners — warehouse must see
         every line. Yellow #FFF3CD background, orange border, ⚠️
         icon, bold black text. print-color-adjust:exact forces
         browsers to keep the background fill in PDF output. */
      var bannerStyle = 'background:#FFF3CD;border:2px solid #E67E22;border-left:6px solid #C0392B;padding:10px 14px;margin:4px 0 10px;border-radius:6px;display:flex;align-items:center;gap:10px;-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact';
      var bannerIconStyle = 'font-size:18px;line-height:1;flex-shrink:0';
      var bannerTextStyle = 'font-size:13px;font-weight:800;color:#000;letter-spacing:0.3px;line-height:1.3';
      remarks.forEach(function (line) {
        h += '<div style="' + bannerStyle + '">';
        h += '<span style="' + bannerIconStyle + '">⚠️</span>';
        h += '<span style="' + bannerTextStyle + '">' + escDocHtml(line) + '</span>';
        h += '</div>';
      });
    }
    h += '</div>';
  }

  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* Backwards-compat alias — earlier code/tests might import the old
   name. Defaults to GFI brand + Inbound kind. */
export function buildInboundSheetHTML(invoice, ctx) {
  return buildPickTicketOrInboundHTML(invoice, ctx, { brandKey: "gfi", kind: "inbound" });
}

/* ──────── BUILDER 2 — PACKING LIST (per-pallet) ────────
   opts: { brandKey: "gfi"|"sl" }
   - GFI: includes Organic Cert strip at the bottom
   - SL : adds "Temp. Recorder #" to header meta, no Organic Cert. */
/* Resolves the packing-list row set for one invoice.
   NEW (palletAllocations present): one row per allocation, resolved
   live against each referenced shipment's pallet manifest by
   (lot, palletNum) — supports multi-lot invoices and reflects only the
   pallets/cases actually assigned to THIS invoice, not the whole lot.
   LEGACY (no allocations — pre-FASE-3 invoices): falls back to the
   old behavior, dumping the entire manifest of the single shipment
   findShipmentForInvoice resolves, so historical invoices keep
   rendering exactly as before until migrated (FASE 7). */
function buildPackingListRows(invoice, shipments) {
  var allocations = Array.isArray(invoice.palletAllocations)
    ? invoice.palletAllocations.filter(function (a) { return a && a.lot && a.palletNum && (parseInt(a.cases, 10) || 0) > 0; })
    : [];
  if (allocations.length > 0) {
    var shipByLot = {};
    (shipments || []).forEach(function (s) { if (s.lot != null) shipByLot[String(s.lot)] = s; });
    var rows = allocations.map(function (a) {
      var shp = shipByLot[String(a.lot)];
      var meta = shp ? (shp.palletManifest || []).find(function (p) { return String(p.palletNum) === String(a.palletNum); }) : null;
      var cases = parseInt(a.cases, 10) || 0;
      return {
        lot: a.lot, palletNum: a.palletNum,
        productBase: meta ? meta.productBase : "", variety: meta ? meta.variety : "", category: meta ? meta.category : "",
        unitsCase: meta ? meta.unitsCase : "", size: meta ? meta.size : "", labelInitials: meta ? meta.labelInitials : "",
        cases: cases,
        weight: docPalletWeight({ size: meta && meta.size, unitsCase: meta && meta.unitsCase, casesPallet: cases }),
        tagId: meta ? meta.tagId : "",
        julian: meta ? (meta.julianCode || meta.traceability || "") : "",
        grower: meta ? meta.growerCode : "",
      };
    });
    return { rows: rows, legacy: false };
  }
  return { rows: [], legacy: true };
}

export function buildPackingListHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brand = brandConfig(opts.brandKey || "gfi");
  var clients = ctx && ctx.clients ? ctx.clients : [];
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var warehouses = ctx && ctx.warehouses ? ctx.warehouses : [];
  var company = (ctx && ctx.company) || loadCompanyProfile();
  var client = clients.find(function (c) { return idMatches(c, invoice.clientId); }) || null;
  /* Header-level fields (ETA, warehouse, entry #, thermograph) stay
     tied to ONE shipment — the primary/header lot — same as before.
     Multi-lot support (FASE 3-6) only affects the per-pallet table
     below and the Lot(s) line; a document only has one ship date. */
  var shipment = findShipmentForInvoice(invoice, shipments);
  var wh = resolveWarehouse(shipment, warehouses);
  var whAddr = wh ? [wh.address, (wh.city || "") + (wh.state ? ", " + wh.state : "") + (wh.zip ? " " + wh.zip : "")].filter(Boolean).join(", ") : "";
  var clientName = client ? (client.fullName || client.shortName || client.name || client.companyName || invoice.clientName || "") : (invoice.clientName || "");
  var clientContact = client && client.contacts && client.contacts.length > 0
    ? (client.contacts[0].name || (client.contacts[0].firstName ? client.contacts[0].firstName + " " + (client.contacts[0].lastName || "") : "")).trim()
    : (client ? (client.contact || "") : "");
  var clientPhone = client ? (client.phone || (client.phoneArea && client.phoneNumber ? "(" + client.phoneArea + ") " + client.phoneNumber : "")) : "";
  var rowSet = buildPackingListRows(invoice, shipments);
  var pallets = rowSet.rows;
  var isLegacy = rowSet.legacy;
  if (isLegacy) {
    /* Legacy fallback — identical to the pre-FASE-3 behavior: the
       whole manifest of the single resolved shipment. */
    pallets = shipment && Array.isArray(shipment.palletManifest)
      ? shipment.palletManifest.filter(function (p) { return p && (p.palletNum || p.productBase || p.casesPallet); }).map(function (p) {
          var cases = parseInt(p.casesPallet, 10) || 0;
          return {
            lot: shipment.lot, palletNum: p.palletNum,
            productBase: p.productBase, variety: p.variety, category: p.category,
            unitsCase: p.unitsCase, size: p.size, labelInitials: p.labelInitials,
            cases: cases, weight: docPalletWeight(p),
            tagId: p.tagId, julian: p.julianCode || p.traceability || "", grower: p.growerCode,
          };
        })
      : [];
  }
  var uniquePalletKeys = new Set(pallets.map(function (p) { return String(p.lot) + "::" + String(p.palletNum); }));
  var totalPallets = uniquePalletKeys.size;
  var totalCases = pallets.reduce(function (s, p) { return s + (p.cases || 0); }, 0);
  var totalKg = pallets.reduce(function (s, p) { return s + (p.weight ? p.weight.kg : 0); }, 0);
  var totalLb = pallets.reduce(function (s, p) { return s + (p.weight ? p.weight.lb : 0); }, 0);
  /* Per FIX: headline date = shipment ETA. */
  var etaDate = shipment ? (shipment.dateArrival || "") : "";
  /* SL header carries the Temp. Recorder # (thermograph) when present.
     Primary: invoice.thermograph (SL leg's own recorder). Fallback:
     the linked Fixed Pricing PO's `loggerNumber` field, looked up via
     linkedPoLineId or gfiPuNumber. Handles case where user captured
     logger in Fixed Pricing but hadn't manually copied it into the
     invoice's thermograph field. */
  var thermograph = invoice.thermograph || "";
  if (!thermograph) {
    try {
      var fixedPOs = loadLS("gfi-fixed-pos", []);
      var poMatch = null;
      /* Match by linkedPoLineId first (composite id "F:<id>"). */
      if (invoice.linkedPoLineId) {
        var parts = String(invoice.linkedPoLineId).split(":");
        var poId = parts[1] || parts[0];
        poMatch = fixedPOs.find(function (p) { return String(p.id) === String(poId); });
      }
      /* Fallback: match by gfiPuNumber against PO puNumber
         (the field that ties SL to Fixed Pricing). */
      if (!poMatch && invoice.gfiPuNumber) {
        poMatch = fixedPOs.find(function (p) { return String(p.puNumber || "") === String(invoice.gfiPuNumber); });
      }
      if (poMatch && poMatch.loggerNumber) thermograph = String(poMatch.loggerNumber).toUpperCase();
    } catch (e) { /* silent — packing list still prints with "—" */ }
  }
  /* Customs / export-cert references. Entry # is one-per-shipment;
     Export Certs are computed per organic invoice line (see
     buildExportCertEntries above) so the auditor sees which cert
     covers each commodity when they differ. */
  var entryNumber = shipment ? (shipment.entryNumber || "") : "";
  var exportCertEntries = buildExportCertEntries(invoice, shipment);
  /* PU# on the customer-facing packing list is ALWAYS the GFI PU#
     (gfiPuNumber on SL records). The SL invoiceNumber (INV-XXXXX)
     never appears on shipping docs. Falls back to puNumber so GFI
     invoices (no gfiPuNumber) keep working. */
  var pu = invoice.gfiPuNumber || invoice.puNumber || "";

  /* Filename: brand-specific naming per spec. */
  var fname;
  if (brand.key === "sl") fname = "Packing_List_PO_" + (invoice.clientRef || "") + "_PU_" + pu;
  else fname = "Packing_List_GFI_PU_" + pu + "_PO_" + (invoice.clientRef || "") + "_Lot_" + (invoice.lot || (shipment ? shipment.lot : ""));

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += brandDocCss(brand);
  h += '</style></head><body><div class="page">';

  /* Header: logo left, centered title, meta right. */
  h += '<div class="header">';
  h += '<div class="logo"><img src="' + brand.logo + '" /></div>';
  h += '<div class="doc-title center">PACKING LIST</div>';
  h += '<div><div class="doc-meta"><b>Date:</b> ' + escDocHtml(fmtDocDate(etaDate) || "—") + '<br><b>PU#:</b> ' + escDocHtml(pu || "—") + '<br><b>PO#:</b> ' + escDocHtml(invoice.clientRef || "—");
  /* Pickup Date/Time (2026-09-12, Arturo: "la fecha y hora de carga...
     aun no aparece en las po de sl, esos campos no se agregaron, el
     del logger si se agrego") — this file has its OWN buildPackingListHTML
     (separate from InvoiceRegistry.js's), printed when Arturo prints
     from inside the SweetLeaf module itself. The InvoiceRegistry.js
     copy got this line when the field was added; this one didn't.
     Only printed once recorded, same as the other copy. */
  if (invoice.pickupDate) {
    h += '<br><b>Pickup:</b> ' + escDocHtml(fmtDocDate(invoice.pickupDate)) + (invoice.pickupTime ? ' ' + escDocHtml(invoice.pickupTime) : '');
  }
  if (brand.tempRecorder) h += '<br><b>Temp. Recorder #:</b> ' + escDocHtml(thermograph || "—");
  h += '<br><b>Entry #:</b> ' + escDocHtml(entryNumber || "—");
  /* When there are multiple per-product certs, emit one labeled
     line per entry so each "Product — cert" pair reads on its own
     row. Single-cert collapse keeps the legacy one-liner. */
  if (exportCertEntries.length === 0) {
    h += '<br><b>Export Cert:</b> —';
  } else if (exportCertEntries.length === 1) {
    h += '<br><b>Export Cert:</b> ' + escDocHtml(exportCertEntries[0]);
  } else {
    exportCertEntries.forEach(function (e) {
      h += '<br><b>Export Cert:</b> ' + escDocHtml(e);
    });
  }
  h += '</div></div>';
  h += '</div>';
  /* ETA disclaimer immediately under the header for visibility. */
  h += '<div class="eta-note" style="margin-top:0;margin-bottom:12px">* Subject to customs clearance</div>';
  /* Legacy banner — invoices saved before palletAllocations existed
     (FASE 3) have no per-pallet allocation, so this document falls back
     to dumping the whole linked shipment's manifest (old behavior).
     English-only like the rest of this generated document (CLAUDE.md:
     client-facing docs are always English regardless of UI language). */
  if (isLegacy) {
    h += '<div style="margin:0 0 12px;padding:6px 12px;background:#FFF3E0;color:#8A5300;border:1px solid #F0C36D;border-radius:5px;font-size:10px;font-weight:600">Legacy view — this invoice does not have per-pallet allocations</div>';
  }

  /* Shipper / Consignee parties */
  h += '<div class="party-grid">';
  h += '<div class="party-block"><div class="lbl">Shipper</div>';
  h += '<div class="name">' + escDocHtml(brand.shipperName) + '</div>';
  h += '<div class="line">' + escDocHtml(brand.shipperAddr) + '</div>';
  if (company.phone) h += '<div class="line">Tel: ' + escDocHtml(company.phone) + '</div>';
  h += '</div>';

  h += '<div class="party-block"><div class="lbl">Consignee</div>';
  h += '<div class="name">' + escDocHtml(clientName || "—") + '</div>';
  /* Consignee address comes from the client registry — NOT the
     warehouse. The warehouse is the physical delivery destination
     but the consignee block on the packing list identifies the
     customer of record (Four Seasons, etc.) with their billing
     address + phone for traceability. */
  if (client) {
    var cCityState = [client.city, client.state].filter(Boolean).join(", ");
    var cCityStateZip = client.zip ? (cCityState ? cCityState + " " + client.zip : client.zip) : cCityState;
    var cAddrLine = [client.address, cCityStateZip].filter(function (s) { return s && String(s).trim(); }).join(", ");
    if (cAddrLine) h += '<div class="line">' + escDocHtml(cAddrLine) + '</div>';
  }
  if (clientPhone) h += '<div class="line">Tel: ' + escDocHtml(clientPhone) + '</div>';
  h += '</div></div>';

  /* Per-pallet table — sourced from invoice.palletAllocations (new) or
     the linked shipment's full manifest (legacy fallback, see
     buildPackingListRows above). */
  /* Julian (traceability) and Grower Code are internal-only —
     confidential per-grower/lot data that must not leave GFI on a
     customer-facing document. palletMetaFor/buildPackingListRows still
     resolve them (p.julian / p.grower) for other uses; this table
     simply doesn't paint those two columns anymore. */
  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:7%">Lot</th>';
  h += '<th class="c" style="width:8%">Pallet #</th>';
  h += '<th style="text-align:left;width:26%">Product</th>';
  h += '<th class="c" style="width:11%">Variety</th>';
  h += '<th class="c" style="width:7%">Label</th>';
  h += '<th class="c" style="width:10%">Cases</th>';
  h += '<th class="c" style="width:12%">Weight</th>';
  h += '<th class="c" style="width:19%">Tag ID</th>';
  h += '</tr></thead><tbody>';
  if (pallets.length === 0) {
    /* Distinguish the two empty cases so the user can debug:
         (a) no matching shipment found at all — usually a lot mismatch
         (b) shipment found but its palletManifest array is empty —
             user hasn't filled the manifest in ShipmentRegistry yet */
    var lotShown = invoice.lot || (invoice.lines && invoice.lines[0] && invoice.lines[0].lot) || "—";
    var emptyMsg = !shipment
      ? "No shipment found for LOT " + escDocHtml(lotShown) + ". Check that the lot number on this invoice matches a record in Shipment Registry."
      : "Shipment LOT " + escDocHtml(shipment.lot) + " has no pallet manifest. Open the shipment and fill the Pallet Manifest section.";
    h += '<tr><td colspan="8" style="text-align:center;color:#999;font-style:italic;padding:16px">' + emptyMsg + '</td></tr>';
  } else {
    pallets.forEach(function (p) {
      var typeLabel = p.category === "OG" ? "Organic" : p.category === "CV" ? "Conventional" : (p.category || "");
      /* Size/format string built from the pallet's own unitsCase+size
         (e.g. "12/6oz") — silently omitted when either is missing so
         legacy rows without that data fall back to plain product name. */
      var sizeStr = (p.unitsCase && p.size) ? (p.unitsCase + "/" + p.size) : "";
      /* USDA Organic note folded into the Product cell (GFI variant
         only — SL doesn't carry the organic cert reference at all per
         spec) since this layout has no separate Notes column. */
      var prodCell = escDocHtml((p.productBase || "—") + (sizeStr ? " " + sizeStr : "")) + ((brand.organicCert && p.category === "OG")
        ? ' <span style="color:' + brand.accent + ';font-weight:700">– Organic</span>'
        : (typeLabel ? ' <span style="color:#666">– ' + escDocHtml(typeLabel) + '</span>' : ""));
      h += '<tr>';
      h += '<td class="c mono">' + escDocHtml(p.lot || "—") + '</td>';
      h += '<td class="c mono"><b>' + escDocHtml(p.palletNum || "—") + '</b></td>';
      h += '<td>' + prodCell + '</td>';
      h += '<td class="c">' + escDocHtml(p.variety || "—") + '</td>';
      h += '<td class="c mono">' + escDocHtml(p.labelInitials || "—") + '</td>';
      h += '<td class="c mono"><b>' + (p.cases || "—") + '</b></td>';
      h += '<td class="c mono">' + (p.weight && p.weight.kg > 0 ? p.weight.kg.toFixed(1) + " kg" : "—") + '</td>';
      h += '<td class="c mono">' + escDocHtml(p.tagId || "—") + '</td>';
      h += '</tr>';
    });
    h += '<tr class="totals"><td colspan="5" style="text-align:right">TOTALS</td>';
    h += '<td class="c mono">' + totalCases + '</td>';
    h += '<td class="c mono">' + totalKg.toFixed(1) + ' kg</td>';
    h += '<td></td></tr>';
  }
  h += '</tbody></table>';

  /* Summary strip. Shipment Date kept as actual departure date —
     ETA is already in the header per FIX. */
  h += '<div class="summary">';
  h += '<div><div class="lbl">Total Pallets</div><div class="val">' + totalPallets + '</div></div>';
  h += '<div><div class="lbl">Total Cases</div><div class="val">' + totalCases + '</div></div>';
  h += '<div><div class="lbl">Total Gross Wt.</div><div class="val">' + totalKg.toFixed(1) + ' kg / ' + totalLb.toFixed(1) + ' lb</div></div>';
  h += '<div><div class="lbl">Shipment Date</div><div class="val">' + escDocHtml(fmtDocDate(shipment ? shipment.dateDeparture : "") || "—") + '</div></div>';
  h += '</div>';

  /* Cert + origin strip — GFI only. SweetLeaf intentionally omits. */
  if (brand.organicCert) {
    h += '<div class="cert-strip">Organic Cert: USDA / Primus Auditing – ' + escDocHtml(company.organicCert || "PA-OR-1186") + '  ·  Origin: Mexico</div>';
  }

  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── BUILDER 2.5 — MASTER INBOUND (combined doc) ────────
   Single PDF that stacks the Inbound sheet on top and the Packing
   List below it. Both halves are produced by the existing builders
   so the visual output stays identical to printing each one
   separately — we just splice the <body> contents into one
   document, deduping the shared <style> block. The two halves use
   the same `.page` wrapper / `brandDocCss` so a single copy of the
   stylesheet covers both. A page break separates them in print.

   opts: { brandKey: "gfi"|"sl" } */
export function buildMasterInboundHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brandKey = opts.brandKey || "gfi";
  var brand = brandConfig(brandKey);
  /* Reuse the existing builders, then trim each half so the combined
     doc reads as one continuous document instead of two stacked
     PDFs. From the Inbound half we drop the Remarks block and the
     footer; from the Packing List half we keep ONLY the pallet
     table (no summary strip, no organic-cert strip, no second
     footer). One consolidated footer goes at the very end. */
  var inboundHtml = buildPickTicketOrInboundHTML(invoice, ctx, { brandKey: brandKey, kind: "inbound" });
  var packingHtml = buildPackingListHTML(invoice, ctx, { brandKey: brandKey });
  var styleMatch = inboundHtml.match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodyMatch1 = inboundHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/);
  var bodyMatch2 = packingHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/);
  var inboundBody = bodyMatch1 ? bodyMatch1[1] : "";
  var packingBody = bodyMatch2 ? bodyMatch2[1] : "";
  /* Cut Inbound at the Remarks block — that and the footer that
     follows both belong to a standalone Inbound PDF, not the Master
     combo. Re-close the .page wrapper since we cut into it. */
  var remarksIdx = inboundBody.indexOf('<div class="remarks">');
  var inboundTop;
  if (remarksIdx >= 0) {
    inboundTop = inboundBody.substring(0, remarksIdx) + '</div>';
  } else {
    /* Fallback: just strip the footer if Remarks block is missing. */
    inboundTop = inboundBody.replace(/<div class="footer">[\s\S]*?<\/div>\s*<\/div>\s*$/, '</div>');
  }
  /* Pallet table only — from `<table class="items">` to just before
     the summary strip. Drops the bottom totals strip + organic cert
     strip + second footer. */
  var palletStart = packingBody.indexOf('<table class="items">');
  var palletEndMarkers = ['<div class="summary">', '<div class="cert-strip">', '<div class="footer">'];
  var palletEnd = -1;
  for (var pi = 0; pi < palletEndMarkers.length; pi++) {
    var idx = packingBody.indexOf(palletEndMarkers[pi]);
    if (idx > palletStart) { palletEnd = idx; break; }
  }
  if (palletEnd < 0) palletEnd = packingBody.length;
  var packingTableOnly = palletStart >= 0 ? packingBody.substring(palletStart, palletEnd) : "";

  /* PU# is always the GFI PU# (gfiPuNumber on SL records); the SL
     invoiceNumber never appears in the Master Inbound filename. */
  var pu = invoice.gfiPuNumber || invoice.puNumber || "";
  var po = invoice.clientRef || "";
  /* Filename matches the regular Inbound — "Master Inbound" is only
     an internal label in the app menu; the customer-facing PDF name
     stays plain "Inbound_*" so it slots in alongside the standalone
     Inbound PDFs on Drive / in customer inboxes. */
  var fname = (brandKey === "sl" ? "PU_" : "Inbound_") + pu + "_PO_" + po;
  /* No page-break here — Master Inbound is a SINGLE-page doc. The
     old `.mi-pagebreak { page-break-before: always }` forced the
     Packing List onto a second sheet even when both halves fit. The
     class is kept as a positional spacer only so the JSX below
     stays unchanged. */
  var extraCss = " .mi-pagebreak{height:0;margin:18px 0 0;padding:0}";
  /* Tighten the section header so it eats less vertical room. */
  extraCss += " .mi-section-header{margin:0 0 10px;padding-bottom:6px;border-bottom:2px solid #1a1a1a;font-size:18px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#1a1a1a;text-align:center}";
  /* Pallet-table compression scoped to the Master Inbound only —
     smaller font + slimmer row padding so the combined doc fits on
     one letter page. The standalone Packing List PDF is unaffected
     because it doesn't import this stylesheet. */
  extraCss += " body .items{font-size:10px}";
  extraCss += " body .items th{padding:5px 6px;font-size:9px}";
  extraCss += " body .items td{padding:4px 6px}";
  /* @page tightens the print margins slightly so a manifest with
     ~30 pallets still fits. Both halves use the same .page wrapper
     so this affects only the printed sheet, not the on-screen
     preview width. */
  extraCss += " @page{size:letter;margin:0.45in}";

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += sharedCss + extraCss;
  h += '</style></head><body>';
  /* Inbound (top half) — header, parties, fields, eta-note,
     product table only. No remarks, no footer here. */
  h += inboundTop;
  /* Page break, then PACKING LIST header + pallet table, then ONE
     consolidated footer to close the doc. */
  h += '<div class="mi-pagebreak"></div>';
  h += '<div class="page"><div class="mi-section-header">PACKING LIST</div>' + packingTableOnly;
  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── BUILDER 3 — DRIVER SIGN-OFF SHEET (SweetLeaf) ────────
   Warehouse-fillable form. No logos, no branding apart from
   "Sweetleaf Produce Inc" in the Vendor field. 28 blank pallet slots
   (1-14 left column, 15-28 right column), each with two empty lines
   for Temp and Tag ID. Driver + shipper acknowledgement block at the
   bottom with print/sign lines. Pre-filled from the invoice: PU#,
   PO#, Total pallets (from the linked shipment's manifest, if any). */
export function buildDriverSignOffSheetHTML(invoice, ctx) {
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var shipment = findShipmentForInvoice(invoice, shipments);
  /* Total pallets — primary source is the invoice line items
     (each line carries a `pallets` field set when the user invoiced).
     Falls back to counting the linked shipment's manifest when the
     invoice lines don't have pallet counts (older invoices, imports). */
  var totalPallets = 0;
  (invoice.lines || []).forEach(function (l) {
    totalPallets += parseInt(l.pallets, 10) || 0;
  });
  if (totalPallets === 0 && shipment && Array.isArray(shipment.palletManifest)) {
    totalPallets = shipment.palletManifest.filter(function (p) { return p && (p.palletNum || p.productBase || p.casesPallet); }).length;
  }

  /* PU# on the Driver Sign-Off is the GFI PU# (gfiPuNumber on SL
     records); the SL invoiceNumber never appears on this form. */
  var pu = invoice.gfiPuNumber || invoice.puNumber || "";
  var fname = "Driver_SignOff_Sheet_PU_" + pu + "_PO_" + (invoice.clientRef || "");

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.55in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:730px;margin:0 auto}';
  /* No-branding title — bold centred header with a navy underline
     so the form still reads like an official doc. */
  h += '.doc-title{text-align:center;font-size:22px;font-weight:700;color:#1a1a1a;letter-spacing:2px;padding-bottom:8px;border-bottom:2px solid #1a1a1a;margin-bottom:14px}';
  /* Top meta strip — half pre-filled (PU#, PO#, Total Pallets,
     Vendor), half blank lines for the user to write in pen
     (Loading Date, Recorder #). */
  h += '.meta-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px 16px;margin-bottom:18px;padding:10px 14px;background:#f7f8fa;border:1px solid #d0d5dd;border-radius:6px}';
  h += '.meta-grid .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#6b7280;font-weight:700}';
  h += '.meta-grid .val{font-size:12px;font-weight:700;color:#1a1a1a;margin-top:3px;font-family:"Courier New",monospace;min-height:16px}';
  /* Empty fields get an underline so the user knows where to write. */
  h += '.meta-grid .blank{border-bottom:1px solid #1a1a1a;min-width:120px;display:inline-block;min-height:14px}';
  /* Pallet grid — two side-by-side columns, 14 rows each. Each row
     uses a fixed 5-column inner grid (label · "Temp:" · temp-line ·
     "Tag ID:" · tag-line) so the two columns align perfectly with
     each other when printed side-by-side. No borders/dividers — the
     space stays clean for handwriting. */
  h += '.pallets{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px;margin-bottom:18px}';
  h += '.pallet-row{display:grid;grid-template-columns:70px 44px 1fr 52px 1fr;gap:6px;padding:6px 0;font-size:11px;align-items:baseline}';
  h += '.pallet-row .num{font-weight:700;color:#1a1a1a;font-family:"Courier New",monospace}';
  h += '.pallet-row .k{font-weight:600;color:#374151}';
  h += '.pallet-row .line{border-bottom:1px solid #1a1a1a;min-height:14px}';
  /* Signature block — two parties (shipper + driver) with statement
     paragraphs, then Print/Sign lines side by side under each. */
  h += '.sig{border-top:2px solid #1a1a1a;padding-top:14px;margin-top:8px}';
  h += '.sig-block{margin-bottom:18px;font-size:11px;line-height:1.7;color:#1a1a1a}';
  h += '.sig-block p{margin-bottom:6px}';
  h += '.sig-block .line{border-bottom:1px solid #1a1a1a;display:inline-block;min-width:200px;min-height:14px;margin:0 4px}';
  h += '.sig-row{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:8px;font-size:11px;font-weight:600}';
  h += '.sig-row .line{border-bottom:1px solid #1a1a1a;display:inline-block;width:80%;min-height:14px;margin-left:6px}';
  h += '</style></head><body><div class="page">';

  h += '<div class="doc-title">DRIVER SIGN-OFF SHEET</div>';

  /* Meta block — fixed values on the left, blank lines on the right. */
  h += '<div class="meta-grid">';
  h += '<div><div class="lbl">PU#</div><div class="val">' + escDocHtml(pu || "—") + '</div></div>';
  h += '<div><div class="lbl">PO#</div><div class="val">' + escDocHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div><div class="lbl">Total Pallets</div><div class="val">' + (totalPallets || "—") + '</div></div>';
  h += '<div><div class="lbl">Vendor</div><div class="val">Sweetleaf Produce Inc</div></div>';
  h += '<div><div class="lbl">Loading Date</div><div class="val"><span class="blank"></span></div></div>';
  h += '<div><div class="lbl">Recorder Number</div><div class="val"><span class="blank"></span></div></div>';
  h += '</div>';

  /* 28 blank pallet slots laid out row-major:
       Row 1: Pallet 1 | Pallet 2
       Row 2: Pallet 3 | Pallet 4
       …
       Row 14: Pallet 27 | Pallet 28
     CSS grid with 2 columns fills row-by-row naturally, so we just
     emit the 28 cells in numeric order and the grid does the rest. */
  function renderPalletCell(n) {
    /* 5-cell row matching the 5-column grid template so the parts
       line up across the left/right pallet columns identically. */
    var s = '<div class="pallet-row">';
    s += '<div class="num">Pallet ' + n + '</div>';
    s += '<div class="k">Temp:</div>';
    s += '<div class="line"></div>';
    s += '<div class="k">Tag ID:</div>';
    s += '<div class="line"></div>';
    s += '</div>';
    return s;
  }
  h += '<div class="pallets">';
  for (var i = 1; i <= 28; i++) h += renderPalletCell(i);
  h += '</div>';

  /* Signature / acknowledgement block. */
  h += '<div class="sig">';
  h += '<div class="sig-block"><p>I shipper <span class="line"></span> have put these pallets on the truck with the following pulp temps.</p>';
  h += '<div class="sig-row"><div>Print: <span class="line"></span></div><div>Sign: <span class="line"></span></div></div>';
  h += '</div>';
  h += '<div class="sig-block"><p>I driver <span class="line"></span> agree that the following pallets with the documented temps have been loaded on my truck.</p>';
  h += '<p>And will maintain the temperature for the duration of the trip.</p>';
  h += '<div class="sig-row"><div>Print: <span class="line"></span></div><div>Sign: <span class="line"></span></div></div>';
  h += '</div>';
  h += '</div>';

  h += '</div></body></html>';
  return h;
}

/* ──────── PRINT HELPER (single + bulk) ────────
   `kind` is one of: "gfi-pick" | "gfi-inbound" | "gfi-packing" |
                     "sl-pick"  | "sl-inbound"  | "sl-packing"
   Encodes brand + doc-type in a single string so the menu's onPick
   handler stays a thin pass-through. */
function printInvoiceDocs(invoices, kind, ctx, onError, extras) {
  if (!Array.isArray(invoices) || invoices.length === 0) return;
  var brandKey = "sl";  /* SweetLeafPortal: always SL branding */
  var docKind = kind.split("-")[1];  /* pick | inbound | packing */
  /* extras.remarks + extras.loadingDate (Pick Ticket only) —
     assembled by the remarks modal, applied to every invoice in a
     bulk run. Loading Date overrides the auto-default
     (shipment.dateDeparture) used by Inbound. */
  var remarks = (extras && Array.isArray(extras.remarks)) ? extras.remarks : null;
  var loadingDateOverride = (extras && extras.loadingDate) || null;
  var build;
  if (docKind === "packing") {
    build = function (inv) { return buildPackingListHTML(inv, ctx, { brandKey: brandKey }); };
  } else if (docKind === "driversignoff") {
    /* Driver Sign-Off — SweetLeaf-only blank form, no brand styling
       beyond the "Sweetleaf Produce Inc" vendor field. */
    build = function (inv) { return buildDriverSignOffSheetHTML(inv, ctx); };
  } else if (docKind === "master") {
    /* Master Inbound — Inbound + Packing List stacked in one PDF. */
    build = function (inv) { return buildMasterInboundHTML(inv, ctx, { brandKey: brandKey }); };
  } else {
    build = function (inv) { return buildPickTicketOrInboundHTML(inv, ctx, { brandKey: brandKey, kind: docKind, remarks: remarks, loadingDate: loadingDateOverride }); };
  }
  var htmls = invoices.map(build);
  var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodies = htmls.map(function (html) { var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/); return m ? m[1] : ""; });

  /* Filename naming per spec, brand × doc × single/bulk.
     PU# in every shipping-doc filename is the GFI PU# (gfiPuNumber
     on SL records); the SL invoiceNumber is reserved for the Invoice
     PDF only and never leaks into Pick/Inbound/Packing/Driver Sign-
     Off / Master filenames. */
  var titleBase;
  if (invoices.length === 1) {
    var inv = invoices[0];
    var lot = inv.lot || (inv.lines && inv.lines[0] && inv.lines[0].lot) || "";
    var docPu = inv.gfiPuNumber || inv.puNumber || "";
    if (docKind === "driversignoff") {
      titleBase = "Driver_SignOff_Sheet_PU_" + docPu + "_PO_" + (inv.clientRef || "");
    } else if (docKind === "master") {
      /* Master Inbound is customer-facing as a plain Inbound — only
         the in-app menu labels it "Master" to differentiate it from
         the standalone Inbound. Filename matches the regular one. */
      titleBase = (brandKey === "sl" ? "PU_" : "Inbound_") + docPu + "_PO_" + (inv.clientRef || "");
    } else if (docKind === "packing") {
      titleBase = brandKey === "sl"
        ? "Packing_List_PO_" + (inv.clientRef || "") + "_PU_" + docPu
        : "Packing_List_GFI_PU_" + docPu + "_PO_" + (inv.clientRef || "") + "_Lot_" + lot;
    } else if (brandKey === "sl") {
      /* SL pick/inbound share the same "PU_" filename pattern. */
      titleBase = "PU_" + docPu + "_PO_" + (inv.clientRef || "");
    } else {
      titleBase = (docKind === "pick" ? "Pick_Ticket_" : "Inbound_") + docPu + "_PO_" + (inv.clientRef || "");
    }
  } else {
    var iso = new Date().toISOString().slice(0, 10);
    var prefix;
    if (docKind === "driversignoff") prefix = "Driver_SignOff_Sheets";
    else if (docKind === "master") prefix = brandKey === "sl" ? "SL_Inbound_Sheets" : "Inbound_Sheets";
    else if (docKind === "packing") prefix = brandKey === "sl" ? "SL_Packing_Lists" : "Packing_Lists";
    else if (brandKey === "sl") prefix = "SL_" + (docKind === "pick" ? "Pick_Tickets" : "Inbound_Sheets");
    else prefix = docKind === "pick" ? "Pick_Tickets" : "Inbound_Sheets";
    titleBase = prefix + "_" + invoices.length + "_" + iso;
  }

  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(titleBase) + '</title>';
  doc += '<style>' + sharedCss + ' .gfi-doc-pagebreak{page-break-after:always;break-after:page;height:0}</style></head><body>';
  bodies.forEach(function (body, i) {
    doc += body;
    if (i < bodies.length - 1) doc += '<div class="gfi-doc-pagebreak"></div>';
  });
  doc += '</body></html>';

  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write(doc);
  w.document.close();
  setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
}

/* Drive-upload twin of printInvoiceDocs for a SINGLE invoice. Builds
   the same HTML, rasterises it, and drops the PDF into the invoice's
   client subfolder under the embarque tree on Drive.

   Why per-invoice (not a bulk PDF): each invoice has a different
   client → different Drive folder. Bulk prints can still call this
   in a sequential loop. */
/* Build a single invoice doc → { filename, blob, mimeType }.
   `kind` can be one of:
     - "gfi-pick" | "gfi-inbound" | "sl-pick" | "sl-inbound"
     - "gfi-packing" | "sl-packing"
     - "sl-driversignoff"
     - "invoice"  (the actual Invoice PDF — no brand prefix)
   The Drive upload + email attachment paths both consume this so the
   PDF a customer downloads from email matches what's archived on
   Drive. */
export async function buildInvoiceDocPdf(inv, kind, ctx, extras) {
  if (!inv || !ctx) throw new Error("buildInvoiceDocPdf: missing args");
  var html;
  var lot = inv.lot || (inv.lines && inv.lines[0] && inv.lines[0].lot) || "";
  var po = inv.clientRef || "";
  /* The Invoice PDF keeps the SL invoiceNumber (mirrored into puNumber)
     in its filename — that's the customer's billing reference. Every
     other shipping doc uses the GFI PU# (gfiPuNumber) so the SL
     INV-XXXXX never appears on Pick / Inbound / Packing / Master /
     Driver Sign-Off filenames or bodies. */
  var pu = inv.puNumber || "";
  var docPu = inv.gfiPuNumber || inv.puNumber || "";
  var filename;
  if (kind === "invoice") {
    var client = resolveClientFull(inv.clientId);
    html = buildInvoicePrintHTML(inv, client);
    filename = "Invoice_PU_" + pu + "_PO_" + po;
  } else {
    var brandKey = "sl";  /* SweetLeafPortal: always SL branding */
    var docKind = kind.split("-")[1];
    var remarks = (extras && Array.isArray(extras.remarks)) ? extras.remarks : null;
    var loadingDateOverride = (extras && extras.loadingDate) || null;
    if (docKind === "packing") {
      html = buildPackingListHTML(inv, ctx, { brandKey: brandKey });
    } else if (docKind === "driversignoff") {
      html = buildDriverSignOffSheetHTML(inv, ctx);
    } else if (docKind === "master") {
      html = buildMasterInboundHTML(inv, ctx, { brandKey: brandKey });
    } else {
      html = buildPickTicketOrInboundHTML(inv, ctx, { brandKey: brandKey, kind: docKind, remarks: remarks, loadingDate: loadingDateOverride });
    }
    if (docKind === "driversignoff") {
      filename = "Driver_SignOff_Sheet_PU_" + docPu + "_PO_" + po;
    } else if (docKind === "master") {
      /* Customer-facing filename — same as regular Inbound. The
         "Master" tag stays in the menu only. */
      filename = (brandKey === "sl" ? "PU_" : "Inbound_") + docPu + "_PO_" + po;
    } else if (docKind === "packing") {
      filename = brandKey === "sl"
        ? "Packing_List_PO_" + po + "_PU_" + docPu
        : "Packing_List_GFI_PU_" + docPu + "_PO_" + po + "_Lot_" + lot;
    } else if (brandKey === "sl") {
      filename = "PU_" + docPu + "_PO_" + po;
    } else {
      filename = (docKind === "pick" ? "Pick_Ticket_" : "Inbound_") + docPu + "_PO_" + po;
    }
  }
  var pdfBlob = await htmlToPdfBlob(html);
  return { filename: safeFilename(filename) + ".pdf", blob: pdfBlob, mimeType: "application/pdf" };
}

async function uploadInvoiceDocToDrive(inv, kind, ctx, extras) {
  if (!inv || !ctx) throw new Error("uploadInvoiceDocToDrive: missing args");
  var { filename, blob } = await buildInvoiceDocPdf(inv, kind, ctx, extras);
  /* Resolve the Drive path. The invoice's linked shipment is the
     bridge to the season + shipper data the embarque folder name
     needs. */
  var shipments = ctx.shipments || [];
  var clients = ctx.clients || [];
  var company = ctx.company || {};
  var shipment = findShipmentForInvoice(inv, shipments);
  if (!shipment) throw new Error("No linked shipment for invoice " + inv.id + " — cannot resolve embarque folder");
  var suppliers = loadLS("gfi-suppliers", []);
  var seasons = loadLS("gfi-seasons", []);
  var sup = suppliers.find(function (s) { return idMatches(s, shipment.supplierId); });
  var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
  var season = seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
  var cli = clients.find(function (c) { return idMatches(c, inv.clientId); });
  var clientShort = (cli && (cli.shortName || cli.fullName)) || inv.clientName || "Unknown";
  var segments = rootPathSegments(company.driveRootPath).concat([
    seasonFolderName(season),
    embarqueFolderName(shipment, shipperShort),
    /* SweetLeaf Drive folders use the GFI PU# (the customer-facing
       reference), not the SL invoiceNumber. Override puNumber on a
       shallow clone so the shared invoiceFolderName helper picks it
       up; falls back to the SL puNumber when gfiPuNumber isn't set. */
    invoiceFolderName(Object.assign({}, inv, { puNumber: inv.gfiPuNumber || inv.puNumber }), clientShort),
  ]);
  var folderId = await driveEnsurePath(segments);
  return driveUploadFile(folderId, filename, blob, { contentType: "application/pdf" });
}

/* ──────── PICK TICKET / INBOUND SELECTOR MODAL ────────
   Mandatory choice between Pick Ticket and Inbound before any
   pick-or-inbound print. Renders centered over the screen until
   the user picks one or cancels. */
function PickKindSelector({ brandLabel, lang, onPick, onCancel }) {
  var t = lang === "es"
    ? { title: "Tipo de documento", subtitle: brandLabel + " · selecciona uno", pick: "Pick Ticket", pickDesc: "El cliente recoge el producto", inbound: "Inbound", inboundDesc: "El producto llega a la bodega del cliente", cancel: "Cancelar" }
    : { title: "Document type", subtitle: brandLabel + " · pick one", pick: "Pick Ticket", pickDesc: "Customer picks up the product", inbound: "Inbound", inboundDesc: "Product arrives at customer warehouse", cancel: "Cancel" };
  /* zIndex bumped to 10006 — above DraggableModal (10001), email
     dialog (10003), preview modal (10004) and batch payment (10005)
     so this picker always lands on top regardless of what's open. */
  var overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10006 };
  var modalStyle = { background: "#ffffff", borderRadius: 10, padding: 22, minWidth: 380, maxWidth: 460, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", border: "1px solid #d0d5dd" };
  var optionStyle = { display: "block", width: "100%", textAlign: "left", padding: "14px 16px", marginTop: 10, background: "#f7f8fa", border: "1px solid #d0d5dd", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#1a1a1a", transition: "background 0.15s" };
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t.subtitle}</div>
        <button onClick={function () { onPick("pick"); }} onMouseEnter={function (e) { e.currentTarget.style.background = "#eef4f5"; }} onMouseLeave={function (e) { e.currentTarget.style.background = "#f7f8fa"; }} style={optionStyle}>
          <div style={{ fontWeight: 700 }}>{t.pick}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{t.pickDesc}</div>
        </button>
        <button onClick={function () { onPick("inbound"); }} onMouseEnter={function (e) { e.currentTarget.style.background = "#eef4f5"; }} onMouseLeave={function (e) { e.currentTarget.style.background = "#f7f8fa"; }} style={optionStyle}>
          <div style={{ fontWeight: 700 }}>{t.inbound}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{t.inboundDesc}</div>
        </button>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onCancel} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "#6b7280", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────── PICK TICKET REMARKS MODAL ────────
   Only triggered for Pick Ticket (not Inbound). Three lines:
     1. Mandatory pre-checked: "SET LOADING TRUCK TEMPERATURE AT 35ºF"
        — checkbox is locked on, the user cannot uncheck it
     2. Optional: "Please Add Digital Template 4"
     3. Free-text input for any additional note
   Returns the assembled string lines to the caller so the Pick Ticket
   builder can drop them into the Remarks block. */
function PickTicketRemarksModal({ brandLabel, lang, defaultLoadingDate, onPrint, onCancel }) {
  /* Loading Date — required field. Defaults to ETA + 1 day (computed
     by the parent and passed in). User can override before printing.
     The Print button stays disabled until a value is present so it's
     impossible to ship a doc without one. */
  var [loadingDate, setLoadingDate] = useState(defaultLoadingDate || "");
  /* Line 1 is locked-on per spec — no state needed since the user
     cannot toggle it. Renders as a disabled checked checkbox. */
  var [digitalChecked, setDigitalChecked] = useState(false);
  var [customNote, setCustomNote] = useState("");
  var t = lang === "es"
    ? { title: "Notas del Pick Ticket", subtitle: brandLabel + " · revisa antes de imprimir", mandatory: "Obligatorio", required: "Requerido", optional: "Opcional", customLabel: "Nota adicional", customPlaceholder: "Escribe cualquier instrucción extra…", cancel: "Cancelar", print: "Imprimir", loadingDateLabel: "Loading Date", loadingDateHint: "Por defecto: día siguiente al ETA. Ajusta si hay feriado o retraso.", loadingDateMissing: "Loading Date es requerido" }
    : { title: "Pick Ticket Remarks", subtitle: brandLabel + " · review before printing", mandatory: "Mandatory", required: "Required", optional: "Optional", customLabel: "Custom note", customPlaceholder: "Type any extra instruction…", cancel: "Cancel", print: "Print", loadingDateLabel: "Loading Date", loadingDateHint: "Defaults to the day after Arrival ETA. Adjust for holidays or delays.", loadingDateMissing: "Loading Date is required" };

  /* Mandatory line is always included. The optional + custom lines
     only flow through when the user checks them / types text. */
  function handlePrint() {
    if (!loadingDate) return;  /* guard — button is disabled too */
    var lines = [];
    lines.push("SET LOADING TRUCK TEMPERATURE AT 35ºF");
    if (digitalChecked) lines.push("Please Add Digital Template 4");
    var trimmed = (customNote || "").trim();
    if (trimmed) lines.push(trimmed);
    onPrint(lines, loadingDate);
  }

  var canPrint = !!loadingDate;
  /* z-index above all other modals — opens after PickKindSelector. */
  var overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10006 };
  var modalStyle = { background: "#ffffff", borderRadius: 10, padding: 22, minWidth: 460, maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", border: "1px solid #d0d5dd" };
  var rowStyle = { display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", marginTop: 10, background: "#f7f8fa", border: "1px solid #d0d5dd", borderRadius: 8 };
  var lockedRowStyle = { ...rowStyle, background: "#fef9e7", border: "1px solid #f5d878" };
  var labelStyle = { fontSize: 12, color: "#1a1a1a", fontWeight: 600, lineHeight: 1.4 };
  var pillStyle = { fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase", marginLeft: 6 };
  var dateRowStyle = { marginTop: 14, padding: "12px 14px", background: "#eef4f5", border: "1.5px solid #2A7A6F", borderRadius: 8 };
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t.subtitle}</div>

        {/* Loading Date — required field, surfaced first so the user
            sees it before the remarks. Highlighted with navy border
            since it's the operational decision being captured. */}
        <div style={dateRowStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#2A7A6F", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.loadingDateLabel} <span style={{ ...pillStyle, background: "#b45309", color: "#fff" }}>{t.required}</span></div>
          </div>
          <input
            type="date"
            value={loadingDate}
            onChange={function (e) { setLoadingDate(e.target.value); }}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1.5px solid " + (loadingDate ? "#d0d5dd" : "#C0392B"), fontSize: 13, fontFamily: "inherit", color: "#1a1a1a", outline: "none", boxSizing: "border-box", fontWeight: 600 }}
          />
          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>{t.loadingDateHint}</div>
        </div>

        {/* Line 1 — mandatory, pre-checked, locked. */}
        <div style={lockedRowStyle}>
          <input type="checkbox" checked readOnly disabled style={{ marginTop: 3, cursor: "not-allowed", accentColor: "#b45309" }} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>SET LOADING TRUCK TEMPERATURE AT 35°F <span style={{ ...pillStyle, background: "#b45309", color: "#fff" }}>{t.required}</span></div>
            <div style={{ fontSize: 10, color: "#8b6914", marginTop: 3, fontStyle: "italic" }}>{lang === "es" ? "Se incluye siempre en el documento — no se puede desmarcar." : "Always included on the document — cannot be unchecked."}</div>
          </div>
        </div>

        {/* Line 2 — optional checkbox. */}
        <label style={{ ...rowStyle, cursor: "pointer" }}>
          <input type="checkbox" checked={digitalChecked} onChange={function (e) { setDigitalChecked(e.target.checked); }} style={{ marginTop: 3, accentColor: "#2A7A6F" }} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Please Add Digital Template 4 <span style={{ ...pillStyle, background: "#e5e7eb", color: "#6b7280" }}>{t.optional}</span></div>
          </div>
        </label>

        {/* Line 3 — free text. */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.customLabel}</div>
          <textarea
            value={customNote}
            onChange={function (e) { setCustomNote(e.target.value); }}
            placeholder={t.customPlaceholder}
            rows={2}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d0d5dd", fontSize: 12, fontFamily: "inherit", color: "#1a1a1a", outline: "none", resize: "vertical", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 18 }}>
          <div style={{ fontSize: 11, color: canPrint ? "transparent" : "#C0392B", fontWeight: 600 }}>{canPrint ? "" : "⚠ " + t.loadingDateMissing}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "#6b7280", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
            <button onClick={handlePrint} disabled={!canPrint} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: canPrint ? "#2A7A6F" : "#9ca3af", color: "#fff", cursor: canPrint ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}>{"🖨"} {t.print}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────── DROPDOWN — "Print Documents" ────────
   Two sections (Ground-Fresh + SweetLeaf), 2 items each. The pick-
   or-inbound items emit the placeholder "<brand>-pickorinbound"; the
   parent receives that and pops the PickKindSelector before calling
   the print helper with the resolved kind. The "packing" items emit
   the final kind directly since they don't need user input. */
function PrintDocsDropdown({ lang, label, onPick, style }) {
  var [open, setOpen] = useState(false);
  var rootRef = useRef(null);
  useEffect(function () {
    if (!open) return;
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return function () { document.removeEventListener("mousedown", onDown); };
  }, [open]);
  var menuStyle = { position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 320, background: "#ffffff", border: "1.5px solid #d0d5dd", borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 1000, overflow: "hidden" };
  var sectionHeaderGfi = { padding: "8px 14px 4px", fontSize: 9, fontWeight: 700, color: "#2A7A6F", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f7f8fa", borderBottom: "1px solid #e5e7eb" };
  var sectionHeaderSl = { padding: "8px 14px 4px", fontSize: 9, fontWeight: 700, color: "#2A7A6F", textTransform: "uppercase", letterSpacing: "0.06em", background: "#eef5f4", borderBottom: "1px solid #d0e3e0", borderTop: "1px solid #d0e3e0" };
  var itemStyle = { display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "#ffffff", border: "none", borderBottom: "1px solid #e5e7eb", cursor: "pointer", fontSize: 12, color: "#1a1a1a" };
  var itemHint = { fontSize: 10, color: "#6b7280", marginTop: 2 };
  var btnStyle = { padding: "6px 12px", borderRadius: 6, border: "1px solid #2A7A6F", background: "rgba(27,58,75,0.06)", color: "#2A7A6F", cursor: "pointer", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 };
  var t = lang === "es"
    ? { btn: label || "Imprimir Documentos", gfiSection: "Ground-Fresh — Documentos de Venta", slSection: "SweetLeaf Produce — Documentos de Venta", pi: "Pick Ticket / Inbound", piDesc: "Elegir tipo al imprimir", packing: "Packing List", packingDesc: "Detalle por pallet del manifiesto", master: "Master Inbound", masterDesc: "Inbound + Packing List en un solo PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Formato en blanco para que la bodega lo llene a mano" }
    : { btn: label || "Print Documents", gfiSection: "Ground-Fresh Sales Documents", slSection: "SweetLeaf Produce Documents", pi: "Pick Ticket / Inbound", piDesc: "Choose type when printing", packing: "Packing List", packingDesc: "Per-pallet manifest detail", master: "Master Inbound", masterDesc: "Inbound + Packing List in a single PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Blank fill-in form for warehouse use" };
  function pick(kind) { setOpen(false); if (onPick) onPick(kind); }
  function MenuItem({ id, title, desc, last }) {
    return (
      <button onClick={function () { pick(id); }}
        onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
        onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
        style={last ? { ...itemStyle, borderBottom: "none" } : itemStyle}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={itemHint}>{desc}</div>
      </button>
    );
  }
  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", ...(style || {}) }}>
      <button onClick={function () { setOpen(!open); }} style={btnStyle}>{"🖨"} {t.btn} <span style={{ fontSize: 10, opacity: 0.7 }}>{"▾"}</span></button>
      {open && (
        <div style={menuStyle}>
          <div style={sectionHeaderGfi}>{t.gfiSection}</div>
          <MenuItem id="gfi-pickorinbound" title={t.pi} desc={t.piDesc} />
          <MenuItem id="gfi-packing" title={t.packing} desc={t.packingDesc} />
          <MenuItem id="gfi-master" title={t.master} desc={t.masterDesc} />
          <div style={sectionHeaderSl}>{t.slSection}</div>
          <MenuItem id="sl-pickorinbound" title={t.pi} desc={t.piDesc} />
          <MenuItem id="sl-packing" title={t.packing} desc={t.packingDesc} />
          <MenuItem id="sl-master" title={t.master} desc={t.masterDesc} />
          <MenuItem id="sl-driversignoff" title={t.driver} desc={t.driverDesc} last />
        </div>
      )}
    </div>
  );
}

/* ──────── BRAND-SPECIFIC DOCS DROPDOWN ────────
   A single-brand variant of PrintDocsDropdown. Shows 2 items
   (Pick Ticket / Inbound · Packing List) for ONE brand. Used by the
   org-chart panel in the invoice detail header where each brand
   gets its own button. The combined PrintDocsDropdown stays in use
   for the bulk-action bar where horizontal space is tight. */
function BrandDocsDropdown({ brand, lang, onPick, style }) {
  var [open, setOpen] = useState(false);
  var rootRef = useRef(null);
  useEffect(function () {
    if (!open) return;
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return function () { document.removeEventListener("mousedown", onDown); };
  }, [open]);
  /* Color tokens hard-coded to brand requirement (navy vs teal). */
  var accent = brand === "sl" ? "#2A7A6F" : "#2A7A6F";
  var accentBg = brand === "sl" ? "rgba(42,122,111,0.06)" : "rgba(27,58,75,0.06)";
  var label = brand === "sl"
    ? (lang === "es" ? "SweetLeaf Docs" : "SweetLeaf Docs")
    : (lang === "es" ? "GFI Docs" : "GFI Docs");
  var pickPlaceholder = brand + "-pickorinbound";
  var packingKind = brand + "-packing";
  var masterKind = brand + "-master";
  var driverKind = brand + "-driversignoff";
  var t = lang === "es"
    ? { pi: "Pick Ticket / Inbound", piDesc: "Elegir tipo al imprimir", packing: "Packing List", packingDesc: "Detalle por pallet del manifiesto", master: "Master Inbound", masterDesc: "Inbound + Packing List en un solo PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Formato en blanco para que la bodega lo llene a mano" }
    : { pi: "Pick Ticket / Inbound", piDesc: "Choose type when printing", packing: "Packing List", packingDesc: "Per-pallet manifest detail", master: "Master Inbound", masterDesc: "Inbound + Packing List in a single PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Blank fill-in form for warehouse use" };
  var btnStyle = { padding: "5px 10px", borderRadius: 5, border: "1px solid " + accent, background: accentBg, color: accent, cursor: "pointer", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" };
  var menuStyle = { position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 4, minWidth: 220, background: "#ffffff", border: "1.5px solid #d0d5dd", borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 1000, overflow: "hidden" };
  var itemStyle = { display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#ffffff", border: "none", borderBottom: "1px solid #e5e7eb", cursor: "pointer", fontSize: 11, color: "#1a1a1a" };
  function pick(k) { setOpen(false); if (onPick) onPick(k); }
  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", ...(style || {}) }}>
      <button onClick={function () { setOpen(!open); }} style={btnStyle}>{"🖨"} {label} <span style={{ fontSize: 9, opacity: 0.7 }}>{"▾"}</span></button>
      {open && (
        <div style={menuStyle}>
          <button onClick={function () { pick(pickPlaceholder); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={itemStyle}>
            <div style={{ fontWeight: 700 }}>{t.pi}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.piDesc}</div>
          </button>
          <button onClick={function () { pick(packingKind); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={itemStyle}>
            <div style={{ fontWeight: 700 }}>{t.packing}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.packingDesc}</div>
          </button>
          <button onClick={function () { pick(masterKind); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={brand === "sl" ? itemStyle : { ...itemStyle, borderBottom: "none" }}>
            <div style={{ fontWeight: 700 }}>{t.master}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.masterDesc}</div>
          </button>
          {/* Driver Sign-Off — SweetLeaf only per spec. Blank form
              the warehouse fills by hand; no GFI counterpart yet. */}
          {brand === "sl" && (
            <button onClick={function () { pick(driverKind); }}
              onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
              onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
              style={{ ...itemStyle, borderBottom: "none" }}>
              <div style={{ fontWeight: 700 }}>{t.driver}</div>
              <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.driverDesc}</div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────── LOADING DOCS ORG CHART ────────
   Compact tree visual: a centered shipment icon up top, two soft-grey
   lines branching down to a GFI column on the left and a SweetLeaf
   column on the right. Each column shows the brand isotipo with the
   brand-coloured dropdown right below. Keeps the visual hierarchy
   "shipment splits into two brand outputs". Self-contained so a
   single render keeps print actions discoverable. */
function LoadingDocsOrgChart({ lang, onPick }) {
  /* Layout constants kept inline so the visual reads top-to-bottom:
       row 1 — icon at center (with white card border)
       row 2 — short vertical stub down
       row 3 — horizontal bar between the two branches
       row 4 — two short vertical stubs down to each column
       row 5 — two columns (isotipo + brand button)
     All connector lines use #d0d5dd (light grey) to feel subtle but
     present in print preview too. */
  var line = "#d0d5dd";
  var subtitle = lang === "es" ? "Documentos de Carga" : "Loading Docs";
  return (
    <div style={{ paddingTop: 6, paddingBottom: 4, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 260 }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{subtitle}</div>
      {/* Center shipment icon */}
      <div style={{ fontSize: 18, lineHeight: 1, padding: "3px 7px", background: "#ffffff", border: "1.5px solid " + line, borderRadius: 6 }} role="img" aria-label="shipment">{"🚚"}</div>
      {/* Vertical stub down from icon */}
      <div style={{ width: 1.5, height: 10, background: line }}></div>
      {/* Horizontal connector + two outer stubs going down to each branch */}
      <div style={{ position: "relative", width: 150, height: 10 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1.5, background: line }}></div>
        <div style={{ position: "absolute", top: 0, left: 0, width: 1.5, height: 10, background: line }}></div>
        <div style={{ position: "absolute", top: 0, right: 0, width: 1.5, height: 10, background: line }}></div>
      </div>
      {/* Two branches — GFI left, SweetLeaf right */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 2, width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <img src={GFI_ISOTIPO_B64} alt="Ground-Fresh" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <BrandDocsDropdown brand="gfi" lang={lang} onPick={onPick} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <img src={SL_ISOTIPO_B64} alt="SweetLeaf" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <BrandDocsDropdown brand="sl" lang={lang} onPick={onPick} />
        </div>
      </div>
    </div>
  );
}

/* ======================== BILL PREVIEW MODAL ========================
   Lightweight read-only preview of the bill that fed an invoice.
   Opened by clicking the LOT cell in the invoices list. Carries an
   "Open Bill" button that pops the Bills module in a new browser
   tab via the URL-hash deeplink (App.js handles the hash on mount). */
function BillPreviewModal({ bill, lang, onClose }) {
  var t = lang === "es"
    ? { title: "Bill", subtitle: "Vista previa", lot: "Lote", date: "Fecha", shipper: "Exportador", proforma: "Proforma", shipperInvoice: "Factura del Exportador", warehouse: "Bodega", product: "Producto", description: "Descripción", category: "Cat", pallets: "Pallets", cases: "Cant", price: "Precio (ref)", lineTotal: "Total", saleStatus: "Status Venta", paymentStatus: "Estado de Pago", noLines: "Sin líneas de producto", total: "Total", openBtn: "Abrir Bill", closeBtn: "Cerrar", statusDraft: "Borrador", statusFinalized: "Finalizada", statusAwaiting: "Pendiente Pago", statusPaid: "Pagada" }
    : { title: "Bill", subtitle: "Preview", lot: "Lot", date: "Date", shipper: "Shipper", proforma: "Proforma", shipperInvoice: "Shipper Invoice", warehouse: "Warehouse", product: "Product", description: "Description", category: "Cat", pallets: "Pallets", cases: "Qty", price: "Price (ref)", lineTotal: "Total", saleStatus: "Sale Status", paymentStatus: "Payment Status", noLines: "No product lines", total: "Total", openBtn: "Open Bill", closeBtn: "Close", statusDraft: "Draft", statusFinalized: "Finalized", statusAwaiting: "Awaiting Payment", statusPaid: "Paid" };

  /* Compute the same shape calcBillTotals does (we don't import it
     to avoid a circular dep; this is read-only and reproduces the
     line-level math used by the bill form). */
  var billDefaultRate = parseFloat(bill.commissionRate) || 0;
  var grossSales = 0, commAmt = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? billDefaultRate : (parseFloat(l.commissionRate) || 0);
    grossSales += amt;
    commAmt += amt * (rate / 100);
  });
  var totalFees = 0; (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var totalCredits = 0; (bill.credits || []).forEach(function (c) { totalCredits += parseFloat(c.amount) || 0; });
  var paid = (bill.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var subtotal = grossSales - commAmt - totalFees + totalCredits;
  var balance = subtotal - paid;
  /* Payment status — same logic the Bills list uses (paid wins when
     balance ≈ 0 regardless of stored status). */
  var paymentStatus = bill.status || "draft";
  if (paymentStatus !== "draft" && paid > 0 && balance <= 0.01) paymentStatus = "paid";

  /* Resolve warehouse name via the shipment linked to this bill's
     lot — bills don't store warehouseId directly. */
  var whName = "";
  try {
    var shipments = JSON.parse(localStorage.getItem("gfi-shipments") || "[]");
    var shp = findBillByLot(bill.lot, shipments) || shipments.find(function (s) { return String(s.lot) === String(bill.lot); });
    if (shp) {
      whName = shp.warehouseName || "";
      if (!whName && shp.warehouseId) {
        var warehouses = JSON.parse(localStorage.getItem("gfi-warehouses") || "[]");
        var w = warehouses.find(function (x) { return idMatches(x, shp.warehouseId); });
        if (w) whName = w.fullName || w.shortName || w.name || "";
      }
    }
  } catch (e) {}

  function openInNewTab() {
    /* Deeplink: opens index.html in a new tab with #open=bill:ID;
       App.js parses the hash on mount, navigates to bills, then calls
       window.__gfiBillOpen(id). The preview modal stays open in the
       current tab (no auto-close per spec). */
    var url = window.location.origin + window.location.pathname + window.location.search + "#open=bill:" + encodeURIComponent(bill.id);
    window.open(url, "_blank");
  }

  var th = { padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid var(--color-border)" };
  var statusBadge = function (st) {
    var styles = {
      draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)", label: t.statusDraft },
      finalized: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)", label: t.statusFinalized },
      awaiting: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.25)", label: t.statusAwaiting },
      paid: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)", label: t.statusPaid },
    };
    var s = styles[st] || styles.draft;
    return <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: s.bg, color: s.text, border: "1px solid " + s.border, boxShadow: SALE_STATUS_BEVEL }}>{s.label}</span>;
  };

  return (
    <DraggableModal title={t.title + " · LOT " + (bill.lot || "—")} onClose={onClose} initialWidth={Math.min(1000, Math.floor(window.innerWidth * 0.8))} initialHeight={Math.min(720, window.innerHeight - 80)}>
      <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "var(--color-accent)" }}>LOT {bill.lot || "—"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{bill.shipperName || "—"} · {bill.proforma || "—"}</div>
          <div style={{ marginTop: 8 }}>{statusBadge(paymentStatus)}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {/* Open Bill — new browser tab. Preview stays open. */}
          <button onClick={openInNewTab} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>{"↗"} {t.openBtn}</button>
          <button onClick={onClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.closeBtn}</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px", fontSize: 12 }}>
        {/* Bill meta — 4 columns, two rows. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px 18px", marginBottom: 18 }}>
          {[
            [t.lot, bill.lot, true],
            [t.date, bill.billDate ? new Date(bill.billDate + "T00:00:00").toLocaleDateString("en-US") : "—"],
            [t.shipper, bill.shipperName],
            [t.warehouse, whName],
            [t.proforma, bill.proforma, true],
            [t.shipperInvoice, bill.shipperInvoice, true],
            [t.paymentStatus, t["status" + paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1)] || paymentStatus],
          ].map(function (arr, i) {
            return (
              <div key={i}>
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{arr[0]}</div>
                <div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", marginTop: 2 }}>{arr[1] || "—"}</div>
              </div>
            );
          })}
        </div>

        {/* Product lines table. */}
        {(bill.lines || []).length === 0 ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", fontSize: 12 }}>{t.noLines}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
            <thead><tr>
              <th style={th}>{t.product}</th>
              <th style={th}>{t.description}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.category}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.pallets}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.cases}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.price}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.lineTotal}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
            </tr></thead>
            <tbody>
              {(bill.lines || []).map(function (l, i) {
                var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
                var sc = SALE_STATUS_COLORS[l.saleStatus] || null;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "7px 8px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}</td>
                    <td style={{ padding: "7px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productDescription || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.category ? <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "rgba(16,185,129,0.10)" : "rgba(107,114,128,0.10)", color: l.category === "OG" ? "#059669" : "#374151" }}>{l.category}</span> : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.pallets || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.reportedPrice ? fmtMoney(l.reportedPrice) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{amt > 0 ? fmtMoney(amt) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.saleStatus && sc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL }}>{l.saleStatus}</span> : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Totals strip — Gross / Commission / Fees / Credits / Subtotal / Paid / Balance. */}
        <div style={{ marginTop: 10, padding: "14px 18px", background: "var(--color-bg)", borderRadius: 10, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, fontSize: 12 }}>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Gross Sales</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(grossSales)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Commission</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(commAmt)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Fees</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(totalFees)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Credits</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(totalCredits)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Subtotal</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(subtotal)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Paid</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(paid)}</div></div>
          <div style={{ gridColumn: "span 2" }}><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Balance</div><div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "var(--color-accent)" }}>{fmtMoney(balance)}</div></div>
        </div>
      </div>
    </DraggableModal>
  );
}

/* ======================== PRINT PREVIEW ======================== */
function PrintPreview({ invoice, t, onClose }) {
  var client = resolveClientFull(invoice.clientId);
  var html = buildInvoicePrintHTML(invoice, client);
  var [driveBusy, setDriveBusy] = useState(false);
  var [driveMsg, setDriveMsg] = useState("");
  var printIt = function() {
    var w = window.open("", "_blank"); if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(function() { try { w.focus(); w.print(); } catch(e) {} }, 600);
    /* Mirror the print to Drive when configured + signed in. Same
       client subfolder the loading docs land in so Arturo has every
       artefact of the deal in one place. */
    if (driveConfigured() && driveSignedIn() && !driveBusy) {
      setDriveBusy(true); setDriveMsg("");
      (async function () {
        try {
          var pdfBlob = await htmlToPdfBlob(html);
          var shipments = loadLS("gfi-shipments", []);
          var suppliers = loadLS("gfi-suppliers", []);
          var seasons = loadLS("gfi-seasons", []);
          var clients = loadLS("gfi-clients", []);
          var company = loadCompanyProfile();
          var shipment = findShipmentForInvoice(invoice, shipments);
          if (!shipment) throw new Error("No linked shipment");
          var sup = suppliers.find(function (s) { return idMatches(s, shipment.supplierId); });
          var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
          var season = seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
          var cli = clients.find(function (c) { return idMatches(c, invoice.clientId); });
          var clientShort = (cli && (cli.shortName || cli.fullName)) || invoice.clientName || "Unknown";
          var segments = rootPathSegments(company.driveRootPath).concat([
            seasonFolderName(season),
            embarqueFolderName(shipment, shipperShort),
            /* GFI PU# leads the SL folder name — see the upload
               helper above for the rationale. */
            invoiceFolderName(Object.assign({}, invoice, { puNumber: invoice.gfiPuNumber || invoice.puNumber }), clientShort),
          ]);
          var folderId = await driveEnsurePath(segments);
          var filename = safeFilename("Invoice_PU_" + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "")) + ".pdf";
          var file = await driveUploadFile(folderId, filename, pdfBlob, { contentType: "application/pdf" });
          setDriveMsg("✓ " + file.name);
        } catch (e) {
          console.error("[Drive upload] invoice PDF failed", e);
          setDriveMsg("⚠ " + (e.message || String(e)));
        } finally { setDriveBusy(false); }
      })();
    }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 20, overflowY: "auto" }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ width: 780, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Invoice \u2014 PU# {invoice.puNumber || "\u2014"}</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {driveBusy && <span style={{ fontSize: 11, color: "#1A73E8", fontStyle: "italic" }}>↑ Drive…</span>}
            {!driveBusy && driveMsg && <span style={{ fontSize: 11, color: driveMsg.indexOf("⚠") === 0 ? "#B91C1C" : "#047857" }}>{driveMsg}</span>}
            <button onClick={printIt} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#2A7A6F", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.printPreview}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.close}</button>
          </div>
        </div>
        <iframe title="invoice-preview" srcDoc={html} style={{ width: "100%", height: "78vh", border: "none", background: "#fff" }} />
      </div>
    </div>,
    document.body
  );
}

/* ======================== INVOICE FORM ======================== */
/* Shared payments-history block. Each row navigates to PaymentRegistry. */
function goToPayment(paymentId) {
  if (window.__gfiNavigate) window.__gfiNavigate("payments");
  setTimeout(function() {
    if (typeof window.__gfiPaymentOpen === "function") window.__gfiPaymentOpen(paymentId);
  }, 220);
}

function PaymentsSection({ payments, total, t }) {
  var paidSum = (payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var balance = total - paidSum;
  var hasPayments = (payments || []).length > 0;
  return (
    <div style={{ marginTop: 18, padding: 14, borderRadius: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c65d00", marginBottom: 10, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.paymentsSection}</div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
        <span style={{ color: "var(--color-text-muted)", fontWeight: 600 }}>{t.originalTotal}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(total)}</span>
      </div>
      {!hasPayments ? (
        <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>{t.noPayments}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 6 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>PAY#</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Date</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>{t.paymentMethodCol}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>{t.paymentRef}</th>
            <th style={{ padding: "5px 6px", textAlign: "right", fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase" }}>Amount</th>
          </tr></thead>
          <tbody>{(payments || []).map(function(p, i) {
            return (
              <tr key={i}
                  onClick={function() { goToPayment(p.paymentId); }}
                  title={"Open " + (p.paymentId || "")}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(198,93,0,0.06)"; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{p.paymentId || "—"}</td>
                <td style={{ padding: "6px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                <td style={{ padding: "6px", color: "var(--color-text-secondary)", textTransform: "capitalize" }}>{p.method || "—"}</td>
                <td style={{ padding: "6px", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{p.reference || "—"}</td>
                <td style={{ padding: "6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "2px solid #c65d00", fontSize: 13, fontWeight: 700 }}>
        <span style={{ color: "var(--color-text)" }}>{t.currentBalance}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: balance > 0.01 ? "#c65d00" : "#059669" }}>{fmtMoney(balance)}</span>
      </div>
    </div>
  );
}

/* Locks edit when an invoice has deposits applied. */
function EditLockDialog({ record, t, onClose }) {
  var payments = record.payments || [];
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ background: "#fff", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{"\u{1F512}"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#B45309" }}>{t.editLockedTitle}</h3>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.5 }}>{t.editLockedMsg}</p>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.editLockedHint}</p>
        <div style={{ borderRadius: 8, border: "1px solid var(--color-border)", overflow: "hidden", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "var(--color-bg)", borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>PAY#</th>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Date</th>
              <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Amount</th>
            </tr></thead>
            <tbody>{payments.map(function(p, i) {
              return (
                <tr key={i}
                    onClick={function() { goToPayment(p.paymentId); onClose(); }}
                    title={"Open " + (p.paymentId || "")}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(198,93,0,0.06)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{p.paymentId || "—"} →</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.close}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* Unified view+edit modal — DraggableModal at center per CLAUDE.md "REGLA DE
   VENTANAS". View mode shows the read-only summary; clicking Edit toggles to
   edit mode inside the SAME modal (renders InvoiceForm with embedded=true).
   Save returns to view mode without closing. */
function DetailPanel({ invoice, invoices, t, lang, onSave, onDelete, onEditLock, onClose, onPrint, onPrintDoc }) {
  var [mode, setMode] = useState("view");
  var [emailDialogOpen, setEmailDialogOpen] = useState(false);
  /* Which EmailDialog kindOptions radio to preselect — "pick" for the
     normal Email Customer action, "pendingPickupReminder" when opened
     from the dedicated reminder kebab item below (2026-09-12, Arturo:
     "agrega un pick up reminder al modulo de sweetleaf tambien para
     enviar a four seasons" — same feature already added on the
     Ground-Fresh Invoice side, InvoiceRegistry.js). */
  var [emailDialogDefaultKind, setEmailDialogDefaultKind] = useState("pick");
  var [emailWarehouseOpen, setEmailWarehouseOpen] = useState(false);
  var [passingDialogOpen, setPassingDialogOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState(null);
  /* Pending Pick Ticket remarks prompt for the EMAIL flow. When the
     user clicks Send inside EmailDialog and the attachment list
     includes a "*-pick" kind, we open the same PickTicketRemarksModal
     the print flow uses and Promise-wait for the resolved
     { remarks, loadingDate } before calling buildInvoiceDocPdf. The
     ref carries the live resolve/reject so the modal can complete
     the Promise without re-binding on every render. */
  var [pendingRemarks, setPendingRemarks] = useState(null);
  var pendingRemarksRef = useRef(null);
  pendingRemarksRef.current = pendingRemarks;
  /* Promise-based prompt — returns { remarks, loadingDate } when the
     user confirms, rejects when they cancel. Reused by both the
     customer-email and warehouse-email buildAttachments paths. */
  var promptPickRemarks = function (defaultLD) {
    return new Promise(function (resolve, reject) {
      setPendingRemarks({ defaultLD: defaultLD || "", resolve: resolve, reject: reject });
    });
  };
  /* "Client Response — Protection" — same collapsible multi-response
     list as InvoiceRegistry.js's own card (2026-10-08, Arturo: "quiero
     aplicar exactamente la misma logica de las respuestas a protected
     files en sweetleaf... solo para reguistrar una o varias
     respuestas... aqui no necesito enviarlas por email desde
     sweetleaf"). No send-to-shipper button here — the shipper only
     ever hears from Ground-Fresh, never SweetLeaf (see handleSave's
     push of the full list to the mirrored GFI invoice below). */
  var [respListOpen, setRespListOpen] = useState(false);
  var [respOpenIdx, setRespOpenIdx] = useState(null);
  var [respEditIdx, setRespEditIdx] = useState(null);
  var [respDraftStatus, setRespDraftStatus] = useState("");
  var [respDraftNote, setRespDraftNote] = useState("");
  var [respDraftDate, setRespDraftDate] = useState("");
  var [respDraftTime, setRespDraftTime] = useState("");
  var fresh = useMemo(function() {
    return (invoices || []).find(function(x) { return x.id === invoice.id; }) || invoice;
  }, [invoice, invoices]);
  /* 2026-10-09, Arturo: "ayer hicimos que las pestañas del navegador
     mostraran informacion de la factura o embarque que tenia abierto
     pero no lo aplicaste en sweetleaf" — same tab-title pattern
     InvoiceRegistry.js/BillRegistry.js already use, just keyed off the
     SL invoice's own invoiceNumber (INV-XXXXX) instead of the GFI PU#. */
  useDocumentTitle(fresh.invoiceNumber ? ("SL Invoice " + fresh.invoiceNumber + (function () { var n = resolveClientName(fresh.clientId) || fresh.clientName; return n ? " | " + n : ""; })()) : null);
  /* 2026-09-14 — Arturo: after the Protection Status Reminder follow-up,
     Four Seasons responds saying the file will pay in full (or will
     carry a credit) — needs a place to record that WITHOUT touching the
     pallet-level saleStatus itself (that stays Protection, per REGLA DE
     STATUS DE PALLET — it's Ground-Fresh Invoice-only, always read-only
     here). Lifted out of the kebab-menu IIFE below (which already
     computed the same flag to gate the reminder button) so the new
     "Respuesta del Cliente" section further down can use it too. */
  var isProtectionInvoice = (fresh.lines || []).some(function(l) {
    var st = String(l.saleStatus || "").toLowerCase().trim();
    return st === "protection" || st === "pas";
  });

  /* 2026-10-20, Arturo: "ya habiamos corregido que las facturas se
     pudieran editar en ground fresh aunque tuvieran pagos, falto
     hacerlo en sweetleaf" — same fix as InvoiceRegistry.js's
     tryEditMode (GFI side, commit 5e49b15 then fully unlocked): this
     used to redirect fully/paid invoices to the read-only EditLock
     dialog, with no way to fix a mistyped field on a settled SL
     invoice. Always enters edit mode now; onEditLock/EditLockDialog
     stay wired (used by other callers of this same prop), just never
     triggered from here. */
  var tryEditMode = function() {
    setMode("edit");
  };
  var handleSaveInline = function(updated) { onSave(updated); setMode("view"); };
  var handleDeleteInline = function(id) { onDelete(id); onClose(); };

  return (
    <DraggableModal title={"PU# " + (fresh.puNumber || "—") + (mode === "edit" ? " · " + t.edit : "")} onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {mode === "edit" ? (
        <InvoiceForm invoice={fresh} invoices={invoices} t={t} lang={lang} onSave={handleSaveInline} onDelete={handleDeleteInline} onCancel={function() { setMode("view"); }} embedded={true} />
      ) : (
        <>
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 18 }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "#2A7A6F" }}>{fresh.invoiceNumber || fresh.puNumber}</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{(resolveClientName(fresh.clientId) || fresh.clientName)} · LOT {fresh.lot}</div>
            {(fresh.gfiPuNumber || fresh.gfiLot) && (
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
                {fresh.gfiPuNumber ? ("GFI PU# " + fresh.gfiPuNumber) : ""}{fresh.gfiPuNumber && fresh.gfiLot ? " · " : ""}{fresh.gfiLot ? ("GFI LOT " + fresh.gfiLot) : ""}
              </div>
            )}
          </div>
          {/* Print + Edit panel on the right.
                Top    : standalone "Print Invoice" button (invoice PDF)
                Middle : org-chart with shipment icon branching into
                         the two brand-specific docs dropdowns
                Bottom : Edit button (returns the panel to its prior
                         compact footprint when not interacting) */}
          {/* Action column — primary action buttons grouped at the
              top (Print Invoice + the client-specific Email HTML
              button), then the loading-doc org chart, then Edit.
              Email button is gated by client identity: Twin River
              → Inbound Five Brothers (cream + gold), Four Seasons
              → Outbound ACS (teal + mint). Resolves the client name
              from both `clientName` and the catalog lookup so
              legacy invoices missing clientName still hit the
              right branch. */}
          {(function () {
            var onPreviewInvoice = function () {
              var client = resolveClientFull(fresh.clientId);
              var html = buildInvoicePrintHTML(fresh, client);
              setPreviewHtml({ html: html, title: t.printPreview });
            };
            var pickDoc = function (kind) { if (onPrintDoc) onPrintDoc(fresh, kind); };
            /* SweetLeaf print menu — flat single section, every doc
               always renders with SL branding (brandKey forced to "sl"
               in buildInvoiceDocPdf / handlePrintDoc). No GFI vs SL
               split, no section headers. */
            var printSections = [
              { items: [
                { key: "invoice-print", label: t.printPreview, icon: "📄", onClick: onPreviewInvoice },
                { key: "sl-pickorinbound", label: "Pick Ticket / Inbound", icon: "🖨", onClick: function () { pickDoc("sl-pickorinbound"); } },
                { key: "sl-packing", label: "Packing List", icon: "🖨", onClick: function () { pickDoc("sl-packing"); } },
                { key: "sl-driversignoff", label: "Driver Sign-Off", icon: "🖨", onClick: function () { pickDoc("sl-driversignoff"); } },
                { key: "sl-taf", label: "Transaction Approval Form", icon: "🖨", onClick: function () {
                  var shipments = loadLS("gfi-shipments", []);
                  var warehouses = loadLS("gfi-warehouses", []);
                  var tafCtx = { shipments: shipments, warehouses: warehouses };
                  /* Auto-detect protection from line saleStatus — any
                     "protection" or "pas" line flips the doc to the
                     yellow-banner variant without prompting the user. */
                  var isProtection = (fresh.lines || []).some(function (l) {
                    var st = String(l.saleStatus || "").toLowerCase().trim();
                    return st === "protection" || st === "pas";
                  });
                  var html = buildTransactionApprovalFormHTML(fresh, tafCtx, { protection: isProtection });
                  setPreviewHtml({ html: html, title: "Transaction Approval Form" });
                  /* Direct download to the browser's Downloads folder —
                     no longer depends on Drive being connected/signed in
                     (fix-dashboard.md: "si el usuario no tiene Drive
                     conectado, no se guarda en ninguna parte"). The old
                     Drive-mirror upload block that lived here is gone;
                     sending this doc to Drive is now a separate manual
                     action if the user wants it. SL Invoice number
                     (INV-XXXX) is fresh.invoiceNumber, same field the
                     old Drive filename used. */
                  (async function () {
                    try {
                      var pdfBlob = await htmlToPdfBlob(html);
                      var invNum = fresh.invoiceNumber || fresh.puNumber || "";
                      var filename = safeFilename("Transaction_Approval_Form_" + invNum) + ".pdf";
                      var url = URL.createObjectURL(pdfBlob);
                      var a = document.createElement("a");
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      console.error("[TAF] PDF download failed", e);
                      alert((lang === "es" ? "Error al generar el PDF: " : "Could not generate PDF: ") + (e && e.message ? e.message : String(e)));
                    }
                  })();
                } },
              ]},
            ];
            /* SweetLeaf kebab — only the two email actions. Drive
               folder creation + the legacy Email-HTML clipboard
               buttons (Inbound Five Brothers / Outbound ACS) are
               GFI-specific and removed here. */
            /* Gates the reminder action to only when this SL invoice
               (always Four Seasons) is still Pending Pick Up — once
               it's confirmed delivered (or flipped to Pending
               Delivery via Passing) a "hasn't been picked up yet"
               nudge no longer makes sense. Same resolvePendingStage-
               style fallback used everywhere else in this file
               (f.passingSentAt), just inlined since SweetLeafPortal
               never needs cross-reference into another SL mirror —
               everything here already IS the SL side. */
            var slInvoiceStatus = deriveDeliveryStatus((fresh.lines || []).map(function (l) { return { saleStatus: l.saleStatus }; })) || (fresh.passingSentAt ? "pendingDelivery" : "pendingPickUp");
            /* Same stale-cache class of bug already fixed in
               InvoiceRegistry.js/ShipmentRegistry.js (2026-09-14) —
               except here the synchronous getSLSettings()/
               slEmailAccounts()/slEmailSignatures() read from
               localStorage["gfi-sl-settings"], not an in-memory
               Supabase cache. getFreshSlSettings() already exists
               (added for the Passing/Factoring mobile "wrong from
               address" fix) but only re-fetches — it never wrote the
               result back to localStorage, so the OTHER email flows
               here (Email Customer, Pickup Reminder, and now the new
               Protection Reminder) still read whatever this device's
               localStorage last had, same risk as the bug already
               fixed elsewhere. Writing the fresh result back before
               opening any of these dialogs closes that gap for good. */
            var ensureSlSettingsFresh = function () {
              return getFreshSlSettings().then(function (fresh2) {
                try { localStorage.setItem("gfi-sl-settings", JSON.stringify(fresh2)); } catch (e) {}
              }).catch(function () {});
            };
            var kebabItems = [
              { key: "email", label: t.emailCustomer, icon: "✉", onClick: function () {
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                getFreshShipments().then(function (freshShipments) {
                  var shp = findShipmentForInvoice(fresh, freshShipments);
                  if (!shp) { alert(t.emailNoShipment); return; }
                  setEmailDialogDefaultKind("pick");
                  ensureSlSettingsFresh().then(function () { setEmailDialogOpen(true); });
                });
              }},
              { key: "email-warehouse", label: t.emailWarehouse, icon: "✉", onClick: function () {
                /* Email routing rule (2026-09-14, Arturo): emails to
                   anyone other than Four Seasons/AGROCAP — shippers,
                   warehouse, customs, other clients — must go out
                   exclusively from the Ground-Fresh module with the
                   Ground-Fresh masked account, never from here. Blocked
                   before the dialog opens, per "no permitir el envio y
                   enviar mensaje de error". The dialog/state this used
                   to open (emailWarehouseOpen) is left in place further
                   down, unreachable now, rather than torn out — keeps
                   this change reversible if the rule ever changes. */
                alert(nonFourSeasonsRoutingBlockedMessage(lang));
              }},
              { key: "email-passing", label: t.sendPassing, icon: "📎", onClick: function () {
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                getFreshShipments().then(function (freshShipments) {
                  var shpP = findShipmentForInvoice(fresh, freshShipments);
                  if (!shpP) { alert(t.emailNoShipment); return; }
                  ensureSlSettingsFresh().then(function () { setPassingDialogOpen(true); });
                });
              }},
            ];
            if (slInvoiceStatus === "pendingPickUp") {
              kebabItems.push({ key: "email-reminder", label: t.emailReminderPickup, icon: "⏰", onClick: function () {
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                getFreshShipments().then(function (freshShipments) {
                  var shpR = findShipmentForInvoice(fresh, freshShipments);
                  if (!shpR) { alert(t.emailNoShipment); return; }
                  setEmailDialogDefaultKind("pendingPickupReminder");
                  ensureSlSettingsFresh().then(function () { setEmailDialogOpen(true); });
                });
              }});
            }
            if (isProtectionInvoice) {
              kebabItems.push({ key: "email-protection-reminder", label: t.emailProtectionReminder, icon: "🛡", onClick: function () {
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                getFreshShipments().then(function (freshShipments) {
                  var shpPR = findShipmentForInvoice(fresh, freshShipments);
                  if (!shpPR) { alert(t.emailNoShipment); return; }
                  setEmailDialogDefaultKind("protectionReminder");
                  ensureSlSettingsFresh().then(function () { setEmailDialogOpen(true); });
                });
              }});
            }
            return (
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={onPreviewInvoice}
                  title={t.preview}
                  style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}
                >👁 {t.preview}</button>
                <ButtonMenu
                  trigger={<><span>🖨</span><span>{t.printPdf}</span><span style={{ fontSize: 9, opacity: 0.7 }}>▾</span></>}
                  sections={printSections}
                  title={t.printPdf}
                />
                <ButtonMenu
                  trigger={<span style={{ fontSize: 16, fontWeight: 700 }}>⋮</span>}
                  items={kebabItems}
                  title={t.more}
                  style={{ padding: "4px 10px" }}
                />
                <button onClick={tryEditMode} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.editBtn}</button>
              </div>
            );
          })()}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", fontSize: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", marginBottom: 18 }}>
            {/* Lot: SL records store the GFI lot as `gfiLot` (see "Copy to
                SL" in InvoiceRegistry.js), never a plain `lot` — this cell
                read the wrong field and always showed "—" even though the
                real lot was visible in the header subtitle right above
                (2026-09-14, Arturo screenshot: "no se ve el lote de donde
                proviene la fruta"). Falls back to `lot` for any legacy
                record that might have used that name instead. */}
            {[[t.invNum, fresh.invoiceNumber || fresh.puNumber, true], [t.gfiPuNum, fresh.gfiPuNumber || "—", true], [t.date, fmtDate(fresh.date)], [t.dueDate, fmtDate(fresh.dueDate)], [t.clientRef, fresh.clientRef, true], [t.lot, fresh.gfiLot || fresh.lot || "", true], [t.pickup, fresh.pickupDate ? (fmtDate(fresh.pickupDate) + (fresh.pickupTime ? " " + fresh.pickupTime : "")) : "", true], [t.thermographLabel, fresh.thermograph || "", true]].map(function(arr, i) {
              return <div key={i}><span style={{ color: "var(--color-text-muted)", fontSize: 10 }}>{arr[0]}</span><div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
            })}
          </div>
          {/* Client-reported data (2026-09-16, Arturo: "TAMBIEN NECESITO
              VISIBLE AL ENTRAR A LA FACTURA EN gf Y SU GEMELA DE SL,
              TODOS LOS DATOS CAPTURADOS EN Client-reported data, AHORA
              SOLO SE VEN SI DAN CLICK EN ENVIAR EMAIL TO SHIPPER") — read-
              only here, same as Pickup/Temp Recorder above: Ground-Fresh
              is the source of truth (captured on the GFI invoice), this
              is just the synced mirror, never edited from SL. */}
          {isProtectionInvoice && (fresh.clientReportedDate || fresh.clientArrivalDate) && (
            <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.03)", border: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#DC2626", marginBottom: 6 }}>{lang === "es" ? "Datos reportados por el cliente" : "Client-reported data"}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Fecha reportada" : "Date reported"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientReportedDate || "—"}</div></div>
                <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Hora reportada" : "Time reported"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientReportedTime || "—"}</div></div>
                <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Llegada a su bodega" : "Arrival at their warehouse"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientArrivalDate || "—"}</div></div>
                <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Hora de llegada" : "Arrival time"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientArrivalTime || "—"}</div></div>
              </div>
            </div>
          )}
          {/* 2026-09-14 — Arturo: record Four Seasons' follow-up response
              to the Protection Status Reminder ("no tuvo problemas y se
              pagará completa" / "tendrá créditos") WITHOUT touching the
              pallet-level saleStatus, which stays Protection — the client
              may confirm how it'll settle long before Ground-Fresh gets a
              real delivery-condition update on the invoice. Only shown on
              invoices actually under Protection/PAS; auto-saves on every
              change, same pattern as the other client-reported fields in
              this panel (clientReportedDate, etc.). */}
          {isProtectionInvoice && (function() {
            /* No longer gated on !fresh.protectionResponseAt — a
               "noResultYet" response is registered (protectionResponseAt
               IS set) but is not final, so the banner must still be able
               to come back due; computeProtectionReminderStatus itself
               already returns null once a FINAL response is on file. */
            var rem = computeProtectionReminderStatus(fresh, null, getSLSettings().protectionReminderIntervalDays);
            if (!rem || !rem.isDue) return null;
            return (
              <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.3)", color: "#B45309", fontSize: 12, fontWeight: 600 }}>
                {t.protectionReminderDueBanner(rem.daysSinceArrival)}
              </div>
            );
          })()}
          {isProtectionInvoice && (function() {
            /* Same shape as InvoiceRegistry.js's own protectionResponses[]
               card (Twin River etc.) — collapsible list, each entry
               ALSO collapsed to a one-line summary until opened, with
               Edit/Remove but NO send button (this module never emails
               the shipper directly). Every mutation mirrors the LAST
               entry back onto the legacy flat fields so
               computeProtectionReminderStatus/the reminder cron (which
               still read protectionResponseStatus/At directly) keep
               working unchanged — and pushes the full array to the
               mirrored GFI invoice in handleSave below. */
            var pad2 = function (n) { return String(n).padStart(2, "0"); };
            var respList = (fresh.protectionResponses && fresh.protectionResponses.length > 0)
              ? fresh.protectionResponses
              : (fresh.protectionResponseStatus ? [{ status: fresh.protectionResponseStatus, note: fresh.protectionResponseNote || "", at: fresh.protectionResponseAt || "" }] : []);
            var saveResponses = function (next) {
              var last = next.length > 0 ? next[next.length - 1] : null;
              onSave(Object.assign({}, fresh, {
                protectionResponses: next,
                protectionResponseStatus: last ? last.status : "",
                protectionResponseNote: last ? last.note : "",
                protectionResponseAt: last ? last.at : "",
              }));
            };
            return (
              <div style={{ marginBottom: 18, borderRadius: 8, background: "rgba(37,99,235,0.05)", border: "1px solid rgba(37,99,235,0.15)" }}>
                <button type="button" onClick={function () { setRespListOpen(function (v) { return !v; }); }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", color: "#2563EB", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <span>{t.protectionResponseLabel + " - " + respList.length}</span>
                  <span>{respListOpen ? "▲" : "▼"}</span>
                </button>
                {respListOpen && (
                  <div style={{ padding: "0 14px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {respList.map(function (r, idx) {
                      var atD = r.at ? new Date(r.at) : null;
                      var dStr = atD && !isNaN(atD.getTime()) ? (atD.getFullYear() + "-" + pad2(atD.getMonth() + 1) + "-" + pad2(atD.getDate())) : "";
                      var tStr = atD && !isNaN(atD.getTime()) ? (pad2(atD.getHours()) + ":" + pad2(atD.getMinutes())) : "";
                      var isRowOpen = respOpenIdx === idx;
                      var isEditing = respEditIdx === idx;
                      var statusLabel = t[r.status] || r.status || "—";
                      return (
                        <div key={idx} style={{ borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", overflow: "hidden" }}>
                          <button type="button" onClick={function () { setRespOpenIdx(isRowOpen ? null : idx); if (isRowOpen) setRespEditIdx(null); }} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "transparent", border: "none", cursor: "pointer", fontSize: 12, textAlign: "left" }}>
                            <span><strong>{"Response " + (idx + 1)}</strong>{" — " + statusLabel + (r.at ? " — " + new Date(r.at).toLocaleDateString() : "")}</span>
                            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{isRowOpen ? "▲" : "▼"}</span>
                          </button>
                          {isRowOpen && (
                            <div style={{ padding: "0 10px 10px" }}>
                              {isEditing ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                  <SearchSelect
                                    value={respDraftStatus}
                                    onChange={function (v) { setRespDraftStatus(v); }}
                                    placeholder={t.protectionResponsePlaceholder}
                                    options={PROTECTION_RESPONSE_STATUSES.map(function (s) { return { value: s, label: t[s] }; })}
                                  />
                                  <textarea value={respDraftNote} onChange={function (e) { setRespDraftNote(e.target.value); }} placeholder={t.protectionResponseNotePlaceholder} rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    <div>
                                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha de la respuesta" : "Response date"}</div>
                                      <input type="date" value={respDraftDate} onChange={function (e) { setRespDraftDate(e.target.value); }} style={inp} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Hora de la respuesta" : "Response time"}</div>
                                      <input type="time" value={respDraftTime} onChange={function (e) { setRespDraftTime(e.target.value); }} style={inp} />
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 8 }}>
                                    <button type="button" onClick={function () {
                                      var d = respDraftDate ? new Date(respDraftDate + "T" + (respDraftTime || "00:00") + ":00") : null;
                                      var next = respList.slice();
                                      next[idx] = { status: respDraftStatus, note: respDraftNote, at: d && !isNaN(d.getTime()) ? d.toISOString() : "" };
                                      saveResponses(next);
                                      setRespEditIdx(null);
                                    }} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#2563EB", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Save</button>
                                    <button type="button" onClick={function () { setRespEditIdx(null); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 700 }}>{statusLabel}</div>
                                  {r.note && <div style={{ fontSize: 12, marginTop: 3, whiteSpace: "pre-line" }}>{r.note}</div>}
                                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{r.at ? new Date(r.at).toLocaleString() : "—"}</div>
                                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                                    <button type="button" onClick={function () { setRespDraftStatus(r.status || ""); setRespDraftNote(r.note || ""); setRespDraftDate(dStr); setRespDraftTime(tStr); setRespEditIdx(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #2563EB", background: "transparent", color: "#2563EB", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Edit</button>
                                    <button type="button" onClick={function () { saveResponses(respList.filter(function (_, i) { return i !== idx; })); setRespOpenIdx(null); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 11 }}>✕</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button type="button" onClick={function () {
                      var now = new Date();
                      var next = respList.concat([{ status: "", note: "", at: now.toISOString() }]);
                      saveResponses(next);
                      setRespDraftStatus(""); setRespDraftNote("");
                      setRespDraftDate(now.getFullYear() + "-" + pad2(now.getMonth() + 1) + "-" + pad2(now.getDate()));
                      setRespDraftTime(pad2(now.getHours()) + ":" + pad2(now.getMinutes()));
                      setRespOpenIdx(next.length - 1);
                      setRespEditIdx(next.length - 1);
                    }} style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6, border: "1px dashed #2563EB", background: "transparent", color: "#2563EB", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {lang === "es" ? "Agregar respuesta" : "Add response"}</button>
                  </div>
                )}
              </div>
            );
          })()}
          {isProtectionInvoice && (
            <div style={{ marginBottom: 18, padding: "10px 14px", borderRadius: 8, background: "rgba(37,99,235,0.05)", border: "1px solid rgba(37,99,235,0.15)" }}>
              {/* Per-invoice on/off for the automated Protection Status
                  Reminder cron (2026-09-23, Arturo: "poder decidir por
                  gactura qi suiero que se envien esos emails automaticos").
                  undefined === enabled (same "no explicit opt-out = on"
                  convention as SHIPPER_EMAIL_CATEGORIES) — an invoice
                  never loses its default just because this field was
                  never touched. A manual send from the kebab menu is
                  never gated by this — only the scheduled job checks it. */}
              <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--color-border)", fontSize: 11, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={fresh.protectionReminderAutoEnabled !== false}
                  onChange={function(e) { onSave(Object.assign({}, fresh, { protectionReminderAutoEnabled: e.target.checked })); }}
                />
                <span>
                  {t.protectionReminderAutoLabel}
                  {(fresh.protectionReminderAutoCount || 0) > 0 && (
                    <span style={{ marginLeft: 6, color: "var(--color-text-muted)" }}>
                      {t.protectionReminderAutoCountLabel(fresh.protectionReminderAutoCount, getSLSettings().protectionReminderMaxAuto || 3)}
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 18 }}>
            <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
              <th style={{ padding: "6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{t.cases}</th>
              <th style={{ padding: "6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{lang === "es" ? "Pallets" : "Pallets"}</th>
              <th style={{ padding: "6px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{t.product}</th>
              <th style={{ padding: "6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{t.salePrice}</th>
              <th style={{ padding: "6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{t.amount}</th>
              <th style={{ padding: "6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)" }}>{t.commPerCase}</th>
              <th style={{ padding: "6px", textAlign: "right", fontSize: 10, fontWeight: 700, color: "#DC2626" }}>{t.lineCommAmt}</th>
            </tr></thead>
            <tbody>{(fresh.lines || []).map(function(l, i) {
              var cases = parseInt(l.cases, 10) || 0;
              var amt = cases * (parseFloat(l.salePrice) || 0);
              var perCase = parseFloat(l.slCommissionPerCase) || 0;
              var isLoss = l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer";
              var lineComm = isLoss ? 0 : cases * perCase;
              return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                <td style={{ padding: "7px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases}</td>
                <td style={{ padding: "7px 6px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{parseInt(l.pallets, 10) || "—"}</td>
                <td style={{ padding: "7px 6px", fontWeight: 600 }}>{l.productName || l.productBase}<div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{l.productDescription}</div></td>
                <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(l.salePrice)}</td>
                <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#c65d00" }}>{fmtMoney(amt)}</td>
                <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{perCase > 0 ? fmtMoney(perCase) : "—"}</td>
                <td style={{ padding: "7px 6px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>{lineComm > 0 ? "(" + fmtMoney(lineComm) + ")" : "—"}</td>
              </tr>;
            })}</tbody>
          </table>
          {/* Fees & Deductions breakdown — fees SUBTRACT from total
              (PTI Labeling, QC, Warehouse, Advance Financing, etc.).
              Stored amounts are positive magnitudes; abs() guards
              against legacy/imported negative values that would
              otherwise render as "+$-…". */}
          {(fresh.fees || []).filter(function(f) { return f && (f.feeType || f.feeCustom || Math.abs(parseFloat(f.amount) || 0) > 0); }).length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#DC2626", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.fees}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
                <tbody>{(fresh.fees || []).map(function(f, i) {
                  var fAmtAbs = Math.abs(parseFloat(f.amount) || 0);
                  if (!f || (!f.feeType && !f.feeCustom && !(fAmtAbs > 0))) return null;
                  var fname = f.feeType === "__custom__" ? f.feeCustom : f.feeType;
                  return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 6px", fontWeight: 500 }}>{fname || (f.feeCustom || "Fee")}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-muted)" }}>{f.qty > 1 ? f.qty + " × " + fmtMoney(f.rate) : (f.pctRate ? f.pctRate + "%" : "")}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#DC2626" }}>({fmtMoney(fAmtAbs)})</td>
                  </tr>;
                })}</tbody>
              </table>
            </>
          )}

          {/* Credits breakdown — credits ADD to total */}
          {(fresh.credits || []).filter(function(c) { return c && (c.concept || c.refPuNumber || Math.abs(parseFloat(c.amount) || 0) > 0); }).length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#059669", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.credits}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
                <tbody>{(fresh.credits || []).map(function(c, i) {
                  var cAmtAbs = Math.abs(parseFloat(c.amount) || 0);
                  if (!c || (!c.concept && !c.refPuNumber && !(cAmtAbs > 0))) return null;
                  var label = c.type === "fromInvoice"
                    ? (lang === "es" ? "Crédito de PU# " : "Credit from PU# ") + (c.refPuNumber || "—")
                    : (c.concept || (lang === "es" ? "Crédito" : "Credit"));
                  return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 6px", fontWeight: 500 }}>{label}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "#059669" }}>+{fmtMoney(cAmtAbs)}</td>
                  </tr>;
                })}</tbody>
              </table>
            </>
          )}

          {/* Balance Transfers breakdown — same logic as in the form */}
          {(fresh.balanceTransfers || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.balanceTransfers}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
                <tbody>{(fresh.balanceTransfers || []).map(function(tr, i) {
                  var amt = parseFloat(tr.amount) || 0;
                  if (!(amt > 0)) return null;
                  var isCredit = tr.type === "credit";
                  var color = isCredit ? "#059669" : "#DC2626";
                  var label = (lang === "es"
                    ? (isCredit ? "Crédito de PU# " : "Débito a PU# ")
                    : (isCredit ? "Credit from PU# " : "Debit to PU# ")) + (tr.otherPuNumber || "?");
                  return <tr key={tr.id || i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 6px", fontWeight: 500 }}>{label}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: color }}>{isCredit ? "+" : "−"}{fmtMoney(amt)}</td>
                  </tr>;
                })}</tbody>
              </table>
            </>
          )}

          {/* Summary box — Total Due = Gross − Commission − Fees + Credits ± Transfers */}
          <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, fontSize: 12, marginBottom: 6 }}>
            {(function() {
              var gross = 0;
              var commission = 0;
              (fresh.lines || []).forEach(function(l) {
                if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
                var cases = parseInt(l.cases, 10) || 0;
                gross += cases * (parseFloat(l.salePrice) || 0);
                commission += cases * (parseFloat(l.slCommissionPerCase) || 0);
              });
              var advFee = Math.abs(parseFloat(fresh.advanceFinancingFee) || 0);
              var tFees = 0; (fresh.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
              var tCredits = 0; (fresh.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
              var tTransfers = 0; (fresh.balanceTransfers || []).forEach(function(tr) { var amt = parseFloat(tr.amount) || 0; if (tr.type === "credit") tTransfers += amt; else if (tr.type === "debit") tTransfers -= amt; });
              var net = gross - commission - advFee - tFees + tCredits + tTransfers;
              return <>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales || "Gross Sales"}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(gross)}</span></div>
                {commission > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {lang === "es" ? "Comisión SweetLeaf" : "SweetLeaf Commission"}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(commission)})</span></div>}
                {advFee > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {t.advanceFinancingFee}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(advFee)})</span></div>}
                {tFees > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {t.fees}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(tFees)})</span></div>}
                {tCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#059669" }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(tCredits)}</span></div>}
                {tTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: tTransfers >= 0 ? "#059669" : "#DC2626" }}><span>{tTransfers >= 0 ? "+" : "−"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tTransfers >= 0 ? fmtMoney(tTransfers) : "(" + fmtMoney(-tTransfers) + ")"}</span></div>}
                <div style={{ borderTop: "2px solid #2A7A6F", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, color: "#2A7A6F" }}><span>{t.totalDue}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(net)}</span></div>
              </>;
            })()}
          </div>
          <PaymentsSection payments={fresh.payments} total={calcInvoiceTotal(fresh)} t={t} />
        </div>
        </>
      )}
      {emailDialogOpen && (function () {
        var clients = loadLS("gfi-clients", []);
        var cli = clients.find(function (c) { return idMatches(c, fresh.clientId); });
        var company = loadCompanyProfile();
        var distList = (cli && cli.distributionList && cli.distributionList.length > 0)
          ? cli.distributionList
          : (cli && cli.contacts || []).filter(function (c) { return c && c.email; }).map(function (c) {
              var name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.nombre || c.name || "";
              return { name: name, email: c.email, docs: { inbound: true, packing: true, invoice: true, master: true } };
            });
        /* Detect brand by client name (TR → GFI; FS → SweetLeaf).
           Falls back to GFI for any other client. */
        var nameLower = (fresh.clientName || (cli && cli.shortName) || "").toLowerCase();
        var brand = "sl";  /* SweetLeafPortal: always SL */
        var pu = fresh.gfiPuNumber || fresh.puNumber || "—";
        var po = fresh.clientRef || "—";
        var lot = fresh.lot || "—";
        /* Recipients start checked only if they're actually opted
           into at least one of the doc kinds this dialog sends
           (inbound/pick/packing/master/invoice) — a contact flagged
           ONLY for "compliance" (or with every relevant flag off)
           must not appear pre-selected for a shipment/invoice email.
           Same fix as InvoiceRegistry.js's identical dialog. A
           contact with no `docs` object at all (legacy data) still
           defaults to checked. They stay listed either way so the
           user can still tick them by hand for a one-off case. */
        var recipientDocKinds = ["inbound", "packing", "invoice", "master"];
        var recipients = distList
          .filter(function (c) { return c && c.email; })
          .map(function (c) {
            var hasAnyRelevantDoc = !c.docs || recipientDocKinds.some(function (k) { return !!c.docs[k]; });
            return { name: c.name || "", email: c.email, checked: hasAnyRelevantDoc };
          });
        /* Doc options. Initial checked state mirrors the client's
           distribution-list config: a doc is pre-checked iff at
           least one contact in the list has its corresponding doc
           flag enabled in Client Profile. Pick stays off — it's
           only flipped on via the Inbound↔Pick radio. If the client
           has no distribution list at all, every doc starts
           unchecked and the user picks manually. */
        var anyContactHas = function (k) {
          return distList.some(function (c) { return c && c.docs && c.docs[k]; });
        };
        /* SweetLeaf customer email defaults: Pick Ticket + Packing
           are checked by default; the other docs stay off until the
           user opts in. defaultKind below also flips to "pick". */
        var docOptions = [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: false },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: true },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: false },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: false },
          { key: "invoice",          label: t.emailCustomerDocInvoice, checked: false },
        ];
        /* Formatted for the Protection Reminder kind below — "—" when
           not yet captured (Ground-Fresh's Protection Notice dialog is
           where Arturo enters these; see InvoiceRegistry.js's "Datos
           reportados por el cliente" box), same MM/DD/YYYY convention
           as the rest of the app (CLAUDE.md date format rule). */
        var fmtReminderDate = function (d) {
          if (!d) return "—";
          var dt = new Date(d + "T00:00:00");
          if (isNaN(dt.getTime())) return "—";
          return String(dt.getMonth() + 1).padStart(2, "0") + "/" + String(dt.getDate()).padStart(2, "0") + "/" + dt.getFullYear();
        };
        var reportedDateStr = fmtReminderDate(fresh.clientReportedDate);
        var reportedTimeStr = fresh.clientReportedTime || "—";
        var arrivalDateStr = fmtReminderDate(fresh.clientArrivalDate);
        var arrivalTimeStr = fresh.clientArrivalTime || "—";
        /* Non-final "noResultYet" response — folded into the next
           Protection Status Reminder so it reads as a real follow-up
           instead of repeating the same request from scratch (2026-09-23,
           Arturo: "quiero agregar esa opcion para que el siguiente
           recordatorio que se envie, tome en cuenta la respuesta
           anterior"). Empty string when there's no such response on
           file, so {previousResponseNote} disappears cleanly. */
        var previousResponseNoteStr = (fresh.protectionResponseStatus === "noResultYet" && fresh.protectionResponseAt)
          ? t.previousResponseNoteLine(fmtReminderDate(fresh.protectionResponseAt.slice(0, 10)))
          : "";
        var tpl = function (s) {
          return String(s || "")
            .replace("{pu}", pu)
            .replace("{po}", po)
            .replace("{lot}", lot)
            .replace("{reportedDate}", reportedDateStr)
            .replace("{reportedTime}", reportedTimeStr)
            .replace("{arrivalDate}", arrivalDateStr)
            .replace("{arrivalTime}", arrivalTimeStr)
            .replace("{previousResponseNote}", previousResponseNoteStr);
        };
        var ctx = {
          clients: clients,
          shipments: loadLS("gfi-shipments", []),
          warehouses: loadLS("gfi-warehouses", []),
          company: company,
        };
        var buildAttachments = async function (kinds) {
          /* If any selected attachment is a Pick Ticket we need
             the same remarks the print flow collects so the PDF
             matches what's sent to print. Computed once per send;
             reused for every "*-pick" kind in the batch. */
          var needsPick = kinds.some(function (k) { return /(^|[-:])pick$/.test(k); });
          var pickExtras = null;
          if (needsPick) {
            var shpForLD = findShipmentForInvoice(fresh, ctx.shipments);
            var etaLD = shpForLD ? (shpForLD.dateArrival || "") : "";
            var defLD = "";
            if (etaLD) {
              var dLD = new Date(etaLD + "T00:00:00");
              if (!isNaN(dLD.getTime())) {
                dLD.setDate(dLD.getDate() + 1);
                defLD = dLD.getFullYear() + "-" + String(dLD.getMonth() + 1).padStart(2, "0") + "-" + String(dLD.getDate()).padStart(2, "0");
              }
            }
            pickExtras = await promptPickRemarks(defLD);
          }
          var out = [];
          for (var i = 0; i < kinds.length; i++) {
            var isPick = /(^|[-:])pick$/.test(kinds[i]);
            var att = await buildInvoiceDocPdf(fresh, kinds[i], ctx, isPick ? pickExtras : null);
            out.push(att);
          }
          return out;
        };
        /* Inline doc embed for email body. Full PDF docs don't render
           well inside Gmail/Outlook (their CSS sanitisers strip
           <style> tags and complex layouts) — and the user prefers
           a short summary anyway. We render JUST the product table
           (Product / Description / Pallets / Fill / UOM + totals)
           using inline styles so every email client renders it
           identically. */
        var buildEmbedHtml = async function (/* kinds */) {
          var lines = (fresh.lines || []).filter(function (l) { return l && (l.productName || l.productBase); });
          if (lines.length === 0) return "";
          var esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
          var totals = { pallets: 0, cases: 0 };
          var rows = lines.map(function (l) {
            var p = parseInt(l.pallets, 10) || 0;
            var c = parseInt(l.cases, 10) || 0;
            totals.pallets += p; totals.cases += c;
            return {
              product: l.productBase || l.productName || "",
              desc: l.productDescription || "",
              pallets: p,
              cases: c,
              uom: l.uom || "Flats",
            };
          });
          var th = 'style="text-align:left;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thNum = 'style="text-align:right;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thCtr = 'style="text-align:center;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.4">';
          html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #d0d5dd;background:#ffffff">';
          html += '<thead><tr style="background:#f7f8fa">';
          html += '<th ' + th + '>Product</th>';
          html += '<th ' + th + '>Description</th>';
          html += '<th ' + thNum + '>Pallets</th>';
          html += '<th ' + thNum + '>Fill (Cases)</th>';
          html += '<th ' + thCtr + '>UOM</th>';
          html += '</tr></thead><tbody>';
          rows.forEach(function (r) {
            html += '<tr style="border-bottom:1px solid #eef0f3">';
            html += '<td style="padding:7px 10px;font-weight:600">' + esc(r.product) + '</td>';
            html += '<td style="padding:7px 10px;color:#444">' + esc(r.desc) + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace">' + r.pallets + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace;font-weight:600">' + r.cases + '</td>';
            html += '<td style="padding:7px 10px;text-align:center">' + esc(r.uom) + '</td>';
            html += '</tr>';
          });
          html += '<tr style="background:#f7f8fa;font-weight:700">';
          html += '<td colspan="2" style="padding:9px 10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.04em">Totals</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.pallets + '</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.cases + '</td>';
          html += '<td></td>';
          html += '</tr></tbody></table></div>';
          return html;
        };
        /* SweetLeaf-only templates — no CompanyProfile crossover.
           Each kind composes from the local SL_EMAIL_TEMPLATES so
           bodies + auto-append field sets never inherit anything
           from Ground-Fresh's Company Profile. */
        var tplCfgInbound = SL_EMAIL_TEMPLATES.inbound;
        var tplCfgPick    = SL_EMAIL_TEMPLATES.pickTicket;
        var sup = (loadLS("gfi-suppliers", []) || []).find(function (s) { var ship = findShipmentForInvoice(fresh, ctx.shipments); return ship && idMatches(s, ship.supplierId); });
        var wh = (ctx.warehouses || []).find(function (w) { var ship = findShipmentForInvoice(fresh, ctx.shipments); return ship && idMatches(w, ship.warehouseId); });
        var shp = findShipmentForInvoice(fresh, ctx.shipments);
        var tplValues = {
          pu: pu, po: po, lot: lot,
          arrival: (shp && (shp.dateArrival || shp.dateDeparture)) || "",
          shipper: (sup && sup.shortName) || (shp && shp.shipperName) || "",
          client: (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "",
          warehouse: (wh && (wh.shortName || wh.fullName)) || "",
        };
        var tplBodyInbound = composeEmailBody(tplCfgInbound, tplValues, { labels: { pu: "Inbound" } });
        /* SL -> customer Pick Ticket body never shows LOT (fix-dashboard.md
           AJUSTE 3), tenga o no valor. SL_EMAIL_TEMPLATES.pickTicket.fields.lot
           itself stays true — that template object is shared with the
           SL -> warehouse email below (emailWarehouseOpen), whose pick
           body must keep showing LOT. Scoping the omission to a values
           copy (not the shared template) keeps that other flow untouched. */
        var tplValuesNoLot = Object.assign({}, tplValues, { lot: "" });
        var tplBodyPick    = composeEmailBody(tplCfgPick, tplValuesNoLot, { labels: { pu: "PU#" } });
        /* Subject + body selector: Inbound (default) ↔ Pick Ticket.
           Picking one swaps the subject + body AND flips the
           corresponding doc checkbox. */
        var kindOptions = [
          { value: "inbound", label: t.emailCustomerKindInbound, subject: tpl(t.emailCustomerSubjectInbound), body: tplBodyInbound, docKeyOn: brand + "-inbound", docKeyOff: brand + "-pick" },
          { value: "pick",    label: t.emailCustomerKindPick,    subject: tpl(t.emailCustomerSubjectPick),    body: tplBodyPick,    docKeyOn: brand + "-pick",    docKeyOff: brand + "-inbound" },
        ];
        /* Reminder kind only ever gets preselected (via emailDialogDefaultKind,
           set by the kebab's "Send Pickup Reminder" action) — never a
           normal radio choice alongside Inbound/Pick Ticket, since it
           only makes sense while the PU is still Pending Pick Up. Same
           pattern as InvoiceRegistry.js's Ground-Fresh side. */
        var kindOptionsWithReminder = kindOptions.concat([
          /* noSignatureByDefault (2026-09-25, Arturo: "esos reminders
             asi como los reminders de protection no deben llevar mi
             firma solo el nombre de sweetleaf... por default que este
             sin firma con la opcion de yo agregarla") — a plain
             status-check message signed only by the template's own
             "Thanks, SweetLeaf Produce" sign-off, not Arturo's saved
             signature block. EmailDialog still lets him pick a
             signature by hand from the dropdown if he wants one for a
             specific send; this only changes the default. */
          { value: "pendingPickupReminder", label: t.emailReminderKindPickup, subject: tpl(t.emailReminderSubjectPickup), body: tpl(t.emailReminderDefaultBody), noAttachments: true, noSignatureByDefault: true },
          /* Protection status reminder (2026-09-14, Arturo: "enviar un
             recordatorio a four seasons referente a files con
             proteccion... preguntar sobre el estatus... con numero de
             PU, numero de PO... la fecha en que ellos lo reportaron y
             la fecha y hora de llegada a su bodega reportada por
             ellos") — same noAttachments treatment as the pickup
             reminder, a plain status-check message, no documents;
             same no-signature-by-default treatment as above. */
          { value: "protectionReminder", label: t.emailProtectionReminderKind, subject: tpl(t.emailProtectionReminderSubject), body: tpl(t.emailProtectionReminderBody), noAttachments: true, noSignatureByDefault: true },
        ]);
        return (
          <EmailDialog
            lang={lang}
            title={(emailDialogDefaultKind === "pendingPickupReminder" ? t.emailReminderPickup : emailDialogDefaultKind === "protectionReminder" ? t.emailProtectionReminder : t.emailCustomerTitle) + " — " + (fresh.clientName || (cli && (cli.shortName || cli.fullName)) || "—")}
            recipients={recipients}
            docOptions={docOptions}
            kindOptions={kindOptionsWithReminder}
            defaultKind={emailDialogDefaultKind}
            defaultSubject={emailDialogDefaultKind === "pendingPickupReminder" ? tpl(t.emailReminderSubjectPickup) : emailDialogDefaultKind === "protectionReminder" ? tpl(t.emailProtectionReminderSubject) : tpl(t.emailCustomerSubjectPick)}
            defaultBody={emailDialogDefaultKind === "pendingPickupReminder" ? tpl(t.emailReminderDefaultBody) : emailDialogDefaultKind === "protectionReminder" ? tpl(t.emailProtectionReminderBody) : (tplBodyPick || tpl(t.emailCustomerDefaultBody))}
            fromEmail={getSLSettings().emailAccount || "a.gonzalez@sweetleafproduce.com"}
            emailAccounts={slEmailAccounts()}
            signatures={slEmailSignatures()}
            postProcessHtmlBody={function (html) { return appendSlDisclosure(html, getSLSettings()); }}
            buildAttachments={buildAttachments}
            buildEmbedHtml={buildEmbedHtml}
            refreshAccountsBeforeSend={refreshSlAccountsForSend}
            onClose={function () { setEmailDialogOpen(false); }}
            onSent={function () {
              /* Stamp emailSentAt on successful send so the list view
                 can render the "✉ sent" badge. Routes through the
                 parent's `onSave` (same path the rest of DetailPanel
                 uses for status flips, sale-status edits, etc.) —
                 that's what persists to Supabase + LS in lockstep.
                 Protection Reminder additionally stamps its own
                 timestamp, same pattern as protectionEmailSentAt on
                 the Ground-Fresh side. */
              var stamp = { emailSentAt: new Date().toISOString() };
              if (emailDialogDefaultKind === "protectionReminder") stamp.protectionReminderSentAt = new Date().toISOString();
              try { onSave(Object.assign({}, fresh, stamp)); }
              catch (e) { console.warn("[invoices] could not stamp emailSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {emailWarehouseOpen && (function () {
        var shipments = loadLS("gfi-shipments", []);
        var warehouses = loadLS("gfi-warehouses", []);
        var clients = loadLS("gfi-clients", []);
        var cli = clients.find(function (c) { return idMatches(c, fresh.clientId); });
        var shp = findShipmentForInvoice(fresh, shipments);
        var wh = shp ? warehouses.find(function (w) { return idMatches(w, shp.warehouseId); }) : null;
        var pu = fresh.gfiPuNumber || fresh.puNumber || "—";
        var po = fresh.clientRef || "—";
        var lot = fresh.lot || "—";
        var whName = wh ? (wh.shortName || wh.fullName || "Warehouse") : "Warehouse";
        var brand = "sl";  /* SweetLeafPortal: always SL */
        var recipients = (wh && Array.isArray(wh.contacts) ? wh.contacts : [])
          .filter(function (c) { return c && c.email; })
          .map(function (c) {
            return { name: (c.name || "") + " · " + whName, email: c.email, checked: true };
          });
        /* SweetLeaf-only templates — no Ground-Fresh CompanyProfile crossover. */
        var tplCfgInbound = SL_EMAIL_TEMPLATES.inbound;
        var tplCfgPick    = SL_EMAIL_TEMPLATES.pickTicket;
        var sup = (loadLS("gfi-suppliers", []) || []).find(function (s) { return shp && idMatches(s, shp.supplierId); });
        var tplValues = {
          pu: pu, po: po, lot: lot,
          arrival: (shp && (shp.dateArrival || shp.dateDeparture)) || "",
          shipper: (sup && sup.shortName) || (shp && shp.shipperName) || "",
          client: (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "",
          warehouse: whName,
        };
        var bodyInbound = composeEmailBody(tplCfgInbound, tplValues, { labels: { pu: "Inbound" } });
        var bodyPick    = composeEmailBody(tplCfgPick, tplValues, { labels: { pu: "PU#" } });
        var tpl = function (s) { return String(s || "").replace("{pu}", pu).replace("{po}", po).replace("{lot}", lot); };
        var subjectInbound = tpl(t.emailWarehouseDefaultSubject);
        var subjectPick    = tpl(t.emailCustomerSubjectPick);
        var docOptions = [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: true },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: false },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: true },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: false },
          { key: brand + "-driversignoff", label: "Driver Sign-Off", checked: false },
        ];
        /* ctx.company is still needed by buildInvoiceDocPdf for the
           printed-doc header — pulled from CompanyProfile so the
           printed PDF keeps its company block. The email itself
           (from address, signature, body templates) is fully
           SweetLeaf and never reads `company`. */
        var ctx = { clients: clients, shipments: shipments, warehouses: warehouses, company: loadCompanyProfile() };
        var buildAttachments = async function (kinds) {
          /* Warehouse-email Pick Ticket also goes through the same
             remarks prompt — the printed PDF the warehouse receives
             must match what the customer sees, including loading
             date and the locked-on temperature line. */
          var needsPick = kinds.some(function (k) { return /(^|[-:])pick$/.test(k); });
          var pickExtras = null;
          if (needsPick) {
            var shpForLD = findShipmentForInvoice(fresh, shipments);
            var etaLD = shpForLD ? (shpForLD.dateArrival || "") : "";
            var defLD = "";
            if (etaLD) {
              var dLD = new Date(etaLD + "T00:00:00");
              if (!isNaN(dLD.getTime())) {
                dLD.setDate(dLD.getDate() + 1);
                defLD = dLD.getFullYear() + "-" + String(dLD.getMonth() + 1).padStart(2, "0") + "-" + String(dLD.getDate()).padStart(2, "0");
              }
            }
            pickExtras = await promptPickRemarks(defLD);
          }
          var out = [];
          for (var i = 0; i < kinds.length; i++) {
            var isPick = /(^|[-:])pick$/.test(kinds[i]);
            var att = await buildInvoiceDocPdf(fresh, kinds[i], ctx, isPick ? pickExtras : null);
            out.push(att);
          }
          return out;
        };
        var buildEmbedHtml = async function () {
          var lines = (fresh.lines || []).filter(function (l) { return l && (l.productName || l.productBase); });
          if (lines.length === 0) return "";
          var esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
          var totals = { pallets: 0, cases: 0 };
          var rows = lines.map(function (l) {
            var p = parseInt(l.pallets, 10) || 0;
            var c = parseInt(l.cases, 10) || 0;
            totals.pallets += p; totals.cases += c;
            return { product: l.productBase || l.productName || "", desc: l.productDescription || "", pallets: p, cases: c, uom: l.uom || "Flats" };
          });
          var th = 'style="text-align:left;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thNum = 'style="text-align:right;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thCtr = 'style="text-align:center;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.4">';
          html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #d0d5dd;background:#ffffff">';
          html += '<thead><tr style="background:#f7f8fa"><th ' + th + '>Product</th><th ' + th + '>Description</th><th ' + thNum + '>Pallets</th><th ' + thNum + '>Fill (Cases)</th><th ' + thCtr + '>UOM</th></tr></thead><tbody>';
          rows.forEach(function (r) {
            html += '<tr style="border-bottom:1px solid #eef0f3"><td style="padding:7px 10px;font-weight:600">' + esc(r.product) + '</td><td style="padding:7px 10px;color:#444">' + esc(r.desc) + '</td><td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace">' + r.pallets + '</td><td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace;font-weight:600">' + r.cases + '</td><td style="padding:7px 10px;text-align:center">' + esc(r.uom) + '</td></tr>';
          });
          html += '<tr style="background:#f7f8fa;font-weight:700"><td colspan="2" style="padding:9px 10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.04em">Totals</td><td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.pallets + '</td><td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.cases + '</td><td></td></tr>';
          html += '</tbody></table></div>';
          return html;
        };
        return (
          <EmailDialog
            lang={lang}
            title={t.emailWarehouseTitle + " — " + whName}
            recipients={recipients}
            docOptions={docOptions}
            kindOptions={[
              { value: "inbound", label: t.emailCustomerKindInbound, subject: subjectInbound, body: bodyInbound, docKeyOn: brand + "-inbound", docKeyOff: brand + "-pick" },
              { value: "pick",    label: t.emailCustomerKindPick,    subject: subjectPick,    body: bodyPick,    docKeyOn: brand + "-pick",    docKeyOff: brand + "-inbound" },
            ]}
            defaultKind="inbound"
            defaultSubject={subjectInbound}
            defaultBody={bodyInbound}
            fromEmail={getSLSettings().emailAccount || "a.gonzalez@sweetleafproduce.com"}
            emailAccounts={slEmailAccounts()}
            signatures={slEmailSignatures()}
            postProcessHtmlBody={function (html) { return appendSlDisclosure(html, getSLSettings()); }}
            buildAttachments={buildAttachments}
            buildEmbedHtml={buildEmbedHtml}
            refreshAccountsBeforeSend={refreshSlAccountsForSend}
            onClose={function () { setEmailWarehouseOpen(false); }}
            onSent={function () {
              try { onSave(Object.assign({}, fresh, { emailWarehouseSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[invoices] could not stamp emailWarehouseSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {passingDialogOpen && (
        <EmailPassingModal
          invoice={fresh}
          lang={lang}
          onClose={function () { setPassingDialogOpen(false); }}
          onSent={function () {
            try { onSave(Object.assign({}, fresh, { passingSentAt: new Date().toISOString() })); }
            catch (e) { console.warn("[invoices] could not stamp passingSentAt:", e && e.message); }
            /* 2026-09-12 — Arturo asked for on-screen confirmation that
               sending the Passing also flips the pallet status, not just
               the document being sent. window.__gfiToast is the bridge
               to the list component's toast (this DetailPanel has no
               toast state of its own — see its useEffect setup). */
            if (window.__gfiToast) window.__gfiToast(lang === "es" ? "✓ Passing enviado — status de pallet cambia a Pendiente de Entrega" : "✓ Passing sent — pallet status changes to Pending Delivery");
          }}
        />
      )}
      {previewHtml && (
        <DocPreviewModal
          title={previewHtml.title}
          html={previewHtml.html}
          lang={lang}
          onClose={function () { setPreviewHtml(null); }}
        />
      )}
      {pendingRemarks && (
        <PickTicketRemarksModal
          brandLabel="SweetLeaf Produce"
          lang={lang}
          defaultLoadingDate={pendingRemarks.defaultLD}
          onCancel={function () {
            /* User cancelled the prompt — reject the awaiting Promise
               so the upstream EmailDialog's Send handler aborts and
               the user can re-open the dialog. */
            var rj = pendingRemarks.reject;
            setPendingRemarks(null);
            if (rj) rj(new Error("pick-remarks-cancelled"));
          }}
          onPrint={function (lines, chosenLD) {
            var rs = pendingRemarks.resolve;
            setPendingRemarks(null);
            if (rs) rs({ remarks: lines, loadingDate: chosenLD });
          }}
        />
      )}
    </DraggableModal>
  );
}

/* Inline picker for the Balance Transfers section. Mirrors the
   BillRegistry version. Holds direction/other-invoice/amount state until
   the user clicks Create. onCreate(direction, otherInvoiceId, amount)
   writes both invoices atomically via the InvoiceForm's
   createBalanceTransfer handler. */
function BalanceTransferCreatorInv({ lang, t, sameClientInvoices, suggestedAmount, onCreate }) {
  var [direction, setDirection] = useState("send");
  var [otherInvoiceId, setOtherInvoiceId] = useState("");
  var [amount, setAmount] = useState(suggestedAmount || "");
  var [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={function() { setOpen(true); setAmount(suggestedAmount || ""); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
        + {t.btCreate}
      </button>
    );
  }
  return (
    <div style={{ padding: 10, background: "var(--color-bg)", borderRadius: 8, border: "1px solid var(--color-border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 130px auto", gap: 8, alignItems: "center" }}>
        <SearchSelect value={direction} onChange={setDirection} options={[{ value: "send", label: t.btSend }, { value: "receive", label: t.btReceive }]} />
        <SearchSelect value={otherInvoiceId} onChange={setOtherInvoiceId} placeholder={t.btOtherInvoice} options={sameClientInvoices.map(function(inv) { return { value: inv.id, label: "PU# " + (inv.puNumber || inv.id) + " · " + (inv.lot ? "LOT " + inv.lot : "—") }; })} />
        <input type="number" min="0" step="0.01" value={amount} onChange={function(e) { setAmount(e.target.value); }} placeholder={t.btAmount} style={{ padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textAlign: "right" }} />
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" onClick={function() {
            if (!otherInvoiceId || !(parseFloat(amount) > 0)) return;
            onCreate(direction, otherInvoiceId, amount);
            setOpen(false); setOtherInvoiceId(""); setAmount("");
          }} style={{ padding: "5px 12px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.btCreate}</button>
          <button type="button" onClick={function() { setOpen(false); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.btCancel}</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceForm({ invoice, invoices, t, lang, onSave, onDelete, onCancel, embedded }) {
  var isNew = !invoice || !invoice.id;
  var clients = useMemo(function() { return getClients(); }, []);
  var availableBills = useMemo(function() { return loadLS("gfi-bills", []); }, []);

  var [f, setF] = useState(function() {
    if (invoice) return {
      ...invoice,
      /* Surface the inv-level commissionRate as the per-line default
         when the line doesn't carry one yet — so the form's Comm %
         column never shows blank for legacy lines. */
      lines: (invoice.lines || []).map(function(l) {
        return (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? { ...l, commissionRate: invoice.commissionRate || "" }
          : { ...l };
      }),
      fees: (invoice.fees || []).map(function(fe) { return { ...fe }; }),
      credits: (invoice.credits || []).map(function(cr) { return { ...cr }; }),
      balanceTransfers: (invoice.balanceTransfers || []).map(function(t) { return { ...t }; }),
      palletAllocations: (invoice.palletAllocations || []).map(function(a) { return { ...a }; }),
    };
    /* SweetLeaf is fixed to Four Seasons — every new invoice in this
       module seeds with the FS client + a freshly-reserved INV-XXXXX
       consecutive from sweetleaf_settings. puNumber mirrors
       invoiceNumber so legacy filters/lookups keyed on puNumber keep
       working without a separate code path. */
    var fsSeed = findFourSeasonsClient();
    /* Suggest the next consecutive without reserving it. The user
       can override the editable INV# field; the counter is only
       bumped on save (and only when the saved value matches the
       suggestion). */
    var slInv = peekSLInvoiceNumber();
    return {
      id: "",
      invoiceNumber: slInv,
      puNumber: slInv,
      gfiInvoiceId: "", gfiPuNumber: "", gfiLot: "",
      date: new Date().toISOString().substring(0, 10),
      dueDate: "", clientId: fsSeed ? fsSeed.id : "", clientInitials: fsSeed ? (fsSeed.initials || "") : "", clientName: fsSeed ? (fsSeed.shortName || fsSeed.fullName || FOUR_SEASONS_FALLBACK_NAME) : FOUR_SEASONS_FALLBACK_NAME, clientRef: "", linkedPoLineId: "",
      lot: "", shipmentId: "", billId: "", shipperName: "", thermograph: "",
      status: "draft", lessAdvance: "", otherFees: "", lessCredits: "",
      remarks: "", lines: [], saleStatus: "",
      fees: [],
      credits: [],
      balanceTransfers: [],
      advanceFinancingFee: "",
      palletAllocations: [],
    };
  });

  /* Filtered list of shipments selectable as line sources. Only those
     dated on/before the invoice date, sorted desc. SearchSelect with
     limit=10 surfaces the most recent; typed query widens the search. */
  var availableLots = useMemo(function() {
    var all = getAvailableLots();
    var invDate = f.date || new Date().toISOString().substring(0, 10);
    return all
      .filter(function(s) {
        var sd = s.dateArrival || s.dateDeparture || "";
        return !sd || sd <= invDate;
      })
      .sort(function(a, b) {
        var ad = a.dateArrival || a.dateDeparture || "";
        var bd = b.dateArrival || b.dateDeparture || "";
        return bd.localeCompare(ad);
      });
  }, [f.date]);
  var availableLotsTop10 = useMemo(function() { return availableLots.slice(0, 10); }, [availableLots]);
  var [lotPickerValue, setLotPickerValue] = useState("");
  var [otherLotPickerValue, setOtherLotPickerValue] = useState("");

  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [dirty, setDirty] = useState(false);
  var [showExitConfirm, setShowExitConfirm] = useState(false);
  var [trVer, setTrVer] = useState(0);
  /* Drag-over flag for the Documents drop zone. Pure UI hint; the
     actual drop handler reads the latest form state via setF's prev
     callback so the slot assignment is consistent even if the user
     drops files faster than React commits. */
  var [docDragOver, setDocDragOver] = useState(false);

  var FEE_TYPES_KEY = "gfi-fee-types";
  var DEFAULT_FEE_TYPES = ["Custom Broker Fee", "Warehouse Fees", "QC Inspection", "Temp Recorders", "Advance Financing Fee", "Wire Transfer Fee", "Shipping", "Repack", "Fumigation", "USDA Inspection", "Cold Treatment", "Palletizing", "Labels/Stickers"];
  var EMPTY_FEE = { feeType: "", feeCustom: "", qty: 1, rate: "", amount: 0 };
  var getFeeTypes = function() { try { var d = localStorage.getItem(FEE_TYPES_KEY); return d ? JSON.parse(d) : DEFAULT_FEE_TYPES; } catch { return DEFAULT_FEE_TYPES; } };
  var [feeTypes, setFeeTypes] = useState(getFeeTypes);
  var saveFeeTypes = function(types) { try { localStorage.setItem(FEE_TYPES_KEY, JSON.stringify(types)); } catch {} };

  var set = function(k, v) { setF(function(prev) { return { ...prev, [k]: v }; }); setDirty(true); };

  var updateFee = function(idx, field, value) {
    setF(function(prev) {
      var newFees = prev.fees.map(function(fe, i) {
        if (i !== idx) return fe;
        var updated = { ...fe, [field]: value };
        if (field === "qty" || field === "rate") updated.amount = ((parseFloat(updated.qty) || 1) * (parseFloat(updated.rate) || 0)).toFixed(2);
        if (field === "amount") updated.amount = value;
        return updated;
      });
      return { ...prev, fees: newFees };
    });
    setDirty(true);
  };
  var addFee = function() { setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), { ...EMPTY_FEE }] }; }); setDirty(true); };
  var removeFee = function(idx) { setF(function(prev) { return { ...prev, fees: (prev.fees || []).filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };

  /* Credits — reduce the invoice total. Each credit has:
       - type: "free" → user-defined concept, OR
                "fromInvoice" → reference (visual only) to another
                invoice from the same client. The link does NOT modify
                the referenced invoice; it just tells the reader where
                this credit came from.
       - concept: free-text label (used when type==="free")
       - refInvoiceId / refPuNumber: identify the source invoice (only
         refPuNumber is shown to the user / on print; id is for record). */
  var updateCredit = function(idx, field, value) {
    setF(function(prev) {
      var newCredits = (prev.credits || []).map(function(cr, i) {
        if (i !== idx) return cr;
        return { ...cr, [field]: value };
      });
      return { ...prev, credits: newCredits };
    });
    setDirty(true);
  };
  var addCredit = function() { setF(function(prev) { return { ...prev, credits: [...(prev.credits || []), { type: "free", concept: "", refInvoiceId: "", refPuNumber: "", amount: 0 }] }; }); setDirty(true); };
  var removeCredit = function(idx) { setF(function(prev) { return { ...prev, credits: (prev.credits || []).filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };

  /* Other invoices for THIS client — used by the "From Invoice" credit
     SearchSelect. Excludes the current invoice itself. */
  var clientInvoices = useMemo(function() {
    if (!f.clientId) return [];
    return (invoices || []).filter(function(inv) { return String(inv.clientId) === String(f.clientId) && String(inv.id) !== String(f.id); });
  }, [invoices, f.clientId, f.id]);

  /* Available POs from Fixed + Consignment Pricing. Filter rules:
       1. Client must match (id, fallback to name for old records)
       2. linkedInvoiceId must be empty
       3. saleStatus NOT in {invoiced, sent, paid}
     No date filtering — the user picks the right PO manually from
     the list. Exception: this invoice's own linked PO always
     passes (so edit mode keeps the chip + banner). Console logs
     each candidate so data drift is diagnosable from the browser
     console. Sort is chronological by PO departure date so the
     newest PO appears at the bottom. */
  var availablePOs = useMemo(function() {
    if (!f.clientId) return [];
    var clientObj = (clients || []).find(function (c) { return idMatches(c, f.clientId); });
    var clientName = clientObj ? (clientObj.shortName || clientObj.fullName || clientObj.name || "") : "";
    var fixedRows = getFixedPOs().map(function (p) { return { ...p, _src: "F" }; });
    var consRows  = getConsignmentPOs().map(function (p) { return { ...p, _src: "C" }; });
    var all = fixedRows.concat(consRows);
    var currentInvoiceId = String(f.id || "");
    var INVOICED_STATUSES = ["invoiced", "sent", "paid"];
    /* eslint-disable no-console */
    console.log("[Invoice PO Picker] Evaluating " + all.length + " POs for invoice id=" + (currentInvoiceId || "(new)") + ", client=" + (clientName || "?"));
    var result = all.filter(function (p) {
      var compositeId = p._src + ":" + p.id;
      var poLink = (p.linkedInvoiceId == null || p.linkedInvoiceId === "") ? "" : String(p.linkedInvoiceId);
      var poStatus = String(p.saleStatus || "").toLowerCase();
      /* Exception — current invoice's own linked PO bypasses every
         other filter so the edit flow doesn't drop its chip + banner. */
      var isOwnLink = currentInvoiceId && poLink && poLink === currentInvoiceId;
      if (isOwnLink) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · linkedInvoiceId=" + poLink + " · KEEP (own link)");
        return true;
      }
      /* Filter 1 — client mismatch. */
      var idMatch   = p.clientId && String(p.clientId) === String(f.clientId);
      var nameMatch = p.clientName && clientName && p.clientName === clientName;
      if (!(idMatch || nameMatch)) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · client=" + (p.clientName || "?") + " · DROP (client mismatch)");
        return false;
      }
      /* Filter 2 — already linked to ANY invoice. */
      if (poLink) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · linkedInvoiceId=" + poLink + " · DROP (already invoiced)");
        return false;
      }
      /* Filter 3 — sale status indicates the PO is already gone. */
      if (INVOICED_STATUSES.indexOf(poStatus) >= 0) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · saleStatus=" + poStatus + " · DROP (status indicates invoiced)");
        return false;
      }
      console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · saleStatus='" + poStatus + "' · KEEP (available)");
      return true;
    });
    console.log("[Invoice PO Picker] Result: " + result.length + " of " + all.length + " POs available");
    /* eslint-enable no-console */
    /* Sort by Loading/Delivery Date ascending — oldest at the top,
       newest at the bottom. Falls back to Departure Date when
       deliveryDate isn't set, and POs missing both dates sink to
       the bottom so they don't crowd the relevant options. */
    return result.sort(function (a, b) {
      var da = a.deliveryDate || a.date || "";
      var db = b.deliveryDate || b.date || "";
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : (da > db ? 1 : 0);
    });
  }, [f.clientId, f.id, invoices, clients]);

  /* Resolve the currently-linked PO directly from storage so the
     informational banner works even if the user changed clients
     after linking (which would filter the PO out of availablePOs)
     or if the PO is no longer in availablePOs for any other reason. */
  var linkedPo = useMemo(function () {
    if (!f.linkedPoLineId) return null;
    var key = poStorageKeyFor(f.linkedPoLineId);
    var poId = poIdFor(f.linkedPoLineId);
    if (!key || !poId) return null;
    var src = String(f.linkedPoLineId).split(":")[0];
    var found = loadLS(key, []).find(function (p) { return String(p.id) === String(poId); });
    return found ? { ...found, _src: src } : null;
  }, [f.linkedPoLineId]);
  /* The reference-price banner only nags the user UNTIL they've
     started entering prices in the line items. Once any line has a
     non-zero salePrice, the banner hides — the user has internalized
     the reference and made their pricing call. */
  var hasPricedLines = useMemo(function () {
    return (f.lines || []).some(function (l) { return parseFloat(l.salePrice) > 0; });
  }, [f.lines]);

  /* Drag-reorder hooks for each line list. Each callback writes the
     reordered array back to f and marks the form dirty. */
  var dndLines = useDragReorder(f.lines, function(next) { setF(function(p) { return { ...p, lines: next }; }); setDirty(true); });
  var dndFees = useDragReorder(f.fees, function(next) { setF(function(p) { return { ...p, fees: next }; }); setDirty(true); });
  var dndCredits = useDragReorder(f.credits, function(next) { setF(function(p) { return { ...p, credits: next }; }); setDirty(true); });
  var dndTransfers = useDragReorder(f.balanceTransfers, function(next) { setF(function(p) { return { ...p, balanceTransfers: next }; }); setDirty(true); });

  /* ─── Balance Transfers (cross-invoice, atomic) ───
     Mirrors BillRegistry's pattern. Each entry stamped on BOTH invoices
     with cross-references. Filtered by SAME CLIENT (vs same shipper for
     bills). Persisted to Supabase immediately so the OTHER invoice is
     updated even if the user cancels editing this one. */
  function nextTransferId() { return "BT-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6); }
  /* direction: "send" → this invoice gets credit, other gets debit
                "receive" → this invoice gets debit, other gets credit
     2026-10-28, Arturo: "a la factura 16255 de sweetleaf transferi
     saldo negativo hacia otras facturas pero al ir a las facturas
     hacia donde lo transferi, ese saldo no se desconto" — root cause:
     the OTHER invoice was only ever persisted to
     localStorage[gfi-sl-invoices] (saveLS), never to Supabase, which
     is this app's real source of truth — same stale-cache bug class
     already fixed repeatedly elsewhere in this file (see
     getFreshSlSettings). Opening that other invoice fresh (another
     device, a reload, or simply a session whose local cache of it
     predates the transfer) never saw it. Now awaits a real
     saveToSupabase of the other invoice BEFORE stamping this one, so
     a failed write never leaves a one-sided transfer on either side. */
  var createBalanceTransfer = async function(direction, otherInvoiceId, amount) {
    var amt = parseFloat(amount) || 0;
    if (!otherInvoiceId || amt <= 0) return;
    var other = (invoices || []).find(function(inv) { return String(inv.id) === String(otherInvoiceId); });
    if (!other) return;
    var tid = nextTransferId();
    var today = new Date().toISOString().substring(0, 10);
    var thisType = direction === "send" ? "credit" : "debit";
    var otherType = direction === "send" ? "debit" : "credit";
    /* 2026-10-28, Arturo: "al entrar en modo edicion debe informarme
       del transfer ya sea credito o debito y pedirme si lo acepto" —
       the entry THIS invoice creates is implicitly already accepted
       (the user making it right here is the one deciding it); the
       mirror entry landing on the OTHER invoice is acknowledged:false
       until whoever opens that invoice in edit mode sees the banner
       below and accepts/rejects it. */
    var thisEntry = { id: tid, type: thisType, otherInvoiceId: other.id, otherPuNumber: other.puNumber || "", otherClientName: other.clientName || "", amount: amt, date: today, acknowledged: true };
    var otherEntry = { id: tid, type: otherType, otherInvoiceId: f.id || "(this invoice)", otherPuNumber: f.puNumber || "", otherClientName: f.clientName || "", amount: amt, date: today, acknowledged: false };
    /* Persist the OTHER invoice immediately — to Supabase, the real
       source of truth, not just the localStorage mirror. */
    try {
      var updatedOther = { ...other, balanceTransfers: [...(other.balanceTransfers || []), otherEntry] };
      await saveToSupabase("sweetleaf_invoices", updatedOther);
      try {
        var current = loadLS(STORAGE_KEY, []);
        var nextStore = current.map(function(inv) { return String(inv.id) === String(other.id) ? updatedOther : inv; });
        saveLS(STORAGE_KEY, nextStore);
      } catch (eCache) { /* legacy mirror only, not fatal */ }
    } catch (e) {
      console.error("[InvoiceBalanceTransfer] failed to persist other invoice", e);
      window.alert(lang === "es" ? "No se pudo guardar la transferencia en la otra factura. Intenta de nuevo." : "Could not save the transfer on the other invoice. Please try again.");
      return;
    }
    /* Update this invoice's in-memory state (will persist when user saves). */
    setF(function(prev) { return { ...prev, balanceTransfers: [...(prev.balanceTransfers || []), thisEntry] }; });
    setDirty(true);
  };
  /* 2026-10-28, Arturo: "aun cuando lo acepto y guardo la factura, al
     volver a entrar en modo edicion lo vuelve a mostrar... una vez
     aplicado debe desaparecer" — root cause: acknowledging only
     updated the in-memory `f` and relied on the user's NEXT click on
     the form's main Save button to actually persist it. If that click
     never came (easy to assume Accept alone was "done"), Supabase kept
     the stale acknowledged:false forever and the banner came right
     back on every re-open. Persists THIS invoice's own
     balanceTransfers change to Supabase immediately, the same way
     createBalanceTransfer already does for the OTHER invoice — off the
     ORIGINAL `invoice` prop (not `f`), so clicking Accept/Reject can
     never smuggle in an unrelated half-finished edit elsewhere in the
     form. */
  var persistThisInvoiceTransfers = async function(newList) {
    var base = (invoices || []).find(function(inv) { return String(inv.id) === String(invoice.id); }) || invoice;
    var updatedThis = { ...base, balanceTransfers: newList };
    await saveToSupabase("sweetleaf_invoices", updatedThis);
    try {
      var current = loadLS(STORAGE_KEY, []);
      var nextStore = current.map(function(inv) { return String(inv.id) === String(invoice.id) ? updatedThis : inv; });
      saveLS(STORAGE_KEY, nextStore);
    } catch (eCache) { /* legacy mirror only, not fatal */ }
  };
  var removeBalanceTransfer = async function(idx) {
    var entry = (f.balanceTransfers || [])[idx];
    if (!entry) return;
    if (entry.otherInvoiceId && entry.id) {
      try {
        var other = (invoices || []).find(function(inv) { return String(inv.id) === String(entry.otherInvoiceId); });
        var updatedOther = other
          ? { ...other, balanceTransfers: (other.balanceTransfers || []).filter(function(t) { return t.id !== entry.id; }) }
          : null;
        if (updatedOther) await saveToSupabase("sweetleaf_invoices", updatedOther);
        var current = loadLS(STORAGE_KEY, []);
        var nextStore = current.map(function(inv) {
          if (String(inv.id) !== String(entry.otherInvoiceId)) return inv;
          return updatedOther || { ...inv, balanceTransfers: (inv.balanceTransfers || []).filter(function(t) { return t.id !== entry.id; }) };
        });
        saveLS(STORAGE_KEY, nextStore);
      } catch (e) {
        console.error("[InvoiceBalanceTransfer] failed to update other invoice on remove", e);
        window.alert(lang === "es" ? "No se pudo quitar la transferencia en la otra factura. Intenta de nuevo." : "Could not remove the transfer on the other invoice. Please try again.");
        return;
      }
    }
    var newList = (f.balanceTransfers || []).filter(function(_, i) { return i !== idx; });
    try {
      await persistThisInvoiceTransfers(newList);
    } catch (e) {
      console.error("[InvoiceBalanceTransfer] failed to persist this invoice on remove", e);
      window.alert(lang === "es" ? "No se pudo quitar la transferencia en esta factura. Intenta de nuevo." : "Could not remove the transfer on this invoice. Please try again.");
      return;
    }
    setF(function(prev) { return { ...prev, balanceTransfers: newList }; });
  };
  /* 2026-10-28, Arturo (same report as above): Accept must be final
     right away too, not just locally — persisted immediately, same
     reasoning as removeBalanceTransfer above. */
  var acknowledgeBalanceTransfer = async function(idx) {
    var newList = (f.balanceTransfers || []).map(function(t, i) { return i === idx ? { ...t, acknowledged: true } : t; });
    try {
      await persistThisInvoiceTransfers(newList);
    } catch (e) {
      console.error("[InvoiceBalanceTransfer] failed to persist acknowledgement", e);
      window.alert(lang === "es" ? "No se pudo guardar la aceptación. Intenta de nuevo." : "Could not save the acceptance. Please try again.");
      return;
    }
    setF(function(prev) { return { ...prev, balanceTransfers: newList }; });
  };
  var addCustomFeeType = function(name) { if (!name || feeTypes.includes(name)) return; var updated = [...feeTypes, name]; setFeeTypes(updated); saveFeeTypes(updated); };

  var handleClientChange = function(clientId) {
    var cl = clients.find(function(c) { return String(c.id) === String(clientId); });
    var initials = cl ? (cl.initials || (cl.shortName || "").substring(0, 2).toUpperCase()) : "";
    var clientName = cl ? (cl.shortName || cl.fullName || cl.name || cl.companyName || "") : "";
    /* Auto-calculate due date from payment term */
    var dueDateCalc = "";
    if (cl && cl.paymentTerm) {
      var pt = String(cl.paymentTerm); var nums = pt.match(/\d+/);
      var days = nums ? parseInt(nums[0], 10) : NaN;
      if (!isNaN(days) && days > 0) {
        var baseDate = new Date((f.date || new Date().toISOString().substring(0, 10)) + "T00:00:00");
        baseDate.setDate(baseDate.getDate() + days);
        dueDateCalc = baseDate.toISOString().substring(0, 10);
      }
    }
    /* Pull client default commission rate. Only override the invoice's
       current rate when one isn't already set on this invoice (so the
       user's manual edit survives a client re-pick). */
    var clientComm = cl && cl.commissionRate != null ? parseFloat(cl.commissionRate) || 0 : 0;
    setF(function(prev) {
      var nextRate = (prev.commissionRate === undefined || prev.commissionRate === null || prev.commissionRate === "") ? clientComm : prev.commissionRate;
      /* When this is a brand-new invoice with no manual commission yet,
         use the client's default; otherwise keep what the user typed. */
      var rateToUse = (prev.commissionRate === undefined || prev.commissionRate === null || prev.commissionRate === "") ? clientComm : prev.commissionRate;
      return { ...prev, clientId: clientId, clientInitials: initials, clientName: clientName, dueDate: dueDateCalc || prev.dueDate, commissionRate: rateToUse };
    });
    setDirty(true);
  };

  var addFromShipment = function(shipmentId) {
    var shipment = availableLots.find(function(s) { return String(s.id) === String(shipmentId); });
    if (!shipment) return;
    var supName = shipment.shipperName || "";
    if (!supName && shipment.supplierId) {
      var sup = getSuppliers().find(function(s) { return idMatches(s, shipment.supplierId); });
      if (sup) supName = sup.shortName || sup.fullName || sup.name || "";
    }
    var prods = getProducts();
    var newLines = (shipment.lines || []).map(function(l) {
      var desc = l.productDescription || "";
      if (!desc && l.productId) {
        var prod = prods.find(function(p) { return String(p.id) === String(l.productId); });
        if (prod) {
          var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
          desc = (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
        }
      }
      return { ...l, productDescription: desc, salePrice: l.price || "", lot: shipment.lot, shipmentId: shipment.id, shipperName: supName };
    });
    var lotStr = shipment.lot != null ? String(shipment.lot) : "";
    var alreadyHasLot = lotStr && (f.palletAllocations || []).some(function(a) { return String(a.lot) === lotStr; });
    var applyAdd = function() {
      var newAllocRows = populateAllocationsForLot(shipment);
      setF(function(prev) {
        var shipperName = prev.shipperName || supName;
        var lot = prev.lot || shipment.lot;
        var keptAllocs = (prev.palletAllocations || []).filter(function(a) { return String(a.lot) !== lotStr; });
        return { ...prev, lot: lot, shipperName: shipperName, lines: [...(prev.lines || []), ...newLines], palletAllocations: [...keptAllocs, ...newAllocRows] };
      });
      setDirty(true);
    };
    /* Re-picking a lot that already has captured allocations regenerates
       that lot's rows from scratch (100% cases again) — warn first since
       it discards any manual edits the user made to those rows. A brand
       new lot just appends, nothing to lose. */
    if (alreadyHasLot) {
      if (window.confirm(t.confirmRelot)) applyAdd();
    } else {
      applyAdd();
    }
  };
  /* ─── GF → SL sub-allocation ───
     A SweetLeaf invoice with a gfiPuNumber represents the SAME physical
     pallets already allocated on the GF invoice (SL sells to Four
     Seasons under the SweetLeaf brand for merchandise GF already
     invoiced). SL doesn't compete with GF for the shipment's manifest —
     it sub-divides whatever the GF invoice itself claimed. sourceGfInvoiceFor
     resolves that parent; every pallet-allocation helper below branches
     on it so GF-linked SL invoices pull from the GF invoice's own
     palletAllocations instead of the shipment manifest, and only compete
     against OTHER SL invoices sharing the same gfiPuNumber — never
     against the GF parent itself or unrelated invoices. gfiPuNumber is
     set once when GF copies an invoice into SL (InvoiceRegistry.js
     slCopyModal) and is read-only in this form, so this is re-evaluated
     from whatever f.gfiPuNumber already holds — no live "change GF PU#"
     UI exists yet to regenerate against (that's a separate, not-yet-built
     feature, tracked outside this fix). */
  var sourceGfInvoiceFor = function(form) {
    if (!form || !form.gfiPuNumber) return null;
    var gfInvoices = loadLS("gfi-invoices", []);
    return gfInvoices.find(function(inv) { return String(inv.puNumber) === String(form.gfiPuNumber); }) || null;
  };
  var sourceGfInvoice = useMemo(function() { return sourceGfInvoiceFor(f); }, [f.gfiPuNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  /* Pool ceiling for one physical pallet: the GF invoice's own claim on
     it when GF-linked, otherwise the shipment manifest's real
     casesPallet (unchanged behavior for GF invoices and unlinked SL). */
  var poolTotalFor = function(lot, palletNum) {
    if (sourceGfInvoice) {
      var sum = 0;
      (sourceGfInvoice.palletAllocations || []).forEach(function(a) {
        if (a && String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum)) sum += parseInt(a.cases, 10) || 0;
      });
      return sum;
    }
    var meta = palletMetaFor(lot, palletNum);
    return meta ? (parseInt(meta.casesPallet, 10) || 0) : 0;
  };
  /* Full-cases pallet rows to seed palletAllocations when a lot is
     added (or regenerated on re-pick). GF-linked: deep copy from the
     GF invoice's own allocations for that lot (NOT the shipment
     manifest — the GF invoice may have allocated less than 100%).
     Unlinked: original behavior, full shipment manifest. */
  var populateAllocationsForLot = function(shipment) {
    var lotStr = shipment.lot != null ? String(shipment.lot) : "";
    if (sourceGfInvoice) {
      return (sourceGfInvoice.palletAllocations || [])
        .filter(function(a) { return a && String(a.lot) === lotStr && a.palletNum && (parseInt(a.cases, 10) || 0) > 0; })
        .map(function(a) { return { lot: lotStr, palletNum: a.palletNum, cases: parseInt(a.cases, 10) || 0 }; });
    }
    return (shipment.palletManifest || [])
      .filter(function(p) { return p && p.palletNum && (parseInt(p.casesPallet, 10) || 0) > 0; })
      .map(function(p) { return { lot: lotStr, palletNum: p.palletNum, cases: parseInt(p.casesPallet, 10) || 0 }; });
  };
  var removeProductLine = function(idx) {
    setF(function(prev) { return { ...prev, lines: prev.lines.filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };

  /* ─── Pallet Allocations (per-invoice, per-physical-pallet case tracking) ───
     Reads the shipment's pallet manifest live (not duplicated into the
     allocation row) so Product/Variety/Grower/Weight always reflect the
     current manifest. Availability is computed against BOTH GF invoices
     (gfi-invoices) and SL invoices (gfi-sl-invoices) since both draw
     cases from the same physical pallet pool, excluding this invoice's
     own rows so re-opening it doesn't count itself as "already taken". */
  var shipmentByLot = function(lot) {
    return getShipments().find(function(s) { return String(s.lot) === String(lot); });
  };
  var palletMetaFor = function(lot, palletNum) {
    var shp = shipmentByLot(lot);
    if (!shp) return null;
    return (shp.palletManifest || []).find(function(p) { return String(p.palletNum) === String(palletNum); }) || null;
  };
  var otherInvoicesForAlloc = useMemo(function() {
    var gf = loadLS("gfi-invoices", []);
    var sl = loadLS("gfi-sl-invoices", []);
    /* String() both sides — f.id (from the Supabase-shaped `invoice`
       prop, legacy_id is a text column) and the id on records read
       fresh from loadLS("gfi-invoices") can end up as different JS
       types (number vs string) even for the SAME invoice, since they
       come from two different load paths. A raw !== then never
       excludes the invoice being edited from its own "used elsewhere"
       pool, so re-saving an unchanged allocation reads as 100%
       over-allocated (available = total - itself = 0). */
    return gf.concat(sl).filter(function(i) { return String(i.id) !== String(f.id); });
  }, [f.id]);
  /* GF-linked: only OTHER SL invoices sharing the same gfiPuNumber
     compete for the pool — never the GF parent (its own claim IS the
     pool ceiling via poolTotalFor, not a competing consumer of it) nor
     unrelated GF/SL invoices. Unlinked: original behavior, the whole
     GF+SL pool. */
  var casesAllocatedElsewhere = function(lot, palletNum) {
    var sum = 0;
    if (sourceGfInvoice) {
      var slSiblings = loadLS("gfi-sl-invoices", []).filter(function(inv) {
        return String(inv.id) !== String(f.id) && String(inv.gfiPuNumber || "") === String(f.gfiPuNumber || "");
      });
      slSiblings.forEach(function(inv) {
        (inv.palletAllocations || []).forEach(function(a) {
          if (a && String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum)) sum += parseInt(a.cases, 10) || 0;
        });
      });
      return sum;
    }
    otherInvoicesForAlloc.forEach(function(inv) {
      (inv.palletAllocations || []).forEach(function(a) {
        if (String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum)) sum += parseInt(a.cases, 10) || 0;
      });
    });
    return sum;
  };
  var updateAllocCases = function(idx, value) {
    setF(function(prev) { return { ...prev, palletAllocations: (prev.palletAllocations || []).map(function(a, i) { return i === idx ? { ...a, cases: value } : a; }) }; });
    setDirty(true);
  };
  var removeAlloc = function(idx) {
    setF(function(prev) { return { ...prev, palletAllocations: (prev.palletAllocations || []).filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };
  /* Picker for "+ Add pallet from another lot" — restricted to lots
     already pulled in via "Add from shipment" (f.lines), excludes
     pallets already represented as a row here, and excludes pallets
     with zero cases left after subtracting other invoices' claims. */
  var addedLots = useMemo(function() {
    return [...new Set((f.lines || []).map(function(l) { return l.lot; }).filter(Boolean).map(String))];
  }, [f.lines]);
  var otherLotPalletOptions = useMemo(function() {
    var existing = new Set((f.palletAllocations || []).map(function(a) { return String(a.lot) + "::" + String(a.palletNum); }));
    var opts = [];
    /* GF-linked: pallets come from the GF invoice's own allocations
       (never the shipment manifest) — still restricted to lots already
       added to this SL invoice via "Add from shipment" (addedLots), so
       the pallet table never shows a lot with no product-line
       representation. */
    if (sourceGfInvoice) {
      (sourceGfInvoice.palletAllocations || []).forEach(function(a) {
        if (!a || !a.lot || !a.palletNum) return;
        var lot = String(a.lot);
        if (addedLots.indexOf(lot) < 0) return;
        var key = lot + "::" + String(a.palletNum);
        if (existing.has(key)) return;
        var available = poolTotalFor(lot, a.palletNum) - casesAllocatedElsewhere(lot, a.palletNum);
        if (available <= 0) return;
        var meta = palletMetaFor(lot, a.palletNum);
        opts.push({ value: key, label: "LOT " + lot + " · Plt " + a.palletNum + " · " + (meta && meta.productBase || "") + " · " + available + " " + t.allocCases, available: available, lot: lot, palletNum: a.palletNum });
      });
      return opts;
    }
    addedLots.forEach(function(lot) {
      var shp = shipmentByLot(lot);
      if (!shp) return;
      (shp.palletManifest || []).forEach(function(p) {
        if (!p || !p.palletNum) return;
        var key = String(lot) + "::" + String(p.palletNum);
        if (existing.has(key)) return;
        var available = poolTotalFor(lot, p.palletNum) - casesAllocatedElsewhere(lot, p.palletNum);
        if (available <= 0) return;
        opts.push({ value: key, label: "LOT " + lot + " · Plt " + p.palletNum + " · " + (p.productBase || "") + " · " + available + " " + t.allocCases, available: available, lot: lot, palletNum: p.palletNum });
      });
    });
    return opts;
  }, [addedLots, f.palletAllocations, otherInvoicesForAlloc, sourceGfInvoice]); // eslint-disable-line react-hooks/exhaustive-deps
  var addFromOtherLotPallet = function(key) {
    var opt = otherLotPalletOptions.find(function(o) { return o.value === key; });
    if (!opt) return;
    setF(function(prev) { return { ...prev, palletAllocations: [...(prev.palletAllocations || []), { lot: opt.lot, palletNum: opt.palletNum, cases: opt.available }] }; });
    setDirty(true);
  };
  /* 2026-10-30, Arturo: "los pallets en invoice de ground fresh se
     pueden popular del embarque todos a la vez, en sweetleaf tengo
     que popular uno por uno... todos los que envio desde la factura
     de ground-fresh a sweetleaf deben poder popularse juntos con un
     solo click". populateFromLot (above) already does this, but only
     while palletAllocations is still completely empty — once a
     single row exists (e.g. the picker below was used once, or a
     prior save already has some rows), that bulk button disappears
     for good and the picker-one-at-a-time flow was the only way to
     pull in the REST. This adds everything currently sitting in
     otherLotPalletOptions (every pallet from the linked GF invoice —
     or, for an unlinked SL invoice, the shipment manifest — not yet
     represented as a row here) in one click, available any time
     there's something left to add, not just on an empty invoice. */
  var addAllOtherLotPallets = function() {
    if (otherLotPalletOptions.length === 0) return;
    var newRows = otherLotPalletOptions.map(function(o) { return { lot: o.lot, palletNum: o.palletNum, cases: o.available }; });
    setF(function(prev) { return { ...prev, palletAllocations: [...(prev.palletAllocations || []), ...newRows] }; });
    setDirty(true);
  };
  /* FASE 7 — opt-in migration for historical invoices with no
     palletAllocations. Only surfaces when there's actually something
     to populate from: allocations empty, a header lot resolved, and
     that lot's shipment has a non-empty manifest. Never runs
     automatically — the button + confirm are the only trigger. */
  var populateFromLotShipment = useMemo(function() {
    if ((f.palletAllocations || []).length > 0) return null;
    if (!f.lot) return null;
    var shp = findShipmentForInvoice(f, getShipments());
    if (!shp) return null;
    /* GF-linked: the source is the GF invoice's own allocations for
       this lot, not the shipment manifest — check that instead. */
    if (sourceGfInvoice) {
      var hasAlloc = (sourceGfInvoice.palletAllocations || []).some(function(a) { return a && String(a.lot) === String(f.lot) && (parseInt(a.cases, 10) || 0) > 0; });
      return hasAlloc ? shp : null;
    }
    if (!Array.isArray(shp.palletManifest) || shp.palletManifest.length === 0) return null;
    return shp;
  }, [f.palletAllocations, f.lot, sourceGfInvoice]); // eslint-disable-line react-hooks/exhaustive-deps
  var populateFromLot = function() {
    if (!populateFromLotShipment) return;
    var msg = lang === "es"
      ? "Esto poblará las asignaciones de pallets desde el manifiesto del embarque. ¿Continuar?"
      : "This will populate pallet allocations from the shipment's pallet manifest. Continue?";
    if (!window.confirm(msg)) return;
    var rows = populateAllocationsForLot(populateFromLotShipment);
    setF(function(prev) { return { ...prev, palletAllocations: rows }; });
    setDirty(true);
  };

  /* Editable Qty (cases) per line — was read-only before. The user
     needs to split a single bill line into two invoice lines with
     different cases + sale status (e.g. 240 Good Delivery + 240
     Protection on the same product). Empty input is allowed and
     stored as "" so calcInvoiceTotal treats it as 0. */
  var updateLineCases = function(idx, cases) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, cases: cases } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };
  /* Editable Pallets per line — symmetric with Cases. Lets the user
     reapportion both quantities when splitting a line (e.g. 4 pallets
     / 480 cases → 2/240 + 2/240). The invoice totals reduce over
     .lines so they recompute automatically. */
  var updateLinePallets = function(idx, pallets) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, pallets: pallets } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  /* Split a product line in halves — mirrors BillRegistry.splitLine.
     Cases AND pallets are halved (round-down on first half). Both
     halves keep the full product metadata so descriptions and certs
     survive. User then tweaks either half. */
  var splitProductLine = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      if (!orig) return prev;
      var total = parseInt(orig.cases, 10) || 0;
      var halfA = total >= 2 ? Math.floor(total / 2) : (total > 0 ? 1 : 0);
      var halfB = total - halfA;
      var totalP = parseInt(orig.pallets, 10) || 0;
      var halfPA = totalP >= 2 ? Math.floor(totalP / 2) : (totalP > 0 ? 1 : 0);
      var halfPB = totalP - halfPA;
      newLines[idx] = { ...orig, cases: String(halfA), pallets: totalP > 0 ? String(halfPA) : orig.pallets };
      newLines.splice(idx + 1, 0, { ...orig, cases: String(halfB), pallets: totalP > 0 ? String(halfPB) : orig.pallets });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  /* Append a clone of an existing product line directly below it.
     Preserves all product metadata (productBase, productName,
     productDescription, category, lot, shipmentId, productId, etc.)
     so the same SKU is referenced, but clears the per-instance
     fields (cases + pallets + saleStatus) so the user fills them in.
     Price is kept since the spread is usually the same. */
  var addProductLineCopy = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      if (!orig) return prev;
      newLines.splice(idx + 1, 0, { ...orig, cases: "", pallets: "", saleStatus: "" });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  var updateLinePrice = function(idx, price) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, salePrice: price } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  /* Per-line SweetLeaf commission — dollars PER CASE, not a percent
     of the line value. Empty input is allowed (stored as ""), in
     which case the line contributes $0 to the commission total. */
  var updateLineCommPerCase = function(idx, val) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, slCommissionPerCase: val } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  var totalAmount = useMemo(function() {
    var total = 0;
    (f.lines || []).forEach(function(l) {
      if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
        total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
      }
    });
    return total;
  }, [f.lines]);
  var totalFees = useMemo(function() { var t = 0; (f.fees || []).forEach(function(fe) { t += Math.abs(parseFloat(fe.amount) || 0); }); return t; }, [f.fees]);
  var totalCredits = useMemo(function() { var t = 0; (f.credits || []).forEach(function(cr) { t += Math.abs(parseFloat(cr.amount) || 0); }); return t; }, [f.credits]);
  /* Signed sum of balance transfers — type "credit" adds, "debit" subtracts.
     See calcInvoiceTotal for the same formula in the persisted-record path. */
  var totalTransfers = useMemo(function() {
    var t = 0;
    (f.balanceTransfers || []).forEach(function(tr) {
      var amt = parseFloat(tr.amount) || 0;
      if (tr.type === "credit") t += amt;
      else if (tr.type === "debit") t -= amt;
    });
    return t;
  }, [f.balanceTransfers]);
  /* SweetLeaf commission is per-CASE, not a percentage. Each line
     carries its own slCommissionPerCase dollars-per-case figure;
     the displayed total at the bottom is the SUM of cases ×
     perCase across every non-excluded line. Excluded sale statuses
     (dumped/donated/samples/transfer) contribute $0. */
  var commissionAmount = useMemo(function () {
    var sum = 0;
    (f.lines || []).forEach(function (l) {
      if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
      var cases = parseInt(l.cases, 10) || 0;
      var perCase = parseFloat(l.slCommissionPerCase) || 0;
      sum += cases * perCase;
    });
    return sum;
  }, [f.lines]);
  /* Advance Financing Fee — invoice-level flat deduction, separate
     from the per-fee table so it always renders as its own row in
     the printed Invoice. */
  var advanceFinancingFee = useMemo(function () { return Math.abs(parseFloat(f.advanceFinancingFee) || 0); }, [f.advanceFinancingFee]);
  /* Fees SUBTRACT (deductions GFI passes to client: PTI, QC, warehouse,
     advance financing, etc.), Credits ADD, Transfers ± per type,
     Commission SUBTRACTS, AdvanceFinancing SUBTRACTS.
       TOTAL DUE = Gross − Commission − AdvFinFee − Fees + Credits + Transfers */
  var due = totalAmount - commissionAmount - advanceFinancingFee - totalFees + totalCredits + totalTransfers;

  var validate = function() {
    var errs = {};
    /* SweetLeaf — puNumber + clientId are auto-seeded from the
       reserveSLInvoiceNumber()/findFourSeasonsClient() helpers, so
       they can't be empty under normal operation. The validation
       below is intentionally permissive. */
    if (!f.puNumber && !f.invoiceNumber) errs.puNumber = true;
    if ((f.lines || []).length === 0) errs.lot = true;
    if (!(f.lines || []).length) errs.lines = true;
    setErrors(errs); return Object.keys(errs).length === 0;
  };

  /* Live reconciliation banner + save guard — lines[].cases (what the
     invoice document shows) vs palletAllocations[].cases (the physical
     source of truth calcLotProfit reads for shipment/bill profit).
     Recomputed on every render off current form state so it updates as
     the user edits either side, no debounce needed (cheap sums). */
  var lotBalances = useMemo(function() { return computeLotBalances(f.lines, f.palletAllocations); }, [f.lines, f.palletAllocations]);
  var hasLotMismatch = lotBalances.some(function(b) { return !b.balanced; });

  /* Pallet-allocation save guard. Groups this invoice's own rows by
     (lot, palletNum) — in case the same pallet ended up on two rows —
     then checks elsewhere-usage + this invoice's total against the
     pallet's real case count. Returns one message per over-allocated
     pallet so a single alert can list all of them at once. */
  var validatePalletAllocations = function(allocs) {
    var perPallet = {};
    allocs.forEach(function(a) {
      var key = String(a.lot) + "::" + String(a.palletNum);
      perPallet[key] = (perPallet[key] || 0) + (parseInt(a.cases, 10) || 0);
    });
    var errors = [];
    Object.keys(perPallet).forEach(function(key) {
      var idx = key.indexOf("::");
      var lot = key.slice(0, idx), palletNum = key.slice(idx + 2);
      var total = poolTotalFor(lot, palletNum);
      var usedElsewhere = casesAllocatedElsewhere(lot, palletNum);
      var thisInvoiceCases = perPallet[key];
      var available = total - usedElsewhere;
      if (usedElsewhere + thisInvoiceCases > total) {
        errors.push(lang === "es"
          ? "Pallet " + palletNum + " del lote " + lot + ": intentas asignar " + thisInvoiceCases + " pero solo hay " + available + " disponibles"
          : "Pallet " + palletNum + " of lot " + lot + ": you're trying to assign " + thisInvoiceCases + " but only " + available + " are available");
      }
    });
    return errors;
  };

  var handleSave = function() {
    if (!validate()) return;
    /* Drop rows that can't mean anything (0/blank cases, or missing
       lot/palletNum from a corrupted/legacy row) before validating —
       these are silently cleaned up, not reported as errors, since
       there's nothing actionable for the user to fix on them. */
    var cleanAllocs = (f.palletAllocations || []).filter(function(a) {
      return a && a.lot && a.palletNum && (parseInt(a.cases, 10) || 0) > 0;
    });
    var allocErrors = validatePalletAllocations(cleanAllocs);
    if (allocErrors.length > 0) {
      alert((lang === "es" ? "No se puede guardar — excede lo disponible:\n\n" : "Cannot save — exceeds available cases:\n\n") + allocErrors.join("\n"));
      return;
    }
    /* Lines[].cases vs palletAllocations reconciliation — same rule the
       live banner above the Pallet Allocations table shows, enforced
       again here so it can't be bypassed by saving before the banner
       re-renders. Uses cleanAllocs (post-cleanup) so a stray 0-case row
       doesn't falsely count toward a lot's allocation total. */
    var lotMismatches = lotBalanceMismatches(f.lines, cleanAllocs);
    if (lotMismatches.length > 0) {
      var mismatchMsg = lotMismatches.map(function(b) {
        return lang === "es"
          ? "El lote " + b.lot + ": la línea de producto muestra " + b.linesCases + " cajas pero los pallets asignados suman " + b.allocCases + " cajas. Corrige antes de guardar."
          : "Lot " + b.lot + ": the product line shows " + b.linesCases + " cases but the assigned pallets add up to " + b.allocCases + " cases. Fix before saving.";
      }).join("\n\n");
      alert(mismatchMsg);
      return;
    }
    (f.fees || []).forEach(function(fe) { if (fe.feeType === "__custom__" && fe.feeCustom) addCustomFeeType(fe.feeCustom); });
    /* SweetLeaf: if the user cleared the INV# field, auto-fill from
       the suggested next consecutive. puNumber mirrors invoiceNumber
       since downstream consumers still key off puNumber. */
    var resolvedInvNum = (f.invoiceNumber && String(f.invoiceNumber).trim()) || peekSLInvoiceNumber();
    var invoiceToSave = { ...f, palletAllocations: cleanAllocs, id: f.id || nextId(invoices), invoiceNumber: resolvedInvNum, puNumber: resolvedInvNum };
    /* Bump the SL consecutive ONLY when the saved INV# matches the
       suggestion — manual overrides (historical imports etc.) leave
       the counter alone. Only fires on brand-new invoices so editing
     an existing record never advances the counter. */
    if (!f.id) commitSLInvoiceNumberIfNext(resolvedInvNum);
    /* Create fee transfers for fees marked transferToBill */
    var transferFees = (f.fees || []).filter(function(fe) { return fe.transferToBill && parseFloat(fe.amount) > 0 && fe.transferTargetBillId; });
    if (transferFees.length > 0) {
      try {
        var transfers = loadLS("gfi-fee-transfers", []);
        transfers = transfers.filter(function(t) { return t.sourceInvoice !== invoiceToSave.id; });
        transferFees.forEach(function(fe) {
          var targetBill = availableBills.find(function(b) { return b.id === fe.transferTargetBillId; });
          transfers.push({
            id: "TRF-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
            sourceType: "invoice", sourceInvoice: invoiceToSave.id, sourcePU: invoiceToSave.puNumber,
            targetType: "bill", targetLot: targetBill ? targetBill.lot : f.lot, targetBillId: fe.transferTargetBillId,
            feeType: fe.feeType, feeCustom: fe.feeCustom || "",
            qty: fe.qty, rate: fe.rate, amount: fe.amount, pctRate: fe.pctRate || "",
            status: "pending", dateCreated: new Date().toISOString().substring(0, 10),
          });
        });
        localStorage.setItem("gfi-fee-transfers", JSON.stringify(transfers));
      } catch(e) { console.error("Fee transfer error", e); }
    }
    /* Sync PO linkage with the Client Pricing modules. The previous
       linkage (if any) is read from the persisted version of the
       invoice so a "Save & New"-style flow doesn't end up with two
       invoices claiming the same PO. Done before onSave so the
       persisted state on disk is consistent with the back-link.
       Passes full invoice context (id + puNumber + status) so the
       PO row mirrors the invoice's PU# and status, not just the id. */
    try {
      var existing = invoice && invoice.id ? (loadLS(STORAGE_KEY, []).find(function (x) { return x.id === invoice.id; }) || null) : null;
      var prevLink = existing && existing.linkedPoLineId ? existing.linkedPoLineId : "";
      var newLink  = invoiceToSave.linkedPoLineId || "";
      if (prevLink || newLink) {
        syncPoLinkage(prevLink, newLink, {
          id: invoiceToSave.id,
          puNumber: invoiceToSave.puNumber || "",
          status: invoiceToSave.status || "draft",
        });
      }
    } catch (e) { /* linkage best-effort — don't block the save */ }
    onSave(invoiceToSave);
  };

  var tryCancel = function() { if (dirty) setShowExitConfirm(true); else onCancel(); };
  var errS = { borderColor: "#ef4444" };

  var body = (
    <>
        {/* SUB-HEADER: status pill + transitions */}
        <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", alignItems: "center", background: "rgba(0,0,0,0.015)" }}>
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            {(function() { var st = f.status || "draft"; var sty = INV_STATUS_STYLES[st] || INV_STATUS_STYLES.draft; var key = st === "draft" ? "statusDraft" : st === "sent" ? "statusSent" : "statusPaid"; return <span style={{ padding: "3px 10px", borderRadius: 7, border: "1px solid " + sty.border, background: sty.bg, color: sty.text, fontSize: 10, fontWeight: 700 }}>{t[key]}</span>; })()}
            {f.status === "draft" && <button onClick={function() { set("status", "sent"); }} style={{ padding: "3px 10px", borderRadius: 7, border: "1px solid rgba(42,122,111,0.3)", background: "rgba(42,122,111,0.06)", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2192"} {t.statusSent}</button>}
            {f.status === "sent" && <>
              <button onClick={function() { set("status", "draft"); }} style={{ padding: "3px 10px", borderRadius: 7, border: "1px solid rgba(107,114,128,0.3)", background: "rgba(107,114,128,0.06)", color: "#6B7280", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2190"} {t.statusDraft}</button>
              <button onClick={function() { set("status", "paid"); }} style={{ padding: "3px 10px", borderRadius: 7, border: "1px solid rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.06)", color: "#059669", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 3 }}>{"\u2192"} {t.statusPaid}</button>
            </>}
            {f.status === "paid" && <button onClick={function() { set("status", "sent"); }} style={{ padding: "3px 10px", borderRadius: 7, border: "1px solid rgba(42,122,111,0.3)", background: "rgba(42,122,111,0.06)", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 6 }}>{"\u2190"} {t.statusSent}</button>}
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* PENDING FEE TRANSFERS from Bills */}
          {(function() {
            void trVer;
            var allTransfers = loadLS("gfi-fee-transfers", []);
            var transfers = allTransfers.filter(function(tr) {
              if (tr.status !== "pending") return false;
              if (tr.targetType !== "invoice") return false;
              if (tr.targetPU !== f.puNumber && tr.targetLot !== f.lot) return false;
              /* Also skip if fee already exists in current form (already accepted in a prior session) */
              var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
              var alreadyIn = (f.fees || []).some(function(ef) { return ef.fromBill && (ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType) === fname; });
              return !alreadyIn;
            });
            if (transfers.length === 0) return null;
            return <div style={{ margin: "10px 20px 0", padding: "10px 14px", borderRadius: 8, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#D97706" }}>{lang === "es" ? "Costos transferidos del Bill (LOT " + f.lot + ")" : "Transferred costs from Bill (LOT " + f.lot + ")"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={function() {
                    var existingFees = f.fees || [];
                    var newFees = []; var conflicts = [];
                    transfers.forEach(function(tr) {
                      var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
                      var existing = existingFees.find(function(ef) { var efName = ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType; return efName === fname; });
                      if (existing) { conflicts.push({ transfer: tr, existing: existing }); }
                      else { newFees.push({ feeType: tr.feeType, feeCustom: tr.feeCustom || "", qty: tr.qty || 1, rate: tr.rate || "", amount: tr.amount, pctRate: tr.pctRate || "", fromBill: true }); }
                    });
                    if (conflicts.length > 0) {
                      var msg = conflicts.map(function(c) { var fname = c.transfer.feeType === "__custom__" ? c.transfer.feeCustom : c.transfer.feeType; return fname + ": " + (lang === "es" ? "existe con " : "exists at ") + fmtMoney(c.existing.amount) + (lang === "es" ? ", viene " : ", incoming ") + fmtMoney(c.transfer.amount); }).join("\n");
                      var action = prompt((lang === "es" ? "Conflictos encontrados:\n" : "Conflicts found:\n") + msg + "\n\n" + (lang === "es" ? "Escriba: agregar / sustituir / rechazar" : "Type: add / replace / reject"));
                      if (action && action.toLowerCase().startsWith("a")) { conflicts.forEach(function(c) { newFees.push({ feeType: c.transfer.feeType, feeCustom: c.transfer.feeCustom || "", qty: c.transfer.qty || 1, rate: c.transfer.rate || "", amount: c.transfer.amount, pctRate: c.transfer.pctRate || "", fromBill: true }); }); }
                      else if (action && (action.toLowerCase().startsWith("s") || action.toLowerCase().startsWith("r") && action.toLowerCase().includes("epl"))) {
                        setF(function(prev) { var updated = prev.fees.map(function(ef) { var efName = ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType; var match = conflicts.find(function(c) { var fn = c.transfer.feeType === "__custom__" ? c.transfer.feeCustom : c.transfer.feeType; return fn === efName; }); return match ? { ...ef, amount: match.transfer.amount, rate: match.transfer.rate || ef.rate } : ef; }); return { ...prev, fees: updated }; });
                      }
                    }
                    if (newFees.length > 0) setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), ...newFees] }; });
                    try { var allTr = loadLS("gfi-fee-transfers", []); localStorage.setItem("gfi-fee-transfers", JSON.stringify(allTr.map(function(t) { return transfers.some(function(tr) { return tr.id === t.id; }) ? { ...t, status: "accepted" } : t; }))); } catch(e) {}
                    setDirty(true); setTrVer(function(v) { return v + 1; });
                  }} style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: "#059669", color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Aceptar" : "Accept"}</button>
                  <button onClick={function() { try { var allTr = loadLS("gfi-fee-transfers", []); localStorage.setItem("gfi-fee-transfers", JSON.stringify(allTr.map(function(t) { return transfers.some(function(tr) { return tr.id === t.id; }) ? { ...t, status: "rejected" } : t; }))); } catch(e) {} setTrVer(function(v) { return v + 1; }); }} style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Rechazar" : "Reject"}</button>
                </div>
              </div>
              <div style={{ fontSize: 11 }}>{transfers.map(function(tr, i) { var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType; return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#92400E" }}><span>{fname}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(tr.amount)}</span></div>; })}</div>
            </div>;
          })()}
          {/* 2026-10-28, Arturo: "al entrar en modo edicion debe
              informarme del transfer ya sea credito o debito y
              pedirme si lo acepto" — this invoice received a Balance
              Transfer created from the OTHER invoice (acknowledged:
              false, stamped by createBalanceTransfer above); flag it
              here until Arturo explicitly accepts or rejects it.
              Legacy entries with no `acknowledged` field at all
              (saved before this feature existed) are treated as
              already-reviewed — never re-surfaced retroactively. */}
          {(f.balanceTransfers || []).map(function(tr, idx) { return { tr: tr, idx: idx }; }).filter(function(x) { return x.tr.acknowledged === false; }).map(function(x) {
            var tr = x.tr, idx = x.idx;
            var isCredit = tr.type === "credit";
            return (
              <div key={tr.id || idx} style={{ margin: "10px 20px 0", padding: "10px 14px", borderRadius: 8, background: isCredit ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)", border: "1px solid " + (isCredit ? "rgba(5,150,105,0.25)" : "rgba(220,38,38,0.25)") }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 12, color: isCredit ? "#059669" : "#DC2626" }}>
                    <span style={{ fontWeight: 700 }}>
                      {lang === "es"
                        ? (isCredit ? "Se agregó un crédito de " : "Se agregó un débito de ") + fmtMoney(tr.amount) + " a esta factura"
                        : (isCredit ? "A credit of " : "A debit of ") + fmtMoney(tr.amount) + " was added to this invoice"}
                    </span>
                    <span style={{ opacity: 0.85 }}>
                      {" "}{lang === "es" ? "— transferido desde PU# " : "— transferred from PU# "}{tr.otherPuNumber || "—"}{tr.otherClientName ? " (" + tr.otherClientName + ")" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button type="button" onClick={function() { acknowledgeBalanceTransfer(idx); }} style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: "#059669", color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Aceptar" : "Accept"}</button>
                    <button type="button" onClick={function() { if (window.confirm(lang === "es" ? "¿Rechazar esta transferencia? Esto también la quita de la otra factura." : "Reject this transfer? This also removes it from the other invoice.")) removeBalanceTransfer(idx); }} style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Rechazar" : "Reject"}</button>
                  </div>
                </div>
              </div>
            );
          })}
          {/* ROW 1 — INV# (read-only, auto-generated) + GFI PU# (read-only
              ref from the source GFI invoice) + Date + Due Date. The
              client picker was hidden and replaced with the Four
              Seasons chip below. */}
          <div style={{ padding: "12px 20px 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.invNum}</div>
              {/* INV# is editable so Arturo can override the suggested
                  consecutive (e.g. for historical imports like
                  INV-16100). If the field is empty on save the form
                  auto-fills from peekSLInvoiceNumber(). */}
              <input
                type="text"
                value={f.invoiceNumber || ""}
                onChange={function (e) { var v = e.target.value; setF(function (prev) { return Object.assign({}, prev, { invoiceNumber: v, puNumber: v }); }); setDirty(true); }}
                placeholder={peekSLInvoiceNumber()}
                style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#2A7A6F" }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.gfiPuNum}</div>
              <div style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: "var(--color-text-secondary)", background: "#f7f8fa", cursor: "default" }}>{f.gfiPuNumber || "—"}</div>
            </div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.date}</div><input type="date" value={f.date || ""} onChange={function(e) { set("date", e.target.value); }} style={inp} /></div>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{t.dueDate}</span>
                <button type="button" onClick={function() {
                  var cli = loadLS("gfi-clients", []).find(function(c) { return idMatches(c, f.clientId); });
                  var days = parseTermDays(cli && cli.paymentTerm);
                  if (days === null) days = 21;
                  set("dueDate", addDaysToISO(f.date, days));
                }} style={{ fontSize: 9, padding: "1px 6px", border: "1px solid var(--color-border)", borderRadius: 4, background: "transparent", color: "#c65d00", cursor: "pointer", fontWeight: 600 }} title="Calculate from Invoice Date + Client Term">↻ Default</button>
              </div>
              <input type="date" value={f.dueDate || ""} onChange={function(e) { set("dueDate", e.target.value); }} style={inp} />
            </div>
          </div>

          {/* ROW 2 */}
          <div style={{ padding: "6px 20px 12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, borderBottom: "1px solid var(--color-border)" }}>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.clientRef}</div>
              {(function () {
                /* PO picker — pulls from Fixed + Consignment Pricing
                   filtered by client + unlinked, sorted by date
                   proximity to the invoice date. Selecting a PO
                   stamps both linkedPoLineId (for back-link on save)
                   and clientRef (the PO# itself). Selecting "manual"
                   shows the legacy free-text input so users can type
                   a PO# that hasn't been captured in the system yet.
                   When a stored linkedPoLineId points to a PO that
                   no longer exists (deleted upstream), we fall back
                   to manual display so the PO# isn't lost. */
                var currentLink = f.linkedPoLineId || "";
                var linkExists = currentLink && availablePOs.some(function (p) { return p._src + ":" + p.id === currentLink; });
                var manualMode = !currentLink || !linkExists;
                var poOpts = availablePOs.map(function (p) {
                  var compositeId = p._src + ":" + p.id;
                  var ddate = p.deliveryDate || p.date || "—";
                  var prod  = p.productDescription || p.productBase || p.productName || "";
                  var cs = parseInt(p._src === "F" ? (p.casesReceived || p.casesSent) : p.cases, 10) || 0;
                  /* 2026-11-02 — same fix as InvoiceRegistry.js's PO
                     picker: Fixed and Semi-fixed both live in
                     fixed_pos (_src==="F"), so this must check the
                     row's own `term`, not just which table it came
                     from, or every semifixed PO shows as "Fixed". */
                  var typeLabel = p._src !== "F" ? t.poTypeConsignment : (p.term === "semifixed" ? t.poTypeSemifixed : t.poTypeFixed);
                  return {
                    value: compositeId,
                    label: (p.poNumber || "?") + " · " + ddate + " · " + prod + " · " + cs + "c · " + typeLabel,
                    search: (p.poNumber || "") + " " + (p.puNumber || "") + " " + prod,
                  };
                });
                poOpts.push({ value: "__manual__", label: t.enterPOManually });
                var pickerValue = manualMode ? "__manual__" : currentLink;
                var onPickerChange = function (v) {
                  if (v === "__manual__" || !v) {
                    setF(function (prev) { return { ...prev, linkedPoLineId: "" }; });
                    setDirty(true);
                    return;
                  }
                  var po = availablePOs.find(function (p) { return p._src + ":" + p.id === v; });
                  if (po) {
                    setF(function (prev) { return { ...prev, linkedPoLineId: v, clientRef: po.poNumber || "", thermograph: (po.loggerNumber || prev.thermograph || "").toUpperCase() }; });
                    setDirty(true);
                  }
                };
                /* Reference-price banner — shows the PO's source
                   prices so the user can eyeball them while keying
                   the invoice. Hides once any line carries a price
                   (user has internalized the reference). Distinct
                   format per source: Fixed shows the GF price; TR
                   Consignment shows the raw range BEFORE TR's
                   commission deduction (per spec — user does the
                   commission math themselves). */
                var banner = null;
                if (linkedPo && !hasPricedLines) {
                  if (linkedPo._src === "F") {
                    var gf = parseFloat(linkedPo.priceGF) || 0;
                    banner = (
                      <div style={{ marginTop: 6, padding: "8px 12px", background: "#FFF3E0", border: "1px solid #FF9800", borderRadius: 6, fontSize: 12, color: "#7c4a03", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>📋</span>
                        <span>
                          <strong>PO {linkedPo.poNumber || "?"}</strong>
                          {" — "}{lang === "es" ? "Precio GF" : "GF Price"}{": "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{gf > 0 ? fmtMoney(gf) : "—"}</strong>
                        </span>
                      </div>
                    );
                  } else if (linkedPo._src === "C") {
                    var minTR = parseFloat(linkedPo.minTR) || 0;
                    var maxTR = parseFloat(linkedPo.maxTR) || 0;
                    banner = (
                      <div style={{ marginTop: 6, padding: "8px 12px", background: "#FFF3E0", border: "1px solid #FF9800", borderRadius: 6, fontSize: 12, color: "#7c4a03", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>📋</span>
                        <span>
                          <strong>PO {linkedPo.poNumber || "?"}</strong>
                          {" — "}{lang === "es" ? "Rango TR" : "TR Range"}{": "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{minTR > 0 ? fmtMoney(minTR) : "—"}</strong>
                          {" - "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{maxTR > 0 ? fmtMoney(maxTR) : "—"}</strong>
                        </span>
                      </div>
                    );
                  }
                }
                return (
                  <>
                    <SearchSelect
                      value={pickerValue}
                      onChange={onPickerChange}
                      options={poOpts}
                      placeholder={availablePOs.length === 0 ? t.noAvailablePOs : t.selectPO}
                      disabled={!f.clientId}
                    />
                    {manualMode ? (
                      <input value={f.clientRef || ""} onChange={function(e) { set("clientRef", e.target.value); }} placeholder={t.manualPOPlaceholder} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }} />
                    ) : (
                      <div style={{ marginTop: 4, padding: "6px 8px", background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 5, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "#047857", fontWeight: 700 }}>
                        ✓ {f.clientRef}
                      </div>
                    )}
                    {banner}
                  </>
                );
              })()}
            </div>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Agregar productos de embarque" : "Add products from shipment"}</div>
              <div style={{ display: "flex", gap: 4 }}>
                <SearchSelect
                  value={lotPickerValue}
                  onChange={function(v) { setLotPickerValue(v); }}
                  options={availableLots.map(function(s) { return { value: String(s.id), label: "LOT " + s.lot + " \u2014 " + (s.shipperName || "?") + " \u00b7 " + (s.dateArrival || s.dateDeparture || "") }; })}
                  placeholder={t.selectLot}
                  style={{ flex: 1 }}
                  limit={10}
                />
                <button onClick={function() { if (lotPickerValue) { addFromShipment(lotPickerValue); setLotPickerValue(""); } }} style={{ padding: "6px 12px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>+ {lang === "es" ? "Agregar" : "Add"}</button>
              </div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>{lang === "es" ? "Solo embarques con fecha \u2264 fecha de factura. Escribe el lot si no aparece en los " : "Only shipments dated on/before invoice date. Type the lot if older than the top "}{availableLotsTop10.length}.</div>
            </div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.shipper}</div><input value={f.shipperName || ""} readOnly style={{ ...inp, background: "var(--color-bg)" }} /></div>
            {/* Temp Recorder # — assigned at the warehouse for the SL
                leg to Four Seasons, distinct from the shipment's own
                thermograph. Always uppercased so packing list reads
                consistently. */}
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.thermograph}</div>
              <input
                value={f.thermograph || ""}
                onChange={function (e) { set("thermograph", e.target.value.toUpperCase()); }}
                style={{ ...inp, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
          </div>

          {/* PRODUCT LINES */}
          {(f.lines || []).length > 0 && (
            <div style={{ padding: "0 20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ width: "3%" }}></th>
                  <th style={{ padding: "9px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "6%" }}>LOT</th>
                  <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "6%" }}>{t.cases}</th>
                  <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "13%" }}>{t.product}</th>
                  <th style={{ padding: "9px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "14%" }}>{t.description}</th>
                  <th style={{ padding: "9px 3px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "6%" }}>{t.pallets}</th>
                  <th style={{ padding: "9px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.salePrice}</th>
                  <th style={{ padding: "9px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "11%" }}>{t.amount}</th>
                  {/* SweetLeaf commission columns — dollars-per-case
                      (editable) + computed line commission total. */}
                  <th style={{ padding: "9px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "8%" }}>{t.commPerCase}</th>
                  <th style={{ padding: "9px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "#DC2626", width: "8%" }}>{t.lineCommAmt}</th>
                  <th style={{ padding: "9px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-accent)", width: "9%" }}>{t.lineNetAmt}</th>
                  <th style={{ padding: "9px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "9%" }}>{t.saleStatus}</th>
                  <th style={{ width: "9%" }}></th>
                </tr></thead>
                <tbody>{f.lines.map(function(line, idx) {
                  var isLoss = line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer";
                  var amt = isLoss ? 0 : (parseInt(line.cases, 10) || 0) * (parseFloat(line.salePrice) || 0);
                  var rp = dndLines.rowProps(idx);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", opacity: isLoss ? 0.6 : 1, ...(rp.style || {}) }}>
                    <td style={{ padding: "6px 0", textAlign: "center" }}><DragHandle handleProps={dndLines.handleProps()} /></td>
                    <td style={{ padding: "6px 4px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textAlign: "center", fontSize: 11, color: "var(--color-accent)" }}>{line.lot || "\u2014"}</td>
                    {/* Qty \u2014 now an editable number input so the user
                        can split a line into two with different cases
                        + sale statuses (e.g. 240 Good Delivery + 240
                        Protection). Empty is allowed. */}
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="1" value={line.cases === undefined || line.cases === null ? "" : line.cases} onChange={function(e) { updateLineCases(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 4px", fontWeight: 600 }}>{line.productName || line.productBase}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)" }}>{line.productDescription || ""}</td>
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="1" value={line.pallets === undefined || line.pallets === null ? "" : line.pallets} onChange={function(e) { updateLinePallets(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="0.01" value={line.salePrice || ""} onChange={function(e) { updateLinePrice(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: isLoss ? "#DC2626" : "var(--color-accent)" }}>{fmtMoney(amt)}</td>
                    {/* Per-line SweetLeaf commission — $ per case
                        (editable) + computed line total (cases × $). */}
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="0.01" value={line.slCommissionPerCase === undefined || line.slCommissionPerCase === null ? "" : line.slCommissionPerCase} onChange={function(e) { updateLineCommPerCase(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#DC2626" }}>{(function() { var lc = isLoss ? 0 : (parseInt(line.cases, 10) || 0) * (parseFloat(line.slCommissionPerCase) || 0); return lc > 0 ? "(" + fmtMoney(lc) + ")" : "—"; })()}</td>
                    {/* Net Amount = Value − Comm$. Calculated, read-only.
                        Excluded sale statuses contribute $0 to gross, so
                        Net is also $0 for those. */}
                    <td style={{ padding: "6px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "var(--color-accent)" }}>{(function() { var lc = isLoss ? 0 : (parseInt(line.cases, 10) || 0) * (parseFloat(line.slCommissionPerCase) || 0); var net = amt - lc; return net > 0 ? fmtMoney(net) : "—"; })()}</td>
                    <td style={{ padding: "6px 3px" }}>{(function() {
                      /* 2026-09-12 \u2014 SweetLeaf invoices are a mirror of
                         their linked GFI invoice (Arturo: "en sweetleaf no
                         se tocan los status, estos se tocan a nivel factura
                         en ground-fresh") \u2014 ALWAYS read-only here, both
                         the computed delivery statuses AND the 4 manual
                         dispositions, which used to be editable in SL by
                         mistake. Every status change happens on the GFI
                         invoice and gets pushed down; SL never sets its own. */
                      var shownStatus = line.saleStatus || (f.passingSentAt ? "pendingDelivery" : "pendingPickUp");
                      var sc = SALE_STATUS_COLORS[shownStatus];
                      return sc
                        ? <span title={line.saleStatus ? t.computedFromPallets : ""} style={{ display: "inline-block", padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, cursor: line.saleStatus ? "help" : "default" }}>{t[shownStatus] || shownStatus}</span>
                        : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>\u2014</span>;
                    })()}</td>
                    <td style={{ padding: "6px 1px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                        {/* "\u21c6" halves cases + pallets into a new sibling
                            row; "+" clones the line keeping product info
                            so the user can record a split (qty + sale
                            status left blank for them to fill in). */}
                        <button type="button" onClick={function() { splitProductLine(idx); }} title={lang === "es" ? "Dividir l\u00ednea" : "Split line"} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"\u21c6"}</button>
                        <button type="button" onClick={function() { addProductLineCopy(idx); }} title={lang === "es" ? "Agregar l\u00ednea del mismo producto" : "Add row (same product)"} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"+"}</button>
                        <button type="button" onClick={function() { removeProductLine(idx); }} title={lang === "es" ? "Eliminar l\u00ednea" : "Remove line"} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{"\u00d7"}</button>
                      </div>
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}

          {/* PALLET ALLOCATIONS — per-physical-pallet case tracking,
              separate from the aggregate product Lines above. Seeded
              100%-cases from a lot's manifest via "Add from shipment";
              editable per row; additional pallets from other lots
              already on this invoice come from the picker below. */}
          <div style={{ padding: "10px 20px 0", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8 }}>{t.palletAllocations}</div>
            {lotBalances.length > 0 && (
              <div style={{ marginBottom: 8, padding: "7px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: hasLotMismatch ? "rgba(220,38,38,0.08)" : "rgba(5,150,105,0.08)", color: hasLotMismatch ? "#DC2626" : "#059669", border: "1px solid " + (hasLotMismatch ? "rgba(220,38,38,0.25)" : "rgba(5,150,105,0.25)") }}>
                {hasLotMismatch
                  ? lotBalances.filter(function(b) { return !b.balanced; }).map(function(b) {
                      return <div key={b.lot}>{"⚠ " + (lang === "es" ? "Descuadre: Lote " + b.lot + " — Línea: " + b.linesCases + " vs Allocations: " + b.allocCases : "Mismatch: Lot " + b.lot + " — Line: " + b.linesCases + " vs Allocations: " + b.allocCases)}</div>;
                    })
                  : <div>{"✓ " + (lang === "es" ? "Pallets balanceados" : "Allocations balanced")}</div>}
              </div>
            )}
            {(f.palletAllocations || []).length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", marginBottom: 8 }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "9%" }}>{t.lot}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "7%" }}>{t.palletNumCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "9%" }}>{t.tagIdCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "14%" }}>{t.product}</th>
                  <th style={{ padding: "7px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.varietyCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "9%" }}>{t.allocCases}</th>
                  <th style={{ padding: "7px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.growerCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "10%" }}>{t.weightCol}</th>
                  <th style={{ padding: "7px 4px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "13%" }}>{t.palletStatusCol}</th>
                  <th style={{ width: "7%" }}></th>
                </tr></thead>
                <tbody>{f.palletAllocations.map(function(a, idx) {
                  var meta = palletMetaFor(a.lot, a.palletNum) || {};
                  var w = docPalletWeight({ size: meta.size, unitsCase: meta.unitsCase, casesPallet: a.cases });
                  /* 2026-09-12 — read-only, pulled live from whichever GFI
                     invoice(s) currently allocate this exact pallet (see
                     saleStatusCalc.js derivePalletStatus). SL invoices are
                     a mirror of the GFI invoice, never the source of the
                     pallet-level status themselves — same as Bill/Shipment.
                     Nothing confirmed yet defaults to Pending Pick Up, or
                     Pending Delivery once THIS invoice's own Passing/BOL
                     has been sent — every record in this module is Four
                     Seasons by definition, so no cross-invoice lookup is
                     needed here, unlike Bill/Shipment. */
                  var pStatus = derivePalletStatus(a.lot, a.palletNum, loadLS("gfi-invoices", [])) || (f.passingSentAt ? "pendingDelivery" : "pendingPickUp");
                  var pSc = SALE_STATUS_COLORS[pStatus];
                  return <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 11, color: "var(--color-accent)" }}>{a.lot || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{a.palletNum || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.tagId || "—"}</td>
                    <td style={{ padding: "6px 4px", fontWeight: 600 }}>{meta.productBase || "—"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.variety || "—"}</td>
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="1" value={a.cases === undefined || a.cases === null ? "" : a.cases} onChange={function(e) { updateAllocCases(idx, e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 4px", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.growerCode || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{w.lb > 0 ? w.lb.toFixed(1) + " lb" : "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>{pStatus && pSc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: pSc.bg, color: pSc.text, border: "1px solid " + pSc.border, boxShadow: SALE_STATUS_BEVEL }}>{t[pStatus] || pStatus}</span> : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                    <td style={{ padding: "6px 1px", textAlign: "center" }}>
                      <button type="button" onClick={function() { removeAlloc(idx); }} title={lang === "es" ? "Eliminar renglón" : "Remove row"} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{"🗑"}</button>
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            )}
            {populateFromLotShipment && (
              <button type="button" onClick={populateFromLot} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, marginBottom: 10 }}>+ {sourceGfInvoice ? (lang === "es" ? "Traer de Factura GF PU-" + f.gfiPuNumber : "Populate from GF Invoice PU-" + f.gfiPuNumber) : t.populateFromLot}</button>
            )}
            <div style={{ display: "flex", gap: 4, paddingBottom: 10 }}>
              <SearchSelect
                value={otherLotPickerValue}
                onChange={function(v) { setOtherLotPickerValue(v); }}
                options={otherLotPalletOptions.map(function(o) { return { value: o.value, label: o.label }; })}
                placeholder={otherLotPalletOptions.length > 0 ? t.addFromOtherLot : t.noPalletsToAdd}
                style={{ flex: 1 }}
                disabled={otherLotPalletOptions.length === 0}
              />
              <button onClick={function() { if (otherLotPickerValue) { addFromOtherLotPallet(otherLotPickerValue); setOtherLotPickerValue(""); } }} disabled={!otherLotPickerValue} style={{ padding: "6px 12px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: otherLotPickerValue ? "pointer" : "not-allowed", opacity: otherLotPickerValue ? 1 : 0.5, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>+ {lang === "es" ? "Agregar" : "Add"}</button>
              {otherLotPalletOptions.length > 1 && (
                <button type="button" onClick={addAllOtherLotPallets} title={lang === "es" ? "Agregar los " + otherLotPalletOptions.length + " pallets restantes de un solo click" : "Add all " + otherLotPalletOptions.length + " remaining pallets in one click"} style={{ padding: "6px 12px", borderRadius: 5, border: "1px dashed var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>+ {lang === "es" ? "Agregar todos (" + otherLotPalletOptions.length + ")" : "Add all (" + otherLotPalletOptions.length + ")"}</button>
              )}
            </div>
          </div>

          {/* COMMISSION — read-only summary of per-line commissions.
              The user captures commission % per product line in the
              Comm% column above; this block just totals them up so
              the running deduction is visible without scrolling to
              the final summary. SUBTRACTS from total. */}
          <div style={{ padding: "8px 20px", borderTop: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>{lang === "es" ? "Comisión" : "Commission"}:</span>
            <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{lang === "es" ? "Total de comisiones por línea" : "Sum of per-line commissions"}: <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: commissionAmount > 0 ? "#DC2626" : "var(--color-text-muted)", marginLeft: 4 }}>{commissionAmount > 0 ? "(" + fmtMoney(commissionAmount) + ")" : fmtMoney(0)}</span></span>
          </div>

          {/* FEES */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c65d00", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.fees}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "27%" }}>{t.feeType}</th>
                <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "8%" }}>{t.feeQty}</th>
                <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "12%" }}>{t.feeRate}</th>
                <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "13%" }}>{t.feeAmount}</th>
                <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "7%" }} title={lang === "es" ? "Traspasar al Bill del embarque" : "Transfer to shipment Bill"}>{"\u2192"}Bill</th>
                <th style={{ width: "4%" }}></th>
              </tr></thead>
              <tbody>
                {(f.fees || []).map(function(fee, idx) {
                  var isFinancing = fee.feeType === "Advance Financing Fee";
                  var pctRef = isFinancing && fee.pctRate ? fmtMoney(totalAmount * ((parseFloat(fee.pctRate) || 0) / 100)) : "";
                  var rp = dndFees.rowProps(idx);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndFees.handleProps()} /></td>
                    <td style={{ padding: "5px 3px" }}>
                      <SearchSelect value={fee.feeType || ""} onChange={v => updateFee(idx, "feeType", v)} placeholder={t.selectFee} options={[].concat(feeTypes.map(function(ft) { return { value: ft, label: ft }; }), [{ value: "__custom__", label: t.customFee }])} />
                      {fee.feeType === "__custom__" && <input value={fee.feeCustom || ""} onChange={function(e) { updateFee(idx, "feeCustom", e.target.value); }} placeholder={lang === "es" ? "Nombre del gasto" : "Fee name"} style={{ ...inp, marginTop: 3, fontSize: 10, borderStyle: "dashed" }} />}
                      {isFinancing && <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}><input type="number" min="0" max="100" step="0.1" value={fee.pctRate || ""} onChange={function(e) { updateFee(idx, "pctRate", e.target.value); var calc = (totalAmount * ((parseFloat(e.target.value) || 0) / 100)).toFixed(2); updateFee(idx, "amount", calc); }} style={{ ...inp, width: 45, fontSize: 9, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} placeholder="%" /><span style={{ fontSize: 9, color: "var(--color-text-muted)" }}>% ref: {pctRef}</span></div>}
                    </td>
                    <td style={{ padding: "5px 3px" }}><input type="number" min="1" value={fee.qty} onChange={function(e) { updateFee(idx, "qty", e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "5px 3px" }}><input type="number" min="0" step="0.01" value={fee.rate} onChange={function(e) { updateFee(idx, "rate", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "5px 3px" }}><input type="number" min="0" step="0.01" value={fee.amount} onChange={function(e) { updateFee(idx, "amount", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600 }} /></td>
                    <td style={{ padding: "5px 2px", textAlign: "center" }}>
                      <input type="checkbox" checked={!!fee.transferToBill} onChange={function(e) {
                        updateFee(idx, "transferToBill", e.target.checked);
                        if (e.target.checked) {
                          var match = availableBills.find(function(b) { return b.lot === f.lot; });
                          if (match) updateFee(idx, "transferTargetBillId", match.id);
                        }
                      }} style={{ accentColor: "var(--color-accent)" }} />
                      {fee.transferToBill && <div style={{ marginTop: 2 }}><SearchSelect value={fee.transferTargetBillId || ""} onChange={v => updateFee(idx, "transferTargetBillId", v)} placeholder="Bill" options={[].concat(availableBills.filter(function(b) { return b.lot === f.lot; }).map(function(b) { return { value: b.id, label: "LOT " + b.lot + " " + (b.shipperName || "") }; }), availableBills.filter(function(b) { return b.lot !== f.lot; }).map(function(b) { return { value: b.id, label: "LOT " + (b.lot || "?") + " " + (b.shipperName || "") }; }))} /></div>}
                    </td>
                    <td style={{ padding: "5px 1px", textAlign: "center" }}><button onClick={function() { removeFee(idx); }} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12 }}>{"\u2715"}</button></td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr><td colSpan={4} style={{ padding: "8px 4px" }}><button onClick={addFee} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addFee}</button></td><td style={{ padding: "8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "var(--color-accent)" }}>{totalFees > 0 ? "+" + fmtMoney(totalFees) : ""}</td><td colSpan={2}></td></tr></tfoot>
            </table>
          </div>

          {/* CREDITS — reduce total. Each row is either a free credit
              (user-typed concept) or a reference to another invoice from
              this same client (visual link only; the referenced invoice
              is NOT modified). */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#DC2626", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.credits}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "17%" }}>{t.creditType}</th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "60%" }}>{t.creditConcept} / {t.creditRefInvoice}</th>
                <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "16%" }}>{t.feeAmount}</th>
                <th style={{ width: "4%" }}></th>
              </tr></thead>
              <tbody>
                {(f.credits || []).map(function(cr, idx) {
                  var rp = dndCredits.rowProps(idx);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndCredits.handleProps()} /></td>
                    <td style={{ padding: "5px 3px" }}>
                      <SearchSelect value={cr.type || "free"} onChange={v => updateCredit(idx, "type", v)} options={[{ value: "free", label: t.creditFree }, { value: "fromInvoice", label: t.creditFromInvoice }]} />
                    </td>
                    <td style={{ padding: "5px 3px" }}>
                      {cr.type === "fromInvoice" ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <div style={{ flex: 1 }}>
                            <SearchSelect value={cr.refInvoiceId || ""} onChange={v => {
                              var pickedInv = clientInvoices.find(function(inv) { return String(inv.id) === String(v); });
                              setF(function(prev) {
                                var newCredits = (prev.credits || []).map(function(c, i) {
                                  if (i !== idx) return c;
                                  return { ...c, refInvoiceId: v, refPuNumber: pickedInv ? (pickedInv.puNumber || "") : c.refPuNumber };
                                });
                                return { ...prev, credits: newCredits };
                              });
                              setDirty(true);
                            }} placeholder={lang === "es" ? "Selecciona factura del cliente…" : "Select client invoice…"} options={clientInvoices.map(function(inv) { return { value: inv.id, label: "PU# " + (inv.puNumber || inv.id) + " · " + (inv.lot ? "LOT " + inv.lot : "—") }; })} />
                          </div>
                          <input value={cr.refPuNumber || ""} onChange={function(e) { updateCredit(idx, "refPuNumber", e.target.value); }} placeholder={lang === "es" ? "o teclea PU#" : "or type PU#"} style={{ ...inp, width: 120, fontSize: 10, fontFamily: "'JetBrains Mono', monospace", borderStyle: "dashed" }} />
                        </div>
                      ) : (
                        <input value={cr.concept || ""} onChange={function(e) { updateCredit(idx, "concept", e.target.value); }} placeholder={lang === "es" ? "Concepto del crédito" : "Credit concept"} style={{ ...inp, fontSize: 11 }} />
                      )}
                    </td>
                    <td style={{ padding: "5px 3px" }}><input type="number" min="0" step="0.01" value={cr.amount || ""} onChange={function(e) { updateCredit(idx, "amount", e.target.value); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600 }} /></td>
                    <td style={{ padding: "5px 1px", textAlign: "center" }}><button onClick={function() { removeCredit(idx); }} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12 }}>{"✕"}</button></td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={3} style={{ padding: "8px 4px" }}><button onClick={addCredit} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addCredit}</button></td>
                <td style={{ padding: "8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "#DC2626" }}>{totalCredits > 0 ? "(" + fmtMoney(totalCredits) + ")" : ""}</td>
                <td></td>
              </tr></tfoot>
            </table>
          </div>

          {/* BALANCE TRANSFERS — bidirectional cross-invoice linkage,
              filtered by SAME CLIENT. Each entry is mirrored on the
              other invoice (atomic write to gfi-sl-invoices on
              create/remove). type "credit" adds; type "debit" subtracts. */}
          <div style={{ padding: "0 20px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.balanceTransfers}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "20%" }}>{t.btType}</th>
                <th style={{ padding: "6px 4px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "57%" }}>{t.btOtherInvoice}</th>
                <th style={{ padding: "6px 3px", textAlign: "right", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", width: "16%" }}>{t.btAmount}</th>
                <th style={{ width: "4%" }}></th>
              </tr></thead>
              <tbody>
                {(f.balanceTransfers || []).map(function(tr, idx) {
                  var rp = dndTransfers.rowProps(idx);
                  var isCredit = tr.type === "credit";
                  var tone = isCredit ? "#059669" : "#DC2626";
                  return <tr key={tr.id || idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndTransfers.handleProps()} /></td>
                    <td style={{ padding: "5px 3px", fontSize: 11, fontWeight: 700, color: tone }}>{isCredit ? t.btCredit : t.btDebit}</td>
                    <td style={{ padding: "5px 3px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>PU# {tr.otherPuNumber || "?"} · <span style={{ color: "var(--color-text-muted)" }}>{tr.otherClientName || ""}</span> {tr.date && <span style={{ color: "var(--color-text-muted)", fontSize: 10 }}> · {tr.date}</span>}</td>
                    <td style={{ padding: "5px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: tone }}>{isCredit ? "+" : "−"}{fmtMoney(tr.amount)}</td>
                    <td style={{ padding: "5px 1px", textAlign: "center" }}><button onClick={function() { if (window.confirm(lang === "es" ? "¿Eliminar transferencia? Esto también la quita de la otra factura." : "Remove transfer? This also removes it from the other invoice.")) removeBalanceTransfer(idx); }} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12 }}>{"✕"}</button></td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={5} style={{ padding: "8px 4px" }}>
                  <BalanceTransferCreatorInv
                    lang={lang} t={t} sameClientInvoices={clientInvoices}
                    suggestedAmount={Math.max(0, -due).toFixed(2)}
                    onCreate={createBalanceTransfer}
                  />
                </td>
              </tr></tfoot>
            </table>
          </div>

          {/* ADVANCE FINANCING FEE — SweetLeaf-specific invoice-level
              deduction (separate from the per-fee table so it gets
              its own line on the printed Invoice). Stored as a
              positive number; the form subtracts its absolute value. */}
          <div style={{ padding: "10px 20px 0", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#DC2626", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.advanceFinancingFee}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={f.advanceFinancingFee === undefined || f.advanceFinancingFee === null ? "" : f.advanceFinancingFee}
                onChange={function (e) { set("advanceFinancingFee", e.target.value); }}
                style={{ ...inp, width: 160, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* DOCUMENTS — two single-file slots uploaded directly to
              Supabase Storage so the PDFs round-trip reliably. The
              invoice record stores only the small reference object
              { name, size, type, path, url } — never the binary —
              so stripBlobs/JSONB size caps don't fire. Legacy
              records with base64 in `data` still display correctly.

              Drag & drop zone above the two slots accepts PDFs and
              auto-assigns them to the first empty slot (Xero, then
              BOL). When both slots are filled the user gets a
              confirm-style prompt to pick the replace target.
              Both drag-drop AND the per-slot button upload to
              Supabase Storage and store only the { name, size,
              type, path, url } reference — never inline base64. */}
          {(function () {
            var hasSlot = function (record) { return !!(record && (record.url || record.data)); };
            var handleDocDrop = async function (e) {
              e.preventDefault();
              setDocDragOver(false);
              var dropped = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
              var pdfs = dropped.filter(function (file) {
                return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
              });
              if (pdfs.length === 0) {
                if (dropped.length > 0) alert(t.dropZoneNotPdf);
                return;
              }
              /* Upload each dropped PDF to Supabase Storage — same path
                 as the per-slot button — and store only the small
                 { name, size, type, path, url } reference on the record.
                 NEVER inline base64: sweetleaf_invoices has no promoted
                 column, so a base64 `data` field lands in `extra` and
                 stripBlobs nulls it on save (the bug that wiped the docs
                 on invoices 16229/16230). `taken` is seeded from the live
                 form so the first dropped file fills the first empty slot
                 (Xero), and a second file in the same drop goes to BOL. */
              var taken = {
                xeroInvoiceFile: hasSlot(f.xeroInvoiceFile),
                bolFile: hasSlot(f.bolFile),
              };
              for (var i = 0; i < pdfs.length; i++) {
                var file = pdfs[i];
                var target = null;
                if (!taken.xeroInvoiceFile) target = "xeroInvoiceFile";
                else if (!taken.bolFile) target = "bolFile";
                else if (window.confirm(t.dropZoneReplaceXero)) target = "xeroInvoiceFile";
                else if (window.confirm(t.dropZoneReplaceBol)) target = "bolFile";
                else break; /* user cancelled — stop the batch */
                try {
                  var stem = slSafeSeg(f.invoiceNumber || f.id || ("tmp-" + Date.now()));
                  var ext = slExtFromFile(file);
                  var slotName = target === "xeroInvoiceFile" ? "xero" : "bol";
                  var path = "sweetleaf/" + stem + "/" + slotName + "-" + Date.now() + "." + ext;
                  var up = await uploadFileToStorage(SL_DOCS_BUCKET, path, file);
                  var payload = {
                    name: file.name,
                    size: (file.size / 1024).toFixed(1) + " KB",
                    type: file.type || "application/pdf",
                    path: up.path,
                    url: up.publicUrl,
                  };
                  (function (tgt, pl) {
                    setF(function (prev) { var n = Object.assign({}, prev); n[tgt] = pl; return n; });
                  })(target, payload);
                  taken[target] = true;
                  setDirty(true);
                } catch (err) {
                  alert((lang === "es" ? "Error al subir archivo: " : "Upload failed: ") + (err && err.message ? err.message : String(err)));
                }
              }
            };
            return (
              <div
                onDragOver={function (e) { e.preventDefault(); if (!docDragOver) setDocDragOver(true); }}
                onDragEnter={function (e) { e.preventDefault(); setDocDragOver(true); }}
                onDragLeave={function (e) {
                  /* Only clear when leaving the zone itself, not when
                     hovering over an inner child (relatedTarget is
                     null when leaving the window entirely). */
                  if (!e.currentTarget.contains(e.relatedTarget)) setDocDragOver(false);
                }}
                onDrop={handleDocDrop}
                style={{
                  margin: "10px 20px 0",
                  padding: "12px 14px",
                  border: "2px dashed " + (docDragOver ? "#10B981" : "var(--color-border)"),
                  borderRadius: 8,
                  background: docDragOver ? "rgba(16,185,129,0.08)" : "var(--color-bg)",
                  textAlign: "center",
                  transition: "background 0.15s, border-color 0.15s",
                  cursor: "copy",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: docDragOver ? "#047857" : "var(--color-text-secondary)" }}>{"⬇ "}{t.dropZoneText}</div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{t.dropZoneHint}</div>
              </div>
            );
          })()}
          <div style={{ padding: "10px 20px 0", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#2A7A6F", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.docsSection}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { fieldKey: "xeroInvoiceFile", label: t.xeroInvoicePdf, slot: "xero" },
                { fieldKey: "bolFile",         label: t.bolPdf,         slot: "bol"  },
              ].map(function (slot) {
                var current = f[slot.fieldKey];
                var openHref = current ? (current.url || current.data || "") : "";
                return (
                  <div key={slot.fieldKey} style={{ border: "1px solid var(--color-border)", borderRadius: 8, padding: 10, background: "var(--color-bg)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{slot.label}</div>
                    {current && (current.url || current.data) ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#047857", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={current.name}>✓ {current.name}</span>
                        {openHref && <a href={openHref} target="_blank" rel="noopener noreferrer" style={{ padding: "4px 8px", fontSize: 10, color: "var(--color-text-secondary)", border: "1px solid var(--color-border)", borderRadius: 4, textDecoration: "none" }}>↗</a>}
                        <button type="button" onClick={function () {
                          /* Best-effort delete from Storage when the
                             field carries a path; clear the slot. */
                          if (current.path) { try { removeFromStorage(SL_DOCS_BUCKET, current.path); } catch (e) {} }
                          setF(function (prev) { var n = Object.assign({}, prev); n[slot.fieldKey] = null; return n; });
                          setDirty(true);
                        }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>× {t.removeFile}</button>
                      </div>
                    ) : (
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", border: "1px dashed var(--color-border)", borderRadius: 6, cursor: "pointer", fontSize: 11, color: "#2A7A6F", fontWeight: 600 }}>
                        ⬆ {t.uploadPdf}
                        <input
                          type="file"
                          accept=".pdf,application/pdf"
                          style={{ display: "none" }}
                          onChange={async function (e) {
                            var file = e.target.files && e.target.files[0];
                            e.target.value = "";
                            if (!file) return;
                            try {
                              /* Path keys: sweetleaf/{invoiceNumber|id|tmp-ts}/{slot}-{ts}.{ext} */
                              var stem = slSafeSeg(f.invoiceNumber || f.id || ("tmp-" + Date.now()));
                              var ext = slExtFromFile(file);
                              var path = "sweetleaf/" + stem + "/" + slot.slot + "-" + Date.now() + "." + ext;
                              var up = await uploadFileToStorage(SL_DOCS_BUCKET, path, file);
                              var payload = {
                                name: file.name,
                                size: (file.size / 1024).toFixed(1) + " KB",
                                type: file.type || "application/pdf",
                                path: up.path,
                                url: up.publicUrl,
                              };
                              setF(function (prev) { var n = Object.assign({}, prev); n[slot.fieldKey] = payload; return n; });
                              setDirty(true);
                            } catch (err) {
                              alert((lang === "es" ? "Error al subir archivo: " : "Upload failed: ") + (err && err.message ? err.message : String(err)));
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SUMMARY — Total Due = Gross − Commission − AdvFinFee − Fees + Credits ± Transfers. */}
          <div style={{ padding: "10px 20px 12px", borderTop: "1px solid var(--color-border)" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: 320, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(totalAmount)}</span></div>
                {commissionAmount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {lang === "es" ? "Comisión SweetLeaf" : "SweetLeaf Commission"}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(commissionAmount)})</span></div>}
                {advanceFinancingFee > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {t.advanceFinancingFee}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(advanceFinancingFee)})</span></div>}
                {totalFees > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>− {t.fees}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(totalFees)})</span></div>}
                {totalCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#059669" }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totalCredits)}</span></div>}
                {totalTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: totalTransfers >= 0 ? "#059669" : "#DC2626" }}><span>{totalTransfers >= 0 ? "+" : "−"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{totalTransfers >= 0 ? fmtMoney(totalTransfers) : "(" + fmtMoney(-totalTransfers) + ")"}</span></div>}
                <div style={{ borderTop: "2px solid #2A7A6F", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 700, color: "#2A7A6F" }}><span>{t.totalDue}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(due)}</span></div>
              </div>
            </div>
          </div>

          {/* PAYMENTS HISTORY */}
          <div style={{ padding: "0 20px 12px" }}>
            <PaymentsSection payments={f.payments} total={due} t={t} />
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {!isNew && !showDel && <button onClick={function() { setShowDel(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}
            {showDel && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmDel}</span><button onClick={function() { onDelete(invoice.id); }} style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11 }}>{t.yes}</button><button onClick={function() { setShowDel(false); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>{t.no}</button></div>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={tryCancel} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.cancel}</button>
            <button onClick={handleSave} disabled={hasLotMismatch} title={hasLotMismatch ? (lang === "es" ? "Corrige el descuadre de pallets antes de guardar" : "Fix the pallet mismatch before saving") : undefined} style={{ padding: "7px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: hasLotMismatch ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, opacity: hasLotMismatch ? 0.5 : 1 }}>{t.save}</button>
          </div>
        </div>

        {showExitConfirm && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}><div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 300, textAlign: "center" }}><p style={{ fontSize: 13, marginBottom: 14 }}>{t.confirmExit}</p><div style={{ display: "flex", gap: 8, justifyContent: "center" }}><button onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button><button onClick={function() { setShowExitConfirm(false); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button></div></div></div>}
    </>
  );
  if (embedded) return body;
  return (
    <DraggableModal title={(isNew ? t.add : t.edit) + (f.puNumber ? "  ·  PU# " + f.puNumber : "")} onClose={tryCancel} initialWidth={820} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {body}
    </DraggableModal>
  );
}

/* ======================== BATCH DEPOSIT MODAL ======================== */
function calcInvoiceTotal(inv) {
  /* SweetLeaf total:
       Net = gross − commission − advanceFinancingFee − fees + credits ± transfers
       commission = SUM(cases × slCommissionPerCase) per line (excl. losses)
       advanceFinancingFee = flat invoice-level deduction
       fees, credits, transfers = same semantics as InvoiceRegistry. */
  var total = 0, commission = 0;
  (inv.lines || []).forEach(function(l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var cases = parseInt(l.cases, 10) || 0;
    total += cases * (parseFloat(l.salePrice) || 0);
    commission += cases * (parseFloat(l.slCommissionPerCase) || 0);
  });
  var advFee = Math.abs(parseFloat(inv.advanceFinancingFee) || 0);
  var tFees = 0;
  (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0;
  (inv.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  var tTransfers = 0;
  (inv.balanceTransfers || []).forEach(function(t) {
    var amt = parseFloat(t.amount) || 0;
    if (t.type === "credit") tTransfers += amt;
    else if (t.type === "debit") tTransfers -= amt;
  });
  return total - commission - advFee - tFees + tCredits + tTransfers;
}

/* Per-line commission $ for an invoice line. 0 for excluded statuses
   so it matches the calc above. */
function invLineCommissionAmount(line, _invDefaultRate) {
  /* SweetLeaf: commission is per-case dollars on each line; the
     second argument is kept for compatibility with the original
     signature but unused. */
  if (!line) return 0;
  if (line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer") return 0;
  var cases = parseInt(line.cases, 10) || 0;
  return cases * (parseFloat(line.slCommissionPerCase) || 0);
}

/* (legacy invLineCommissionAmount removed — see slCommissionPerCase model above) */

function buildInvRef(inv) {
  var parts = [];
  if (inv.puNumber) parts.push("PU " + inv.puNumber);
  if (inv.lot) parts.push("LOT " + inv.lot);
  if (inv.clientRef) parts.push(inv.clientRef);
  return parts.join(" · ");
}

/* `existingPayment` (optional): when set, the modal is in EDIT mode for
   one already-recorded payment instead of CREATE mode for a fresh batch.
   `invoices` must then be just the specific invoices that payment's
   allocations reference (the caller — SLPaymentsRegistry.js — resolves
   those before opening the modal; this component doesn't fetch on its
   own). Edit mode adds the payment's own existing allocation amount
   back onto each row's `due` so re-saving the same amount doesn't look
   like an over-application, seeds date/method/reference/notes/concept
   from the existing record, and `onConfirm`'s payload carries `id` so
   the caller knows to update rather than create. Scope is deliberately
   narrow: editing adjusts amount/concept on the SAME set of invoices
   already allocated — it doesn't support adding/removing invoices from
   an existing payment (not asked for, and it would need to reconcile
   stale payments[] entries on invoices that dropped out). */
export function BatchDepositModal({ invoices, t, lang, onConfirm, onCancel, existingPayment }) {
  var allocByInvId = useMemo(function () {
    var m = {};
    if (existingPayment) (existingPayment.allocations || []).forEach(function (a) { m[a.invoiceId] = a; });
    return m;
  }, [existingPayment]);

  var initRows = useMemo(function() {
    return invoices.map(function(inv) {
      var totalDue = calcInvoiceTotal(inv);
      var paid = parseFloat(inv.amountPaid) || 0;
      var existingAlloc = allocByInvId[inv.id];
      var existingAmt = existingAlloc ? (parseFloat(existingAlloc.amount) || 0) : 0;
      /* Add the payment-being-edited's own contribution back onto due —
         otherwise re-confirming the same amount reads as over-applying
         against a balance that already accounts for it. */
      var due = +(totalDue - paid + existingAmt).toFixed(2);
      var amount = existingAlloc ? existingAmt : (due > 0 ? due : 0);
      return { inv: inv, totalDue: totalDue, paid: paid, due: due, amount: amount, concept: existingAlloc ? (existingAlloc.concept || "") : "" };
    });
  }, [invoices, allocByInvId]);

  var [rows, setRows] = useState(initRows);
  var [depositDate, setDepositDate] = useState(existingPayment ? (existingPayment.paymentDate || todayISO()) : todayISO());
  var [depositMethod, setDepositMethod] = useState(existingPayment ? (existingPayment.paymentMethod || "wire") : "wire");
  var [reference, setReference] = useState(existingPayment ? (existingPayment.reference || "") : "");
  var [notes, setNotes] = useState(existingPayment ? (existingPayment.notes || "") : "");
  var [error, setError] = useState("");

  var openRows = useMemo(function() { return rows.filter(function(r) { return r.due > 0.01; }); }, [rows]);
  var total = useMemo(function() { return openRows.reduce(function(s, r) { return s + (parseFloat(r.amount) || 0); }, 0); }, [openRows]);

  /* Store the raw string the user is typing as-is — no parseFloat
     while typing. 2026-09-18, Arturo: "los campos para ingresar
     monto a pagar estan raros, no me dejan ingresar la cantidad
     correctamente". Root cause: this used to run every keystroke
     through parseFloat() immediately (dropping a trailing "." the
     instant it was typed, so "720." collapsed back to "720" and you
     could never get to "720.50"), AND the displayed value re-ran
     toLocaleString(...,{minimumFractionDigits:2}) on every render —
     forcing "7" to redisplay as "7.00" mid-keystroke and jumping the
     cursor. Same raw-string + normalize-on-blur pattern already used
     by PaymentRegistry.js's amount input (setAmtStr/normalizeAmtStr)
     — only parseFloat's the value at blur/save time, never while
     the field has focus. */
  var setRowAmount = function(invId, raw) {
    setRows(function(prev) { return prev.map(function(r) { return r.inv.id === invId ? { ...r, amount: raw } : r; }); });
    setError("");
  };
  var setRowConcept = function(invId, val) {
    setRows(function(prev) { return prev.map(function(r) { return r.inv.id === invId ? { ...r, concept: val } : r; }); });
  };

  /* Sortable columns (Arturo: "al hacer un pago/deposito a facturas
     me abre una ventana y muestra todo desordenado, necesito poder
     ordenar el contenido por cualquier criterio de cualquier
     columna") — same click-header pattern already used elsewhere in
     this file (EmailFactoringModal, main SL invoice table). Local to
     this modal, doesn't persist between opens. */
  var [sortCol, setSortCol] = useState("dueDate");
  var [sortDir, setSortDir] = useState("asc");
  var toggleSort = function (col) {
    if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
    else { setSortCol(col); setSortDir("asc"); }
  };
  var sortArrow = function (col) { return sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""; };
  var sortedOpenRows = useMemo(function () {
    var arr = openRows.slice();
    arr.sort(function (a, b) {
      var va, vb;
      switch (sortCol) {
        case "client": va = (resolveClientName(a.inv.clientId) || a.inv.clientName || ""); vb = (resolveClientName(b.inv.clientId) || b.inv.clientName || ""); break;
        case "ref": va = buildInvRef(a.inv); vb = buildInvRef(b.inv); break;
        case "dueDate": va = a.inv.dueDate || ""; vb = b.inv.dueDate || ""; break;
        case "due": va = parseFloat(a.due) || 0; vb = parseFloat(b.due) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "amount": va = parseFloat(a.amount) || 0; vb = parseFloat(b.amount) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "concept": va = a.concept || ""; vb = b.concept || ""; break;
        default: va = ""; vb = "";
      }
      var cmp = String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [openRows, sortCol, sortDir]);

  var validate = function() {
    if (!depositDate) return lang === "es" ? "Selecciona una fecha" : "Select a date";
    for (var i = 0; i < openRows.length; i++) {
      var r = openRows[i];
      var amt = parseFloat(r.amount) || 0;
      if (amt < 0) return t.overApplyInv + " (" + r.inv.id + ")";
      if (amt > r.due + 0.01) return t.overApplyInv + " (" + r.inv.id + ")";
    }
    if (total <= 0) return lang === "es" ? "Captura al menos un monto > 0" : "Enter at least one amount > 0";
    return null;
  };

  var handleConfirm = function() {
    var err = validate();
    if (err) { setError(err); return; }
    /* Build the saved allocations array from the SORTED (displayed)
       rows, not the raw invoice order — otherwise the order Arturo
       arranged on screen (click-to-sort, added this same session)
       never survives into the saved payment, and the printed
       Payment Confirmation PDF (which just walks payment.allocations
       in array order) comes out in a different, unrelated order.
       2026-09-18, Arturo: "el pdf que se imprime debe mantener el
       orden que se tiene en la ventana de pagos". */
    var allocations = sortedOpenRows
      .filter(function(r) { return (parseFloat(r.amount) || 0) > 0; })
      .map(function(r) { return { invoiceId: r.inv.id, puNumber: r.inv.puNumber, lot: r.inv.lot, clientName: r.inv.clientName || "", amount: +(parseFloat(r.amount) || 0).toFixed(2), concept: r.concept || "" }; });
    onConfirm({
      id: existingPayment ? existingPayment.id : undefined,
      paymentDate: depositDate,
      paymentMethod: depositMethod,
      reference: reference,
      notes: notes,
      allocations: allocations,
    });
  };

  var methodLabel = function(m) { return t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m; };

  return (
    <DraggableModal title={existingPayment ? (t.editPaymentTitle || t.batchDepositTitle) : t.batchDepositTitle} onClose={onCancel} initialWidth={920} initialHeight={680} zIndex={10005}>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositDate} *</label>
              <input type="date" value={depositDate} onChange={function(e) { setDepositDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositMethod}</label>
              <SearchSelect value={depositMethod} onChange={v => setDepositMethod(v)} options={PAYMENT_METHODS.map(function(m) { return { value: m, label: methodLabel(m) }; })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositReference}</label>
              <input type="text" value={reference} onChange={function(e) { setReference(e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="e.g. WIRE-2026-0123" />
            </div>
          </div>

          {openRows.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, background: "var(--color-bg)", borderRadius: 10, border: "1px dashed var(--color-border)" }}>{t.noOpenInvoices}</div>
          ) : (
            <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
                  <th onClick={function () { toggleSort("client"); }} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.client}{sortArrow("client")}</th>
                  <th onClick={function () { toggleSort("ref"); }} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.refColumn}{sortArrow("ref")}</th>
                  <th onClick={function () { toggleSort("dueDate"); }} style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.dueDate}{sortArrow("dueDate")}</th>
                  <th onClick={function () { toggleSort("due"); }} style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.dueAmount}{sortArrow("due")}</th>
                  <th onClick={function () { toggleSort("amount"); }} style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.depositAmount}{sortArrow("amount")}</th>
                  <th onClick={function () { toggleSort("concept"); }} style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" }}>{t.concept}{sortArrow("concept")}</th>
                </tr></thead>
                <tbody>{sortedOpenRows.map(function(r) {
                  var inv = r.inv;
                  return <tr key={inv.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "10px", fontSize: 12, fontWeight: 600 }}>{resolveClientName(inv.clientId) || inv.clientName || "—"}</td>
                    <td style={{ padding: "10px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{buildInvRef(inv)}</td>
                    <td style={{ padding: "10px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(inv.dueDate)}</td>
                    <td style={{ padding: "10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "#c65d00" }}>{fmtMoney(r.due)}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={r.amount === "" || r.amount == null ? "" : String(r.amount)}
                        onChange={function(e) {
                          /* Allow free typing; strip commas but keep
                             the raw digits/decimal exactly as typed
                             (never reformat mid-edit) until blur. */
                          var raw = String(e.target.value || "").replace(/[^0-9.]/g, "");
                          setRowAmount(inv.id, raw);
                        }}
                        onBlur={function(e) {
                          /* Normalize on blur — round to 2 decimals
                             so the display always reads "720.00" not
                             "720". Empty stays empty. */
                          var raw = String(e.target.value || "").replace(/,/g, "");
                          if (raw.trim() === "") { setRowAmount(inv.id, ""); return; }
                          var n = parseFloat(raw);
                          if (!isNaN(n)) setRowAmount(inv.id, n.toFixed(2));
                        }}
                        style={{ ...inp, width: 130, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: "auto" }}
                      />
                    </td>
                    <td style={{ padding: "6px 10px", width: 130 }}>
                      <SearchSelect value={r.concept || ""} onChange={function (v) { setRowConcept(inv.id, v); }} options={[{ value: "", label: t.conceptDeposit }, { value: "advance", label: t.conceptAdvance }]} />
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.notes}</label>
              <textarea value={notes} onChange={function(e) { setNotes(e.target.value); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, alignSelf: "end" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 4 }}>{t.totalDeposit}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "#059669" }}>{fmtMoney(total)}</div>
            </div>
          </div>

          {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, color: "#DC2626", fontSize: 12, fontWeight: 600 }}>{error}</div>}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
          <button onClick={handleConfirm} disabled={openRows.length === 0 || total <= 0} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: (openRows.length === 0 || total <= 0) ? "var(--color-border)" : "#059669", color: "#fff", cursor: (openRows.length === 0 || total <= 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{t.recordDeposits}</button>
        </div>
    </DraggableModal>
  );
}

/* ======================== EMAIL FACTORING MODAL ========================
   Weekly AGROCAP factoring email composer. Lets the user pick which
   SL invoices to include, attach one Xero invoice PDF per invoice,
   and send the HTML body that lists the loads + 85% advance per row.
   Recipients are seeded with Ben Wright + Brian Arena (Four Seasons
   factoring contacts); the user can uncheck them and/or add more
   emails in the free-form box below. */
function overdueGrossAmount(inv) {
  var total = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
  });
  return total;
}
function overdueDate(d) {
  if (!d) return "—";
  var p = String(d).slice(0, 10).split("-");
  if (p.length !== 3) return d;
  return p[1] + "/" + p[2] + "/" + p[0].slice(2);
}
function overdueMoney(n) { return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function buildOutstandingReportHtml(rows) {
  var esc = function (v) { return String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
  var today = new Date();
  var reportDate = (today.getMonth() + 1 < 10 ? "0" : "") + (today.getMonth() + 1) + "/" + (today.getDate() < 10 ? "0" : "") + today.getDate() + "/" + String(today.getFullYear()).slice(2);
  var grand = rows.reduce(function (s, inv) { return s + overdueGrossAmount(inv); }, 0);
  var body = rows.map(function (inv) {
    return '<tr><td>' + esc(overdueDate(inv.date)) + '</td><td>' + esc(overdueDate(inv.dueDate)) + '</td><td>' + esc(inv.gfiPuNumber || inv.puNumber || "—") + '</td><td>' + esc(inv.clientRef || inv.poNumber || "—") + '</td><td>' + esc(inv.invoiceNumber || inv.id || "—") + '</td><td class="r">' + esc(overdueMoney(overdueGrossAmount(inv))) + '</td></tr>';
  }).join("");
  return '<!doctype html><html><head><meta charset="utf-8"><title>Outstanding Invoices Report</title><style>'
    + '@page{size:letter;margin:0.6in}'
    + 'body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;padding:0;font-size:12px}'
    + '.page{padding:40px 48px}'
    + '.hdr{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #2A7A6F;padding-bottom:12px}'
    + '.hdr img{height:95px;width:auto;display:block}'
    + '.ttl{font-size:22px;letter-spacing:2px;color:#2A7A6F;font-weight:300;text-align:right}'
    + '.sent{font-size:11px;color:#5BBCB0;text-align:right;margin-top:4px}'
    + 'table{width:100%;border-collapse:collapse;margin-top:14px;table-layout:fixed}'
    + 'th,td{vertical-align:middle}'
    + 'th{background:#2A7A6F;color:#fff;padding:7px 8px;font-size:10px;text-transform:uppercase;text-align:left;white-space:nowrap}'
    + 'td{padding:7px 8px;border-bottom:1px solid #d7ece8;font-size:12px}'
    + 'tr:nth-child(even) td{background:#f7fbfa}'
    + '.r{text-align:right}'
    + '.tot td{background:#E8F5F3;font-weight:700;border-top:2px solid #2A7A6F}'
    + '</style></head><body><div class="page">'
    + '<div class="hdr"><img src="' + SWEETLEAF_LOGO_B64 + '"/><div><div class="ttl">OUTSTANDING INVOICES REPORT</div><div class="sent">Report date: ' + reportDate + '</div></div></div>'
    + '<table><thead><tr><th style="width:15%">Invoice Date</th><th style="width:15%">Due Date</th><th style="width:14%">PU#</th><th style="width:16%">PO#</th><th style="width:16%">Invoice</th><th class="r" style="width:24%">Amount</th></tr></thead>'
    + '<tbody>' + body + '<tr class="tot"><td colspan="5" class="r">Total</td><td class="r">' + esc(overdueMoney(grand)) + '</td></tr></tbody></table>'
    + '</div></body></html>';
}

function OverdueNoticeModal({ invoices, lang, onClose, onSent }) {
  var today = new Date().toISOString().slice(0, 10);
  var puOf = function (inv) { return String(inv.gfiPuNumber || inv.puNumber || ""); };
  var openRows = (invoices || []).slice().sort(function (a, b) { return puOf(a).localeCompare(puOf(b), undefined, { numeric: true }); });
  var isOverdue = function (inv) { return !!inv.dueDate && inv.dueDate < today; };
  var [selected, setSelected] = useState(function () {
    var s = {};
    openRows.forEach(function (inv) { s[inv.id] = true; });
    return s;
  });
  var [step, setStep] = useState("select");
  var [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  var [reportBusy, setReportBusy] = useState(false);
  var picked = openRows.filter(function (inv) { return selected[inv.id]; });
  var downloadReport = async function () {
    setReportBusy(true);
    try {
      var blob = await htmlToPdfBlob(buildOutstandingReportHtml(picked));
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = safeFilename("Outstanding Invoices Report " + new Date().toISOString().slice(0, 10)) + ".pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF error: " + (e.message || String(e)));
    } finally {
      setReportBusy(false);
    }
  };
  var [pastDueList, setPastDueList] = useState(function () { return getSLSettings().pastDueRecipients || []; });
  useEffect(function () {
    var cancelled = false;
    getFreshSlSettings().then(function (s) { if (!cancelled) setPastDueList(s.pastDueRecipients || []); });
    return function () { cancelled = true; };
  }, []);
  var recipients = (pastDueList || []).filter(function (r) { return r && r.email; })
    .map(function (r) { return { name: r.name || "", email: r.email, checked: true }; });
  var OVERDUE_FROM = "sweetleaf-alerts@gfinexus.com";
  var account = { id: "sl-alerts", name: OVERDUE_FROM, email: getSLSettings().emailAccount || "a.gonzalez@sweetleafproduce.com", maskedFrom: OVERDUE_FROM, signature: "" };
  var subject = "Past Due Invoices — " + picked.length + (picked.length === 1 ? " invoice" : " invoices");
  var grandTotal = picked.reduce(function (s, inv) { return s + overdueGrossAmount(inv); }, 0);
  var esc = function (v) { return String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
  var th = 'style="padding:6px 8px;text-align:left;background:#f3f4f6;border-bottom:2px solid #d1d5db;font-size:12px;"';
  var td = 'style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;"';
  var tdR = 'style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;"';
  var tableHtml = '<table style="border-collapse:collapse;width:100%;margin:12px 0;"><thead><tr>'
    + '<th ' + th + '>Invoice Date</th><th ' + th + '>Due Date</th><th ' + th + '>PU#</th><th ' + th + '>PO#</th><th ' + th + '>Invoice</th><th ' + th.replace('text-align:left', 'text-align:right') + '>Amount</th>'
    + '</tr></thead><tbody>'
    + picked.map(function (inv) {
        return '<tr><td ' + td + '>' + esc(overdueDate(inv.date)) + '</td><td ' + td + '>' + esc(overdueDate(inv.dueDate)) + '</td><td ' + td + '>' + esc(inv.gfiPuNumber || inv.puNumber || "—") + '</td><td ' + td + '>' + esc(inv.clientRef || inv.poNumber || "—") + '</td><td ' + td + '>' + esc(inv.invoiceNumber || inv.id || "—") + '</td><td ' + tdR + '>' + esc(overdueMoney(overdueGrossAmount(inv))) + '</td></tr>';
      }).join("")
    + '<tr><td colspan="5" style="padding:8px;font-weight:700;font-size:12px;text-align:right;">Total</td><td style="padding:8px;font-weight:700;font-size:12px;text-align:right;">' + esc(overdueMoney(grandTotal)) + '</td></tr>'
    + '</tbody></table>';
  tableHtml += '<p style="font-size:13px;">Please confirm the payment status at your earliest convenience.</p>'
    + '<p style="font-size:13px;margin:16px 0 0;">Best regards,<br>SweetLeaf Produce</p>';
  var body = "Dear team,\n\nThe following invoices are past due. Please see the detail below.\n";

  if (step === "email") {
    return (
      <EmailDialog
        lang="en"
        title={"Past Due Notice"}
        recipients={recipients}
        docOptions={[]}
        hideDocs={true}
        defaultSubject={subject}
        defaultBody={body}
        insertBeforeSignatureHtml={tableHtml}
        alwaysBuildAttachments={true}
        buildAttachments={async function () {
          var blob = await htmlToPdfBlob(buildOutstandingReportHtml(picked));
          return [{ filename: safeFilename("Outstanding Invoices Report " + new Date().toISOString().slice(0, 10)) + ".pdf", blob: blob, mimeType: "application/pdf" }];
        }}
        topContent={<div style={{ marginBottom: 10, padding: "8px 10px", border: "1px solid var(--color-border)", borderRadius: 6, background: "#fff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: tableHtml }} />}
        fromEmail={account.email}
        emailAccounts={[account]}
        refreshAccountsBeforeSend={function () { return Promise.resolve([account]); }}
        signatures={[]}
        postProcessHtmlBody={function (html) { return appendSlDisclosure(html, getSLSettings()); }}
        onClose={function () { setStep("select"); }}
        onSent={function () { onSent(picked); }}
      />
    );
  }

  return (
    <>
    <DraggableModal title={lang === "es" ? "Aviso de facturas vencidas" : "Past due notice"} onClose={onClose} initialWidth={Math.min(720, Math.floor(window.innerWidth * 0.7))} initialHeight={Math.min(560, window.innerHeight - 60)} zIndex={10005}>
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 10 }}>
          {lang === "es" ? "Se incluyen PU#, PO#, Invoice y monto total (sin deducciones ni pagos efectuados)." : "Includes PU#, PO#, Invoice and total amount (no deductions or payments applied)."}
        </div>
        {openRows.length === 0 && <div style={{ fontSize: 12, fontStyle: "italic" }}>{lang === "es" ? "No hay facturas abiertas." : "No open invoices."}</div>}
        {openRows.map(function (inv) {
          return (
            <label key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 4px", borderBottom: "1px solid var(--color-border)", fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={!!selected[inv.id]} onChange={function () { setSelected(function (prev) { var n = Object.assign({}, prev); n[inv.id] = !prev[inv.id]; return n; }); }} />
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, minWidth: 90 }}>{"PU# " + (inv.gfiPuNumber || inv.puNumber || "—")}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", minWidth: 110 }}>{"PO# " + (inv.clientRef || inv.poNumber || "—")}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{"Invoice " + (inv.invoiceNumber || inv.id || "—")}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{(lang === "es" ? "Factura " : "Inv. date ") + overdueDate(inv.date)}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{(lang === "es" ? "Vence " : "Due ") + overdueDate(inv.dueDate)}</span>
              <span style={{ marginLeft: "auto", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{overdueMoney(overdueGrossAmount(inv))}</span>
              {isOverdue(inv) && <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "#B91C1C" }}>{lang === "es" ? "Vencida" : "Overdue"}</span>}
            </label>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "10px 18px", borderTop: "1px solid var(--color-border)" }}>
        <span style={{ marginRight: "auto", fontSize: 12, fontWeight: 700 }}>{(lang === "es" ? "Total: " : "Total: ") + overdueMoney(picked.reduce(function (s, inv) { return s + overdueGrossAmount(inv); }, 0))}</span>
        <button type="button" disabled={picked.length === 0} onClick={function () { setReportPreviewOpen(true); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: picked.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: picked.length === 0 ? 0.5 : 1 }}>{lang === "es" ? "Vista previa del reporte" : "Report preview"}</button>
        <button type="button" onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
        <button type="button" disabled={picked.length === 0} onClick={function () { setStep("email"); }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: picked.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: picked.length === 0 ? 0.5 : 1 }}>{lang === "es" ? "Continuar al correo" : "Continue to email"}</button>
      </div>
    </DraggableModal>
    {reportPreviewOpen && (
      <DraggableModal title={lang === "es" ? "Vista previa — Outstanding Invoices Report" : "Preview — Outstanding Invoices Report"} onClose={function () { setReportPreviewOpen(false); }} initialWidth={Math.min(860, Math.floor(window.innerWidth * 0.8))} initialHeight={Math.min(760, window.innerHeight - 60)} zIndex={10006}>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 12px", borderBottom: "1px solid var(--color-border)" }}>
          <button type="button" disabled={reportBusy} onClick={downloadReport} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #2A7A6F", background: "#2A7A6F", color: "#fff", cursor: reportBusy ? "wait" : "pointer", fontSize: 12, fontWeight: 700 }}>{reportBusy ? "…" : "💾 Download"}</button>
        </div>
        <iframe title="outstanding-report-preview" srcDoc={buildOutstandingReportHtml(picked)} style={{ flex: 1, width: "100%", minHeight: 520, border: "none", background: "#fff" }} />
      </DraggableModal>
    )}
    </>
  );
}

function EmailFactoringModal({ invoices, lang, t, onClose, onSent, onMarkSent }) {
  var [selected, setSelected] = useState({});
  var [xeroFiles, setXeroFiles] = useState({});
  /* Separate BOL fallback uploads — parallels xeroFiles. Used when
     the invoice doesn't already carry inv.bolFile. */
  var [bolFiles, setBolFiles] = useState({});
  /* Filters for the available-invoices table — text search across
     INV#/BOL/PO#, exact status match, week, and a date range.
     Filters never drop already-selected rows so the user can
     refine the list without losing their picks. */
  var [filterText, setFilterText] = useState("");
  var [filterStatus, setFilterStatus] = useState("");
  var [filterWeek, setFilterWeek] = useState("");
  var [filterFrom, setFilterFrom] = useState("");
  var [filterTo, setFilterTo] = useState("");
  /* 2026-09-15 (Arturo): "no se puede ver si las facturas ya tienen
     emails enviados y si es asi deberia filtrarlos por default aunque
     despues yo pueda quitar el filtro manualmente" — hide invoices
     already marked factoringEmailSentAt by default (see doSend below,
     which stamps it after a successful send), with a checkbox to
     bring them back into view manually. */
  var [hideSent, setHideSent] = useState(true);
  /* Preview-the-email modal — holds the prebuilt HTML body (with
     signature) the moment the user clicks Preview. Rendered via
     dangerouslySetInnerHTML inside a DraggableModal so styled
     content displays exactly as AGROCAP will receive it. */
  var [previewHtml, setPreviewHtml] = useState(null);
  /* CC the SL outbound account on every send so Arturo always has
     a copy in his inbox. Off by default would be a regression — keep
     on. */
  var [sendCopy, setSendCopy] = useState(true);
  /* Local overlay for in-session document uploads. When the user
     uploads Xero/BOL from inside the modal we persist to Supabase
     immediately AND stash the payload here so the row turns green
     without waiting for the parent to refetch.
     Shape: { [invoiceId]: { xeroInvoiceFile?, bolFile? } }. */
  var [localStored, setLocalStored] = useState({});

  /* Effective stored payload — overlay wins, else the invoice's
     own field. Used by both the row renderer and the missing-docs
     guard so they agree on what's present. */
  function effectiveStored(inv, key) {
    var overlay = localStored[inv.id] && localStored[inv.id][key];
    return overlay || (inv && inv[key]) || null;
  }
  function isPresent(payload) {
    return !!(payload && (payload.url || payload.path || payload.data));
  }

  /* Upload + persist a document onto the invoice. Goes to the same
     SL_DOCS_BUCKET the form uses so the file shows up identically
     on the invoice detail view. Saves the merged invoice back to
     Supabase right after the upload succeeds. */
  async function persistInvoiceDoc(inv, fieldKey, file) {
    var stem = slSafeSeg(inv.invoiceNumber || inv.id || ("tmp-" + Date.now()));
    var ext = slExtFromFile(file);
    var slot = fieldKey === "xeroInvoiceFile" ? "xero" : "bol";
    var path = "sweetleaf/" + stem + "/" + slot + "-" + Date.now() + "." + ext;
    var up = await uploadFileToStorage(SL_DOCS_BUCKET, path, file);
    var payload = {
      name: file.name,
      size: (file.size / 1024).toFixed(1) + " KB",
      type: file.type || "application/pdf",
      path: up.path,
      url: up.publicUrl,
    };
    /* Merge any sibling overlay so we don't lose the other doc the
       user uploaded earlier this session. */
    var existingOverlay = localStored[inv.id] || {};
    var merged = Object.assign({}, inv, existingOverlay, { [fieldKey]: payload });
    await saveToSupabase("sweetleaf_invoices", merged);
    setLocalStored(function (prev) {
      var n = Object.assign({}, prev);
      n[inv.id] = Object.assign({}, n[inv.id] || {}, { [fieldKey]: payload });
      return n;
    });
  }
  /* Recipients come from sweetleaf_settings.factoringRecipients
     (managed in the SweetLeaf Settings panel — AGROCAP's real
     contacts). 2026-09-24 (Arturo): "el envio de emails de factiraje
     tiene un errror... el email se envio pero al cliente no a agrocap"
     → confirmed with a screenshot: the email went to
     benw@fsproduce.com/briana@fsproduce.com — real Four Seasons
     Produce (the CLIENT) contacts, not AGROCAP — and, critically:
     "ese fue el que envie ayer... lo envie de mi telefono" (the one
     sent from his phone) vs "el que envie hoy si llego a agrocap"
     (sent from a device with fresh settings). Same staleness class of
     bug already fixed elsewhere in this file for Passing/Factoring's
     FROM address (getFreshSlSettings) — `getSLSettings()` here was
     synchronous, LOCALSTORAGE-ONLY, so a phone/device whose local
     cache had never picked up the real AGROCAP `factoringRecipients`
     (e.g. never opened SweetLeaf Settings on that device) fell through
     to the hard-coded "Ben + Brian" fallback below — which, per
     Arturo's confirmation, are NOT AgroCap, they're Four Seasons'
     contacts. Two fixes: (1) re-fetch fresh from Supabase on mount,
     same pattern as doSend's fromEmail resolution, so a stale local
     cache can't silently substitute wrong recipients; (2) the
     hard-coded fallback is REMOVED entirely — silently mailing a
     factoring-advance email full of pricing/status data to the wrong
     external company is far worse than an empty list forcing Arturo to
     notice and configure Settings first. */
  var [recipients, setRecipients] = useState(function () {
    var stored = (getSLSettings().factoringRecipients || []).filter(function (r) { return r && r.email; });
    return stored.map(function (r) { return { name: r.name || "", email: r.email, checked: true }; });
  });
  useEffect(function () {
    var alive = true;
    getFreshSlSettings().then(function (fresh) {
      if (!alive) return;
      var stored = (fresh.factoringRecipients || []).filter(function (r) { return r && r.email; });
      setRecipients(stored.map(function (r) { return { name: r.name || "", email: r.email, checked: true }; }));
    }).catch(function (e) { console.warn("[SL Factoring] could not refresh recipients:", e && e.message); });
    return function () { alive = false; };
  }, []);
  var [extraEmails, setExtraEmails] = useState("");
  var [sending, setSending] = useState(false);
  var [error, setError] = useState("");

  /* Only invoices with at least one line carrying cases > 0. Sorted
     most-recent first by date so the user sees the current week's
     invoices at the top of the list. */
  var seasonsList = useMemo(function () { return loadLS("gfi-seasons", []); }, []);
  var openSeasonIds = useMemo(function () { return defaultOpenSeasonIds(); }, []);
  var available = useMemo(function () {
    return (invoices || []).filter(function (inv) {
      if (!(inv.lines || []).some(function (l) { return parseInt(l.cases, 10) > 0; })) return false;
      if (openSeasonIds.length > 0) {
        var sid = resolveSeasonId(seasonsList, inv.date, inv.seasonId);
        if (!sid || openSeasonIds.indexOf(String(sid)) < 0) return false;
      }
      return true;
    }).sort(function (a, b) { return (b.date || "").localeCompare(a.date || ""); });
  }, [invoices, seasonsList, openSeasonIds]);

  var toggleSelect = function (id) {
    setSelected(function (prev) { var n = Object.assign({}, prev); n[id] = !n[id]; return n; });
  };
  var selectedInvoices = available.filter(function (inv) { return selected[inv.id]; });

  /* Filtered table rows. Defined AFTER invoiceStatus so the status
     filter can compare against the same labels the table renders.
     Selected invoices intentionally bypass the filter so a refine
     doesn't visually drop the user's picks. */

  function invoiceTotal(inv) {
    var total = 0;
    (inv.lines || []).forEach(function (l) {
      if (l.saleStatus === "dumped" || l.saleStatus === "donated") return;
      total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    });
    return total;
  }
  function invoiceStatus(inv) {
    var hasProtection = (inv.lines || []).some(function (l) {
      var st = String(l.saleStatus || "").toLowerCase().trim();
      return st === "protection" || st === "pas";
    });
    if (hasProtection) return "Protection";
    var hasGood = (inv.lines || []).some(function (l) {
      var st = String(l.saleStatus || "").toLowerCase().trim();
      return st === "good delivery" || st === "gooddelivery";
    });
    if (hasGood) return "Good Delivery";
    return "Pending";
  }

  /* Apply filters to `available`. Selected invoices bypass the
     filter so picks aren't visually dropped on refine. */
  var displayed = useMemo(function () {
    var txt = String(filterText || "").trim().toLowerCase();
    var st = filterStatus || "";
    var wk = filterWeek || "";
    var from = filterFrom || "";
    var to = filterTo || "";
    return available.filter(function (inv) {
      if (selected[inv.id]) return true; /* always keep selected visible */
      if (hideSent && inv.factoringEmailSentAt) return false;
      if (txt) {
        var hay = (String(inv.invoiceNumber || "") + " " + String(inv.gfiPuNumber || "") + " " + String(inv.puNumber || "") + " " + String(inv.clientRef || "")).toLowerCase();
        if (hay.indexOf(txt) < 0) return false;
      }
      if (st && invoiceStatus(inv) !== st) return false;
      if (wk) {
        var w = inv.date ? ("W" + getISOWeekInv(inv.date)) : "";
        if (w !== wk) return false;
      }
      if (from && (inv.date || "") < from) return false;
      if (to && (inv.date || "") > to) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, filterText, filterStatus, filterWeek, filterFrom, filterTo, selected, hideSent]);

  /* Distinct ISO weeks present in `available` for the Week filter
     dropdown. Sorted descending so the most recent week is first. */
  var availableWeeks = useMemo(function () {
    var s = new Set();
    available.forEach(function (inv) { if (inv.date) s.add("W" + getISOWeekInv(inv.date)); });
    return Array.from(s).sort(function (a, b) { return b.localeCompare(a); });
  }, [available]);

  /* 2026-09-15 (Arturo): "al enviar un email de factoring desde sl, no
     me permite ordenar ninguna columna" — this table's headers were
     plain <th>, no click handler, unlike the main SL invoices table
     further down in this file which already has sortCol/sortDir.
     Mirrors that same pattern, scoped locally to this modal. */
  var [sortCol, setSortCol] = useState("date");
  var [sortDir, setSortDir] = useState("desc");
  var toggleSort = function (col) {
    if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
    else { setSortCol(col); setSortDir("asc"); }
  };
  var sortArrow = function (col) { return sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""; };
  var sortedDisplayed = useMemo(function () {
    var arr = displayed.slice();
    arr.sort(function (a, b) {
      var va, vb;
      switch (sortCol) {
        case "gfiPuNumber": va = parseInt(a.gfiPuNumber, 10) || 0; vb = parseInt(b.gfiPuNumber, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "amount": va = invoiceTotal(a); vb = invoiceTotal(b); return sortDir === "asc" ? va - vb : vb - va;
        case "invoiceNumber": va = String(a.invoiceNumber || ""); vb = String(b.invoiceNumber || ""); break;
        case "clientRef": va = String(a.clientRef || ""); vb = String(b.clientRef || ""); break;
        case "status": va = invoiceStatus(a); vb = invoiceStatus(b); break;
        case "xero": va = isPresent(effectiveStored(a, "xeroInvoiceFile")) ? 1 : 0; vb = isPresent(effectiveStored(b, "xeroInvoiceFile")) ? 1 : 0; return sortDir === "asc" ? va - vb : vb - va;
        case "bol": va = isPresent(effectiveStored(a, "bolFile")) ? 1 : 0; vb = isPresent(effectiveStored(b, "bolFile")) ? 1 : 0; return sortDir === "asc" ? va - vb : vb - va;
        case "factoringSent": va = a.factoringEmailSentAt || ""; vb = b.factoringEmailSentAt || ""; break;
        case "date":
        default: va = String(a.date || ""); vb = String(b.date || "");
      }
      var cmp = va.localeCompare(vb);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [displayed, sortCol, sortDir]);

  var grandTotal = selectedInvoices.reduce(function (s, inv) { return s + invoiceTotal(inv); }, 0);
  var advanceTotal = grandTotal * 0.85;

  function buildBodyHtml() {
    var html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.5">';
    /* Intro paragraphs come from settings (factoringEmailTemplate)
       so Arturo controls the wording. \n\n splits paragraphs, \n
       becomes <br>. Email addresses get auto-linked. Falls back to
       the original hardcoded copy when the setting is empty. */
    var introTpl = getSLSettings().factoringEmailTemplate;
    if (!introTpl) {
      introTpl = "Anexo facturas para esta semana, favor de enviar correo a Ben Wright benw@fsproduce.com y copiar a Brian\n\nBen, can you please confirm you have received the following loads under the status mentioned?, documents attached.";
    }
    function escHtml(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
    String(introTpl).split(/\n{2,}/).forEach(function (para, i) {
      var safe = escHtml(para).replace(/\n/g, "<br>").replace(/([\w.+-]+@[\w-]+\.[\w.-]+)/g, '<a href="mailto:$1">$1</a>');
      /* Bold the second paragraph by convention (matches the
         original layout where the "Ben, can you please confirm…"
         line was emphasized). */
      html += '<p>' + (i === 1 ? '<strong>' + safe + '</strong>' : safe) + '</p>';
    });
    html += '<h3 style="text-align:center;margin:20px 0 10px">Received Confirmation Request</h3>';
    html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #000;font-size:12px">';
    html += '<thead><tr style="background:#D9D9D9">';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:left">BOL</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:left">Invoice #</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:left">PO #</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:right">Amount</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:center">Status</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:center">%</th>';
    html += '<th style="padding:6px 8px;border:1px solid #000;text-align:right">Advance</th>';
    html += '</tr></thead><tbody>';
    /* Arturo: "tambien necesito que la tabla del email donde se
       muestran los invoices, esten ordenados ascendente por BOL" —
       independent of whatever sort the selection modal's own table
       happens to be in when the invoices were checked; this is the
       order the SHIPPER actually sees in the sent email. */
    var emailRows = selectedInvoices.slice().sort(function (a, b) {
      return (parseInt(a.gfiPuNumber, 10) || 0) - (parseInt(b.gfiPuNumber, 10) || 0);
    });
    emailRows.forEach(function (inv) {
      var total = invoiceTotal(inv);
      var status = invoiceStatus(inv);
      var statusStyle = status === "Protection" ? "background:#FFFF00;padding:2px 6px;font-weight:700" : "";
      html += '<tr>';
      html += '<td style="padding:6px 8px;border:1px solid #000">' + (inv.gfiPuNumber || "—") + '</td>';
      html += '<td style="padding:6px 8px;border:1px solid #000">' + (inv.invoiceNumber || "—") + '</td>';
      html += '<td style="padding:6px 8px;border:1px solid #000">' + (inv.clientRef || "—") + '</td>';
      html += '<td style="padding:6px 8px;border:1px solid #000;text-align:right;font-family:Courier New,monospace">$ ' + total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '</td>';
      html += '<td style="padding:6px 8px;border:1px solid #000;text-align:center"><span style="' + statusStyle + '">' + status + '</span></td>';
      html += '<td style="padding:6px 8px;border:1px solid #000;text-align:center">85%</td>';
      html += '<td style="padding:6px 8px;border:1px solid #000;text-align:right;font-family:Courier New,monospace">$ ' + (total * 0.85).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '</td>';
      html += '</tr>';
    });
    html += '</tbody></table>';
    html += '<br><table cellspacing="0" cellpadding="0" style="border-collapse:collapse;font-size:12px">';
    html += '<tr><td style="padding:4px 8px;font-weight:700">Total:</td><td style="padding:4px 8px;font-family:Courier New,monospace;border-top:1px solid #000">$ ' + grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '</td>';
    html += '<td style="padding:4px 8px;width:30px"></td><td style="padding:4px 8px;font-weight:700">Advance Total:</td><td style="padding:4px 8px;font-family:Courier New,monospace;border-top:1px solid #000">$ ' + advanceTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '</td></tr>';
    html += '</table>';
    html += '<br><div style="border:2px solid #2A7A6F;padding:10px 14px;display:inline-block;margin-top:10px"><strong style="color:#2A7A6F">' + t.emailFactoringNote + '</strong></div>';
    html += '</div>';
    return html;
  }

  /* Resolve an attachment slot to a { filename, blob, mimeType }
     entry. `stored` can be either:
       - Supabase Storage shape { name, url, path, type } — fetched
         from the public URL into a Blob.
       - Legacy base64 shape { name, data, type } — atob'd in-place.
     `fileUpload` is the per-send File from the modal (fallback).
     Returns null when neither is set. Throws on fetch failure so
     the caller surfaces it. */
  async function resolveDocAttachment(stored, fileUpload, fallbackBaseName) {
    if (stored && stored.url) {
      try {
        var res = await fetch(stored.url);
        if (!res.ok) throw new Error("fetch " + res.status);
        var blob = await res.blob();
        var mime = stored.type || blob.type || "application/pdf";
        return { filename: stored.name || (fallbackBaseName + ".pdf"), blob: blob, mimeType: mime };
      } catch (e) {
        if (typeof console !== "undefined" && console.warn) console.warn("[SL factoring] could not fetch stored doc, falling back:", e && e.message);
      }
    }
    if (stored && stored.data) {
      try {
        var parts = String(stored.data).split(",");
        var b64 = parts.length > 1 ? parts[1] : parts[0];
        var meta = parts.length > 1 ? parts[0].match(/data:([^;]+)/) : null;
        var mime2 = (stored.type) || (meta && meta[1]) || "application/pdf";
        var byteString = atob(b64);
        var ia = new Uint8Array(byteString.length);
        for (var i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        return { filename: stored.name || (fallbackBaseName + ".pdf"), blob: new Blob([ia], { type: mime2 }), mimeType: mime2 };
      } catch (e) { /* fall through */ }
    }
    if (fileUpload) {
      return { filename: fileUpload.name || (fallbackBaseName + ".pdf"), blob: fileUpload, mimeType: fileUpload.type || "application/pdf" };
    }
    return null;
  }

  var doSend = async function () {
    if (selectedInvoices.length === 0) { setError(t.emailFactoringNoInvoices); return; }
    /* Block send if ANY selected invoice is missing Xero or BOL —
       avoids AGROCAP receiving a partial bundle. The detail block
       lists each offending invoice plus the doc(s) it's missing
       (e.g. "INV-16228 (PU 17424): missing BOL") so the operator
       can fix the gap without hunting through the picker. */
    var missingLines = [];
    selectedInvoices.forEach(function (inv) {
      var noXero = !isPresent(effectiveStored(inv, "xeroInvoiceFile"));
      var noBol  = !isPresent(effectiveStored(inv, "bolFile"));
      if (!noXero && !noBol) return;
      var label = (inv.invoiceNumber || inv.id || "?");
      if (inv.gfiPuNumber) label += " (PU " + inv.gfiPuNumber + ")";
      var what = (noXero && noBol) ? t.emailFactoringMissingBoth
               : noXero            ? t.emailFactoringMissingXero
               :                     t.emailFactoringMissingBol;
      missingLines.push(label + ": " + what);
    });
    if (missingLines.length > 0) {
      setError(t.emailFactoringMissingHeader + "\n" + missingLines.join("\n"));
      return;
    }
    /* 2026-09-24 (Arturo): "el envio de emails de factiraje tiene un
       errror, ayer envioe uno pero no alcance a dar send... el email
       se envio... hoy volvio a suceder, seleccione 4 facturas... al
       seleccioanr la quinta en correo se envio solo y no considero esa
       ultima que habia seleccionado" — a real send happened without a
       deliberate click on the Send button, at least once with a stale
       (pre-update) invoice selection. No code path that could cause
       that was found on review (the checkbox's onChange only toggles
       selection, DraggableModal has no click-outside handler, there's
       no <form> anywhere in this file to explain an implicit submit),
       but sending to AGROCAP is exactly the kind of external,
       irreversible action that should never fire without an explicit,
       separate confirmation — this closes the door on the reported
       symptom regardless of its exact trigger, and the confirm dialog
       itself shows the real invoice count so a stale/wrong selection
       would be visibly obvious before anything goes out. Same
       `window.confirm` pattern already used elsewhere in this file for
       destructive actions (see e.g. the relot/delete confirms above). */
    var checkedEmails = recipients.filter(function (r) { return r.checked && r.email; }).map(function (r) { return r.email; });
    var extraList = String(extraEmails || "").split(/[\s,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
    var allEmails = Array.from(new Set(checkedEmails.concat(extraList)));
    /* 2026-09-24 (Arturo): never silently send to nobody — with the
       hard-coded fallback removed above, an unconfigured/never-synced
       device now legitimately CAN have zero recipients checked. */
    if (allEmails.length === 0) {
      setError(lang === "es"
        ? "No hay destinatarios seleccionados — configura los Factoring Recipients en SweetLeaf Settings."
        : "No recipients selected — configure Factoring Recipients in SweetLeaf Settings.");
      return;
    }
    /* Confirmation gate — includes the real recipient list AND invoice
       list so both the reported symptoms (email sent without clicking
       Send, and email reaching the wrong company) become visibly
       obvious before anything actually goes out, whatever their exact
       trigger. Same `window.confirm` pattern already used elsewhere in
       this file for destructive actions. */
    var confirmLines = selectedInvoices.map(function (inv) { return "• " + (inv.invoiceNumber || inv.gfiPuNumber || inv.id); });
    var confirmMsg = (lang === "es"
      ? "¿Enviar el email de Factoring a:\n" + allEmails.join(", ") + "\n\ncon " + selectedInvoices.length + " factura(s)?\n\n"
      : "Send the Factoring email to:\n" + allEmails.join(", ") + "\n\nwith " + selectedInvoices.length + " invoice(s)?\n\n"
    ) + confirmLines.join("\n");
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    setError("");
    try {
      if (!driveSignedIn()) { await driveSignIn(); }
      var attachments = [];
      var shipments = loadLS("gfi-shipments", []);
      var warehouses = loadLS("gfi-warehouses", []);
      var tafCtx = { shipments: shipments, warehouses: warehouses };
      for (var i = 0; i < selectedInvoices.length; i++) {
        var inv = selectedInvoices[i];
        var isProtection = (inv.lines || []).some(function (l) {
          var st = String(l.saleStatus || "").toLowerCase().trim();
          return st === "protection" || st === "pas";
        });
        var tafHtml = buildTransactionApprovalFormHTML(inv, tafCtx, { protection: isProtection });
        var tafBlob = await htmlToPdfBlob(tafHtml);
        attachments.push({
          filename: "TAF_" + (inv.invoiceNumber || inv.gfiPuNumber || "doc") + ".pdf",
          blob: tafBlob,
          mimeType: "application/pdf",
        });
        /* Xero invoice — prefer the file already stored on the
           invoice (Storage URL or legacy base64). Fall back to
           the per-send modal upload. */
        var xeroAtt = await resolveDocAttachment(effectiveStored(inv, "xeroInvoiceFile"), xeroFiles[inv.id], "Xero_Invoice_" + (inv.invoiceNumber || ""));
        if (xeroAtt) attachments.push(xeroAtt);
        /* BOL — same resolve pattern. */
        var bolAtt = await resolveDocAttachment(effectiveStored(inv, "bolFile"), bolFiles[inv.id], "BOL_" + (inv.invoiceNumber || ""));
        if (bolAtt) attachments.push(bolAtt);
      }
      /* checkedEmails/extraList/allEmails already computed above, before
         the confirm dialog — reused here as-is (nothing between there
         and here can change `recipients`/`extraEmails`). Fetched fresh
         from Supabase (not the local cache) — see
         getFreshSlSettings() comment for why (Arturo: factoring email
         has the exact same masked-email resolution as Passing, so it
         carries the same staleness risk on a device whose local
         cache hasn't been refreshed since maskedEmailAccount changed). */
      var slSettings = await getFreshSlSettings();
      /* Real inbox (receives mail): a.gonzalez@sweetleafproduce.com */
      var realEmail = slSettings.emailAccount || "a.gonzalez@sweetleafproduce.com";
      /* Masked (used only as From because gfinexus.com is the verified
         Resend domain): a.gonzalez.sweetleaf@gfinexus.com */
      var maskedEmail = slSettings.maskedEmailAccount || realEmail;
      var fromEmail = realEmail; /* kept for backward compat below */
      var ccList = sendCopy ? [realEmail] : [];
      var sigHtml = slSettings.emailSignature || "";
      var bodyHtml = buildBodyHtml();
      if (sigHtml) bodyHtml += '<br><br>' + sigHtml;
      bodyHtml = appendSlDisclosure(bodyHtml, slSettings);
      /* SweetLeaf settings should carry a maskedEmailAccount field
         (e.g. a.gonzalez.sweetleaf@gfinexus.com). If not set yet, fall
         back to the real address — but Resend will reject it because
         the domain isn't verified, so the user must configure
         maskedEmailAccount in SweetLeaf settings before sending. */
      console.log("[SL Factoring] Sending email with:", { to: allEmails, cc: ccList, from: maskedEmail, replyTo: realEmail, sendCopy: sendCopy, realEmail: realEmail, maskedEmail: maskedEmail });
      await sendEmail({
        to: allEmails,
        cc: ccList,
        from: maskedEmail,
        replyTo: realEmail,
        subject: slSettings.factoringEmailSubject || t.emailFactoringSubject,
        text: "",
        html: bodyHtml,
        attachments: attachments,
      });
      /* 2026-09-15 (Arturo): stamp factoringEmailSentAt on every invoice
         just included so the table can show/filter "already sent" —
         best-effort, a persistence failure here shouldn't block the
         success toast since the email itself already went out. */
      if (typeof onMarkSent === "function") {
        try { await onMarkSent(selectedInvoices, new Date().toISOString()); }
        catch (e) { console.warn("[SL Factoring] could not stamp factoringEmailSentAt:", e && e.message); }
      }
      if (typeof onSent === "function") onSent();
    } catch (e) {
      setError("Error: " + (e.message || String(e)));
    } finally {
      setSending(false);
    }
  };

  var thStyle = { textAlign: "left", padding: "6px 8px", fontSize: 10, textTransform: "uppercase", color: "var(--color-text-muted)", fontWeight: 700, borderBottom: "1px solid var(--color-border)" };
  var tdStyle = { padding: "8px", borderBottom: "1px solid var(--color-border)", fontSize: 11 };

  return (
    <DraggableModal title={t.emailFactoringTitle} onClose={onClose} initialWidth={Math.min(960, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(750, window.innerHeight - 60)} defaultMaximized zIndex={10005}>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
        {/* Recipients */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 6 }}>Recipients</div>
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 8, background: "var(--color-bg)" }}>
            {recipients.map(function (r, i) {
              return (
                <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={!!r.checked}
                    onChange={function () {
                      setRecipients(function (prev) { return prev.map(function (rr, j) { return j === i ? Object.assign({}, rr, { checked: !rr.checked }) : rr; }); });
                    }}
                  />
                  <span><strong>{r.name}</strong> <span style={{ color: "var(--color-text-muted)" }}>·</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.email}</span></span>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4 }}>{lang === "es" ? "Agregar más correos (separados por comas)" : "Add more emails (comma-separated)"}</div>
            <textarea
              value={extraEmails}
              onChange={function (e) { setExtraEmails(e.target.value); }}
              rows={2}
              style={{ width: "100%", padding: "6px 8px", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", resize: "vertical", boxSizing: "border-box" }}
              placeholder="name@example.com, other@example.com"
            />
          </div>
          {/* CC self toggle — when on, the SL outbound account is
              added to the cc list on send so Arturo keeps the
              thread in his inbox. */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={sendCopy} onChange={function (e) { setSendCopy(e.target.checked); }} style={{ marginTop: 3 }} />
            <span>
              {lang === "es" ? "Enviarme una copia" : "Send myself a copy"}
              <span style={{ display: "block", fontSize: 10, color: "var(--color-text-muted)", marginTop: 1, fontFamily: "'JetBrains Mono', monospace" }}>{getSLSettings().emailAccount || "a.gonzalez@sweetleafproduce.com"}</span>
            </span>
          </label>
        </div>

        {/* Invoice selector */}
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{t.emailFactoringSelect}</div>
        {/* Filter bar — search text + status + date range. Filters
            don't drop already-selected rows. */}
        {available.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 1fr 1fr auto", gap: 8, marginBottom: 8, alignItems: "center" }}>
            <input
              type="text"
              value={filterText}
              onChange={function (e) { setFilterText(e.target.value); }}
              placeholder={lang === "es" ? "Buscar INV#, BOL, PO#…" : "Search INV#, BOL, PO#…"}
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", boxSizing: "border-box" }}
            />
            <select
              value={filterStatus}
              onChange={function (e) { setFilterStatus(e.target.value); }}
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none" }}
            >
              <option value="">{lang === "es" ? "Todos los estados" : "All statuses"}</option>
              <option value="Good Delivery">{lang === "es" ? "Good Delivery" : "Good Delivery"}</option>
              <option value="Protection">{lang === "es" ? "Protection" : "Protection"}</option>
              <option value="Pending">{lang === "es" ? "Pending" : "Pending"}</option>
            </select>
            <select
              value={filterWeek}
              onChange={function (e) { setFilterWeek(e.target.value); }}
              title={lang === "es" ? "Semana" : "Week"}
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none" }}
            >
              <option value="">{lang === "es" ? "Todas las semanas" : "All weeks"}</option>
              {availableWeeks.map(function (w) { return <option key={w} value={w}>{w}</option>; })}
            </select>
            <input
              type="date"
              value={filterFrom}
              onChange={function (e) { setFilterFrom(e.target.value); }}
              title={lang === "es" ? "Desde" : "From"}
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none" }}
            />
            <input
              type="date"
              value={filterTo}
              onChange={function (e) { setFilterTo(e.target.value); }}
              title={lang === "es" ? "Hasta" : "To"}
              style={{ padding: "6px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none" }}
            />
            {(filterText || filterStatus || filterWeek || filterFrom || filterTo) ? (
              <button
                type="button"
                onClick={function () { setFilterText(""); setFilterStatus(""); setFilterWeek(""); setFilterFrom(""); setFilterTo(""); }}
                style={{ padding: "6px 10px", fontSize: 11, border: "1px solid var(--color-border)", borderRadius: 6, background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}
              >{lang === "es" ? "Limpiar" : "Clear"}</button>
            ) : <span />}
          </div>
        )}
        {available.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={hideSent} onChange={function (e) { setHideSent(e.target.checked); }} />
            {lang === "es" ? "Ocultar facturas con Factoring ya enviado" : "Hide invoices with Factoring already sent"}
          </label>
        )}
        {available.length === 0 ? (
          <div style={{ padding: "20px 8px", fontStyle: "italic", color: "var(--color-text-muted)", fontSize: 12 }}>{t.emailFactoringNoInvoices}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={Object.assign({}, thStyle, { width: 32 })}></th>
                <th style={Object.assign({}, thStyle, { cursor: "pointer" })} onClick={function () { toggleSort("gfiPuNumber"); }}>BOL{sortArrow("gfiPuNumber")}</th>
                <th style={Object.assign({}, thStyle, { cursor: "pointer" })} onClick={function () { toggleSort("invoiceNumber"); }}>INV#{sortArrow("invoiceNumber")}</th>
                <th style={Object.assign({}, thStyle, { cursor: "pointer" })} onClick={function () { toggleSort("clientRef"); }}>PO#{sortArrow("clientRef")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "center", cursor: "pointer" })} onClick={function () { toggleSort("date"); }}>Wk{sortArrow("date")}</th>
                <th style={Object.assign({}, thStyle, { cursor: "pointer" })} onClick={function () { toggleSort("date"); }}>Date{sortArrow("date")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "right", cursor: "pointer" })} onClick={function () { toggleSort("amount"); }}>Amount{sortArrow("amount")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "center", cursor: "pointer" })} onClick={function () { toggleSort("status"); }}>Status{sortArrow("status")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "center", cursor: "pointer" })} onClick={function () { toggleSort("xero"); }}>Xero Invoice{sortArrow("xero")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "center", cursor: "pointer" })} onClick={function () { toggleSort("bol"); }}>BOL{sortArrow("bol")}</th>
                <th style={Object.assign({}, thStyle, { textAlign: "center", cursor: "pointer" })} onClick={function () { toggleSort("factoringSent"); }}>{(lang === "es" ? "Factoring Enviado" : "Factoring Sent")}{sortArrow("factoringSent")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedDisplayed.map(function (inv) {
                var status = invoiceStatus(inv);
                var total = invoiceTotal(inv);
                var isSelected = !!selected[inv.id];
                var statusBg = status === "Protection" ? "#FFFF00" : status === "Good Delivery" ? "#D1FAE5" : "transparent";
                var statusColor = status === "Protection" ? "#7F1D1D" : status === "Good Delivery" ? "#047857" : "var(--color-text-muted)";
                /* Per-doc cell. Looks at the EFFECTIVE stored payload
                   (in-session overlay first, then the invoice's own
                   field). When present, shows a green ✓ + truncated
                   filename. When missing, shows the upload button —
                   uploading goes to Supabase Storage AND saves the
                   updated invoice back to Supabase so the green state
                   sticks across reloads (no waiting for a manual save). */
                function docCell(inv, fieldKey, accentLabel) {
                  var stored = effectiveStored(inv, fieldKey);
                  if (isPresent(stored)) {
                    var fn = String(stored.name || "");
                    var truncated = fn.length > 15 ? fn.substring(0, 13) + "…" : fn;
                    return (
                      <span title={fn} style={{ fontSize: 10, color: "#059669", fontWeight: 700 }}>✓ {truncated || (lang === "es" ? "Guardado" : "Stored")}</span>
                    );
                  }
                  return (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", border: "1px dashed var(--color-border)", borderRadius: 4, cursor: "pointer", fontSize: 10, color: "var(--color-text-secondary)" }}>
                      ⬆ {accentLabel}
                      <input
                        type="file"
                        accept=".pdf"
                        style={{ display: "none" }}
                        onChange={async function (e) {
                          var f = e.target.files && e.target.files[0];
                          e.target.value = "";
                          if (!f) return;
                          try { await persistInvoiceDoc(inv, fieldKey, f); }
                          catch (err) { alert((lang === "es" ? "Error al subir archivo: " : "Upload failed: ") + (err && err.message ? err.message : String(err))); }
                        }}
                      />
                    </label>
                  );
                }
                return (
                  <tr key={inv.id} style={{ background: isSelected ? "rgba(42,122,111,0.05)" : "transparent" }}>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center" })}>
                      <input type="checkbox" checked={isSelected} onChange={function () { toggleSelect(inv.id); }} />
                    </td>
                    <td style={Object.assign({}, tdStyle, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 })}>{inv.gfiPuNumber || "—"}</td>
                    <td style={Object.assign({}, tdStyle, { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#2A7A6F" })}>{inv.invoiceNumber || "—"}</td>
                    <td style={Object.assign({}, tdStyle, { fontFamily: "'JetBrains Mono', monospace" })}>{inv.clientRef || "—"}</td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" })}>{inv.date ? ("W" + getISOWeekInv(inv.date)) : "—"}</td>
                    <td style={Object.assign({}, tdStyle, { fontSize: 11, color: "var(--color-text-muted)" })}>{inv.date || "—"}</td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 })}>${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center" })}><span style={{ padding: "2px 8px", background: statusBg, color: statusColor, borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{status}</span></td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center" })}>{docCell(inv, "xeroInvoiceFile", "Xero")}</td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center" })}>{docCell(inv, "bolFile", "BOL")}</td>
                    <td style={Object.assign({}, tdStyle, { textAlign: "center" })}>
                      {inv.factoringEmailSentAt ? (
                        <span title={(lang === "es" ? "Enviado el " : "Sent on ") + new Date(inv.factoringEmailSentAt).toLocaleString()} style={{ fontSize: 10, color: "#059669", fontWeight: 700, whiteSpace: "nowrap" }}>
                          ✓ {new Date(inv.factoringEmailSentAt).toLocaleDateString()}
                        </span>
                      ) : <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Summary */}
        {selectedInvoices.length > 0 && (
          <div style={{ padding: 12, background: "rgba(42,122,111,0.06)", borderRadius: 8, border: "1px solid rgba(42,122,111,0.2)", marginBottom: 16, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>{selectedInvoices.length} invoice(s) selected</span>
            <span>Total: <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{"$" + grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></span>
            <span>Advance (85%): <strong style={{ fontFamily: "'JetBrains Mono', monospace", color: "#2A7A6F" }}>{"$" + advanceTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong></span>
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, marginBottom: 12, whiteSpace: "pre-line", lineHeight: 1.5 }}>{error}</div>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} disabled={sending} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: sending ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{t.emailFactoringCancel}</button>
        <button onClick={function () {
          /* Build the exact HTML body AGROCAP will receive — the
             same string doSend feeds to sendEmail.html — and stash
             it on previewHtml so the modal below renders it. */
          var slSettingsPreview = getSLSettings();
          var html = buildBodyHtml();
          var sig = slSettingsPreview.emailSignature || "";
          if (sig) html += "<br><br>" + sig;
          html = appendSlDisclosure(html, slSettingsPreview);
          setPreviewHtml(html);
        }} disabled={sending || selectedInvoices.length === 0} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #2A7A6F", background: "transparent", color: "#2A7A6F", cursor: sending || selectedInvoices.length === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: selectedInvoices.length === 0 ? 0.5 : 1 }}>{lang === "es" ? "Vista previa" : "Preview"}</button>
        <button onClick={doSend} disabled={sending || selectedInvoices.length === 0} style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: sending || selectedInvoices.length === 0 ? "var(--color-border)" : "#2A7A6F", color: "#fff", cursor: sending || selectedInvoices.length === 0 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{sending ? "..." : t.emailFactoringSend}</button>
      </div>
      {/* Email preview — render the exact HTML body AGROCAP will
          receive via dangerouslySetInnerHTML inside a draggable
          modal so the inline AGROCAP styles paint correctly. */}
      {previewHtml && (
        <DraggableModal
          title={(lang === "es" ? "Vista previa — " : "Preview — ") + (getSLSettings().factoringEmailSubject || t.emailFactoringSubject)}
          onClose={function () { setPreviewHtml(null); }}
          initialWidth={Math.min(900, Math.floor(window.innerWidth * 0.85))}
          initialHeight={Math.min(700, window.innerHeight - 60)}
          zIndex={10006}
        >
          <div style={{ flex: 1, overflowY: "auto", padding: 24, background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DraggableModal>
      )}
    </DraggableModal>
  );
}

/* ======================== EMAIL PASSING/BOL MODAL ========================
   Per-invoice "Send Passing / BOL" composer — a dedicated modal, NOT the
   shared EmailDialog (see fix-dashboard.md's confirmed approach): its
   body is fixed HTML the user can only preview, never edit, which
   EmailDialog's always-editable plain-text body doesn't support without
   changing behavior for its 14 other callers. Mirrors EmailFactoringModal
   above — same "special SL flow gets its own modal" precedent — reusing
   its recipients/extra-emails/sendCopy conventions and the
   realEmail/maskedEmail/ccList send-logic verbatim. */
function EmailPassingModal({ invoice, lang, onClose, onSent }) {
  /* gfiPuNumber ONLY — no fallback to invoice.puNumber (the SL-internal
     INV-XXXXX consecutive), same rule as every other customer-facing
     doc in this file (see buildPackingListHTML's own PU# comment). */
  var pu = invoice.gfiPuNumber || "";
  var po = invoice.clientRef || "";

  var [recipients, setRecipients] = useState(function () {
    var slSettings = getSLSettings();
    console.log("[EmailPassingModal] slSettings full:", slSettings);
    console.log("[EmailPassingModal] passingRecipients raw:", slSettings.passingRecipients);
    var stored = (slSettings.passingRecipients || []).filter(function (r) { return r && r.email; });
    console.log("[EmailPassingModal] passingRecipients filtered:", stored);
    return stored.map(function (r) { return { name: r.name || "", email: r.email, checked: true }; });
  });
  var [extraEmails, setExtraEmails] = useState("");
  /* Same default-on CC-to-real-email pattern as Pick Ticket (via
     EmailDialog) and Factoring — see doSend below. */
  var [sendCopy, setSendCopy] = useState(true);
  var [passingFile, setPassingFile] = useState(null);
  var [passingDragOver, setPassingDragOver] = useState(false);
  /* "Passing_{gfiPuNumber}_PO_{poNumber}" — no spaces, no "PU#" prefix,
     same in EN/ES (it's just the literal word "Passing"/"PO" + numbers). */
  var [subject, setSubject] = useState("Passing_" + pu + "_PO_" + po);
  var [sending, setSending] = useState(false);
  var [error, setError] = useState("");
  var [done, setDone] = useState(false);

  var suggestedFilename = "Passing_" + pu + "_" + po + ".pdf";

  /* Editable body — greeting + BOL line, NO "Regards" (the signature
     supplies its own closing, see fix-dashboard.md Corrección 2). Seeded
     once from the active language; the user can freely edit from there
     (add context, personalize per client, fix typos, etc.). */
  var [bodyText, setBodyText] = useState(function () {
    return lang === "es"
      ? "Estimado cliente:\n\nSu BOL está adjunto, ¡gracias por su preferencia!"
      : "Dear Customer:\n\nYour BOL is attached, thank you for your business!!!.";
  });

  /* Disclosure — NEVER editable here. Now sourced from Settings
     (slSettings.confidentialityDisclosure, editable in SweetLeaf
     Settings > Email Confidentiality Notice) via the shared
     appendSlDisclosure() helper instead of a hardcoded string, per
     fix-dashboard.md. Passing "" as the body just returns the wrapped
     disclosure fragment, reused below for this modal's own preview
     block. */
  var disclosureHtml = appendSlDisclosure("", getSLSettings());

  /* Drag-and-drop for the Passing file — same handler shape as the
     Xero/BOL drop zone in InvoiceForm (docDragOver/handleDocDrop, see
     ~L4864-4929): preventDefault on every drag event, only clear the
     highlight when actually leaving the zone (not hovering an inner
     child), accept PDFs only. One slot here (not two), so a drop
     always just replaces passingFile — no target-picking prompt needed. */
  var handlePassingDrop = function (e) {
    e.preventDefault();
    setPassingDragOver(false);
    var dropped = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    var pdf = dropped.find(function (file) { return file.type === "application/pdf" || /\.pdf$/i.test(file.name); });
    if (!pdf) {
      if (dropped.length > 0) setError(lang === "es" ? "Solo se aceptan archivos PDF" : "Only PDF files are accepted");
      return;
    }
    setPassingFile(pdf);
  };

  var doSend = async function () {
    if (!passingFile) { setError(lang === "es" ? "Archivo Passing requerido antes de enviar" : "Passing file required before sending"); return; }
    var checkedEmails = recipients.filter(function (r) { return r.checked && r.email; }).map(function (r) { return r.email; });
    var extraList = String(extraEmails || "").split(/[\s,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
    var allEmails = Array.from(new Set(checkedEmails.concat(extraList)));
    if (allEmails.length === 0) { setError(lang === "es" ? "Selecciona al menos un destinatario" : "Select at least one recipient"); return; }
    setSending(true);
    setError("");
    try {
      var ctx = {
        clients: loadLS("gfi-clients", []),
        shipments: loadLS("gfi-shipments", []),
        warehouses: loadLS("gfi-warehouses", []),
        company: loadCompanyProfile(),
      };
      /* Mandatory attachments: the packing list is always auto-generated
         (same helper the customer/warehouse email flows use), the
         Passing file is the one the user just uploaded — neither is
         optional/toggleable, per spec. */
      var packingAtt = await buildInvoiceDocPdf(invoice, "sl-packing", ctx, null);
      var passingAtt = { filename: passingFile.name, blob: passingFile, mimeType: passingFile.type || "application/pdf" };
      var attachments = [packingAtt, passingAtt];

      /* Same realEmail/maskedEmail/ccList formula as EmailFactoringModal
         (SweetLeafPortal.js ~L5547-5554) — literal reuse, not a new
         pattern. Fetched fresh from Supabase (not the local cache) —
         see getFreshSlSettings() comment for why. */
      var slSettings = await getFreshSlSettings();
      var realEmail = slSettings.emailAccount || "a.gonzalez@sweetleafproduce.com";
      var maskedEmail = slSettings.maskedEmailAccount || realEmail;
      var ccList = sendCopy ? [realEmail] : [];

      /* Final body = {edited textarea} + {signature} + {disclosure} —
         exact order from fix-dashboard.md Corrección 1. bodyText is
         plain text (line breaks, no markup) so it's escaped and
         \n -> <br> converted here, same as EmailDialog's own body
         handling — signature and disclosure are already HTML and are
         appended as-is, never escaped, never editable. */
      var escapeHtml = function (s) { return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
      var messageHtml = escapeHtml(bodyText).replace(/\n/g, "<br>");
      var sigHtml = slSettings.emailSignature || "";
      var bodyHtml = appendSlDisclosure('<div>' + messageHtml + (sigHtml ? ('<br><br>' + sigHtml) : '') + '</div>', slSettings);

      await sendEmail({
        to: allEmails,
        cc: ccList,
        from: maskedEmail,
        replyTo: realEmail,
        subject: subject,
        text: bodyText,
        html: bodyHtml,
        attachments: attachments,
      });
      setDone(true);
      if (typeof onSent === "function") onSent();
    } catch (e) {
      setError("Error: " + (e && e.message ? e.message : String(e)));
    } finally {
      setSending(false);
    }
  };

  return (
    <DraggableModal title={lang === "es" ? "Enviar Passing / BOL" : "Send Passing / BOL"} onClose={onClose} initialWidth={640} initialHeight={Math.min(760, window.innerHeight - 60)} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
        {/* Recipients */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{lang === "es" ? "Destinatarios" : "Recipients"}</div>
          {recipients.length > 0 ? (
            <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 8, background: "var(--color-bg)" }}>
              {recipients.map(function (r, i) {
                return (
                  <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 12 }}>
                    <input type="checkbox" checked={!!r.checked} onChange={function () {
                      setRecipients(function (prev) { return prev.map(function (rr, j) { return j === i ? Object.assign({}, rr, { checked: !rr.checked }) : rr; }); });
                    }} />
                    <span>{r.name ? <strong>{r.name}</strong> : null}{r.name ? <span style={{ color: "var(--color-text-muted)" }}> · </span> : null}<span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.email}</span></span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", padding: "6px 0" }}>
              {lang === "es" ? "No hay destinatarios configurados — agrégalos en SweetLeaf Settings › Destinatarios de Passing." : "No recipients configured — add them in SweetLeaf Settings › Passing Recipients."}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4 }}>{lang === "es" ? "Agregar más correos (separados por comas)" : "Additional emails (comma-separated)"}</div>
            <textarea value={extraEmails} onChange={function (e) { setExtraEmails(e.target.value); }} rows={2} style={{ width: "100%", padding: "6px 8px", fontSize: 12, fontFamily: "'JetBrains Mono', monospace", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", resize: "vertical", boxSizing: "border-box" }} placeholder="name@example.com, other@example.com" />
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={sendCopy} onChange={function (e) { setSendCopy(e.target.checked); }} style={{ marginTop: 3 }} />
            <span>
              {lang === "es" ? "Enviarme una copia" : "Send copy to me"}
              <span style={{ display: "block", fontSize: 10, color: "var(--color-text-muted)", marginTop: 1, fontFamily: "'JetBrains Mono', monospace" }}>{getSLSettings().emailAccount || "a.gonzalez@sweetleafproduce.com"}</span>
            </span>
          </label>
        </div>

        {/* Attachments */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{lang === "es" ? "Adjuntos" : "Attachments"}</div>
          <div style={{ fontSize: 12, padding: "6px 0", color: "var(--color-text)" }}>✓ {lang === "es" ? "Packing list (generado automáticamente)" : "Packing list (auto-generated)"}</div>
          <div
            onDragOver={function (e) { e.preventDefault(); if (!passingDragOver) setPassingDragOver(true); }}
            onDragEnter={function (e) { e.preventDefault(); setPassingDragOver(true); }}
            onDragLeave={function (e) { if (!e.currentTarget.contains(e.relatedTarget)) setPassingDragOver(false); }}
            onDrop={handlePassingDrop}
            style={{
              border: "1px " + (passingDragOver ? "dashed #10B981" : "solid var(--color-border)"),
              borderRadius: 6, padding: 10,
              background: passingDragOver ? "rgba(16,185,129,0.08)" : "var(--color-bg)",
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            {passingFile ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#047857", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✓ {passingFile.name}</span>
                <label style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>
                  {lang === "es" ? "Cambiar archivo" : "Change file"}
                  <input type="file" accept=".pdf,application/pdf" style={{ display: "none" }} onChange={function (e) { var f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) setPassingFile(f); }} />
                </label>
              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", border: "1px dashed " + (passingDragOver ? "#10B981" : "var(--color-border)"), borderRadius: 6, cursor: "pointer", fontSize: 11, color: passingDragOver ? "#047857" : "#2A7A6F", fontWeight: 600 }}>
                  ⬆ {lang === "es" ? "Subir archivo Passing" : "Upload Passing file"}
                  <input type="file" accept=".pdf,application/pdf" style={{ display: "none" }} onChange={function (e) { var f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) setPassingFile(f); }} />
                </label>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>
                  {lang === "es" ? "o arrastra un PDF aquí" : "or drag a PDF here"}
                </div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>
                  {(lang === "es" ? "Nombre sugerido: " : "Suggested filename: ") + suggestedFilename}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subject — editable, seeded with the spec default. */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Asunto" : "Subject"}</div>
          <input type="text" value={subject} onChange={function (e) { setSubject(e.target.value); }} style={{ padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
        </div>

        {/* Body — editable (fix-dashboard.md Corrección 1). Signature and
            disclosure are NOT part of the textarea — both are appended
            automatically at send time (see doSend above); the disclosure
            gets its own fixed preview right below, visually separated. */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Mensaje" : "Message"}</div>
          <textarea
            value={bodyText}
            onChange={function (e) { setBodyText(e.target.value); }}
            rows={5}
            style={{ padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box", resize: "vertical" }}
          />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>
            {lang === "es" ? "Tu firma se agrega automáticamente al enviar." : "Your signature is added automatically on send."}
          </div>
        </div>

        {/* Disclosure — FIXED preview, never editable. Separated from the
            textarea above with its own label so it reads as a distinct,
            locked block. */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
            {lang === "es" ? "Aviso legal" : "Disclosure"}
            <span style={{ marginLeft: 6, fontWeight: 400, fontSize: 10, color: "var(--color-text-muted)", textTransform: "none", letterSpacing: "normal", fontStyle: "italic" }}>
              {lang === "es" ? "— fijo, no editable" : "— fixed, cannot be edited"}
            </span>
          </div>
          <div style={{ padding: 12, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: disclosureHtml }} />
        </div>

        {error && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, marginBottom: 12, whiteSpace: "pre-line" }}>{error}</div>
        )}
        {done && !error && (
          <div style={{ padding: "10px 12px", borderRadius: 6, background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)", color: "#047857", fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
            ✓ {lang === "es" ? "Correo enviado exitosamente" : "Email sent successfully"}
          </div>
        )}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "#B7791F", fontWeight: 600 }}>
          {!done && !passingFile ? (lang === "es" ? "Archivo Passing requerido antes de enviar" : "Passing file required before sending") : ""}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} disabled={sending} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: sending ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500 }}>{done ? (lang === "es" ? "Cerrar" : "Close") : (lang === "es" ? "Cancelar" : "Cancel")}</button>
          {!done && (
            <button onClick={doSend} disabled={sending || !passingFile} style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: (sending || !passingFile) ? "var(--color-border)" : "#2A7A6F", color: (sending || !passingFile) ? "var(--color-text-muted)" : "#fff", cursor: (sending || !passingFile) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
              {sending ? (lang === "es" ? "Enviando..." : "Sending...") : (lang === "es" ? "Enviar" : "Send")}
            </button>
          )}
        </div>
      </div>
    </DraggableModal>
  );
}

/* ======================== MAIN REGISTRY ======================== */
export default function SweetLeafPortal({ lang: globalLang, initialTab }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [invoices, setInvoices] = useState([]);
  var [search, setSearch] = useState(""); var [filterClient, setFilterClient] = useState(""); var [filterStatus, setFilterStatus] = useState(""); var [filterSaleStatus, setFilterSaleStatus] = useState(""); var [filterWeek, setFilterWeek] = useState("");
  /* 2026-10-23, Arturo: "necesito agregar un filtro en las facturas de
     sweetleaf para poder ver filtradas las que ya tienen un outcome es
     decir protection outcome filter" — then, same day: "pero necesito
     poder filtrar mas de un concepto" — multi-select (array), same
     `multi` SearchSelect pattern already used by filterSeasons below.
     Values are the 3 real protectionResponseStatus values (willPayFull/
     settled/noResultYet, see PROTECTION_RESPONSE_STATUSES) plus one
     synthetic "__none__" bucket for "sin respuesta todavía" — with
     multi-select, picking all 3 real statuses already covers "ya tiene
     un outcome", so that combined shortcut option was dropped. */
  var [filterProtectionOutcome, setFilterProtectionOutcome] = useState([]);
  /* Filter popover state — Xero-style "⋮ Filters" button collapses
     the full filter set into an anchored panel. Mirrors the
     identically-named pattern in BillRegistry. */
  var [showFilters, setShowFilters] = useState(false);
  var filtersRef = useRef(null);
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  var seasons = useMemo(function() { return loadLS("gfi-seasons", []); }, []);
  /* Resolves the season for an invoice. invoice.date is the primary key
     for the lookup; falls back to invoice.seasonId for historical imports. */
  var seasonNameForInv = useCallback(function(invDate, sid) {
    if (sid) { var byId = seasons.find(function(s) { return String(s.id) === String(sid); }); if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4); }
    if (!invDate) return "";
    var d = new Date(invDate + "T00:00:00");
    var s = seasons.find(function(s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  var uniqueSeasons = useMemo(function() { return [...new Set(seasons.map(function(s) { return s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4); }).filter(Boolean))].sort(); }, [seasons]);
  var [sortCol, setSortCol] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-sl-invoices-sort") || "null"); return (s && s.column) || "puNumber"; } catch { return "puNumber"; } });
  var [sortDir, setSortDir] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-sl-invoices-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(function() { try { localStorage.setItem("gfi-sl-invoices-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  /* Close the filter popover on outside click + Escape. */
  useEffect(function() {
    if (!showFilters) return;
    var onDown = function(e) { if (filtersRef.current && !filtersRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setShowFilters(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);
  var [viewing, setViewing] = useState(null); var [editing, setEditing] = useState(null); var [showForm, setShowForm] = useState(false);
  /* "Passing Sent" list-column shortcut — opens EmailPassingModal
     directly for one invoice without going through DetailPanel. */
  var [passingModalInvoice, setPassingModalInvoice] = useState(null);
  var [showPrint, setShowPrint] = useState(null);
  /* Bill preview opened from the LOT cell in the invoices list. */
  var [lotPreviewBill, setLotPreviewBill] = useState(null);
  /* Fresh bills snapshot used by the LOT cell to decide whether to
     render a link or plain text. Memo on invoices so an invoice
     edit / status change reflects without a full reload. */
  var billsByLot = useMemo(function () {
    var bills = loadLS("gfi-bills", []);
    var map = {};
    bills.forEach(function (b) { if (b && b.lot) map[String(b.lot).trim()] = b; });
    return map;
    /* `invoices` is intentionally the trigger — bills and invoices
       often co-update (creating an invoice from a bill, status
       changes, etc.) and the invoice list re-renders on any of
       those, refreshing this map alongside. Reading bills here
       doesn't depend on the invoices value itself, but the dep keeps
       the cache in sync with normal user activity in this module. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);
  /* Helper used by the LOT cell; null when no bill matches. */
  function lookupBillByLot(lot) {
    if (!lot && lot !== 0) return null;
    var hit = billsByLot[String(lot)];
    if (hit) return hit;
    hit = billsByLot[String(lot).trim()];
    if (hit) return hit;
    /* Numeric fallback covers "57916" vs 57916 mismatches. */
    var n = parseInt(lot, 10);
    if (isNaN(n)) return null;
    var keys = Object.keys(billsByLot);
    for (var i = 0; i < keys.length; i++) {
      var bn = parseInt(keys[i], 10);
      if (!isNaN(bn) && bn === n) return billsByLot[keys[i]];
    }
    return null;
  }
  var [toast, setToast] = useState(null); var [loading, setLoading] = useState(true);
  /* Weekly AGROCAP factoring email — composes one email with a
     TAF PDF + Xero PDF attachment per selected invoice. */
  var [emailFactoringOpen, setEmailFactoringOpen] = useState(false);
  var [overdueNoticeOpen, setOverdueNoticeOpen] = useState(false);
  /* Expose a global toast hook so child components (DetailPanel)
     can fire toasts without prop-drilling. Cleaned up on unmount. */
  useEffect(function () {
    window.__gfiToast = function (msg) { setToast(msg); };
    return function () { try { delete window.__gfiToast; } catch (e) {} };
  }, []);
  var [selected, setSelected] = useState({});
  var [showBatchDeposit, setShowBatchDeposit] = useState(false);
  var [lockedRecord, setLockedRecord] = useState(null);
  var [confirmationPayment, setConfirmationPayment] = useState(null);
  var [cardFilter, setCardFilter] = useState(null); /* draft | sent | overdue | paid */

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var invoicesRef = useRef([]);
  useEffect(function () { invoicesRef.current = invoices; }, [invoices]);

  /* Source of truth = Supabase `invoices`. Dual-write to localStorage
     `gfi-sl-invoices` so legacy sync consumers (ProfitTracker,
     Settlements, SalesReports, computeCertUsage's invoice scan, etc.)
     keep working until they migrate. */
  var writeInvoicesCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  var reload = async function () {
    setError(null); setLoading(true);
    try {
      var rows = await loadFromSupabase("sweetleaf_invoices", { force: true });
      if (aliveRef.current) { setInvoices(rows); writeInvoicesCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load invoices: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("sweetleaf_invoices")
      .then(function (rows) { if (aliveRef.current) { setInvoices(rows); writeInvoicesCache(rows); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load invoices: " + (e.message || String(e))); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);

  /* CRUD helpers — row-targeted Supabase writes that keep local state
     and localStorage cache in sync. */
  var persistSingleInv = async function (inv) {
    var saved = await saveToSupabase("sweetleaf_invoices", inv);
    var current = invoicesRef.current || [];
    var ex = current.find(function (x) { return x.id === inv.id; });
    var next = ex ? current.map(function (x) { return x.id === inv.id ? saved : x; }) : current.concat([saved]);
    setInvoices(next); writeInvoicesCache(next);
    return saved;
  };
  var persistManyInv = async function (records) {
    if (records.length === 0) return [];
    var saved = await saveManyToSupabase("sweetleaf_invoices", records);
    var map = {};
    saved.forEach(function (s) { map[s.id] = s; });
    var next = (invoicesRef.current || []).map(function (x) { return map[x.id] || x; });
    setInvoices(next); writeInvoicesCache(next);
    return saved;
  };
  var persistDeleteInv = async function (id) {
    await deleteFromSupabase("sweetleaf_invoices", id);
    var next = (invoicesRef.current || []).filter(function (x) { return x.id !== id; });
    setInvoices(next); writeInvoicesCache(next);
  };
  /* Cross-write to Supabase bills for the line sync (mirror of
     BillRegistry's syncInvoicesToSupabase). Best-effort. */
  var syncBillsToSupabase = function (updatedBills) {
    if (!updatedBills || updatedBills.length === 0) return;
    saveManyToSupabase("bills", updatedBills)
      .catch(function (e) { console.warn("[InvoiceRegistry] bill line-sync to Supabase failed:", e && e.message); });
  };

  /* Legacy localStorage-only migrations kept for new installs that
     bootstrap from a backup JSON. All flag-gated; no-ops once Supabase
     is the primary store. */
  // eslint-disable-next-line no-unused-vars
  var _legacyMigrationsBlockInv = function () {
    var loaded = loadLS(STORAGE_KEY, []);
    /* One-time migration: copy inv.commissionRate → each line.
       commissionRate when not yet set. Math no-op for totals. Same
       LS flag as BillRegistry's so the two migrations move in
       lockstep when both are first loaded. */
    try {
      var migDone = localStorage.getItem("gfi-mig-line-commission-inv-v1");
      if (!migDone) {
        var changed = false;
        loaded = loaded.map(function (inv) {
          if (!inv.lines || inv.lines.length === 0) return inv;
          var rate = inv.commissionRate;
          var newLines = inv.lines.map(function (l) {
            if (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") {
              changed = true;
              return { ...l, commissionRate: rate };
            }
            return l;
          });
          return changed ? { ...inv, lines: newLines } : inv;
        });
        if (changed) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-line-commission-inv-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    /* "partialProtection" → "protection" one-time migration REMOVED
       2026-09-12 — that status is back, computed from pallet-level
       status on the GFI invoice and pushed here (see saleStatusCalc.js
       and InvoiceRegistry.js's SL push). Leaving the old migration
       running would have silently stripped every freshly-pushed
       Partial Protection value back down to Protection on next load. */
    /* One-time migration: normalize legacy raw-label saleStatus values
       on invoice lines to the canonical keys used by SALE_STATUSES.
       Pre-fix Excel imports stored the human label which the badge can
       render but the edit-mode SearchSelect can't match. Mirror of
       ImportFromExcel.normalizeSaleStatus(). Flag-gated. */
    try {
      var nsDoneInv = localStorage.getItem("gfi-mig-ss-norm-inv-v1");
      if (!nsDoneInv) {
        var SS_CANON_INV = { goodDelivery: 1, protection: 1, pas: 1, partialProtection: 1, samples: 1, dumped: 1, donated: 1, transfer: 1 };
        var SS_MAP_INV = { gooddelivery: "goodDelivery", protection: "protection", pas: "pas", sample: "samples", samples: "samples", dumped: "dumped", donated: "donated", transfer: "transfer" };
        var anyNS = false;
        loaded = loaded.map(function (inv) {
          if (!inv.lines || inv.lines.length === 0) return inv;
          var hit = false;
          var nl = inv.lines.map(function (l) {
            if (!l || !l.saleStatus) return l;
            if (SS_CANON_INV[l.saleStatus]) return l;
            var key = String(l.saleStatus).toLowerCase().replace(/[^a-z0-9]/g, "");
            var mapped = SS_MAP_INV[key];
            if (mapped && mapped !== l.saleStatus) { hit = true; anyNS = true; return { ...l, saleStatus: mapped }; }
            return l;
          });
          return hit ? { ...inv, lines: nl } : inv;
        });
        if (anyNS) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-ss-norm-inv-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    setInvoices(loaded);
    setLoading(false);
  }; /* end _legacyMigrationsBlockInv \u2014 dead code, kept for reference */

  var resolvedInvoices = useMemo(function() {
    return invoices.map(function(inv) {
      var clientName = inv.clientName || resolveClientName(inv.clientId);
      return clientName !== inv.clientName ? { ...inv, clientName: clientName } : inv;
    });
  }, [invoices]);

  var handleSave = async function(inv) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistSingleInv(inv);
    /* Sync saleStatus (ONLY) from Invoice lines to matching Bill lines
       — PER LINE, not per product, pairing by per-product index: 1st
       Blackberry on the invoice ↔ 1st Blackberry on the bill, 2nd ↔
       2nd, etc. If the counts differ, only the overlap is synced;
       unpaired lines on either side are left untouched.
       Cases/pallets are deliberately NOT synced here — the shipper's
       bill reflects what was physically shipped (720 cases), which is
       independent of how the client-side invoice(s) later split that
       same lot. Syncing cases positionally also breaks the moment a
       lot spans 2+ invoices: each invoice save only knows its own
       partial cases, so whichever invoice saved last would silently
       overwrite the bill line with its own partial total instead of
       the lot's real 720 (bug fixed 2026-09-08 — see profitCalc.js /
       lotBalanceCheck.js for the actual cases source of truth). */
    try {
      var allBills = loadLS("gfi-bills", []);
      var changed = false;
      var updated = allBills.map(function(b) {
        var billChanged = false;
        /* Bucket THIS invoice's lines by product key, filtered to
           lines that belong to this bill's lot. The bucket preserves
           array order so index-pairing is deterministic. */
        var invByProdIdx = {};
        (inv.lines || []).forEach(function(il) {
          var lineLot = il.lot || inv.lot;
          if (lineLot !== b.lot) return;
          var k = String(il.productName || il.productBase || "").trim().toLowerCase();
          if (!k) return;
          if (!invByProdIdx[k]) invByProdIdx[k] = [];
          invByProdIdx[k].push(il);
        });
        var cursor = {};
        var newLines = (b.lines || []).map(function(bl) {
          var k = String(bl.productName || bl.productBase || "").trim().toLowerCase();
          if (!k) return bl;
          var bucket = invByProdIdx[k] || [];
          var i = cursor[k] || 0;
          cursor[k] = i + 1;
          if (i >= bucket.length) return bl; /* no paired invoice line — leave bill line alone */
          var matchInv = bucket[i];
          if (!matchInv) return bl;
          if (matchInv.saleStatus && matchInv.saleStatus !== bl.saleStatus) { billChanged = true; return { ...bl, saleStatus: matchInv.saleStatus }; }
          return bl;
        });
        if (billChanged) { changed = true; return { ...b, lines: newLines }; }
        return b;
      });
      if (changed) {
        localStorage.setItem("gfi-bills", JSON.stringify(updated));
        var changedBills = updated.filter(function (b) {
          var orig = allBills.find(function (o) { return o.id === b.id; });
          return orig && orig !== b;
        });
        syncBillsToSupabase(changedBills);
      }
    } catch(e) {}
    /* Push protection-response fields (2026-09-14, Arturo: "trasladalo de
       manera backward a su espejo invoice de ground fresh... este status
       se origina en sweetleaf y se distribuye hacia ground-fresh") — the
       ONE field on this mirror that flows the opposite direction of
       everything else (saleStatus/pickupDate/thermograph/etc. always go
       GFI→SL; this is SL→GFI, since FS's response to the reminder only
       ever reaches Arturo through this module). Only meaningful for a
       Four Seasons invoice with a linked GFI invoice — every SL invoice
       qualifies by definition (this whole module is FS-only). Never
       touches saleStatus itself on the GFI side, same non-negotiable as
       everywhere else. */
    try {
      if (inv.gfiInvoiceId) {
        var gfiInvoicesForPush = loadLS("gfi-invoices", []);
        var gfiInvForPush = gfiInvoicesForPush.find(function (x) { return String(x.id) === String(inv.gfiInvoiceId); });
        /* 2026-10-08, Arturo: "quiero aplicar exactamente la misma logica
           de las respuestas a protected files en sweetleaf... esta
           respuesta se esparce a su factura gemela de ground fresh" —
           pushes the FULL protectionResponses[] list now, not just the
           legacy single fields, so InvoiceRegistry.js's own collapsible
           card (read-only for Four Seasons, see its isFourSeasonsClientName
           gate) can show every registered response, not just the latest.
           The 3 flat fields keep going along too (JSON-stringify compare,
           simplest way to diff an array) — every other reader of this
           invoice (the reminder cron, the list-view badge) still only
           ever looks at those, same as before this feature. */
        var respForPush = (inv.protectionResponses && inv.protectionResponses.length > 0)
          ? inv.protectionResponses
          : (inv.protectionResponseStatus ? [{ status: inv.protectionResponseStatus, note: inv.protectionResponseNote || "", at: inv.protectionResponseAt || "" }] : []);
        if (gfiInvForPush && (
          JSON.stringify(respForPush) !== JSON.stringify(gfiInvForPush.protectionResponses || []) ||
          (inv.protectionResponseStatus || "") !== (gfiInvForPush.protectionResponseStatus || "") ||
          (inv.protectionResponseNote || "") !== (gfiInvForPush.protectionResponseNote || "") ||
          (inv.protectionResponseAt || "") !== (gfiInvForPush.protectionResponseAt || "")
        )) {
          var updatedGfiInv = { ...gfiInvForPush, protectionResponses: respForPush, protectionResponseStatus: inv.protectionResponseStatus || "", protectionResponseNote: inv.protectionResponseNote || "", protectionResponseAt: inv.protectionResponseAt || "" };
          try { localStorage.setItem("gfi-invoices", JSON.stringify(gfiInvoicesForPush.map(function (x) { return String(x.id) === String(updatedGfiInv.id) ? updatedGfiInv : x; }))); } catch (e2) {}
          await saveToSupabase("invoices", updatedGfiInv);
        }
      }
    } catch (e) { console.warn("[SL] failed to push protection response to GFI invoice:", e && e.message); }
      setShowForm(false); setEditing(null); setToast(t.saved);
    } catch (e) {
      setError("Could not save invoice: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleDelete = async function(id) {
    if (saving) return;
    setSaving(true); setError(null);
    /* Snapshot the record before the delete so we can use its
       gfiInvoiceId (if any) to clear slImported on the source GFI
       invoice once the SL delete succeeds. */
    var invoiceToDelete = invoices.find(function (x) { return x.id === id; });
    /* If the invoice was linked to a Client-Pricing PO, release the
       link so the PO reverts to "pending" + clears the PU# and shows
       up in other invoices' PO picker again. */
    try {
      if (invoiceToDelete && invoiceToDelete.linkedPoLineId) {
        syncPoLinkage(invoiceToDelete.linkedPoLineId, "", { id: id, puNumber: "", status: "draft" });
      }
    } catch (e) {}
    try {
      await persistDeleteInv(id);
      /* Clear slImported on the source GFI invoice so Copy-to-SL
         becomes available again. Best-effort; an error here doesn't
         roll back the SL delete. */
      if (invoiceToDelete && invoiceToDelete.gfiInvoiceId) {
        try {
          var gfiInvoices = loadLS("gfi-invoices", []);
          var gfiInv = gfiInvoices.find(function (x) { return String(x.id) === String(invoiceToDelete.gfiInvoiceId); });
          if (gfiInv && gfiInv.slImported) {
            gfiInv.slImported = false;
            try { localStorage.setItem("gfi-invoices", JSON.stringify(gfiInvoices.map(function (x) { return String(x.id) === String(gfiInv.id) ? gfiInv : x; }))); } catch (e2) {}
            saveToSupabase("invoices", gfiInv).catch(function (e2) { console.warn("[SL] failed to clear slImported on GFI invoice:", e2 && e2.message); });
          }
        } catch (e2) { /* best-effort */ }
      }
      setShowForm(false); setEditing(null); setViewing(null); setToast(t.deleted);
    } catch (e) {
      setError("Could not delete invoice: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Confirm batch deposit: append to inv.payments, update amountPaid + status, and log to gfi-payments */
  var handleBatchDepositConfirm = async function(payload) {
    var allPayments = loadLS("gfi-payments", []);
    var newPayId = nextPaymentId(allPayments);
    var clientNames = [...new Set(payload.allocations.map(function(a) { return a.clientName; }).filter(Boolean))];
    var paymentRecord = {
      id: newPayId,
      paymentDate: payload.paymentDate,
      type: "inbound",
      /* SL deposits land in the SAME shared Supabase `payments` table
         as GF payments/deposits (InvoiceRegistry.js's own
         handleBatchDepositConfirm builds a near-identical record with
         no distinguishing field). Without this tag, PaymentRegistry.js
         has no way to exclude SL rows short of cross-referencing
         sweetleaf_invoices, which it's explicitly forbidden from
         touching. Records saved before this field existed have no
         `source` at all and are treated as GF for backward
         compatibility — see the historical-backfill SQL for cleaning
         those up. */
      source: "sl",
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: payload.allocations.map(function(a) { return { invoiceId: a.invoiceId, puNumber: a.puNumber, lot: a.lot, clientName: a.clientName, amount: a.amount, concept: a.concept || "" }; }),
      shipperName: clientNames.join(", "),
      createdAt: new Date().toISOString(),
    };
    var allocByInvId = {};
    payload.allocations.forEach(function(a) { allocByInvId[a.invoiceId] = a.amount; });
    var toUpdate = invoices.filter(function(inv) { return allocByInvId[inv.id]; }).map(function(inv) {
      var amt = allocByInvId[inv.id];
      /* Dedupe by paymentId — defensive against retries / double-
         clicks. If a row with this paymentId already exists, drop
         it before appending the fresh one. */
      var existingPays = (inv.payments || []).filter(function (p) { return p.paymentId !== newPayId; });
      var newPayments = [...existingPays, {
        paymentId: newPayId,
        date: payload.paymentDate,
        amount: amt,
        method: payload.paymentMethod,
        reference: payload.reference,
      }];
      var totalPaid = newPayments.reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var totalDue = calcInvoiceTotal(inv);
      var due = totalDue - totalPaid;
      var newStatus = inv.status;
      if (inv.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
      return { ...inv, payments: newPayments, amountPaid: +totalPaid.toFixed(2), status: newStatus };
    });
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistManyInv(toUpdate);
      try { await saveToSupabase("payments", paymentRecord); } catch (e) { /* swallow */ }
      saveLS("gfi-payments", [...allPayments, paymentRecord]);
      setSelected({});
      setShowBatchDeposit(false);
      setToast(t.depositSaved + " (" + newPayId + ")");
      setConfirmationPayment(paymentRecord);
    } catch (e) {
      setError("Could not record deposit: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  /* Expose for cross-module use. createFromBill force-reloads invoices
     from Supabase first so nextPU(...) is computed against the latest
     committed state — the closure-captured `invoices` array can be
     stale right after a save (state hasn't propagated yet) or on
     fresh mount before the initial Supabase fetch settled. Without
     this re-fetch, a second invoice created right after the first
     was reusing the prior PU number. */
  var createFromBill = useCallback(async function(bill) {
    var latestInvoices = invoicesRef.current || invoices;
    try {
      var fresh = await loadFromSupabase("sweetleaf_invoices", { force: true });
      if (Array.isArray(fresh) && fresh.length > 0) latestInvoices = fresh;
    } catch (e) { /* offline / Supabase down — fall back to the cached state */ }
    /* SweetLeaf is fixed to Four Seasons — bill-derived invoices keep
       the same constraint regardless of the source bill's client. */
    var fsForBill = findFourSeasonsClient();
    /* Bill-derived invoices also just SUGGEST the next consecutive;
       the form's save path commits the counter if the user keeps it. */
    var slInvBill = peekSLInvoiceNumber();
    var newInv = {
      id: "",
      invoiceNumber: slInvBill,
      puNumber: slInvBill,
      gfiInvoiceId: "", gfiPuNumber: "", gfiLot: bill.lot || "",
      date: bill.billDate || new Date().toISOString().substring(0, 10),
      dueDate: "", clientId: fsForBill ? fsForBill.id : "", clientInitials: fsForBill ? (fsForBill.initials || "") : "", clientName: fsForBill ? (fsForBill.shortName || fsForBill.fullName || FOUR_SEASONS_FALLBACK_NAME) : FOUR_SEASONS_FALLBACK_NAME, clientRef: "", linkedPoLineId: "",
      advanceFinancingFee: "",
      lot: bill.lot, shipmentId: bill.shipmentId, billId: bill.id,
      shipperName: bill.shipperName, status: "draft",
      lessAdvance: "", otherFees: "", lessCredits: "", remarks: "",
      /* Stamp lot / shipmentId / shipperName onto every inherited line.
         Bills carry the lot at the top level only — their lines spread
         straight from shipment.lines (BillRegistry line ~1144) without
         per-line lot. The LOT column on the invoice form reads from
         line.lot, so without this stamping every line on a bill→
         invoice handoff renders with an empty LOT cell and the user
         has to delete + re-add the line via the lot dropdown.
         commissionRate is intentionally CLEARED — the bill's rate is
         the shipper's commission (% GFI keeps from the supplier);
         the invoice's rate (if any) is what GFI charges the CLIENT.
         Carrying the bill's rate over would prefill the client invoice
         with the wrong number. */
      lines: (bill.lines || []).map(function(l) { return {
        ...l,
        salePrice: "",
        commissionRate: "",
        lot: l.lot || bill.lot,
        shipmentId: l.shipmentId || bill.shipmentId,
        shipperName: l.shipperName || bill.shipperName,
      }; }),
      fees: (bill.fees || []).map(function(fe) { return {
        feeType: fe.feeType || "",
        feeCustom: fe.feeCustom || "",
        qty: fe.qty || 1,
        rate: fe.rate || "",
        amount: fe.amount || 0,
        pctRate: fe.pctRate || "",
        fromBill: true,
      }; }),
    };
    setEditing(null); setShowForm(false);
    setTimeout(function() { setEditing(newInv); setShowForm(true); }, 50);
  }, [invoices]);
  useEffect(function() { window.__gfiInvoiceCreate = createFromBill; return function() { delete window.__gfiInvoiceCreate; }; }, [createFromBill]);

  /* Open-by-id hook for global search */
  useEffect(function() {
    window.__gfiInvoiceOpen = function(id) {
      var src = invoicesRef.current && invoicesRef.current.length ? invoicesRef.current : loadLS(STORAGE_KEY, []);
      var inv = src.find(function(x) { return x.id === id; });
      if (inv) setViewing(inv);
    };
    return function() { delete window.__gfiInvoiceOpen; };
  }, []);

  /* Blank-form create hook for Create New menu */
  useEffect(function() {
    window.__gfiInvoiceNew = function() { setEditing(null); setShowForm(true); };
    return function() { delete window.__gfiInvoiceNew; };
  }, []);

  var uniqueClients = useMemo(function() { return [...new Set(resolvedInvoices.map(function(i) { return i.clientName; }).filter(Boolean))].sort(); }, [resolvedInvoices]);

  var seasonFilteredInvoices = useMemo(function() {
    if (filterSeasons.length === 0) return resolvedInvoices;
    return resolvedInvoices.filter(function(x) { return filterSeasons.indexOf(resolveSeasonId(seasons, x.date, x.seasonId)) >= 0; });
  }, [resolvedInvoices, filterSeasons, seasons]);

  var filtered = useMemo(function() {
    var list = seasonFilteredInvoices;
    if (search) { var s = search.toLowerCase(); list = list.filter(function(x) { return String(x.puNumber || "").includes(s) || (x.clientName || "").toLowerCase().includes(s) || String(x.lot || "").includes(s) || (x.clientRef || "").toLowerCase().includes(s); }); }
    if (filterClient) list = list.filter(function(x) { return x.clientName === filterClient; });
    if (filterStatus) list = list.filter(function(x) { return x.status === filterStatus; });
    /* Sale status filter — matches if ANY product line on the invoice
       carries the selected status (mirrors the multi-badge column). */
    if (filterSaleStatus) list = list.filter(function(x) { return (x.lines || []).some(function(l) { return l.saleStatus === filterSaleStatus; }); });
    if (filterProtectionOutcome.length > 0) {
      list = list.filter(function(x) {
        return x.protectionResponseStatus
          ? filterProtectionOutcome.indexOf(x.protectionResponseStatus) >= 0
          : filterProtectionOutcome.indexOf("__none__") >= 0;
      });
    }
    if (dateFrom || dateTo) list = list.filter(function(x) { var d = x.date || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    if (filterWeek) {
      /* Week derives from the linked shipment's arrival date (matches
         what the W## column upstream in Bills uses). Falls back to
         the invoice's own date when the lot doesn't link to a
         shipment we know about. */
      var shpsForWeek = loadLS("gfi-shipments", []);
      list = list.filter(function(x) {
        var shp = x.lot ? shpsForWeek.find(function(s) { return String(s.lot) === String(x.lot); }) : null;
        var d = shp ? (shp.dateArrival || shp.dateDeparture || x.date) : x.date;
        if (!d) return false;
        return ("W" + getISOWeekInv(d)) === filterWeek;
      });
    }
    if (cardFilter) {
      if (cardFilter === "overdue") {
        var todayD = new Date(); todayD.setHours(0, 0, 0, 0);
        list = list.filter(function(x) {
          if (x.status !== "sent") return false;
          if (!x.dueDate) return false;
          if (new Date(x.dueDate + "T00:00:00") >= todayD) return false;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return (total - paid) > 0.01;
        });
      } else if (cardFilter === "paid") {
        list = list.filter(function(x) {
          if ((x.status || "draft") === "paid") return true;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return paid > 0 && (total - paid) <= 0.01;
        });
      } else if (cardFilter === "sent") {
        list = list.filter(function(x) {
          if (x.status !== "sent") return false;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return (total - paid) > 0.01;
        });
      } else {
        list = list.filter(function(x) { return (x.status || "draft") === cardFilter; });
      }
    }
    return list;
  }, [seasonFilteredInvoices, search, filterClient, filterStatus, filterSaleStatus, filterProtectionOutcome, dateFrom, dateTo, filterWeek, cardFilter]);

  /* Sorted ISO weeks present in the current invoice set, used to
     populate the Week filter dropdown options. Same lot → shipment
     resolution as the filter logic above. */
  var uniqueWeeks = useMemo(function() {
    var shpsForWeeks = loadLS("gfi-shipments", []);
    var weeks = new Set();
    resolvedInvoices.forEach(function(inv) {
      var shp = inv.lot ? shpsForWeeks.find(function(s) { return String(s.lot) === String(inv.lot); }) : null;
      var d = shp ? (shp.dateArrival || shp.dateDeparture || inv.date) : inv.date;
      if (d) weeks.add("W" + getISOWeekInv(d));
    });
    return Array.from(weeks).sort(function(a, b) { return (parseInt(b.slice(1), 10) || 0) - (parseInt(a.slice(1), 10) || 0); });
  }, [resolvedInvoices]);

  var sorted = useMemo(function() {
    return [...filtered].sort(function(a, b) {
      var va, vb;
      switch (sortCol) {
        case "puNumber": va = parseInt(a.puNumber, 10) || 0; vb = parseInt(b.puNumber, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "season": va = seasonNameForInv(a.date, a.seasonId); vb = seasonNameForInv(b.date, b.seasonId); break;
        case "date": va = a.date || ""; vb = b.date || ""; break;
        case "week": va = a.date ? getISOWeekInv(a.date) : 0; vb = b.date ? getISOWeekInv(b.date) : 0; return sortDir === "asc" ? va - vb : vb - va;
        case "dueDate": va = a.dueDate || ""; vb = b.dueDate || ""; break;
        case "clientName": va = a.clientName || ""; vb = b.clientName || ""; break;
        case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir, seasonNameForInv]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  var allSelected = sorted.length > 0 && sorted.every(function(i) { return selected[i.id]; });
  var toggleAll = function() { var ns = {}; if (!allSelected) sorted.forEach(function(i) { ns[i.id] = true; }); setSelected(ns); };
  var toggleOne = function(id, e) { if (e) e.stopPropagation(); setSelected(function(p) { var ns = Object.assign({}, p); if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  var selectedCount = Object.keys(selected).length;
  var clearSelection = function() { setSelected({}); };
  var selectedInvoices = useMemo(function() { return resolvedInvoices.filter(function(inv) { return selected[inv.id]; }); }, [resolvedInvoices, selected]);

  /* Aggregate breakdown for the selection bar AND the module-total
     chip. Returns { amount (gross), deductions (commission + fees +
     transfer debits), paid, due }. Used by both the selection chip
     (sum over selectedInvoices) and the module-total chip (sum over
     all resolvedInvoices) — single function, no divergence. */
  function aggregateInvoices(list) {
    var amount = 0, deductions = 0, paid = 0, due = 0, cases = 0;
    (list || []).forEach(function(inv) {
      /* Sum per-line commission (each line carries its own
         commissionRate post-migration). Legacy fallback to
         invoice-level commissionRate when a line's rate is blank. */
      var invDefault = parseFloat(inv.commissionRate) || 0;
      var gross = 0; var commission = 0;
      (inv.lines || []).forEach(function (l) {
        if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
        var lineCases = parseInt(l.cases, 10) || 0;
        var lineAmt = lineCases * (parseFloat(l.salePrice) || 0);
        gross += lineAmt;
        cases += lineCases;
        var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? invDefault
          : (parseFloat(l.commissionRate) || 0);
        commission += lineAmt * (rate / 100);
      });
      var tFees = 0; (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
      var tCredits = 0; (inv.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
      var p = paidAmount(inv);
      var total = calcInvoiceTotal(inv);
      amount += gross;
      /* Deductions = everything that REDUCES gross sales: commission
         + fees − credits (credits add back). Floored at 0 so a
         credit-heavy invoice doesn't show negative deductions. */
      deductions += commission + tFees - tCredits;
      paid += p;
      due += (total - p);
    });
    return { amount: amount, deductions: deductions, paid: paid, due: due, cases: cases };
  }
  var selectedTotals = useMemo(function() { return aggregateInvoices(selectedInvoices); }, [selectedInvoices]);
  var grandTotals = useMemo(function() { return aggregateInvoices(sorted); }, [sorted]);
  /* Backwards-compat aliases for any external code paths still
     referencing these. */
  var selectedDueTotal = selectedTotals.due;
  var grandDueTotal = grandTotals.due;
  var [showBulkDelete, setShowBulkDelete] = useState(false);
  var [showBulkStatus, setShowBulkStatus] = useState(false);
  var bulkLocked = useMemo(function() {
    return selectedInvoices.filter(function(inv) { return (inv.payments || []).length > 0; }).map(function(inv) { return { id: inv.id, label: "PU# " + (inv.puNumber || inv.id) }; });
  }, [selectedInvoices]);

  /* Excel export. Convention: if there is a selection, export that;
     otherwise export the full filtered view. Summary follows the spec
     exactly: PU#, Date, Client, Customer Ref, Lot, Gross Sales,
     Commission, Advance Financing Fee, Fees, Other Fees, Credits,
     Balance Transfers, Total Due. Detail expands every invoice into
     Product / Commission / Fee / Credit / Transfer rows discriminated
     by the Type column. */
  var invoicesToExport = function () { return selectedInvoices.length > 0 ? selectedInvoices : sorted; };
  var buildInvSummaryRows = function () {
    return invoicesToExport().map(function (inv) {
      var gross = 0;
      (inv.lines || []).forEach(function (l) {
        if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
          gross += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
        }
      });
      var commRate = parseFloat(inv.commissionRate) || 0;
      var commission = gross * (commRate / 100);
      var advFee = 0; var knownFees = 0; var otherFees = 0;
      (inv.fees || []).forEach(function (fe) {
        var amt = Math.abs(parseFloat(fe.amount) || 0);
        if (isAdvanceFinancingFeeInv(fe)) advFee += amt;
        else if (isKnownFeeName(fe)) knownFees += amt;
        else otherFees += amt;
      });
      var totalCredits = 0;
      (inv.credits || []).forEach(function (cr) { totalCredits += Math.abs(parseFloat(cr.amount) || 0); });
      var totalTransfers = 0;
      (inv.balanceTransfers || []).forEach(function (tr) {
        var a = parseFloat(tr.amount) || 0;
        if (tr.type === "credit") totalTransfers += a;
        else if (tr.type === "debit") totalTransfers -= a;
      });
      var totalDue = calcInvoiceTotal(inv);
      return {
        "PU#": xs(inv.puNumber),
        Date: xs(inv.date),
        Client: xs(resolveClientName(inv.clientId) || inv.clientName),
        "Customer Ref": xs(inv.clientRef),
        Lot: xs(inv.lot),
        "Gross Sales": num2(gross),
        Commission: num2(commission),
        "Advance Financing Fee": num2(advFee),
        Fees: num2(knownFees),
        "Other Fees": num2(otherFees),
        Credits: num2(totalCredits),
        "Balance Transfers": num2(totalTransfers),
        "Total Due": num2(totalDue),
        Status: xs(inv.status),
      };
    });
  };
  var buildInvDetailRows = function () {
    var out = [];
    invoicesToExport().forEach(function (inv) {
      var meta = { "PU#": xs(inv.puNumber), Date: xs(inv.date), Client: xs(resolveClientName(inv.clientId) || inv.clientName), "Customer Ref": xs(inv.clientRef), Lot: xs(inv.lot) };
      (inv.lines || []).forEach(function (l) {
        var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
        /* Product = commodity (Blackberry / Blueberry / Raspberry).
           Description = FULL presentation (pack/label) via the
           resolver — never falls back to commodity, so a pivot by
           Description groups by real SKU. Comm % / Comm $ are the
           per-line rate and computed amount (fallback to inv-level). */
        var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? (parseFloat(inv.commissionRate) || 0)
          : (parseFloat(l.commissionRate) || 0);
        var lineComm = invLineCommissionAmount(l, inv.commissionRate);
        out.push({ ...meta, Type: "Product", Product: xs(l.productBase || l.productName), Description: xs(resolveInvLineDesc(l)), Category: xs(l.category), Cases: intOr(l.cases), Pallets: intOr(l.pallets), Rate: num2(l.salePrice), Amount: num2(amt), "Comm %": lineRate, "Comm $": num2(-lineComm), "Sale Status": xs(l.saleStatus) });
      });
      /* Aggregated total commission row (sum of per-line) for quick
         pivot consumption. */
      var totalComm = 0;
      (inv.lines || []).forEach(function (l) { totalComm += invLineCommissionAmount(l, inv.commissionRate); });
      if (totalComm > 0) {
        out.push({ ...meta, Type: "Commission", Product: "", Description: "Total Commission", Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(-totalComm), "Comm %": "", "Comm $": "", "Sale Status": "" });
      }
      (inv.fees || []).forEach(function (fe) {
        out.push({ ...meta, Type: "Fee", Product: "", Description: feeNameInv(fe), Category: "", Cases: "", Pallets: intOr(fe.qty), Rate: num2(fe.rate), Amount: num2(-Math.abs(parseFloat(fe.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (inv.credits || []).forEach(function (cr) {
        var label = cr.type === "fromInvoice" ? ("Credit from PU# " + (cr.refPuNumber || "—")) : (cr.concept || "Credit");
        out.push({ ...meta, Type: "Credit", Product: "", Description: label, Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(Math.abs(parseFloat(cr.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (inv.balanceTransfers || []).forEach(function (tr) {
        var amt = parseFloat(tr.amount) || 0;
        var signed = tr.type === "credit" ? amt : -amt;
        out.push({ ...meta, Type: "Transfer", Product: "", Description: (tr.type === "credit" ? "Credit from PU# " : "Debit to PU# ") + (tr.otherPuNumber || "?"), Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(signed), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
    });
    return out;
  };
  /* Bulk status change. Records with payments can only move to "paid". */
  /* Build the context bundle for the GFI sales docs (Inbound Sheet
     + Packing List). One read of each LS source per print so a bulk
     run doesn't re-read N times. */
  var buildSalesDocCtx = useCallback(function () {
    return {
      clients: getClients(),
      shipments: loadLS("gfi-shipments", []),
      warehouses: loadLS("gfi-warehouses", []),
      company: loadCompanyProfile(),
    };
  }, []);
  /* Pending pick-or-inbound state. The dropdown emits a placeholder
     "<brand>-pickorinbound" when the user has to choose Pick Ticket
     vs Inbound; we stash brand + scope here, show the modal, and on
     confirm dispatch the actual print with the resolved kind. */
  var [pickKindModal, setPickKindModal] = useState(null);
  /* Second-stage modal — only shown after the user picks "Pick Ticket"
     in the kind modal. Holds the same { brand, invoices } payload so
     the eventual print call carries through unchanged. */
  var [remarksModal, setRemarksModal] = useState(null);
  var doPrint = function (invs, kind, extras) {
    var ctx = buildSalesDocCtx();
    var fallbackPrint = function () {
      printInvoiceDocs(invs, kind, ctx, function (err) {
        if (err === "popup-blocked") setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
      }, extras);
    };
    /* Digital-first: save each doc straight to its client Drive folder.
       Sign in on the spot if there's no live session (Nexus drops it on
       reload), same as the upload button. No print window (it can't be
       dismissed on tablets/PWA). Only fall back to the print window when
       Drive isn't configured or sign-in fails. */
    if (driveConfigured()) {
      (async function () {
        var ok = 0, fail = 0;
        setToast(lang === "es" ? "Guardando en Drive…" : "Saving to Drive…");
        try {
          if (!driveSignedIn()) { await driveSignIn(); }
        } catch (e) {
          console.error("[Drive sign-in] failed", e);
          setToast((lang === "es" ? "⚠ No se pudo conectar a Drive: " : "⚠ Could not connect to Drive: ") + (e.message || String(e)));
          fallbackPrint();
          return;
        }
        for (var i = 0; i < invs.length; i++) {
          try { await uploadInvoiceDocToDrive(invs[i], kind, ctx, extras); ok++; }
          catch (e) { fail++; console.error("[Drive upload] invoice doc failed", invs[i].id, kind, e); }
        }
        setToast((lang === "es" ? "✓ Guardado en Drive: " : "✓ Saved to Drive: ") + ok + (fail ? " · " + fail + (lang === "es" ? " con error" : " failed") : ""));
      })();
    } else {
      fallbackPrint();
    }
  };
  var handleSalesDocSingle = function (inv, kind) {
    if (kind === "gfi-pickorinbound" || kind === "sl-pickorinbound") {
      var brandKey = "sl";  /* SweetLeafPortal: always SL branding */
      setPickKindModal({ brand: brandKey, invoices: [inv] });
      return;
    }
    doPrint([inv], kind);
  };
  var handleSalesDocBulk = function (kind) {
    if (selectedInvoices.length === 0) return;
    if (kind === "gfi-pickorinbound" || kind === "sl-pickorinbound") {
      var brandKey = "sl";  /* SweetLeafPortal: always SL branding */
      setPickKindModal({ brand: brandKey, invoices: selectedInvoices });
      return;
    }
    doPrint(selectedInvoices, kind);
  };

  var handleBulkStatusChange = async function(newStatus) {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    if (newStatus !== "paid") bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toUpdate = invoices.filter(function(inv) { return selected[inv.id] && !lockedIds[inv.id]; }).map(function(inv) { return { ...inv, status: newStatus }; });
    try {
      await persistManyInv(toUpdate);
      setShowBulkStatus(false); setSelected({}); setToast(t.saved);
    } catch (e) {
      setError("Could not bulk update: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleBulkDelete = async function() {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toDelete = invoices.filter(function(inv) { return selected[inv.id] && !lockedIds[inv.id]; });
    try {
      await Promise.all(toDelete.map(function (inv) { return deleteFromSupabase("sweetleaf_invoices", inv.id); }));
      var deletedSet = {};
      toDelete.forEach(function (inv) { deletedSet[inv.id] = true; });
      var keep = invoices.filter(function (inv) { return !deletedSet[inv.id]; });
      setInvoices(keep); writeInvoicesCache(keep);
      setShowBulkDelete(false);
      setSelected({});
      setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Summary cards: Draft (count) | Sent (count) $due | Overdue (count) $due rojo | Paid (count)
     Paid amount uses sum of payments[] (authoritative). Records with balance <= 0 count as Paid
     regardless of stored status. */
  var summary = useMemo(function() {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var r = { draft: 0, sent: 0, sentAmt: 0, overdue: 0, overdueAmt: 0, paid: 0 };
    seasonFilteredInvoices.forEach(function(inv) {
      var total = calcInvoiceTotal(inv);
      var paid = paidAmount(inv);
      var due = +(total - paid).toFixed(2);
      var st = inv.status || "draft";
      if (st === "draft") { r.draft++; return; }
      if (paid > 0 && due <= 0.01) { r.paid++; return; }
      if (st === "paid") { r.paid++; return; }
      r.sent++; r.sentAmt += due;
      if (inv.dueDate && new Date(inv.dueDate + "T00:00:00") < today && due > 0.01) {
        r.overdue++; r.overdueAmt += due;
      }
    });
    return r;
  }, [seasonFilteredInvoices]);

  if (loading) return null;
  var th = { padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", borderBottom: "2px solid var(--color-border)" };

  /* Xero-style header layout — mirrors BillRegistry. Tabs replace
     the big summary cards; search lives in its own always-visible
     row; the rest of the filters collapse into a ⋮ popover; module
     totals render as a tight right-aligned 2-column ledger block
     above the table. */
  var tabsCfg = [
    { key: "all",     label: lang === "es" ? "Todas" : "All",    count: seasonFilteredInvoices.length, filterValue: null },
    { key: "draft",   label: t.statusDraft,                       count: summary.draft || 0,      filterValue: "draft" },
    { key: "sent",    label: t.statusSent,                        count: summary.sent || 0,       filterValue: "sent" },
    { key: "overdue", label: t.statusOverdue,                     count: summary.overdue || 0,    filterValue: "overdue" },
    { key: "paid",    label: t.statusPaid,                        count: summary.paid || 0,       filterValue: "paid" },
  ];
  var activeTabKey = cardFilter || "all";
  /* Active filter count drives the badge next to the ⋮ button.
     `cardFilter` (tabs) + `search` (always-visible row) excluded
     since their state is already self-evident in the UI. */
  var activeFilterCount =
      (filterClient ? 1 : 0)
    + (filterSaleStatus ? 1 : 0)
    + (filterSeasons.length > 0 ? 1 : 0)
    + ((dateFrom || dateTo) ? 1 : 0)
    + (filterWeek ? 1 : 0)
    + (filterStatus ? 1 : 0)
    + (filterProtectionOutcome.length > 0 ? 1 : 0);
  var clearAllFilters = function() {
    setFilterClient(""); setFilterStatus(""); setFilterSaleStatus(""); setFilterProtectionOutcome([]);
    setFilterSeasons([]); setDateFrom(""); setDateTo(""); setFilterWeek("");
  };
  /* Outline-style action button — Xero-look (white bg, neutral
     border, dark text). Used for every action in the action bar. */
  var actBtn = { padding: "5px 12px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "var(--color-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var actBtnDanger = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.35)", color: "#B91C1C" });
  var totalsLabel = { textAlign: "right", color: "var(--color-text-muted)", fontSize: 11, whiteSpace: "nowrap" };
  var totalsValue = { textAlign: "right", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };
  var totalsRed   = Object.assign({}, totalsValue, { color: "#DC2626" });

  /* Settings panel — rendered instead of the list when the parent
     passed initialTab="settings". Wraps the four sweetleaf_settings
     fields in a compact form that persists on save to Supabase + LS. */
  if (initialTab === "settings") {
    return <SweetLeafSettingsPanel lang={lang} />;
  }

  /* SL Payments — rendered instead of the invoice list when the parent
     passed initialTab="payments". Own registry file (SLPaymentsRegistry.js,
     not inline here — SweetLeafPortal.js is already large) per
     fix-dashboard.md; reuses BatchDepositModal (exported above, edit
     mode via its `existingPayment` prop) and paymentConfirmation.js. */
  if (initialTab === "payments") {
    return <SLPaymentsRegistry lang={lang} />;
  }

  return (
    <div>
      {/* HEADER ROW — title (left), filters/export/print-docs/new
          button + module totals (right). Print Documents stays at
          the TOP, not in the action bar. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        <div style={{ lineHeight: 1.1, display: "flex", alignItems: "center", gap: 10 }}>
          <img src={SL_ISOTIPO_B64} alt="SweetLeaf" style={{ width: 36, height: 36, objectFit: "contain", flexShrink: 0 }} />
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#2A7A6F" }}>{t.title}</h2>
            <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.showing} {sorted.length} {t.of} {seasonFilteredInvoices.length} {t.total}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action triplet plus Print Documents dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            <button
              type="button"
              onClick={function() { setShowFilters(function(v) { return !v; }); }}
              style={Object.assign({}, actBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              <span>{lang === "es" ? "Filtros" : "Filters"}</span>
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            <ExportButton lang={lang} filenameBase="Invoices" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildInvSummaryRows} buildDetail={buildInvDetailRows} />
            <button type="button" onClick={function () { setOverdueNoticeOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #B91C1C", background: "rgba(220,38,38,0.06)", color: "#B91C1C", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              {lang === "es" ? "Aviso de vencidas" : "Past due notice"}
            </button>
            <button type="button" onClick={function () { setEmailFactoringOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #2A7A6F", background: "rgba(42,122,111,0.08)", color: "#2A7A6F", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
              ✉ {t.emailFactoring}
            </button>
            <button onClick={function() { setEditing(null); setShowForm(true); }} style={{ padding: "5px 14px", borderRadius: 8, border: "none", background: "#c65d00", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ {t.add}</button>
            {/* FILTER POPOVER */}
            {showFilters && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 720, maxWidth: "92vw", background: "#fff", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 12px 36px rgba(0,0,0,0.18)", padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "5px 10px", fontSize: 11 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={function() { setShowFilters(false); }} style={Object.assign({}, actBtn, { padding: "5px 10px", fontSize: 11 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Cliente" : "Client"}</div>
                    <SearchSelect value={filterClient} onChange={setFilterClient} placeholder={t.allClients} options={uniqueClients.map(function(c) { return { value: c, label: c }; })} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                    <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} style={inp} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} style={inp} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Semana" : "Week"}</div>
                    <SearchSelect value={filterWeek} onChange={setFilterWeek} placeholder={lang === "es" ? "Todas las semanas" : "All weeks"} options={uniqueWeeks.map(function(w) { return { value: w, label: w }; })} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Status venta" : "Sale status"}</div>
                    <SearchSelect value={filterSaleStatus} onChange={setFilterSaleStatus} placeholder={t.allSaleStatuses} options={SALE_STATUSES.map(function(s) { return { value: s, label: t[s] || s }; })} />
                  </div>
                  {/* 2026-10-23, Arturo: "necesito agregar un filtro...
                      para poder ver filtradas las que ya tienen un
                      outcome es decir protection outcome filter" — then
                      "pero necesito poder filtrar mas de un concepto" —
                      multi-select (same `multi` SearchSelect prop as
                      the Season filter above), "__none__" lets "sin
                      respuesta todavía" be combined with any of the 3
                      real statuses in one filter. */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.protectionResponseColLine1 + " " + t.protectionResponseColLine2}</div>
                    <SearchSelect multi value={filterProtectionOutcome} onChange={setFilterProtectionOutcome} placeholder={t.allProtectionOutcomes} options={[
                      { value: "__none__", label: t.protectionOutcomeNone },
                      ...PROTECTION_RESPONSE_STATUSES.map(function(s) { return { value: s, label: t[s] || s }; }),
                    ]} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — accounting-style 2-column right-aligned
              ledger. Cases first, then dollars; deductions + paid in
              red WITHOUT parentheses per spec; Total Due gets an
              accent border-top + bold-extra. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 14, rowGap: 2, alignItems: "center" }}>
            <span style={totalsLabel}>{lang === "es" ? "Cajas" : "Cases"}:</span>
            <span style={totalsValue}>{grandTotals.cases.toLocaleString("en-US")}</span>
            <span style={totalsLabel}>{lang === "es" ? "Monto" : "Amount"}:</span>
            <span style={totalsValue}>{fmtMoney(grandTotals.amount)}</span>
            <span style={totalsLabel}>{lang === "es" ? "Deducciones" : "Deductions"}:</span>
            <span style={totalsRed}>{fmtMoney(grandTotals.deductions)}</span>
            <span style={totalsLabel}>{lang === "es" ? "Pagado" : "Paid"}:</span>
            <span style={totalsRed}>{fmtMoney(grandTotals.paid)}</span>
            <span style={Object.assign({}, totalsLabel, { fontWeight: 700, color: "var(--color-text)" })}>{lang === "es" ? "Total a Pagar" : "Total Due"}:</span>
            <span style={Object.assign({}, totalsValue, { fontWeight: 800, color: "#c65d00", borderTop: "1px solid var(--color-border)", paddingTop: 2, marginTop: 1 })}>{fmtMoney(grandTotals.due)}</span>
          </div>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 6, padding: "6px 10px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={function() { setError(null); reload(); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button>
        </div>
      )}
      {/* SEARCH — always visible. */}
      <div style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <input
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          placeholder={t.search}
          style={Object.assign({}, inp, { maxWidth: 420, width: "100%", padding: "5px 8px", fontSize: 12 })}
        />
        {search && (
          <button
            type="button"
            onClick={function() { setSearch(""); }}
            style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}
            title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}
          >×</button>
        )}
      </div>
      {/* TABS — All / Draft / Sent / Overdue / Paid. */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--color-border)", marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(function(tab) {
          var isActive = activeTabKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={function() { setCardFilter(tab.filterValue); }}
              style={{
                padding: "6px 14px",
                background: "transparent",
                border: "none",
                borderBottom: isActive ? "2px solid #c65d00" : "2px solid transparent",
                marginBottom: -2,
                color: isActive ? "#c65d00" : "var(--color-text-secondary)",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
      </div>
      {/* ACTION BAR — outline buttons. Print Documents lives in the
          header above, not here. Disabled state mirrors selection
          count so the row stays present even with nothing selected. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <button type="button" onClick={function() { setShowBatchDeposit(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{t.recordDeposit}</button>
        <button type="button" onClick={function() {
          if (selectedInvoices.length === 0) return;
          printMultipleInvoices(selectedInvoices, getClients(), function(err) {
            if (err === "popup-blocked") setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
          });
        }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>Invoice PDF</button>
        <button type="button" onClick={function() { setShowBulkStatus(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Cambiar Estado" : "Change Status"}</button>
        <button type="button" onClick={function() { setShowBulkDelete(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
        <button type="button" onClick={clearSelection} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        {/* Selection summary chip — running totals of the checked
            rows next to the buttons. */}
        {selectedCount > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
            <span style={{ fontWeight: 700, color: "#c65d00" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString("en-US")}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Monto" : "Amount"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(selectedTotals.amount)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Deducciones" : "Deductions"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.deductions)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Pagado" : "Paid"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.paid)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Total a Pagar" : "Total Due"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{fmtMoney(selectedTotals.due)}</span></span>
          </div>
        )}
      </div>
      {sorted.length === 0 ? <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div> : (
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              {/* Passing Sent — leftmost data column per fix-dashboard.md
                  PARTE 3, before Invoice #. 2-line header, fixed 65px. */}
              <th style={{ ...th, width: 65, textAlign: "center", cursor: "default", lineHeight: 1.2 }}>
                <div>{lang === "es" ? "Passing" : "Passing"}</div>
                <div>{lang === "es" ? "Enviado" : "Sent"}</div>
              </th>
              <th onClick={function() { toggleSort("invoiceNumber"); }} style={{ ...th, textAlign: "center", color: "#2A7A6F" }}>{t.invNum}{arrow("invoiceNumber")}</th>
              <th onClick={function() { toggleSort("gfiPuNumber"); }} style={{ ...th, textAlign: "center" }}>{t.gfiPuNum}{arrow("gfiPuNumber")}</th>
              <th onClick={function() { toggleSort("season"); }} style={th}>{lang === "es" ? "Temporada" : "Season"}{arrow("season")}</th>
              <th onClick={function() { toggleSort("date"); }} style={th}>{t.date}{arrow("date")}</th>
              {/* Week # (2026-09-18, Arturo: "necesito la tabla de
                  facturas de sweetleaf... una columna con numero de
                  semana despues de la fecha de factura") — computed
                  live from Invoice Date via the same getISOWeekInv
                  helper already used for the season filter/dropdown
                  in this file, never persisted. */}
              <th onClick={function() { toggleSort("week"); }} style={{ ...th, textAlign: "center" }}>{lang === "es" ? "Semana" : "Week"}{arrow("week")}</th>
              <th onClick={function() { toggleSort("dueDate"); }} style={th}>{t.dueDate}{arrow("dueDate")}</th>
              {/* Client column hidden — SweetLeaf is fixed to
                  Four Seasons so the column would be 100% identical. */}
              <th style={{ ...th, lineHeight: 1.2 }}><div>{t.clientRefColLine1}</div><div>{t.clientRefColLine2}</div></th>
              <th onClick={function() { toggleSort("lot"); }} style={{ ...th, textAlign: "center" }}>{t.lot}{arrow("lot")}</th>
              <th style={{ ...th, textAlign: "center" }}>{lang === "es" ? "Cajas" : "Cases"}</th>
              <th style={{ ...th, textAlign: "center" }} title={lang === "es" ? "Total de pallets de la factura" : "Total pallets on the invoice"}>{lang === "es" ? "Pallets" : "Pallets"}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.amount}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.deductions}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.paidCol}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.totalDue}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
              {/* 2026-09-14 — client's follow-up response to the Protection
                  Status Reminder (Full Pay / Credit). Separate from the
                  SALE STATUS column above (saleStatus itself, the pallet-
                  level truth), never overwrites it — see PROTECTION_RESPONSE_STATUSES. */}
              <th style={{ ...th, textAlign: "center", lineHeight: 1.2 }}><div>{t.protectionResponseColLine1}</div><div>{t.protectionResponseColLine2}</div></th>
              <th style={{ ...th, textAlign: "center" }}>{t.status}</th>
              <th style={{ ...th, textAlign: "center" }}></th>
              {/* Trailing Xero-style icon columns — docs count + email-sent flag. */}
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
            </tr></thead>
            <tbody>{sorted.map(function(inv) {
              var total = 0, tFees = 0, totalCases = 0;
              (inv.lines || []).forEach(function(l) {
                if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
                  var c = parseInt(l.cases, 10) || 0;
                  total += c * (parseFloat(l.salePrice) || 0);
                  totalCases += c;
                }
              });
              /* tFees only drives the "Deductions" column display (abs).
                 The "Total Due" column uses calcInvoiceTotal so it stays
                 in lock-step with the form/print summary (single source
                 of truth — fixes the prior list-vs-detail divergence
                 that double-applied imported fees). */
              (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
              var paid = paidAmount(inv);
              var due = +(calcInvoiceTotal(inv) - paid).toFixed(2);
              var st = inv.status || "draft";
              if (st !== "draft" && paid > 0 && due <= 0.01) st = "paid";
              var ss = INV_STATUS_STYLES[st]; var statusKey = st === "draft" ? "statusDraft" : st === "sent" ? "statusSent" : "statusPaid";
              return (
                <tr key={inv.id} onClick={function() { setViewing(inv); }} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[inv.id] ? "rgba(198,93,0,0.04)" : "transparent" }} onMouseEnter={function(e) { e.currentTarget.style.background = selected[inv.id] ? "rgba(198,93,0,0.08)" : "var(--color-bg-hover)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = selected[inv.id] ? "rgba(198,93,0,0.04)" : "transparent"; }}>
                  <td style={{ padding: "9px 8px", textAlign: "center" }} onClick={function(e) { e.stopPropagation(); }}><input type="checkbox" checked={!!selected[inv.id]} onChange={function(e) { toggleOne(inv.id, e); }} /></td>
                  {/* Passing Sent — click opens EmailPassingModal directly
                      for this invoice, shortcut around DetailPanel. */}
                  <td style={{ padding: "9px 4px", textAlign: "center", width: 65 }} onClick={function(e) { e.stopPropagation(); setPassingModalInvoice(inv); }}>
                    {inv.passingSentAt ? (
                      <span title={(lang === "es" ? "Enviado el " : "Sent on ") + fmtPassingSentAt(inv.passingSentAt)} style={{ display: "inline-flex", cursor: "pointer" }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: "#16a34a", lineHeight: 1 }}>{"✓"}</span>
                      </span>
                    ) : (
                      <span title={lang === "es" ? "Passing aún no enviado" : "Passing not sent yet"} style={{ display: "inline-flex", cursor: "pointer" }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="8" cy="8" r="6.5"/>
                          <path d="M8 4.5V8l2.5 1.5"/>
                        </svg>
                      </span>
                    )}
                  </td>
                  {/* INV # — primary SweetLeaf consecutive in teal.
                      Per the project design rule numbers stay clean;
                      docs + email-sent indicators live in the two
                      trailing icon columns at the end of the row. */}
                  <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#2A7A6F", textAlign: "center" }}>{inv.invoiceNumber || inv.puNumber}</td>
                  {/* GFI PU# — source ref from the original GFI invoice. */}
                  <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)", textAlign: "center" }}>{inv.gfiPuNumber || "—"}</td>
                  <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{seasonNameForInv(inv.date, inv.seasonId) || "—"}</td>
                  <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(inv.date)}</td>
                  <td style={{ padding: "9px 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)", textAlign: "center" }}>{inv.date ? ("W" + getISOWeekInv(inv.date)) : "—"}</td>
                  <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(inv.dueDate)}</td>
                  {/* Client cell dropped \u2014 SweetLeaf is fixed to Four Seasons. */}
                  <td style={{ padding: "9px 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{inv.clientRef || "\u2014"}</td>
                  <td style={{ padding: "9px 8px", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, textAlign: "center", color: "var(--color-accent)" }} onClick={function(e) { e.stopPropagation(); }}>{(function() {
                    /* Collect ALL distinct lots from this invoice's
                       lines (plus the invoice-level lot as a fallback
                       when lines don't carry one). Each lot becomes
                       its own clickable link routed to the matching
                       bill \u2014 invoices that span multiple shipments
                       finally surface every lot. Seen-map dedupes
                       across lines + invoice level. */
                    var seen = {};
                    var lots = [];
                    (inv.lines || []).forEach(function(l) {
                      var v = l && l.lot;
                      if (v != null && v !== "" && !seen[String(v)]) { seen[String(v)] = true; lots.push(v); }
                    });
                    if (inv.lot && !seen[String(inv.lot)]) { seen[String(inv.lot)] = true; lots.push(inv.lot); }
                    if (lots.length === 0) return "\u2014";
                    return (
                      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
                        {lots.map(function(lot, i) {
                          var matchedBill = lookupBillByLot(lot);
                          var sep = i < lots.length - 1 ? <span style={{ color: "var(--color-text-muted)" }}>,</span> : null;
                          /* No bill yet \u2192 plain text, no link.
                             Bill exists \u2192 blue link, opens preview. */
                          if (!matchedBill) {
                            return <span key={lot}>{lot}{sep}</span>;
                          }
                          return (
                            <span key={lot}>
                              <span
                                onClick={function() { setLotPreviewBill(matchedBill); }}
                                onMouseEnter={function(e) { e.currentTarget.style.textDecoration = "underline"; }}
                                onMouseLeave={function(e) { e.currentTarget.style.textDecoration = "none"; }}
                                style={{ color: "#2563EB", cursor: "pointer", textDecoration: "none" }}
                                title={lang === "es" ? "Ver bill" : "View bill"}
                              >{lot}</span>
                              {sep}
                            </span>
                          );
                        })}
                      </span>
                    );
                  })()}</td>
                  <td style={{ padding: "9px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600 }}>{totalCases > 0 ? totalCases.toLocaleString("en-US") : "—"}</td>
                  <td style={{ padding: "9px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600 }}>{(function () { var p = (inv.lines || []).reduce(function (acc, l) { return acc + (parseInt(l.pallets, 10) || 0); }, 0); return p > 0 ? p.toLocaleString("en-US") : "—"; })()}</td>
                  <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{fmtMoney(total)}</td>
                  <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#DC2626" }}>{tFees > 0 ? "(" + fmtMoney(tFees) + ")" : "—"}</td>
                  <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: paid > 0 ? "#059669" : "var(--color-text-muted)" }}>{paid > 0 ? fmtMoney(paid) : "—"}</td>
                  <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, color: "#c65d00" }}>{fmtMoney(due)}</td>
                <td style={{ padding: "9px 8px", textAlign: "center" }}>{(function() {
                  /* Show ALL distinct sale-status values across the
                     invoice's product lines \u2014 was previously collapsed
                     to a single "worst" badge (protection > partial >
                     good) which dropped any line-level claim/rejected/
                     past-due/etc. Set-dedup so two lines with the same
                     status render one badge. Wrap so 3+ badges flow
                     without breaking the row. */
                  var statuses = [];
                  var seen = {};
                  (inv.lines || []).forEach(function(l) {
                    var s = l.saleStatus;
                    if (s && !seen[s]) { seen[s] = true; statuses.push(s); }
                  });
                  if (statuses.length === 0) statuses.push(inv.passingSentAt ? "pendingDelivery" : "pendingPickUp");
                  /* Fallback colours for statuses not in SALE_STATUS_COLORS
                     (e.g. claim, rejected, pastDue once introduced). */
                  var fallbackSc = { bg: "rgba(107,114,128,0.10)", text: "#374151", border: "rgba(107,114,128,0.25)" };
                  return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>{statuses.map(function(s) {
                    var sc = SALE_STATUS_COLORS[s] || fallbackSc;
                    return <span key={s} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[s] || s}</span>;
                  })}</span>;
                })()}</td>
                  <td style={{ padding: "9px 8px", textAlign: "center" }}>{inv.protectionResponseStatus
                    ? (function() { var sc = PROTECTION_RESPONSE_COLORS[inv.protectionResponseStatus]; return <span title={inv.protectionResponseNote || ""} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[inv.protectionResponseStatus]}</span>; })()
                    : (function() { var rem = computeProtectionReminderStatus(inv, null, getSLSettings().protectionReminderIntervalDays); return rem && rem.isDue
                        ? <span title={t.protectionReminderDueDay(rem.daysSinceArrival)} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: "rgba(217,119,6,0.12)", color: "#B45309", border: "1px solid rgba(217,119,6,0.3)", boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t.protectionReminderDueBadge}</span>
                        : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>; })()}</td>
                  <td style={{ padding: "9px 8px", textAlign: "center" }}><span style={{ padding: "3px 9px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: SALE_STATUS_BEVEL }}>{t[statusKey]}</span></td>
                  <td style={{ padding: "9px 4px", textAlign: "center" }}><button onClick={function(e) { e.stopPropagation(); setShowPrint(inv); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--color-text-muted)" }} title={t.printPreview}>{"\ud83d\udda8"}</button></td>
                  {/* Docs indicator \u2014 counts xeroInvoiceFile + bolFile
                      when either carries a Storage URL or legacy
                      base64. Hidden when count = 0. */}
                  <td style={{ padding: "9px 4px", textAlign: "center", width: 30 }}>
                    {(function () {
                      var n = ((inv.xeroInvoiceFile && (inv.xeroInvoiceFile.url || inv.xeroInvoiceFile.data)) ? 1 : 0)
                            + ((inv.bolFile && (inv.bolFile.url || inv.bolFile.data)) ? 1 : 0);
                      if (n === 0) return null;
                      return (
                        <span title={(lang === "es" ? "Archivos adjuntos: " : "Attached files: ") + n} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="#6B7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1.5 1h7l4 4v10h-11V1z"/>
                            <path d="M8.5 1v4h4" fill="#D1D5DB"/>
                            <line x1="4" y1="8" x2="10" y2="8" strokeWidth="1"/>
                            <line x1="4" y1="10.5" x2="10" y2="10.5" strokeWidth="1"/>
                            <line x1="4" y1="13" x2="7" y2="13" strokeWidth="1"/>
                          </svg>
                          <span style={{ fontSize: 10, fontWeight: 500, color: "#6B7280" }}>{n}</span>
                        </span>
                      );
                    })()}
                  </td>
                  {/* Email-sent indicator \u2014 envelope with green check. */}
                  <td style={{ padding: "9px 4px", textAlign: "center", width: 30 }}>
                    {inv.emailSentAt && (
                      <span title={(lang === "es" ? "Enviada por email " : "Emailed ") + new Date(inv.emailSentAt).toLocaleString()} style={{ display: "inline-flex", alignItems: "center" }}>
                        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="0.5" y="0.5" width="14" height="11" rx="2"/>
                          <path d="M0.5 0.5L7.5 6.5L14.5 0.5"/>
                          <circle cx="14" cy="10" r="3.5" fill="#059669" stroke="none"/>
                          <path d="M12.5 10L13.5 11L15.5 9" stroke="white" strokeWidth="1.5"/>
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {viewing && <DetailPanel
        invoice={viewing}
        invoices={invoices}
        t={t} lang={lang}
        onSave={function(updated) { handleSave(updated); setViewing(updated); }}
        onDelete={handleDelete}
        onEditLock={function(rec) { setViewing(null); setLockedRecord(rec); }}
        onClose={function() {
          setViewing(null);
          /* 2026-10-20, Arturo: "agregar a las facturas de four seasons
             el numero de invoice generado en sweetleaf ligada a esta
             factura con un link para que se abra el modulo sweetleaf
             de dicha factura y regrese aqui mismo" — same
             open-and-return convention InvoiceRegistry.js already uses
             for Bill/Shipment/PO links (window.__gfiReturnModule +
             a per-module reopen-id watcher). */
          if (window.__gfiReturnModule) {
            var ret = window.__gfiReturnModule; window.__gfiReturnModule = null;
            if (window.__gfiNavigate) window.__gfiNavigate(ret);
          }
        }}
        onPrint={function(inv) { setShowPrint(inv); }}
        onPrintDoc={handleSalesDocSingle}
      />}
      {showForm && !viewing && <InvoiceForm invoice={editing} invoices={invoices} t={t} lang={lang} onSave={handleSave} onDelete={handleDelete} onCancel={function() { setShowForm(false); setEditing(null); }} />}
      {passingModalInvoice && (
        <EmailPassingModal
          invoice={passingModalInvoice}
          lang={lang}
          onClose={function () { setPassingModalInvoice(null); }}
          onSent={function () {
            persistSingleInv(Object.assign({}, passingModalInvoice, { passingSentAt: new Date().toISOString() }))
              .catch(function (e) { console.warn("[invoices] could not stamp passingSentAt:", e && e.message); });
            setToast(lang === "es" ? "✓ Passing enviado — status de pallet cambia a Pendiente de Entrega" : "✓ Passing sent — pallet status changes to Pending Delivery");
          }}
        />
      )}
      {showBatchDeposit && <BatchDepositModal invoices={selectedInvoices} t={t} lang={lang} onConfirm={handleBatchDepositConfirm} onCancel={function() { setShowBatchDeposit(false); }} />}
      {lockedRecord && <EditLockDialog record={lockedRecord} t={t} onClose={function() { setLockedRecord(null); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lockedRecords={bulkLocked} lang={lang} onCancel={function() { setShowBulkDelete(false); }} onConfirm={handleBulkDelete} />}
      {showBulkStatus && <BulkStatusDialog count={selectedCount} lockedRecords={bulkLocked} statuses={INV_STATUSES.map(function(s) { return { value: s, label: t[s === "draft" ? "statusDraft" : s === "sent" ? "statusSent" : "statusPaid"] || s }; })} lang={lang} onCancel={function() { setShowBulkStatus(false); }} onConfirm={handleBulkStatusChange} />}
      {confirmationPayment && (function() { var pr = partyAndRowsForInvoiceDeposit(confirmationPayment, { invoices: invoices, brand: "sl" }); return <PaymentConfirmationPreview payment={confirmationPayment} party={pr.party} rows={pr.rows} lang={lang} onClose={function() { setConfirmationPayment(null); }} />; })()}
      {showPrint && <PrintPreview invoice={showPrint} t={t} onClose={function() { setShowPrint(null); }} />}
      {lotPreviewBill && <BillPreviewModal bill={lotPreviewBill} lang={lang} onClose={function() { setLotPreviewBill(null); }} />}
      {pickKindModal && <PickKindSelector
        brandLabel={pickKindModal.brand === "sl" ? "SweetLeaf Produce" : "Ground-Fresh"}
        lang={lang}
        onCancel={function () { setPickKindModal(null); }}
        onPick={function (chosenKind) {
          /* chosenKind = "pick" | "inbound".
             - Pick Ticket → second step (remarks modal) before printing
             - Inbound     → print directly, no extra prompt */
          var payload = { brand: pickKindModal.brand, invoices: pickKindModal.invoices };
          setPickKindModal(null);
          if (chosenKind === "pick") {
            setRemarksModal(payload);
          } else {
            doPrint(payload.invoices, payload.brand + "-inbound");
          }
        }}
      />}
      {remarksModal && (function () {
        /* Default Loading Date = ETA + 1 day per spec (Sunday→Tuesday
           after Monday arrival, etc.). Resolved from the FIRST invoice's
           shipment ETA — for bulk prints the user can adjust if needed.
           Falls back to today if no shipment ETA is available. */
        var ctx = buildSalesDocCtx();
        var firstInv = remarksModal.invoices[0];
        var firstShp = firstInv ? findShipmentForInvoice(firstInv, ctx.shipments) : null;
        var eta = firstShp ? (firstShp.dateArrival || "") : "";
        var defaultLD = "";
        if (eta) {
          var d = new Date(eta + "T00:00:00");
          if (!isNaN(d.getTime())) {
            d.setDate(d.getDate() + 1);
            defaultLD = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
          }
        }
        return <PickTicketRemarksModal
          brandLabel={remarksModal.brand === "sl" ? "SweetLeaf Produce" : "Ground-Fresh"}
          lang={lang}
          defaultLoadingDate={defaultLD}
          onCancel={function () { setRemarksModal(null); }}
          onPrint={function (remarksLines, chosenLoadingDate) {
            /* User confirmed — fire the print with remarks + chosen
               Loading Date. The Loading Date overrides the auto-default
               (shipment.dateDeparture) inside the builder. */
            var invs = remarksModal.invoices;
            var brand = remarksModal.brand;
            setRemarksModal(null);
            doPrint(invs, brand + "-pick", { remarks: remarksLines, loadingDate: chosenLoadingDate });
          }}
        />;
      })()}
      {overdueNoticeOpen && (
        <OverdueNoticeModal
          invoices={selectedInvoices}
          lang={lang}
          onClose={function () { setOverdueNoticeOpen(false); }}
          onSent={function () { setToast(lang === "es" ? "Aviso de vencidas enviado" : "Past due notice sent"); setOverdueNoticeOpen(false); }}
        />
      )}
      {emailFactoringOpen && (
        <EmailFactoringModal
          invoices={invoices}
          lang={lang}
          t={t}
          onClose={function () { setEmailFactoringOpen(false); }}
          onSent={function () { setToast(t.emailFactoringSent); setEmailFactoringOpen(false); }}
          onMarkSent={function (sentInvoices, sentAt) {
            return persistManyInv(sentInvoices.map(function (inv) { return Object.assign({}, inv, { factoringEmailSentAt: sentAt }); }));
          }}
        />
      )}
      {toast && <Toast message={toast} onClose={function() { setToast(null); }} />}
    </div>
  );
}